import * as i0 from '@angular/core';
import { InjectionToken, Optional, Inject, Injectable, SkipSelf, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  'csv': ',',
  'tsv': '   ',
  'ssv': ' ',
  'pipes': '|'
};
class Configuration {
  /**
   *  @deprecated Since 5.0. Use credentials instead
   */
  apiKeys;
  username;
  password;
  /**
   *  @deprecated Since 5.0. Use credentials instead
   */
  accessToken;
  basePath;
  withCredentials;
  /**
   * Takes care of encoding query- and form-parameters.
   */
  encoder;
  /**
   * Encoding of various path parameter
   * <a href="https://github.com/OAI/OpenAPI-Specification/blob/main/versions/3.1.0.md#style-values">styles</a>.
   * <p>
   * See {@link README.md} for more details
   * </p>
   */
  encodeParam;
  /**
   * The keys are the names in the securitySchemes section of the OpenAPI
   * document. They should map to the value used for authentication
   * minus any standard prefixes such as 'Basic' or 'Bearer'.
   */
  credentials;
  constructor(configurationParameters = {}) {
    this.apiKeys = configurationParameters.apiKeys;
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;
    this.withCredentials = configurationParameters.withCredentials;
    this.encoder = configurationParameters.encoder;
    if (configurationParameters.encodeParam) {
      this.encodeParam = configurationParameters.encodeParam;
    } else {
      this.encodeParam = param => this.defaultEncodeParam(param);
    }
    if (configurationParameters.credentials) {
      this.credentials = configurationParameters.credentials;
    } else {
      this.credentials = {};
    }
    // init default jwtAuth credential
    if (!this.credentials['jwtAuth']) {
      this.credentials['jwtAuth'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class AccountingService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  accountingGetInvoicesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/accounting/get_invoices`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function AccountingService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || AccountingService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: AccountingService,
    factory: AccountingService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AccountingService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ActivityStreamService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  activityStreamCommentCreate(postCommentRequest, observe = 'body', reportProgress = false, options) {
    if (postCommentRequest === null || postCommentRequest === undefined) {
      throw new Error('Required parameter postCommentRequest was null or undefined when calling activityStreamCommentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/comment`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCommentRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamCommentDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling activityStreamCommentDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/comment`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamCommentList(activityStreamCommonId, streamType, page, pageSize, parentId, viewerBusinessId, observe = 'body', reportProgress = false, options) {
    if (activityStreamCommonId === null || activityStreamCommonId === undefined) {
      throw new Error('Required parameter activityStreamCommonId was null or undefined when calling activityStreamCommentList.');
    }
    if (streamType === null || streamType === undefined) {
      throw new Error('Required parameter streamType was null or undefined when calling activityStreamCommentList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (activityStreamCommonId !== undefined && activityStreamCommonId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, activityStreamCommonId, 'activity_stream_common_id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (parentId !== undefined && parentId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, parentId, 'parent_id');
    }
    if (streamType !== undefined && streamType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, streamType, 'stream_type');
    }
    if (viewerBusinessId !== undefined && viewerBusinessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, viewerBusinessId, 'viewer_business_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/comment`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamPostCreate(createPostRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/post`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: createPostRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamPostDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling activityStreamPostDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/post`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamPostDetailRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/post_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamPostList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/post`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamPostPartialUpdate(patchedPatchPostRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/post`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchPostRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamPostPollCreate(postPollRequest, observe = 'body', reportProgress = false, options) {
    if (postPollRequest === null || postPollRequest === undefined) {
      throw new Error('Required parameter postPollRequest was null or undefined when calling activityStreamPostPollCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/post_poll`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPollRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamReactionCreate(postReactionRequest, observe = 'body', reportProgress = false, options) {
    if (postReactionRequest === null || postReactionRequest === undefined) {
      throw new Error('Required parameter postReactionRequest was null or undefined when calling activityStreamReactionCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/reaction`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postReactionRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamReactionDestroy(activityStreamCommonId, id, observe = 'body', reportProgress = false, options) {
    if (activityStreamCommonId === null || activityStreamCommonId === undefined) {
      throw new Error('Required parameter activityStreamCommonId was null or undefined when calling activityStreamReactionDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling activityStreamReactionDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (activityStreamCommonId !== undefined && activityStreamCommonId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, activityStreamCommonId, 'activity_stream_common_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/reaction`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamReactionList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/reaction`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamReactionTypesList(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/reaction_types`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamResourceExchangeCreate(postResourceExchangeRequest, observe = 'body', reportProgress = false, options) {
    if (postResourceExchangeRequest === null || postResourceExchangeRequest === undefined) {
      throw new Error('Required parameter postResourceExchangeRequest was null or undefined when calling activityStreamResourceExchangeCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/resource_exchange`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postResourceExchangeRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamResourceExchangeDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling activityStreamResourceExchangeDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/resource_exchange`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamVotePollCreate(postVotePollRequest, observe = 'body', reportProgress = false, options) {
    if (postVotePollRequest === null || postVotePollRequest === undefined) {
      throw new Error('Required parameter postVotePollRequest was null or undefined when calling activityStreamVotePollCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/vote_poll`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postVotePollRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  activityStreamVotePollDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling activityStreamVotePollDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/activity_stream/vote_poll`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ActivityStreamService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ActivityStreamService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ActivityStreamService,
    factory: ActivityStreamService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ActivityStreamService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class AdsService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  adsAdCalendarAvailabilityList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/ads/ad_calendar_availability`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  adsAdComponentDetailList(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/ads/ad_component_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  adsAdDetailRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/ads/ad_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  adsAdPaymentDetailList(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/ads/ad_payment_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  adsAdsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/ads/ads`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function AdsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || AdsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: AdsService,
    factory: AdsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AdsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ApiService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  apiTokenBlacklistCreate(tokenBlacklistRequest, observe = 'body', reportProgress = false, options) {
    if (tokenBlacklistRequest === null || tokenBlacklistRequest === undefined) {
      throw new Error('Required parameter tokenBlacklistRequest was null or undefined when calling apiTokenBlacklistCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/api/token/blacklist/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: tokenBlacklistRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  apiTokenVerifyCreate(tokenVerifyRequest, observe = 'body', reportProgress = false, options) {
    if (tokenVerifyRequest === null || tokenVerifyRequest === undefined) {
      throw new Error('Required parameter tokenVerifyRequest was null or undefined when calling apiTokenVerifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/api/token/verify/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: tokenVerifyRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ApiService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApiService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ApiService,
    factory: ApiService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ApiService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class AuthService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  authTokenCreate(jwtObtainPairRequest, observe = 'body', reportProgress = false, options) {
    if (jwtObtainPairRequest === null || jwtObtainPairRequest === undefined) {
      throw new Error('Required parameter jwtObtainPairRequest was null or undefined when calling authTokenCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/auth/token/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: jwtObtainPairRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  authTokenRefreshCreate(tokenRefreshRequest, observe = 'body', reportProgress = false, options) {
    if (tokenRefreshRequest === null || tokenRefreshRequest === undefined) {
      throw new Error('Required parameter tokenRefreshRequest was null or undefined when calling authTokenRefreshCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/auth/token/refresh/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: tokenRefreshRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function AuthService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || AuthService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: AuthService,
    factory: AuthService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class BanksService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  banksBankAccountCreate(postBankAccountRequest, observe = 'body', reportProgress = false, options) {
    if (postBankAccountRequest === null || postBankAccountRequest === undefined) {
      throw new Error('Required parameter postBankAccountRequest was null or undefined when calling banksBankAccountCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/bank_account`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBankAccountRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksBankAccountDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling banksBankAccountDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling banksBankAccountDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/bank_account`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksBankAccountList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/bank_account`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksPaymentMethodAccountCreate(postPaymentOnSpotAccountRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/payment_method_account`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPaymentOnSpotAccountRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksPaymentMethodAccountDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling banksPaymentMethodAccountDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling banksPaymentMethodAccountDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/payment_method_account`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksPaymentMethodAccountList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/payment_method_account`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksPaymentModifyCreate(postPaymentModifyRequest, observe = 'body', reportProgress = false, options) {
    if (postPaymentModifyRequest === null || postPaymentModifyRequest === undefined) {
      throw new Error('Required parameter postPaymentModifyRequest was null or undefined when calling banksPaymentModifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/payment_modify`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPaymentModifyRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  banksPaymentModifyDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling banksPaymentModifyDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/banks/payment_modify`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function BanksService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || BanksService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: BanksService,
    factory: BanksService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BanksService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class BidService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  bidAdsBidList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/bid/ads_bid`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  bidBidCreate(postSmartBidRequest, observe = 'body', reportProgress = false, options) {
    if (postSmartBidRequest === null || postSmartBidRequest === undefined) {
      throw new Error('Required parameter postSmartBidRequest was null or undefined when calling bidBidCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/bid/bid`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartBidRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  bidBidDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling bidBidDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/bid/bid`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  bidBidList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/bid/bid`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function BidService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || BidService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: BidService,
    factory: BidService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BidService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class BrandedDomainHandlingService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  brandedDomainHandlingDomainCreate(postDomainRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDomainRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainDestroy(id, siteId, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling brandedDomainHandlingDomainDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (siteId !== undefined && siteId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, siteId, 'site_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPageCreate(postDomainPageRequest, observe = 'body', reportProgress = false, options) {
    if (postDomainPageRequest === null || postDomainPageRequest === undefined) {
      throw new Error('Required parameter postDomainPageRequest was null or undefined when calling brandedDomainHandlingDomainPageCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_page`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDomainPageRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPageDestroy(id, languageId, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling brandedDomainHandlingDomainPageDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (languageId !== undefined && languageId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, languageId, 'language_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_page`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPagePartialUpdate(patchedPatchDomainPageRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_page`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchDomainPageRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPreviewPageDetailsRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_preview_page_details`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPreviewPagesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_preview_pages`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPreviewSiteDetailsRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_preview_site_details`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPreviewSitesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_preview_sites`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPreviewWidgetCollectionDetailsRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_preview_widget_collection_details`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainPreviewWidgetCollectionsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_preview_widget_collections`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainSiteCreate(postDomainSiteRequest, observe = 'body', reportProgress = false, options) {
    if (postDomainSiteRequest === null || postDomainSiteRequest === undefined) {
      throw new Error('Required parameter postDomainSiteRequest was null or undefined when calling brandedDomainHandlingDomainSiteCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_site`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDomainSiteRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainSiteDestroy(id, languageId, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling brandedDomainHandlingDomainSiteDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (languageId !== undefined && languageId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, languageId, 'language_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_site`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainSiteList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_site`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainSitePartialUpdate(patchedPatchDomainSiteRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_site`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchDomainSiteRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainTranslationCreate(postDomainTranslationRequest, observe = 'body', reportProgress = false, options) {
    if (postDomainTranslationRequest === null || postDomainTranslationRequest === undefined) {
      throw new Error('Required parameter postDomainTranslationRequest was null or undefined when calling brandedDomainHandlingDomainTranslationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_translation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDomainTranslationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingDomainTranslationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/domain_translation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  brandedDomainHandlingPublishedDomainPageRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/branded_domain_handling/published_domain_page`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function BrandedDomainHandlingService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || BrandedDomainHandlingService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: BrandedDomainHandlingService,
    factory: BrandedDomainHandlingService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BrandedDomainHandlingService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class BusinessRulesService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  businessRulesBidIncrementRuleCreate(postBidIncrementRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postBidIncrementRuleRequest === null || postBidIncrementRuleRequest === undefined) {
      throw new Error('Required parameter postBidIncrementRuleRequest was null or undefined when calling businessRulesBidIncrementRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/bid_increment_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBidIncrementRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesBidIncrementRuleDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling businessRulesBidIncrementRuleDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling businessRulesBidIncrementRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/bid_increment_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesBidIncrementRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/bid_increment_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesInsuranceRuleCreate(postInsuranceRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postInsuranceRuleRequest === null || postInsuranceRuleRequest === undefined) {
      throw new Error('Required parameter postInsuranceRuleRequest was null or undefined when calling businessRulesInsuranceRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/insurance_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postInsuranceRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesInsuranceRuleDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling businessRulesInsuranceRuleDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling businessRulesInsuranceRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/insurance_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesInsuranceRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/insurance_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesMarketDiscountCreate(postMarketDiscountRequest, observe = 'body', reportProgress = false, options) {
    if (postMarketDiscountRequest === null || postMarketDiscountRequest === undefined) {
      throw new Error('Required parameter postMarketDiscountRequest was null or undefined when calling businessRulesMarketDiscountCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/market_discount`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postMarketDiscountRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesMarketDiscountDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling businessRulesMarketDiscountDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling businessRulesMarketDiscountDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/market_discount`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesMarketDiscountList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/market_discount`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesMarketDiscountPartialUpdate(patchedPatchMarketDiscountRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/market_discount`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchMarketDiscountRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesPublicMarketDiscountList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/public_market_discount`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesShippingRuleCreate(postShippingRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postShippingRuleRequest === null || postShippingRuleRequest === undefined) {
      throw new Error('Required parameter postShippingRuleRequest was null or undefined when calling businessRulesShippingRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/shipping_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postShippingRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesShippingRuleDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling businessRulesShippingRuleDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling businessRulesShippingRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/shipping_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesShippingRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/shipping_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesSmartPoliciesCreate(postSmartPolicyRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/smart_policies`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartPolicyRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesSmartPoliciesDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling businessRulesSmartPoliciesDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/smart_policies`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesSmartPoliciesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/smart_policies`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesTaxRuleCreate(postTaxRuleRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/tax_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTaxRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesTaxRuleDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling businessRulesTaxRuleDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling businessRulesTaxRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/tax_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  businessRulesTaxRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/business_rules/tax_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function BusinessRulesService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || BusinessRulesService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: BusinessRulesService,
    factory: BusinessRulesService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(BusinessRulesService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class CalendarService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  calendarAppointmentCreate(calendarAppointmentCreateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarAppointmentCreateRequest === null || calendarAppointmentCreateRequest === undefined) {
      throw new Error('Required parameter calendarAppointmentCreateRequest was null or undefined when calling calendarAppointmentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/appointment`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarAppointmentCreateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarAppointmentDestroy(appointmentId, calendarId, observe = 'body', reportProgress = false, options) {
    if (appointmentId === null || appointmentId === undefined) {
      throw new Error('Required parameter appointmentId was null or undefined when calling calendarAppointmentDestroy.');
    }
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarAppointmentDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (appointmentId !== undefined && appointmentId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, appointmentId, 'appointment_id');
    }
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/appointment`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarAppointmentList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/appointment`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarAvailabilityCreate(postAvaibilityRequest, observe = 'body', reportProgress = false, options) {
    if (postAvaibilityRequest === null || postAvaibilityRequest === undefined) {
      throw new Error('Required parameter postAvaibilityRequest was null or undefined when calling calendarAvailabilityCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/availability`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postAvaibilityRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarCreate(calendarCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarCreateOrUpdateRequest === null || calendarCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarCreateOrUpdateRequest was null or undefined when calling calendarCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarDayPriceModifyCreate(calendarCalendarDayPriceCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarCalendarDayPriceCreateOrUpdateRequest === null || calendarCalendarDayPriceCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarCalendarDayPriceCreateOrUpdateRequest was null or undefined when calling calendarDayPriceModifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/day_price_modify`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarCalendarDayPriceCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarDayPriceModifyDestroy(calendarId, id, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarDayPriceModifyDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling calendarDayPriceModifyDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/day_price_modify`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarDayPriceModifyList(calendarId, id, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarDayPriceModifyList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/day_price_modify`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarDestroy(businessId, calendarId, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling calendarDestroy.');
    }
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarGetCancellationBufferTimeList(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/get_cancellation_buffer_time`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarGetMaxAdviceBookingTimeList(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/get_max_advice_booking_time`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarGetMinAdviceBookingTimeList(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/get_min_advice_booking_time`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarGetRescheduleBufferTimeList(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/get_reschedule_buffer_time`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarHourPriceModifyCreate(calendarCalendarHourPriceCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarCalendarHourPriceCreateOrUpdateRequest === null || calendarCalendarHourPriceCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarCalendarHourPriceCreateOrUpdateRequest was null or undefined when calling calendarHourPriceModifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/hour_price_modify`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarCalendarHourPriceCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarHourPriceModifyDestroy(calendarId, id, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarHourPriceModifyDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling calendarHourPriceModifyDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/hour_price_modify`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarHourPriceModifyList(calendarId, id, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarHourPriceModifyList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/hour_price_modify`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleBreakCreate(calendarBreakTimeCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarBreakTimeCreateOrUpdateRequest === null || calendarBreakTimeCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarBreakTimeCreateOrUpdateRequest was null or undefined when calling calendarScheduleBreakCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_break`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarBreakTimeCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleBreakDestroy(calendarId, id, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarScheduleBreakDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling calendarScheduleBreakDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_break`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleBreakList(calendarId, id, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarScheduleBreakList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_break`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleTimeOffCreate(calendarCalendarScheduleTimeOffCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarCalendarScheduleTimeOffCreateOrUpdateRequest === null || calendarCalendarScheduleTimeOffCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarCalendarScheduleTimeOffCreateOrUpdateRequest was null or undefined when calling calendarScheduleTimeOffCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_time_off`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarCalendarScheduleTimeOffCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleTimeOffDayCreate(calendarCalendarScheduleTimeOffDayCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarCalendarScheduleTimeOffDayCreateOrUpdateRequest === null || calendarCalendarScheduleTimeOffDayCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarCalendarScheduleTimeOffDayCreateOrUpdateRequest was null or undefined when calling calendarScheduleTimeOffDayCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_time_off_day`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarCalendarScheduleTimeOffDayCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleTimeOffDayDestroy(calendarId, id, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarScheduleTimeOffDayDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling calendarScheduleTimeOffDayDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_time_off_day`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleTimeOffDayList(calendarId, id, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarScheduleTimeOffDayList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_time_off_day`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleTimeOffDestroy(calendarId, id, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarScheduleTimeOffDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling calendarScheduleTimeOffDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_time_off`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarScheduleTimeOffList(calendarId, id, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarScheduleTimeOffList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/schedule_time_off`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarTimePeriodPriceModifyCreate(calendarTimePeriodPriceCreateOrUpdateRequest, observe = 'body', reportProgress = false, options) {
    if (calendarTimePeriodPriceCreateOrUpdateRequest === null || calendarTimePeriodPriceCreateOrUpdateRequest === undefined) {
      throw new Error('Required parameter calendarTimePeriodPriceCreateOrUpdateRequest was null or undefined when calling calendarTimePeriodPriceModifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/time_period_price_modify`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: calendarTimePeriodPriceCreateOrUpdateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarTimePeriodPriceModifyDestroy(calendarId, id, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarTimePeriodPriceModifyDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling calendarTimePeriodPriceModifyDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/time_period_price_modify`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  calendarTimePeriodPriceModifyList(calendarId, id, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (calendarId === null || calendarId === undefined) {
      throw new Error('Required parameter calendarId was null or undefined when calling calendarTimePeriodPriceModifyList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (calendarId !== undefined && calendarId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, calendarId, 'calendar_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/calendar/time_period_price_modify`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function CalendarService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CalendarService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: CalendarService,
    factory: CalendarService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CalendarService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ChatService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  chatChatSettingsPartialUpdate(patchedPatchChatSettingsRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/chat_settings/`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchChatSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatMessageReadCreate(messageReadRequest, observe = 'body', reportProgress = false, options) {
    if (messageReadRequest === null || messageReadRequest === undefined) {
      throw new Error('Required parameter messageReadRequest was null or undefined when calling chatMessageReadCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/message_read/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: messageReadRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatMessageTemplatesCreate(postMessageTemplateRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/message_templates/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postMessageTemplateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatMessagesDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling chatMessagesDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/messages/`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatMessagesList(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/messages/`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomMembersCreate(postRoomMemberRequest, observe = 'body', reportProgress = false, options) {
    if (postRoomMemberRequest === null || postRoomMemberRequest === undefined) {
      throw new Error('Required parameter postRoomMemberRequest was null or undefined when calling chatRoomMembersCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/room_members/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postRoomMemberRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomMembersDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling chatRoomMembersDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/room_members/`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomMembersList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/room_members/`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomsCreate(postRoomRequest, observe = 'body', reportProgress = false, options) {
    if (postRoomRequest === null || postRoomRequest === undefined) {
      throw new Error('Required parameter postRoomRequest was null or undefined when calling chatRoomsCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/rooms/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postRoomRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomsDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling chatRoomsDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/rooms/`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/rooms/`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatRoomsPartialUpdate(patchedPatchRoomRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/rooms/`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchRoomRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  chatStartCallCreate(startVideoCallRequest, observe = 'body', reportProgress = false, options) {
    if (startVideoCallRequest === null || startVideoCallRequest === undefined) {
      throw new Error('Required parameter startVideoCallRequest was null or undefined when calling chatStartCallCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/chat/start_call`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: startVideoCallRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ChatService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ChatService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ChatService,
    factory: ChatService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ChatService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class CommunityApplicationService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  communityApplicationActivateCreate(activateCommunityApplicationProfileRequest, observe = 'body', reportProgress = false, options) {
    if (activateCommunityApplicationProfileRequest === null || activateCommunityApplicationProfileRequest === undefined) {
      throw new Error('Required parameter activateCommunityApplicationProfileRequest was null or undefined when calling communityApplicationActivateCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/activate`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: activateCommunityApplicationProfileRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationAllActiveProfilesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/all_active_profiles`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationExcludeLanguagesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/exclude_languages`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationLanguagesCreate(postLanguageToCommunityApplicationRequest, observe = 'body', reportProgress = false, options) {
    if (postLanguageToCommunityApplicationRequest === null || postLanguageToCommunityApplicationRequest === undefined) {
      throw new Error('Required parameter postLanguageToCommunityApplicationRequest was null or undefined when calling communityApplicationLanguagesCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/languages`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postLanguageToCommunityApplicationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationLanguagesDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling communityApplicationLanguagesDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/languages`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationLanguagesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/languages`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationProfileSettingsCreate(postCommunityApplicationSettingsRequest, observe = 'body', reportProgress = false, options) {
    if (postCommunityApplicationSettingsRequest === null || postCommunityApplicationSettingsRequest === undefined) {
      throw new Error('Required parameter postCommunityApplicationSettingsRequest was null or undefined when calling communityApplicationProfileSettingsCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/profile_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCommunityApplicationSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationProfileSettingsRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/profile_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationPublicProfileRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/public_profile`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  communityApplicationVisitorsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/community_application/visitors`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function CommunityApplicationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CommunityApplicationService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: CommunityApplicationService,
    factory: CommunityApplicationService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CommunityApplicationService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ContactsService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  contactsContactCreate(postSharedContactRequest, observe = 'body', reportProgress = false, options) {
    if (postSharedContactRequest === null || postSharedContactRequest === undefined) {
      throw new Error('Required parameter postSharedContactRequest was null or undefined when calling contactsContactCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/contacts/contact`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSharedContactRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  contactsContactDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling contactsContactDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/contacts/contact`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  contactsContactList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/contacts/contact`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  contactsContactsEmailList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/contacts/contacts_email`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ContactsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ContactsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ContactsService,
    factory: ContactsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContactsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class CoworkersService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  coworkersBusinessActiveCoWorkersList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/coworkers/business_active_co_workers`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  coworkersBusinessSelectCoWorkersList(businessId, coWorkerId, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling coworkersBusinessSelectCoWorkersList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (coWorkerId !== undefined && coWorkerId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, coWorkerId, 'co_worker_id');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/coworkers/business_select_co_workers`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function CoworkersService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CoworkersService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: CoworkersService,
    factory: CoworkersService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CoworkersService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class CrmService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  crmContactUsCreate(postCrmContactRequest, observe = 'body', reportProgress = false, options) {
    if (postCrmContactRequest === null || postCrmContactRequest === undefined) {
      throw new Error('Required parameter postCrmContactRequest was null or undefined when calling crmContactUsCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/contact_us`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCrmContactRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmContactUsDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/contact_us`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmContactUsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/contact_us`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealCreate(postDealRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDealRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling crmDealDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealDetailsRatingCreate(postDealDetailsRatingRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal_details_rating`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDealDetailsRatingRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealDetailsRatingList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal_details_rating`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealRatingCreate(postDealRatingsRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal_rating`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDealRatingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealRatingList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal_rating`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmDealRatingSummaryRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/deal_rating_summary`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmLeadsCreate(postCrmLeadRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/leads`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCrmLeadRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmLeadsDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/leads`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmLeadsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/leads`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmOffersCreate(postCrmOfferRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/offers`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCrmOfferRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmOffersDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/offers`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmOffersList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/offers`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmPostBusinessProspectCreate(postBusinessRequest, observe = 'body', reportProgress = false, options) {
    if (postBusinessRequest === null || postBusinessRequest === undefined) {
      throw new Error('Required parameter postBusinessRequest was null or undefined when calling crmPostBusinessProspectCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/post_business_prospect`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBusinessRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmPostPersonProspectCreate(postPersonRequest, observe = 'body', reportProgress = false, options) {
    if (postPersonRequest === null || postPersonRequest === undefined) {
      throw new Error('Required parameter postPersonRequest was null or undefined when calling crmPostPersonProspectCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/post_person_prospect`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPersonRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectAddUserOrBusinessCreate(postCoreUserOrBusinessRequest, observe = 'body', reportProgress = false, options) {
    if (postCoreUserOrBusinessRequest === null || postCoreUserOrBusinessRequest === undefined) {
      throw new Error('Required parameter postCoreUserOrBusinessRequest was null or undefined when calling crmProspectAddUserOrBusinessCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect_add_user_or_business`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCoreUserOrBusinessRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectAddUserOrBusinessDestroy(jsonField, observe = 'body', reportProgress = false, options) {
    if (jsonField === null || jsonField === undefined) {
      throw new Error('Required parameter jsonField was null or undefined when calling crmProspectAddUserOrBusinessDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (jsonField !== undefined && jsonField !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, jsonField, 'json_field');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect_add_user_or_business`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectCreate(crmProspectRequestRequest, observe = 'body', reportProgress = false, options) {
    if (crmProspectRequestRequest === null || crmProspectRequestRequest === undefined) {
      throw new Error('Required parameter crmProspectRequestRequest was null or undefined when calling crmProspectCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: crmProspectRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectDestroy(prospectId, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (prospectId !== undefined && prospectId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, prospectId, 'prospect_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectEditCreate(postChangeResponsibleCoWorkerRequest, observe = 'body', reportProgress = false, options) {
    if (postChangeResponsibleCoWorkerRequest === null || postChangeResponsibleCoWorkerRequest === undefined) {
      throw new Error('Required parameter postChangeResponsibleCoWorkerRequest was null or undefined when calling crmProspectEditCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect_edit`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postChangeResponsibleCoWorkerRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectNoitifyCreate(postCrmNotifyRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect_noitify`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCrmNotifyRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectNoitifyList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect_noitify`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmProspectPartialUpdate(patchedPatchCrmProspectRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/prospect`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchCrmProspectRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmReactivateProspectCreate(reactiveCrmProspectRequestRequest, observe = 'body', reportProgress = false, options) {
    if (reactiveCrmProspectRequestRequest === null || reactiveCrmProspectRequestRequest === undefined) {
      throw new Error('Required parameter reactiveCrmProspectRequestRequest was null or undefined when calling crmReactivateProspectCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/reactivate_prospect`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: reactiveCrmProspectRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmUserProspectToBusinessProspectCreate(postCrmBusinessUserProspectRequest, observe = 'body', reportProgress = false, options) {
    if (postCrmBusinessUserProspectRequest === null || postCrmBusinessUserProspectRequest === undefined) {
      throw new Error('Required parameter postCrmBusinessUserProspectRequest was null or undefined when calling crmUserProspectToBusinessProspectCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/user_prospect_to_business_prospect`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCrmBusinessUserProspectRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmUserProspectToBusinessProspectDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling crmUserProspectToBusinessProspectDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/user_prospect_to_business_prospect`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  crmUserProspectToBusinessProspectList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/crm/user_prospect_to_business_prospect`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function CrmService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CrmService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: CrmService,
    factory: CrmService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CrmService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class DriveService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  /**
  * @param consumes string[] mime-types
  * @return true: consumes contains 'multipart/form-data', false: otherwise
  */
  canConsumeForm(consumes) {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  driveAlbumCreate(postDriveAlbumRequest, observe = 'body', reportProgress = false, options) {
    if (postDriveAlbumRequest === null || postDriveAlbumRequest === undefined) {
      throw new Error('Required parameter postDriveAlbumRequest was null or undefined when calling driveAlbumCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDriveAlbumRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveAlbumDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveAlbumItemCreate(deleteDriveAlbumItemRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album_item`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: deleteDriveAlbumItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveAlbumItemList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album_item`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveAlbumItemUpdate(id, businessId, comment, description, name, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling driveAlbumItemUpdate.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (comment !== undefined && comment !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, comment, 'comment');
    }
    if (description !== undefined && description !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, description, 'description');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (name !== undefined && name !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, name, 'name');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album_item`;
    return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveAlbumList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveAlbumUpdate(id, businessId, comment, description, name, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling driveAlbumUpdate.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (comment !== undefined && comment !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, comment, 'comment');
    }
    if (description !== undefined && description !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, description, 'description');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (name !== undefined && name !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, name, 'name');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/album`;
    return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveMediaCreate(postDriveMediaDeletionRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/media`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postDriveMediaDeletionRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveMoveCreate(postMoveDriveAlbumMediaRequest, observe = 'body', reportProgress = false, options) {
    if (postMoveDriveAlbumMediaRequest === null || postMoveDriveAlbumMediaRequest === undefined) {
      throw new Error('Required parameter postMoveDriveAlbumMediaRequest was null or undefined when calling driveMoveCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/move`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postMoveDriveAlbumMediaRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  drivePermanentDeleteCreate(putRestoreDriveItemRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/permanent_delete`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: putRestoreDriveItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveRestoreCreate(putRestoreDriveItemRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/restore`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: putRestoreDriveItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveRestoreList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/restore`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  driveUploadCreate(contentFile, id, businessId, contentUploadType, comment, description, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['multipart/form-data', 'application/x-www-form-urlencoded'];
    const canConsumeForm = this.canConsumeForm(consumes);
    let localVarFormParams;
    let localVarUseForm = false;
    let localVarConvertFormParamsToString = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    localVarUseForm = canConsumeForm;
    if (localVarUseForm) {
      localVarFormParams = new FormData();
    } else {
      localVarFormParams = new HttpParams({
        encoder: this.encoder
      });
    }
    if (contentFile !== undefined) {
      localVarFormParams = localVarFormParams.append('content_file', contentFile) || localVarFormParams;
    }
    if (id !== undefined) {
      localVarFormParams = localVarFormParams.append('id', id) || localVarFormParams;
    }
    if (businessId !== undefined) {
      localVarFormParams = localVarFormParams.append('business_id', businessId) || localVarFormParams;
    }
    if (contentUploadType !== undefined) {
      localVarFormParams = localVarFormParams.append('content_upload_type', contentUploadType) || localVarFormParams;
    }
    if (comment !== undefined) {
      localVarFormParams = localVarFormParams.append('comment', comment) || localVarFormParams;
    }
    if (description !== undefined) {
      localVarFormParams = localVarFormParams.append('description', description) || localVarFormParams;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/drive/upload`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function DriveService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DriveService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DriveService,
    factory: DriveService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DriveService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ElasticService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  elasticBusinessSearchCreate(postElasticSearchBusinessRequest, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (postElasticSearchBusinessRequest === null || postElasticSearchBusinessRequest === undefined) {
      throw new Error('Required parameter postElasticSearchBusinessRequest was null or undefined when calling elasticBusinessSearchCreate.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/elastic/business_search`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postElasticSearchBusinessRequest,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  elasticPublicSearchCreate(postElasticSearchPublicRequest, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (postElasticSearchPublicRequest === null || postElasticSearchPublicRequest === undefined) {
      throw new Error('Required parameter postElasticSearchPublicRequest was null or undefined when calling elasticPublicSearchCreate.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/elastic/public_search`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postElasticSearchPublicRequest,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ElasticService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ElasticService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ElasticService,
    factory: ElasticService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ElasticService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class EmailClientService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  emailClientAddEmailToCoWorkerBoxesCreate(postAddEmailToCoWorkerBoxesRequest, observe = 'body', reportProgress = false, options) {
    if (postAddEmailToCoWorkerBoxesRequest === null || postAddEmailToCoWorkerBoxesRequest === undefined) {
      throw new Error('Required parameter postAddEmailToCoWorkerBoxesRequest was null or undefined when calling emailClientAddEmailToCoWorkerBoxesCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/add_email_to_co_worker_boxes`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postAddEmailToCoWorkerBoxesRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientBusinessEmailAddressList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/business_email_address`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientDeleteEmailDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling emailClientDeleteEmailDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/delete_email`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientEmailBoxesCreate(postEmailBoxesRequest, observe = 'body', reportProgress = false, options) {
    if (postEmailBoxesRequest === null || postEmailBoxesRequest === undefined) {
      throw new Error('Required parameter postEmailBoxesRequest was null or undefined when calling emailClientEmailBoxesCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/email_boxes`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postEmailBoxesRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientEmailBoxesDestroy(emailBoxId, coWorkerId, observe = 'body', reportProgress = false, options) {
    if (emailBoxId === null || emailBoxId === undefined) {
      throw new Error('Required parameter emailBoxId was null or undefined when calling emailClientEmailBoxesDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (coWorkerId !== undefined && coWorkerId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, coWorkerId, 'co_worker_id');
    }
    if (emailBoxId !== undefined && emailBoxId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, emailBoxId, 'email_box_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/email_boxes`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientEmailBoxesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/email_boxes`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientEmailBoxesPartialUpdate(patchedPatchEmailBoxAliasRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/email_boxes`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchEmailBoxAliasRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientEmailsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/emails`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientReplyEmailCreate(postReplyEmailRequest, observe = 'body', reportProgress = false, options) {
    if (postReplyEmailRequest === null || postReplyEmailRequest === undefined) {
      throw new Error('Required parameter postReplyEmailRequest was null or undefined when calling emailClientReplyEmailCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/reply_email`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postReplyEmailRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientSendDraftEmailCreate(postEmailRequest, observe = 'body', reportProgress = false, options) {
    if (postEmailRequest === null || postEmailRequest === undefined) {
      throw new Error('Required parameter postEmailRequest was null or undefined when calling emailClientSendDraftEmailCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/send_draft_email`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postEmailRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientSetEmailFavoriteCreate(postEmailSetFavoriteRequest, observe = 'body', reportProgress = false, options) {
    if (postEmailSetFavoriteRequest === null || postEmailSetFavoriteRequest === undefined) {
      throw new Error('Required parameter postEmailSetFavoriteRequest was null or undefined when calling emailClientSetEmailFavoriteCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/set_email_favorite`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postEmailSetFavoriteRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  emailClientWriteEmailCreate(postEmailRequest, observe = 'body', reportProgress = false, options) {
    if (postEmailRequest === null || postEmailRequest === undefined) {
      throw new Error('Required parameter postEmailRequest was null or undefined when calling emailClientWriteEmailCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/email_client/write_email`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postEmailRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function EmailClientService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || EmailClientService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: EmailClientService,
    factory: EmailClientService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EmailClientService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ErpService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  erpErpCreate(erpRegistrationRequest, observe = 'body', reportProgress = false, options) {
    if (erpRegistrationRequest === null || erpRegistrationRequest === undefined) {
      throw new Error('Required parameter erpRegistrationRequest was null or undefined when calling erpErpCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp/erp`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: erpRegistrationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpUserBusinessesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp/user_businesses`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpUserDetailedBusinessesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp/user_detailed_businesses`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpVerifyEntryCreate(verifyEntryRequest, observe = 'body', reportProgress = false, options) {
    if (verifyEntryRequest === null || verifyEntryRequest === undefined) {
      throw new Error('Required parameter verifyEntryRequest was null or undefined when calling erpVerifyEntryCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp/verify_entry`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: verifyEntryRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ErpService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ErpService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ErpService,
    factory: ErpService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErpService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ErpSideService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  erpSideBusinessEntityDestroy(businessId, reason, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (reason !== undefined && reason !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, reason, 'reason');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/business_entity`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpSideBusinessEntitySettingsCreate(businessProfileRequestRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/business_entity_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: businessProfileRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpSideBusinessEntitySettingsRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/business_entity_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpSideBusinessWalletPaymentCreate(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/business_wallet_payment`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpSideCoWorkerRbacList(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/co_worker_rbac`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpSideGetUsersList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/get_users`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  erpSideStatisticsList(dFrom, dTo, page, pageSize, observe = 'body', reportProgress = false, options) {
    if (dFrom === null || dFrom === undefined) {
      throw new Error('Required parameter dFrom was null or undefined when calling erpSideStatisticsList.');
    }
    if (dTo === null || dTo === undefined) {
      throw new Error('Required parameter dTo was null or undefined when calling erpSideStatisticsList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (dFrom !== undefined && dFrom !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, dFrom, 'd_from');
    }
    if (dTo !== undefined && dTo !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, dTo, 'd_to');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/erp_side/statistics`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ErpSideService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ErpSideService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ErpSideService,
    factory: ErpSideService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErpSideService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class FeaturesService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  featuresAcademyDetailsRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/features/academy_details`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  featuresAcademyList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/features/academy`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  featuresApplicationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/features/application`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function FeaturesService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || FeaturesService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: FeaturesService,
    factory: FeaturesService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FeaturesService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class FileManagerService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  fileManagerCloudStorageCreate(postCloudStorageRequest, observe = 'body', reportProgress = false, options) {
    if (postCloudStorageRequest === null || postCloudStorageRequest === undefined) {
      throw new Error('Required parameter postCloudStorageRequest was null or undefined when calling fileManagerCloudStorageCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/cloud_storage`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCloudStorageRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerCloudStorageDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling fileManagerCloudStorageDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/cloud_storage`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerCloudStorageDetailsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/cloud_storage_details`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerCloudStorageRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/cloud_storage`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerCloudStorageTypeRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/cloud_storage_type`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerFileDeletionProcessDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling fileManagerFileDeletionProcessDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/file_deletion_process`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerFileDeletionProcessRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/file_deletion_process`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerFileRestoringProcessCreate(requestBody, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/file_restoring_process`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: requestBody,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerFileRestoringProcessList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/file_restoring_process`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerFolderDetailUpdate(id, comment, description, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling fileManagerFolderDetailUpdate.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (comment !== undefined && comment !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, comment, 'comment');
    }
    if (description !== undefined && description !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, description, 'description');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/folder_detail`;
    return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerMovingFilesUpdate(requestBody, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/moving_files`;
    return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: requestBody,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerSharedCloudStorageCreate(postSharedCloudStorageRequest, observe = 'body', reportProgress = false, options) {
    if (postSharedCloudStorageRequest === null || postSharedCloudStorageRequest === undefined) {
      throw new Error('Required parameter postSharedCloudStorageRequest was null or undefined when calling fileManagerSharedCloudStorageCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/shared_cloud_storage`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSharedCloudStorageRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerSharedCloudStorageDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling fileManagerSharedCloudStorageDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/shared_cloud_storage`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  fileManagerSharedCloudStorageRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/file_manager/shared_cloud_storage`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function FileManagerService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || FileManagerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: FileManagerService,
    factory: FileManagerService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FileManagerService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class HrmsService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  hrmsCoWorkerJobCreate(postCoWorkerJobRequest, observe = 'body', reportProgress = false, options) {
    if (postCoWorkerJobRequest === null || postCoWorkerJobRequest === undefined) {
      throw new Error('Required parameter postCoWorkerJobRequest was null or undefined when calling hrmsCoWorkerJobCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/co_worker_job`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCoWorkerJobRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsCoWorkerJobDestroy(coWorkerId, id, observe = 'body', reportProgress = false, options) {
    if (coWorkerId === null || coWorkerId === undefined) {
      throw new Error('Required parameter coWorkerId was null or undefined when calling hrmsCoWorkerJobDestroy.');
    }
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling hrmsCoWorkerJobDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (coWorkerId !== undefined && coWorkerId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, coWorkerId, 'co_worker_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/co_worker_job`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsCoWorkerJobList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/co_worker_job`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsGetAllBusinessUsersList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/get_all_business_users`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsHrmsInvitationCreate(postInvitationRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/hrms_invitation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postInvitationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsHrmsInvitationDestroy(hrmsInvitationId, observe = 'body', reportProgress = false, options) {
    if (hrmsInvitationId === null || hrmsInvitationId === undefined) {
      throw new Error('Required parameter hrmsInvitationId was null or undefined when calling hrmsHrmsInvitationDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (hrmsInvitationId !== undefined && hrmsInvitationId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, hrmsInvitationId, 'hrms_invitation_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/hrms_invitation`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsHrmsInvitationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/hrms_invitation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsSendHrmsInvitationCreate(sendInvitationRequest, observe = 'body', reportProgress = false, options) {
    if (sendInvitationRequest === null || sendInvitationRequest === undefined) {
      throw new Error('Required parameter sendInvitationRequest was null or undefined when calling hrmsSendHrmsInvitationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/send_hrms_invitation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: sendInvitationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsSetCoWorkerDataPartialUpdate(patchedModifyCoWorkerRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/set_co_worker_data`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedModifyCoWorkerRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsUserHrmsInvitationHandleUpdate(modifyUserHrmsInvitationRequest, observe = 'body', reportProgress = false, options) {
    if (modifyUserHrmsInvitationRequest === null || modifyUserHrmsInvitationRequest === undefined) {
      throw new Error('Required parameter modifyUserHrmsInvitationRequest was null or undefined when calling hrmsUserHrmsInvitationHandleUpdate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/user_hrms_invitation_handle`;
    return this.httpClient.request('put', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: modifyUserHrmsInvitationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  hrmsUserHrmsInvitationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/hrms/user_hrms_invitation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function HrmsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || HrmsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: HrmsService,
    factory: HrmsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HrmsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class InteractionsService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  interactionsAcceptCreate(postAcceptRequest, observe = 'body', reportProgress = false, options) {
    if (postAcceptRequest === null || postAcceptRequest === undefined) {
      throw new Error('Required parameter postAcceptRequest was null or undefined when calling interactionsAcceptCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/accept`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postAcceptRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsCatalogCollectionCreate(userCollectionRequest, observe = 'body', reportProgress = false, options) {
    if (userCollectionRequest === null || userCollectionRequest === undefined) {
      throw new Error('Required parameter userCollectionRequest was null or undefined when calling interactionsCatalogCollectionCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/catalog_collection`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: userCollectionRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsCatalogCollectionDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling interactionsCatalogCollectionDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/catalog_collection`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsCatalogCollectionList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/catalog_collection`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsCommonSearchUserAndBusinessList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/common_search_user_and_business`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsInteractionCreate(postInteractionRequest, observe = 'body', reportProgress = false, options) {
    if (postInteractionRequest === null || postInteractionRequest === undefined) {
      throw new Error('Required parameter postInteractionRequest was null or undefined when calling interactionsInteractionCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/interaction`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postInteractionRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsInteractionList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/interaction`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsReputationCreate(softReputationPostRequest, observe = 'body', reportProgress = false, options) {
    if (softReputationPostRequest === null || softReputationPostRequest === undefined) {
      throw new Error('Required parameter softReputationPostRequest was null or undefined when calling interactionsReputationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/reputation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: softReputationPostRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  interactionsReputationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/interactions/reputation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function InteractionsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || InteractionsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: InteractionsService,
    factory: InteractionsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(InteractionsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class LocationService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  locationCitiesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/cities`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationCountriesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/countries`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationProvinencesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/provinences`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationRegionedSubregionsList(page, pageSize, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/regioned_subregions`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationRegionsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/regions`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationStreetsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/streets`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationSubregionsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/subregions`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationZipcodesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location/zipcodes`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function LocationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LocationService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: LocationService,
    factory: LocationService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocationService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class LocationManagerService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  locationManagerLocationManagerCreate(locationManagerBusinessRequestRequest, observe = 'body', reportProgress = false, options) {
    if (locationManagerBusinessRequestRequest === null || locationManagerBusinessRequestRequest === undefined) {
      throw new Error('Required parameter locationManagerBusinessRequestRequest was null or undefined when calling locationManagerLocationManagerCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location_manager/location_manager`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: locationManagerBusinessRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationManagerLocationManagerDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling locationManagerLocationManagerDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location_manager/location_manager`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationManagerLocationManagerList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location_manager/location_manager`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  locationManagerLocationManagerPartialUpdate(patchedLocationManagerBusinessPatchRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/location_manager/location_manager`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedLocationManagerBusinessPatchRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function LocationManagerService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LocationManagerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: LocationManagerService,
    factory: LocationManagerService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocationManagerService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class MainProfileService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  mainProfileMainProfileCommunityApplicationSettingsCreate(postUserCommonApplicationProfileSettingsRequest, observe = 'body', reportProgress = false, options) {
    if (postUserCommonApplicationProfileSettingsRequest === null || postUserCommonApplicationProfileSettingsRequest === undefined) {
      throw new Error('Required parameter postUserCommonApplicationProfileSettingsRequest was null or undefined when calling mainProfileMainProfileCommunityApplicationSettingsCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_community_application_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postUserCommonApplicationProfileSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfileCommunityApplicationSettingsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_community_application_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfilePrivacyCreate(updatePrivacyRequestRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_privacy`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: updatePrivacyRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfileSettingsCreate(changeProfileRequestRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: changeProfileRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainProfileSettingsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_profile_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainPublicInformationRetrieve(userId, username, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (userId !== undefined && userId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
    }
    if (username !== undefined && username !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, username, 'username');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_public_information`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  mainProfileMainPublicProfileRetrieve(businessId, userId, username, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (userId !== undefined && userId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
    }
    if (username !== undefined && username !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, username, 'username');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/main_profile/main_public_profile`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function MainProfileService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || MainProfileService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: MainProfileService,
    factory: MainProfileService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MainProfileService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class NotificationsService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  notificationsChatNotificationsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications/chat_notifications`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  notificationsDeleteAllDestroy(bCoWorkerId, userId, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (bCoWorkerId !== undefined && bCoWorkerId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, bCoWorkerId, 'b_co_worker_id');
    }
    if (userId !== undefined && userId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications/delete_all`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  notificationsDestroy(notificationId, observe = 'body', reportProgress = false, options) {
    if (notificationId === null || notificationId === undefined) {
      throw new Error('Required parameter notificationId was null or undefined when calling notificationsDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (notificationId !== undefined && notificationId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, notificationId, 'notification_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  notificationsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  notificationsNotificationSettingCreate(postNotificationSettingRequest, observe = 'body', reportProgress = false, options) {
    if (postNotificationSettingRequest === null || postNotificationSettingRequest === undefined) {
      throw new Error('Required parameter postNotificationSettingRequest was null or undefined when calling notificationsNotificationSettingCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications/notification_setting`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postNotificationSettingRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  notificationsNotificationSettingList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications/notification_setting`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  notificationsSetReadCreate(setReadTheNotificationsRequest, observe = 'body', reportProgress = false, options) {
    if (setReadTheNotificationsRequest === null || setReadTheNotificationsRequest === undefined) {
      throw new Error('Required parameter setReadTheNotificationsRequest was null or undefined when calling notificationsSetReadCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/notifications/set_read`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: setReadTheNotificationsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function NotificationsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NotificationsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: NotificationsService,
    factory: NotificationsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NotificationsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class OrgChartService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  orgChartCreate(postOrgChartRequest, observe = 'body', reportProgress = false, options) {
    if (postOrgChartRequest === null || postOrgChartRequest === undefined) {
      throw new Error('Required parameter postOrgChartRequest was null or undefined when calling orgChartCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/org_chart`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postOrgChartRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  orgChartDestroy(areaType, businessId, orgChartId, observe = 'body', reportProgress = false, options) {
    if (areaType === null || areaType === undefined) {
      throw new Error('Required parameter areaType was null or undefined when calling orgChartDestroy.');
    }
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling orgChartDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (areaType !== undefined && areaType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, areaType, 'area_type');
    }
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (orgChartId !== undefined && orgChartId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orgChartId, 'org_chart_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/org_chart`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  orgChartList(areaType, businessId, orgChartId, observe = 'body', reportProgress = false, options) {
    if (areaType === null || areaType === undefined) {
      throw new Error('Required parameter areaType was null or undefined when calling orgChartList.');
    }
    if (businessId === null || businessId === undefined) {
      throw new Error('Required parameter businessId was null or undefined when calling orgChartList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (areaType !== undefined && areaType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, areaType, 'area_type');
    }
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (orgChartId !== undefined && orgChartId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, orgChartId, 'org_chart_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/org_chart`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  orgChartPaginatedOrgChartList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/org_chart/paginated_org_chart`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function OrgChartService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || OrgChartService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: OrgChartService,
    factory: OrgChartService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OrgChartService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class PartnerOfficeService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  partnerOfficeClientTransactionsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/client_transactions`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  partnerOfficeClientsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/clients`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  partnerOfficeCodeGenerateList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/code_generate`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  partnerOfficeCreateCodeCreate(createCodeRequest, observe = 'body', reportProgress = false, options) {
    if (createCodeRequest === null || createCodeRequest === undefined) {
      throw new Error('Required parameter createCodeRequest was null or undefined when calling partnerOfficeCreateCodeCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/create_code`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: createCodeRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  partnerOfficePartnerDashboardRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/partner_dashboard`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  partnerOfficePartnerVerifyCreate(postPartnerVerifyRequest, observe = 'body', reportProgress = false, options) {
    if (postPartnerVerifyRequest === null || postPartnerVerifyRequest === undefined) {
      throw new Error('Required parameter postPartnerVerifyRequest was null or undefined when calling partnerOfficePartnerVerifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/partner_verify`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPartnerVerifyRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  partnerOfficePartnerVerifyRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/partner_office/partner_verify`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function PartnerOfficeService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || PartnerOfficeService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: PartnerOfficeService,
    factory: PartnerOfficeService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PartnerOfficeService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class PaymentService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  paymentBankTransferCreate(postBankTransferSerializersRequest, observe = 'body', reportProgress = false, options) {
    if (postBankTransferSerializersRequest === null || postBankTransferSerializersRequest === undefined) {
      throw new Error('Required parameter postBankTransferSerializersRequest was null or undefined when calling paymentBankTransferCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/bank_transfer`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBankTransferSerializersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentBankTransferDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling paymentBankTransferDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/bank_transfer`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentBankTransferList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/bank_transfer`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentChargeStripeCreate(postStripeChargeRequest, observe = 'body', reportProgress = false, options) {
    if (postStripeChargeRequest === null || postStripeChargeRequest === undefined) {
      throw new Error('Required parameter postStripeChargeRequest was null or undefined when calling paymentChargeStripeCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/charge_stripe`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postStripeChargeRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentCheckProviderCurrencyRetrieve(currency, paymentProvider, countryId, observe = 'body', reportProgress = false, options) {
    if (currency === null || currency === undefined) {
      throw new Error('Required parameter currency was null or undefined when calling paymentCheckProviderCurrencyRetrieve.');
    }
    if (paymentProvider === null || paymentProvider === undefined) {
      throw new Error('Required parameter paymentProvider was null or undefined when calling paymentCheckProviderCurrencyRetrieve.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (countryId !== undefined && countryId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, countryId, 'country_id');
    }
    if (currency !== undefined && currency !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, currency, 'currency');
    }
    if (paymentProvider !== undefined && paymentProvider !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, paymentProvider, 'payment_provider');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/check_provider_currency`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentCurrencyConverterRetrieve(amount, fromCurrency, toCurrency, observe = 'body', reportProgress = false, options) {
    if (amount === null || amount === undefined) {
      throw new Error('Required parameter amount was null or undefined when calling paymentCurrencyConverterRetrieve.');
    }
    if (fromCurrency === null || fromCurrency === undefined) {
      throw new Error('Required parameter fromCurrency was null or undefined when calling paymentCurrencyConverterRetrieve.');
    }
    if (toCurrency === null || toCurrency === undefined) {
      throw new Error('Required parameter toCurrency was null or undefined when calling paymentCurrencyConverterRetrieve.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (amount !== undefined && amount !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, amount, 'amount');
    }
    if (fromCurrency !== undefined && fromCurrency !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, fromCurrency, 'from_currency');
    }
    if (toCurrency !== undefined && toCurrency !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, toCurrency, 'to_currency');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/currency_converter`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentShoppingCartIntentCreate(paymentIntentRequestWithParametersRequest, observe = 'body', reportProgress = false, options) {
    if (paymentIntentRequestWithParametersRequest === null || paymentIntentRequestWithParametersRequest === undefined) {
      throw new Error('Required parameter paymentIntentRequestWithParametersRequest was null or undefined when calling paymentShoppingCartIntentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/shopping_cart_intent`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: paymentIntentRequestWithParametersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentStripeWebhookCreate(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/stripe_webhook`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentSubscriptionCheckoutsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/subscription_checkouts`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentSubscriptionIntentCreate(paymentIntentRequestRequest, observe = 'body', reportProgress = false, options) {
    if (paymentIntentRequestRequest === null || paymentIntentRequestRequest === undefined) {
      throw new Error('Required parameter paymentIntentRequestRequest was null or undefined when calling paymentSubscriptionIntentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/subscription_intent`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: paymentIntentRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentUserWalletPaymentCreate(postCheckoutWalletSerializersRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/user_wallet_payment`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCheckoutWalletSerializersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  paymentWalletUploadIntentCreate(paymentIntentRequestWithParametersRequest, observe = 'body', reportProgress = false, options) {
    if (paymentIntentRequestWithParametersRequest === null || paymentIntentRequestWithParametersRequest === undefined) {
      throw new Error('Required parameter paymentIntentRequestWithParametersRequest was null or undefined when calling paymentWalletUploadIntentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/payment/wallet_upload_intent`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: paymentIntentRequestWithParametersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function PaymentService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || PaymentService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: PaymentService,
    factory: PaymentService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PaymentService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class PublicBusinessService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  publicBusinessPublicLocationManagerDetailRetrieve(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/public_business/public_location_manager_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  publicBusinessPublicLocationManagerList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/public_business/public_location_manager`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function PublicBusinessService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || PublicBusinessService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: PublicBusinessService,
    factory: PublicBusinessService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PublicBusinessService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class PurchaseService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  purchasePurchasesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/purchase/purchases`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function PurchaseService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || PurchaseService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: PurchaseService,
    factory: PurchaseService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PurchaseService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class PushNotificationService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  pushNotificationPushNotificationCreate(postPushNotificationRequest, observe = 'body', reportProgress = false, options) {
    if (postPushNotificationRequest === null || postPushNotificationRequest === undefined) {
      throw new Error('Required parameter postPushNotificationRequest was null or undefined when calling pushNotificationPushNotificationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/push_notification/push_notification`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPushNotificationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  pushNotificationPushNotificationList(page, pageSize, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/push_notification/push_notification`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function PushNotificationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || PushNotificationService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: PushNotificationService,
    factory: PushNotificationService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PushNotificationService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class RbacRuleService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  rbacRuleAccessOptionCreate(postAccessOptionRequest, observe = 'body', reportProgress = false, options) {
    if (postAccessOptionRequest === null || postAccessOptionRequest === undefined) {
      throw new Error('Required parameter postAccessOptionRequest was null or undefined when calling rbacRuleAccessOptionCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/access_option`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postAccessOptionRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleAccessOptionDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling rbacRuleAccessOptionDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/access_option`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleAccessOptionList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/access_option`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleAccessOptionPatternsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/access_option_patterns`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleEmployeeRuleCreate(postEmployeeRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postEmployeeRuleRequest === null || postEmployeeRuleRequest === undefined) {
      throw new Error('Required parameter postEmployeeRuleRequest was null or undefined when calling rbacRuleEmployeeRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/employee_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postEmployeeRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleEmployeeRuleDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling rbacRuleEmployeeRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/employee_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleEmployeeRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/employee_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleGroupRuleCreate(postGroupRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postGroupRuleRequest === null || postGroupRuleRequest === undefined) {
      throw new Error('Required parameter postGroupRuleRequest was null or undefined when calling rbacRuleGroupRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/group_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postGroupRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleGroupRuleDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling rbacRuleGroupRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/group_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleGroupRuleDetailList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/group_rule_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleGroupRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/group_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleRbacGroupCreate(postRBACGroupRequest, observe = 'body', reportProgress = false, options) {
    if (postRBACGroupRequest === null || postRBACGroupRequest === undefined) {
      throw new Error('Required parameter postRBACGroupRequest was null or undefined when calling rbacRuleRbacGroupCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/rbac_group`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postRBACGroupRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleRbacGroupDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling rbacRuleRbacGroupDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/rbac_group`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleRbacGroupList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/rbac_group`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleRbacRuleCreate(postRBACAccessRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postRBACAccessRuleRequest === null || postRBACAccessRuleRequest === undefined) {
      throw new Error('Required parameter postRBACAccessRuleRequest was null or undefined when calling rbacRuleRbacRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/rbac_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postRBACAccessRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleRbacRuleDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling rbacRuleRbacRuleDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/rbac_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  rbacRuleRbacRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/rbac_rule/rbac_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function RbacRuleService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || RbacRuleService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: RbacRuleService,
    factory: RbacRuleService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RbacRuleService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class SalesTeamsService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  salesTeamsSalesTeamCreate(postSalesTeamRequest, observe = 'body', reportProgress = false, options) {
    if (postSalesTeamRequest === null || postSalesTeamRequest === undefined) {
      throw new Error('Required parameter postSalesTeamRequest was null or undefined when calling salesTeamsSalesTeamCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/sales_teams/sales_team`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSalesTeamRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  salesTeamsSalesTeamDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling salesTeamsSalesTeamDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/sales_teams/sales_team`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  salesTeamsSalesTeamDetailList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/sales_teams/sales_team_detail`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  salesTeamsSalesTeamList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/sales_teams/sales_team`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function SalesTeamsService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SalesTeamsService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SalesTeamsService,
    factory: SalesTeamsService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SalesTeamsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class SearchService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  searchBulkKeywordCreateToKeywordServiceCreate(postBulkKeyWordServiceRequest, observe = 'body', reportProgress = false, options) {
    if (postBulkKeyWordServiceRequest === null || postBulkKeyWordServiceRequest === undefined) {
      throw new Error('Required parameter postBulkKeyWordServiceRequest was null or undefined when calling searchBulkKeywordCreateToKeywordServiceCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/bulk_keyword_create_to_keyword_service`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBulkKeyWordServiceRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchBulkUniversalLanguageTranslationCreate(postBulkUniversalLanguageRequest, observe = 'body', reportProgress = false, options) {
    if (postBulkUniversalLanguageRequest === null || postBulkUniversalLanguageRequest === undefined) {
      throw new Error('Required parameter postBulkUniversalLanguageRequest was null or undefined when calling searchBulkUniversalLanguageTranslationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/bulk_universal_language_translation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBulkUniversalLanguageRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchCategoryTemplateCreate(postCategoryTemplateRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/category_template`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCategoryTemplateRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchCategoryTemplateDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling searchCategoryTemplateDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/category_template`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchCategoryTemplateList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/category_template`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchCategoryTemplateMultiCreate(postKeywordCategoryTemplateMultiRequest, observe = 'body', reportProgress = false, options) {
    if (postKeywordCategoryTemplateMultiRequest === null || postKeywordCategoryTemplateMultiRequest === undefined) {
      throw new Error('Required parameter postKeywordCategoryTemplateMultiRequest was null or undefined when calling searchCategoryTemplateMultiCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/category_template_multi`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postKeywordCategoryTemplateMultiRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchCategoryTemplateParentPathList(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/category_template_parent_path`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchCategoryTemplatePathList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/category_template_path`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordCreate(postKeyWordRequest, observe = 'body', reportProgress = false, options) {
    if (postKeyWordRequest === null || postKeyWordRequest === undefined) {
      throw new Error('Required parameter postKeyWordRequest was null or undefined when calling searchKeywordCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keyword`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postKeyWordRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling searchKeywordDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keyword`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keyword`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordserviceCreate(postKeyWordServiceRequest, observe = 'body', reportProgress = false, options) {
    if (postKeyWordServiceRequest === null || postKeyWordServiceRequest === undefined) {
      throw new Error('Required parameter postKeyWordServiceRequest was null or undefined when calling searchKeywordserviceCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keywordservice`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postKeyWordServiceRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordserviceDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling searchKeywordserviceDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keywordservice`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordserviceList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keywordservice`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordtranslationCreate(postKeyWordTranslationRequest, observe = 'body', reportProgress = false, options) {
    if (postKeyWordTranslationRequest === null || postKeyWordTranslationRequest === undefined) {
      throw new Error('Required parameter postKeyWordTranslationRequest was null or undefined when calling searchKeywordtranslationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keywordtranslation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postKeyWordTranslationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchKeywordtranslationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/keywordtranslation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchSearchbystreetnameList(streetName, observe = 'body', reportProgress = false, options) {
    if (streetName === null || streetName === undefined) {
      throw new Error('Required parameter streetName was null or undefined when calling searchSearchbystreetnameList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (streetName !== undefined && streetName !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, streetName, 'street_name');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/searchbystreetname`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchUniversalLanguageTranslationCreate(postUniversalLanguageTranslationRequest, observe = 'body', reportProgress = false, options) {
    if (postUniversalLanguageTranslationRequest === null || postUniversalLanguageTranslationRequest === undefined) {
      throw new Error('Required parameter postUniversalLanguageTranslationRequest was null or undefined when calling searchUniversalLanguageTranslationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/universal_language_translation`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postUniversalLanguageTranslationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchUniversalLanguageTranslationDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling searchUniversalLanguageTranslationDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/universal_language_translation`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchUniversalLanguageTranslationItemCreate(postUniversalLanguageTranslationItemRequest, observe = 'body', reportProgress = false, options) {
    if (postUniversalLanguageTranslationItemRequest === null || postUniversalLanguageTranslationItemRequest === undefined) {
      throw new Error('Required parameter postUniversalLanguageTranslationItemRequest was null or undefined when calling searchUniversalLanguageTranslationItemCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/universal_language_translation_item`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postUniversalLanguageTranslationItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchUniversalLanguageTranslationItemDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling searchUniversalLanguageTranslationItemDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/universal_language_translation_item`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchUniversalLanguageTranslationItemList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/universal_language_translation_item`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  searchUniversalLanguageTranslationList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/search/universal_language_translation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function SearchService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SearchService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SearchService,
    factory: SearchService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SearchService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ServiceAreaService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  serviceAreaServiceAreaCreate(postServiceAreaRequest, observe = 'body', reportProgress = false, options) {
    if (postServiceAreaRequest === null || postServiceAreaRequest === undefined) {
      throw new Error('Required parameter postServiceAreaRequest was null or undefined when calling serviceAreaServiceAreaCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/service_area/service_area`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postServiceAreaRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  serviceAreaServiceAreaDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/service_area/service_area`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  serviceAreaServiceAreaList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/service_area/service_area`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ServiceAreaService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ServiceAreaService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ServiceAreaService,
    factory: ServiceAreaService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ServiceAreaService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class SharedService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  /**
  * @param consumes string[] mime-types
  * @return true: consumes contains 'multipart/form-data', false: otherwise
  */
  canConsumeForm(consumes) {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  sharedAddressCreate(postCrmAddressRequest, observe = 'body', reportProgress = false, options) {
    if (postCrmAddressRequest === null || postCrmAddressRequest === undefined) {
      throw new Error('Required parameter postCrmAddressRequest was null or undefined when calling sharedAddressCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/address`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCrmAddressRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedAddressDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling sharedAddressDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/address`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedAddressList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/address`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedEventCreate(eventType, serviceType, serviceId, id, noteText, userId, coWorkerId, files, title, fileIds, observe = 'body', reportProgress = false, options) {
    if (eventType === null || eventType === undefined) {
      throw new Error('Required parameter eventType was null or undefined when calling sharedEventCreate.');
    }
    if (serviceType === null || serviceType === undefined) {
      throw new Error('Required parameter serviceType was null or undefined when calling sharedEventCreate.');
    }
    if (serviceId === null || serviceId === undefined) {
      throw new Error('Required parameter serviceId was null or undefined when calling sharedEventCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['multipart/form-data', 'application/x-www-form-urlencoded'];
    const canConsumeForm = this.canConsumeForm(consumes);
    let localVarFormParams;
    let localVarUseForm = false;
    let localVarConvertFormParamsToString = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    localVarUseForm = canConsumeForm;
    if (localVarUseForm) {
      localVarFormParams = new FormData();
    } else {
      localVarFormParams = new HttpParams({
        encoder: this.encoder
      });
    }
    if (id !== undefined) {
      localVarFormParams = localVarFormParams.append('id', id) || localVarFormParams;
    }
    if (eventType !== undefined) {
      localVarFormParams = localVarFormParams.append('event_type', eventType) || localVarFormParams;
    }
    if (serviceType !== undefined) {
      localVarFormParams = localVarFormParams.append('service_type', serviceType) || localVarFormParams;
    }
    if (serviceId !== undefined) {
      localVarFormParams = localVarFormParams.append('service_id', serviceId) || localVarFormParams;
    }
    if (noteText !== undefined) {
      localVarFormParams = localVarFormParams.append('note_text', noteText) || localVarFormParams;
    }
    if (userId !== undefined) {
      localVarFormParams = localVarFormParams.append('user_id', userId) || localVarFormParams;
    }
    if (coWorkerId !== undefined) {
      localVarFormParams = localVarFormParams.append('co_worker_id', coWorkerId) || localVarFormParams;
    }
    if (files !== undefined) {
      const temp = Array.from(files);
      for (let i = 0; i < temp.length; i++) {
        localVarFormParams = localVarFormParams.append('files', temp[i]) || localVarFormParams;
      }
    }
    if (title !== undefined) {
      localVarFormParams = localVarFormParams.append('title', title) || localVarFormParams;
    }
    if (fileIds !== undefined) {
      localVarFormParams = localVarFormParams.append('file_ids', fileIds) || localVarFormParams;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/event`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedEventDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling sharedEventDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/event`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedEventList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/event`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedHistoriesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/histories`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedHistoryEnumsList(historyType, page, pageSize, showType, observe = 'body', reportProgress = false, options) {
    if (historyType === null || historyType === undefined) {
      throw new Error('Required parameter historyType was null or undefined when calling sharedHistoryEnumsList.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (historyType !== undefined && historyType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, historyType, 'history_type');
    }
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (showType !== undefined && showType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, showType, 'show_type');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/history_enums`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedIndicatorsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/indicators`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedMediaStorageList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/media_storage`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedPipelineStatusCreate(postPipelineStatusRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/pipeline_status`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPipelineStatusRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedPiplineStepSettingsCreate(postPipelineStepRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/pipline_step_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPipelineStepRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedPiplineStepSettingsDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/pipline_step_settings`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedSharedContentCreate(postContentRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/shared_content`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postContentRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedSharedContentDestroy(id, sharedType, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling sharedSharedContentDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (sharedType !== undefined && sharedType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, sharedType, 'shared_type');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/shared_content`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedSharedContentList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/shared_content`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedStepPipelineCreate(postPipelineRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/step_pipeline`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPipelineRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedStepPipelineDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/step_pipeline`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  sharedStepPipelineList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shared/step_pipeline`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function SharedService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SharedService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SharedService,
    factory: SharedService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SharedService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class ShoppingCartService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  shoppingCartAddCreate(postShoppingCartRequest, observe = 'body', reportProgress = false, options) {
    if (postShoppingCartRequest === null || postShoppingCartRequest === undefined) {
      throw new Error('Required parameter postShoppingCartRequest was null or undefined when calling shoppingCartAddCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shopping_cart/add`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postShoppingCartRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  shoppingCartCartDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shopping_cart/cart`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  shoppingCartCartList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shopping_cart/cart`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  shoppingCartCheckoutCreate(postSmartCheckoutRequestRequest, observe = 'body', reportProgress = false, options) {
    if (postSmartCheckoutRequestRequest === null || postSmartCheckoutRequestRequest === undefined) {
      throw new Error('Required parameter postSmartCheckoutRequestRequest was null or undefined when calling shoppingCartCheckoutCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shopping_cart/checkout`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartCheckoutRequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  shoppingCartRemoveDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shopping_cart/remove`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  shoppingCartUpdatePartialUpdate(patchedPostShoppingCartItemRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/shopping_cart/update`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPostShoppingCartItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function ShoppingCartService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ShoppingCartService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ShoppingCartService,
    factory: ShoppingCartService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ShoppingCartService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class SmartCubeService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  smartCubeBulkEditCreate(bulkEditRequest, observe = 'body', reportProgress = false, options) {
    if (bulkEditRequest === null || bulkEditRequest === undefined) {
      throw new Error('Required parameter bulkEditRequest was null or undefined when calling smartCubeBulkEditCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/bulk_edit`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: bulkEditRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeBulkSmartPaymentCreate(postBulkSmartPaymentRequest, observe = 'body', reportProgress = false, options) {
    if (postBulkSmartPaymentRequest === null || postBulkSmartPaymentRequest === undefined) {
      throw new Error('Required parameter postBulkSmartPaymentRequest was null or undefined when calling smartCubeBulkSmartPaymentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/bulk_smart_payment`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBulkSmartPaymentRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeExcludeCurrencyOfSmartPriceList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/exclude_currency_of_smart_price`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartAdCreate(postSmartAdRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_ad`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartAdRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartAdDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartAdDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_ad`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartAdList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_ad`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartEmployeeHandleCreate(postSmartStaffHandleRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_employee_handle`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartStaffHandleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartEmployeeHandleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_employee_handle`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartItemCreate(postSmartItemRequest, observe = 'body', reportProgress = false, options) {
    if (postSmartItemRequest === null || postSmartItemRequest === undefined) {
      throw new Error('Required parameter postSmartItemRequest was null or undefined when calling smartCubeSmartItemCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_item`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartItemDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartItemDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_item`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartItemList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_item`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartItemPartialUpdate(patchedPatchSmartItemRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_item`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchSmartItemRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartLocationManagerCreate(postSmartLocationManagerRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_location_manager`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartLocationManagerRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartLocationManagerDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartLocationManagerDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_location_manager`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartLocationManagerGroupList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_location_manager_group`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartLocationManagerList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_location_manager`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartPaymentList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_payment`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartPriceCreate(postSmartPriceRequest, observe = 'body', reportProgress = false, options) {
    if (postSmartPriceRequest === null || postSmartPriceRequest === undefined) {
      throw new Error('Required parameter postSmartPriceRequest was null or undefined when calling smartCubeSmartPriceCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_price`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartPriceRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartPriceDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartPriceDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_price`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartPriceList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_price`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSalesWorkflowCreate(postSmartSalesWorkflowRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_sales_workflow`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartSalesWorkflowRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSalesWorkflowDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartSalesWorkflowDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_sales_workflow`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSalesWorkflowList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_sales_workflow`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSalesWorkflowPriceCreate(postCalendarPriceRequest, observe = 'body', reportProgress = false, options) {
    if (postCalendarPriceRequest === null || postCalendarPriceRequest === undefined) {
      throw new Error('Required parameter postCalendarPriceRequest was null or undefined when calling smartCubeSmartSalesWorkflowPriceCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_sales_workflow_price`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCalendarPriceRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSalesWorkflowPriceDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartSalesWorkflowPriceDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_sales_workflow_price`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSalesWorkflowPriceList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_sales_workflow_price`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSettingsCreate(postSmartSettingsRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSettingsDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartSettingsDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_settings`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartSettingsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartStockCreate(postSmartStockRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_stock`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSmartStockRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartStockDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling smartCubeSmartStockDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_stock`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  smartCubeSmartStockList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/smart_cube/smart_stock`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function SmartCubeService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SmartCubeService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SmartCubeService,
    factory: SmartCubeService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SmartCubeService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class SubscriptionService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  subscriptionCheckoutCreate(combinedCheckoutRequest, observe = 'body', reportProgress = false, options) {
    if (combinedCheckoutRequest === null || combinedCheckoutRequest === undefined) {
      throw new Error('Required parameter combinedCheckoutRequest was null or undefined when calling subscriptionCheckoutCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/subscription/checkout`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: combinedCheckoutRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  subscriptionContentPeriodsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/subscription/content_periods`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  subscriptionDeleteAddonsOrSubscriptionDestroy(systemSubscriptionPriceId, businessId, observe = 'body', reportProgress = false, options) {
    if (systemSubscriptionPriceId === null || systemSubscriptionPriceId === undefined) {
      throw new Error('Required parameter systemSubscriptionPriceId was null or undefined when calling subscriptionDeleteAddonsOrSubscriptionDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (systemSubscriptionPriceId !== undefined && systemSubscriptionPriceId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, systemSubscriptionPriceId, 'system_subscription_price_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/subscription/delete_addons_or_subscription`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  subscriptionSubscriptionPageRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/subscription/subscription_page`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function SubscriptionService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SubscriptionService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SubscriptionService,
    factory: SubscriptionService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SubscriptionService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class SystemService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  /**
  * @param consumes string[] mime-types
  * @return true: consumes contains 'multipart/form-data', false: otherwise
  */
  canConsumeForm(consumes) {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  systemBankDatabaseCreate(postBankDatabaseRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/bank_database`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postBankDatabaseRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemBankDatabaseDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/bank_database`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemBankDatabaseList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/bank_database`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemBiReportsList(searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/bi_reports`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCatalogCreate(postCatalogRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/catalog`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCatalogRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCatalogDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/catalog`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCatalogList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/catalog`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCatalogMultiCreate(catalog, keyword, categoryTemplate, universalLanguageTranslation, selectedCustomField, file, observe = 'body', reportProgress = false, options) {
    if (catalog === null || catalog === undefined) {
      throw new Error('Required parameter catalog was null or undefined when calling systemCatalogMultiCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['multipart/form-data', 'application/x-www-form-urlencoded'];
    const canConsumeForm = this.canConsumeForm(consumes);
    let localVarFormParams;
    let localVarUseForm = false;
    let localVarConvertFormParamsToString = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    localVarUseForm = canConsumeForm;
    if (localVarUseForm) {
      localVarFormParams = new FormData();
    } else {
      localVarFormParams = new HttpParams({
        encoder: this.encoder
      });
    }
    if (catalog !== undefined) {
      localVarFormParams = localVarFormParams.append('catalog', JSON.stringify(catalog)) || localVarFormParams;
    }
    if (keyword !== undefined) {
      localVarFormParams = localVarFormParams.append('keyword', JSON.stringify(keyword)) || localVarFormParams;
    }
    if (categoryTemplate !== undefined) {
      localVarFormParams = localVarFormParams.append('category_template', JSON.stringify(categoryTemplate)) || localVarFormParams;
    }
    if (universalLanguageTranslation !== undefined) {
      localVarFormParams = localVarFormParams.append('universal_language_translation', JSON.stringify(universalLanguageTranslation)) || localVarFormParams;
    }
    if (selectedCustomField !== undefined) {
      localVarFormParams = localVarFormParams.append('selected_custom_field', JSON.stringify(selectedCustomField)) || localVarFormParams;
    }
    if (file !== undefined) {
      localVarFormParams = localVarFormParams.append('file', file) || localVarFormParams;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/catalog_multi`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemConvertImageUrlToBase64Create(convertStringToBase64RequestRequest, observe = 'body', reportProgress = false, options) {
    if (convertStringToBase64RequestRequest === null || convertStringToBase64RequestRequest === undefined) {
      throw new Error('Required parameter convertStringToBase64RequestRequest was null or undefined when calling systemConvertImageUrlToBase64Create.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/convert_image_url_to_base64`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: convertStringToBase64RequestRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCrmTargetedCustomerList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/crm_targeted_customer`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCurrenciesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/currencies`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldAreaCreate(postCustomFieldAreaRequest, observe = 'body', reportProgress = false, options) {
    if (postCustomFieldAreaRequest === null || postCustomFieldAreaRequest === undefined) {
      throw new Error('Required parameter postCustomFieldAreaRequest was null or undefined when calling systemCustomFieldAreaCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field_area`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCustomFieldAreaRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldAreaDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling systemCustomFieldAreaDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field_area`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldAreaList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field_area`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldCreate(postCustomFieldCategoryRequest, observe = 'body', reportProgress = false, options) {
    if (postCustomFieldCategoryRequest === null || postCustomFieldCategoryRequest === undefined) {
      throw new Error('Required parameter postCustomFieldCategoryRequest was null or undefined when calling systemCustomFieldCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCustomFieldCategoryRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling systemCustomFieldDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldMultiCreate(postCustomFieldCategoryMultiRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field_multi`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postCustomFieldCategoryMultiRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemCustomFieldRangeDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling systemCustomFieldRangeDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/custom_field_range`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemErrortypesRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/errortypes`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemFullpathRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/fullpath`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemGenerateguesttokenCreate(searchSettingsRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/generateguesttoken/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: searchSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemGetIpRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/get_ip`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemGetKeywordTranslationRetrieve(filter, businessId, id, languageId, translations, typeIn, usedIn, userId, observe = 'body', reportProgress = false, options) {
    if (filter === null || filter === undefined) {
      throw new Error('Required parameter filter was null or undefined when calling systemGetKeywordTranslationRetrieve.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (filter !== undefined && filter !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, filter, 'filter');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    if (languageId !== undefined && languageId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, languageId, 'language_id');
    }
    if (translations !== undefined && translations !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, translations, 'translations');
    }
    if (typeIn !== undefined && typeIn !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, typeIn, 'type__in');
    }
    if (usedIn !== undefined && usedIn !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, usedIn, 'used_in');
    }
    if (userId !== undefined && userId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userId, 'user_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/get_keyword_translation`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemLanguagesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/languages`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemLegalEntityTypeCreate(postLegalEntityTypeRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/legal_entity_type`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postLegalEntityTypeRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemLegalEntityTypeDestroy(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/legal_entity_type`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemLegalEntityTypeList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/legal_entity_type`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemMrAiTrackerCreate(postMrAiTrackerRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/mr_ai_tracker`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postMrAiTrackerRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemPaymentMethodCreate(postPaymentOnSpotRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/payment_method`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postPaymentOnSpotRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemPaymentMethodDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/payment_method`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemPaymentMethodList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/payment_method`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemRedirectRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/redirect`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSearchSettingsCreate(consolidatedSearchSettingsRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/search_settings`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: consolidatedSearchSettingsRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSearchSettingsRetrieve(userToken, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (userToken !== undefined && userToken !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, userToken, 'user_token');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/search_settings`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSelectCustomFieldCreate(postWfSelectedCustomFieldRequest, observe = 'body', reportProgress = false, options) {
    if (postWfSelectedCustomFieldRequest === null || postWfSelectedCustomFieldRequest === undefined) {
      throw new Error('Required parameter postWfSelectedCustomFieldRequest was null or undefined when calling systemSelectCustomFieldCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/select_custom_field`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postWfSelectedCustomFieldRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSelectCustomFieldDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling systemSelectCustomFieldDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/select_custom_field`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSelectCustomFieldList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/select_custom_field`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSendI18nEmailCreate(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/send_i18n_email`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSiUnitsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/si_units`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSystemTaxRuleCreate(postSystemTaxRuleRequest, observe = 'body', reportProgress = false, options) {
    if (postSystemTaxRuleRequest === null || postSystemTaxRuleRequest === undefined) {
      throw new Error('Required parameter postSystemTaxRuleRequest was null or undefined when calling systemSystemTaxRuleCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/system_tax_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postSystemTaxRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSystemTaxRuleDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/system_tax_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemSystemTaxRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/system_tax_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemTestFielUploadCreate(files, observe = 'body', reportProgress = false, options) {
    if (files === null || files === undefined) {
      throw new Error('Required parameter files was null or undefined when calling systemTestFielUploadCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['multipart/form-data', 'application/x-www-form-urlencoded'];
    const canConsumeForm = this.canConsumeForm(consumes);
    let localVarFormParams;
    let localVarUseForm = false;
    let localVarConvertFormParamsToString = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    localVarUseForm = canConsumeForm;
    if (localVarUseForm) {
      localVarFormParams = new FormData();
    } else {
      localVarFormParams = new HttpParams({
        encoder: this.encoder
      });
    }
    if (files !== undefined) {
      const temp = Array.from(files);
      for (let i = 0; i < temp.length; i++) {
        localVarFormParams = localVarFormParams.append('files', temp[i]) || localVarFormParams;
      }
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/test_fiel_upload`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemTestUserDetailsRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/test_user_details`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  systemTimezonesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/system/timezones`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function SystemService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SystemService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SystemService,
    factory: SystemService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SystemService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class TargetMarketHandlingService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  targetMarketHandlingBulkTargetMarketCharacteristicCreate(postTargetMarketCharasteristicRequest, observe = 'body', reportProgress = false, options) {
    if (postTargetMarketCharasteristicRequest === null || postTargetMarketCharasteristicRequest === undefined) {
      throw new Error('Required parameter postTargetMarketCharasteristicRequest was null or undefined when calling targetMarketHandlingBulkTargetMarketCharacteristicCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/bulk_target_market_characteristic`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTargetMarketCharasteristicRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingBulkTargetMarketCrmProspectCreate(postTargetMarketCrmProspectRequest, observe = 'body', reportProgress = false, options) {
    if (postTargetMarketCrmProspectRequest === null || postTargetMarketCrmProspectRequest === undefined) {
      throw new Error('Required parameter postTargetMarketCrmProspectRequest was null or undefined when calling targetMarketHandlingBulkTargetMarketCrmProspectCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/bulk_target_market_crm_prospect`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTargetMarketCrmProspectRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingGroupedTargetMarketCharacteristicDestroy(serviceType, targetMarketId, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (serviceType !== undefined && serviceType !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, serviceType, 'service_type');
    }
    if (targetMarketId !== undefined && targetMarketId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, targetMarketId, 'target_market_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/grouped_target_market_characteristic`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingTargetMarketCharacteristicDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling targetMarketHandlingTargetMarketCharacteristicDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/target_market_characteristic`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingTargetMarketCharacteristicList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/target_market_characteristic`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingTargetMarketCreate(postTargetMarketRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/target_market`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTargetMarketRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingTargetMarketCrmProspectDestroy(crmProspectId, targetMarketId, observe = 'body', reportProgress = false, options) {
    if (crmProspectId === null || crmProspectId === undefined) {
      throw new Error('Required parameter crmProspectId was null or undefined when calling targetMarketHandlingTargetMarketCrmProspectDestroy.');
    }
    if (targetMarketId === null || targetMarketId === undefined) {
      throw new Error('Required parameter targetMarketId was null or undefined when calling targetMarketHandlingTargetMarketCrmProspectDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (crmProspectId !== undefined && crmProspectId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, crmProspectId, 'crm_prospect_id');
    }
    if (targetMarketId !== undefined && targetMarketId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, targetMarketId, 'target_market_id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/target_market_crm_prospect`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingTargetMarketDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling targetMarketHandlingTargetMarketDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/target_market`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  targetMarketHandlingTargetMarketList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/target_market_handling/target_market`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function TargetMarketHandlingService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TargetMarketHandlingService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: TargetMarketHandlingService,
    factory: TargetMarketHandlingService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TargetMarketHandlingService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class TasksService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  tasksProjectWorkgroupCreate(postTaskWorkGroupRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/project_workgroup`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTaskWorkGroupRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksProjectWorkgroupDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/project_workgroup`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksProjectWorkgroupList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/project_workgroup`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksProjectWorkgroupMemberCreate(postTaskWorkGroupMemberRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/project_workgroup_member`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTaskWorkGroupMemberRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksProjectWorkgroupMemberDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/project_workgroup_member`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksProjectWorkgroupMemberList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/project_workgroup_member`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskCreate(postTaskRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTaskRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskPartialUpdate(patchedPatchTaskRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task`;
    return this.httpClient.request('patch', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: patchedPatchTaskRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskProjectWorkgroupRuleCreate(postTaskRuleRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task_project_workgroup_rule`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postTaskRuleRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskProjectWorkgroupRuleDestroy(id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task_project_workgroup_rule`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  tasksTaskProjectWorkgroupRuleList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/tasks/task_project_workgroup_rule`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function TasksService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TasksService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: TasksService,
    factory: TasksService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TasksService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class TranslateService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  translateAiTextTranslateCreate(postFreeTextTranslationRequest, observe = 'body', reportProgress = false, options) {
    if (postFreeTextTranslationRequest === null || postFreeTextTranslationRequest === undefined) {
      throw new Error('Required parameter postFreeTextTranslationRequest was null or undefined when calling translateAiTextTranslateCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/translate/ai_text_translate`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postFreeTextTranslationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function TranslateService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TranslateService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: TranslateService,
    factory: TranslateService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TranslateService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class UploadService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  /**
  * @param consumes string[] mime-types
  * @return true: consumes contains 'multipart/form-data', false: otherwise
  */
  canConsumeForm(consumes) {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  uploadAlbumCreate(postAlbumRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/album`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postAlbumRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadAlbumDestroy(businessId, id, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (businessId !== undefined && businessId !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, businessId, 'business_id');
    }
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/album`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadAlbumItemsCreate(requestBody, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/album_items`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: requestBody,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadAlbumItemsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/album_items`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadAlbumList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/album`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadAlbumTemplatePathList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/album_template_path`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadBlurContentCreate(contentToGetBlurRequest, observe = 'body', reportProgress = false, options) {
    if (contentToGetBlurRequest === null || contentToGetBlurRequest === undefined) {
      throw new Error('Required parameter contentToGetBlurRequest was null or undefined when calling uploadBlurContentCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/blur_content`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: contentToGetBlurRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadBlurContentRetrieve(hash, observe = 'body', reportProgress = false, options) {
    if (hash === null || hash === undefined) {
      throw new Error('Required parameter hash was null or undefined when calling uploadBlurContentRetrieve.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (hash !== undefined && hash !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, hash, 'hash');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/blur_content`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadDeleteDriveToGalleryCreate(deleteDriveGalleryRelationRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/delete_drive_to_gallery`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: deleteDriveGalleryRelationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadRelateDriveToGalleryCreate(postRelateDriveToGalleryRequest, observe = 'body', reportProgress = false, options) {
    if (postRelateDriveToGalleryRequest === null || postRelateDriveToGalleryRequest === undefined) {
      throw new Error('Required parameter postRelateDriveToGalleryRequest was null or undefined when calling uploadRelateDriveToGalleryCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/relate_drive_to_gallery`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postRelateDriveToGalleryRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadRelateDriveToGalleryList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/relate_drive_to_gallery`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  uploadUploadImageCreate(uploadProfileType, imageUploadType, imageFile, imageCroppedFile, id, observe = 'body', reportProgress = false, options) {
    if (uploadProfileType === null || uploadProfileType === undefined) {
      throw new Error('Required parameter uploadProfileType was null or undefined when calling uploadUploadImageCreate.');
    }
    if (imageUploadType === null || imageUploadType === undefined) {
      throw new Error('Required parameter imageUploadType was null or undefined when calling uploadUploadImageCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['multipart/form-data'];
    const canConsumeForm = this.canConsumeForm(consumes);
    let localVarFormParams;
    let localVarUseForm = false;
    let localVarConvertFormParamsToString = false;
    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    localVarUseForm = canConsumeForm;
    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    localVarUseForm = canConsumeForm;
    if (localVarUseForm) {
      localVarFormParams = new FormData();
    } else {
      localVarFormParams = new HttpParams({
        encoder: this.encoder
      });
    }
    if (imageFile !== undefined) {
      localVarFormParams = localVarFormParams.append('image_file', imageFile) || localVarFormParams;
    }
    if (imageCroppedFile !== undefined) {
      localVarFormParams = localVarFormParams.append('image_cropped_file', imageCroppedFile) || localVarFormParams;
    }
    if (id !== undefined) {
      localVarFormParams = localVarFormParams.append('id', id) || localVarFormParams;
    }
    if (uploadProfileType !== undefined) {
      localVarFormParams = localVarFormParams.append('upload_profile_type', uploadProfileType) || localVarFormParams;
    }
    if (imageUploadType !== undefined) {
      localVarFormParams = localVarFormParams.append('image_upload_type', imageUploadType) || localVarFormParams;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/upload/upload_image`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: localVarConvertFormParamsToString ? localVarFormParams.toString() : localVarFormParams,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function UploadService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || UploadService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: UploadService,
    factory: UploadService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UploadService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class UserService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  userAddressBookCreate(addressBookRequest, observe = 'body', reportProgress = false, options) {
    if (addressBookRequest === null || addressBookRequest === undefined) {
      throw new Error('Required parameter addressBookRequest was null or undefined when calling userAddressBookCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/address_book`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: addressBookRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userAddressBookDestroy(id, observe = 'body', reportProgress = false, options) {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling userAddressBookDestroy.');
    }
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (id !== undefined && id !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, id, 'id');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/address_book`;
    return this.httpClient.request('delete', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userAddressBookList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/address_book`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userDeleteUserCreate(userDeleteRequest, observe = 'body', reportProgress = false, options) {
    if (userDeleteRequest === null || userDeleteRequest === undefined) {
      throw new Error('Required parameter userDeleteRequest was null or undefined when calling userDeleteUserCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/delete_user`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: userDeleteRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userGetcommondataRetrieve(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/getcommondata`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userLoginCreate(loginRequest, observe = 'body', reportProgress = false, options) {
    if (loginRequest === null || loginRequest === undefined) {
      throw new Error('Required parameter loginRequest was null or undefined when calling userLoginCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/login/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: loginRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userLogoutCreate(logoutRequest, observe = 'body', reportProgress = false, options) {
    if (logoutRequest === null || logoutRequest === undefined) {
      throw new Error('Required parameter logoutRequest was null or undefined when calling userLogoutCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/logout`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: logoutRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userPasswordchangeCreate(changeUserPasswordRequest, observe = 'body', reportProgress = false, options) {
    if (changeUserPasswordRequest === null || changeUserPasswordRequest === undefined) {
      throw new Error('Required parameter changeUserPasswordRequest was null or undefined when calling userPasswordchangeCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/passwordchange`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: changeUserPasswordRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userRegistrationCreate(registrationRequest, observe = 'body', reportProgress = false, options) {
    if (registrationRequest === null || registrationRequest === undefined) {
      throw new Error('Required parameter registrationRequest was null or undefined when calling userRegistrationCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/registration/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: registrationRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userRegistrationPasswordresetCreate(passwordResetRequest, observe = 'body', reportProgress = false, options) {
    if (passwordResetRequest === null || passwordResetRequest === undefined) {
      throw new Error('Required parameter passwordResetRequest was null or undefined when calling userRegistrationPasswordresetCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/registration/passwordreset`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: passwordResetRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userRegistrationResendCreate(emialResendRequest, observe = 'body', reportProgress = false, options) {
    if (emialResendRequest === null || emialResendRequest === undefined) {
      throw new Error('Required parameter emialResendRequest was null or undefined when calling userRegistrationResendCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/registration/resend`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: emialResendRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  userSocialLoginCreate(loginWithSocialMediaRequest, observe = 'body', reportProgress = false, options) {
    if (loginWithSocialMediaRequest === null || loginWithSocialMediaRequest === undefined) {
      throw new Error('Required parameter loginWithSocialMediaRequest was null or undefined when calling userSocialLoginCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/user/social_login`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: loginWithSocialMediaRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function UserService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || UserService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: UserService,
    factory: UserService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UserService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class UsersService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  users2faEnableCreate(observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/users/2fa/enable/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  users2faVerifyCreate(verify2FARequest, observe = 'body', reportProgress = false, options) {
    if (verify2FARequest === null || verify2FARequest === undefined) {
      throw new Error('Required parameter verify2FARequest was null or undefined when calling users2faVerifyCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = [];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/users/2fa/verify/`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: verify2FARequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function UsersService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || UsersService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: UsersService,
    factory: UsersService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UsersService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable perfectionist/sort-imports */
class WalletService {
  httpClient;
  basePath = 'http://core';
  defaultHeaders = new HttpHeaders();
  configuration = new Configuration();
  encoder;
  constructor(httpClient, basePath, configuration) {
    this.httpClient = httpClient;
    if (configuration) {
      this.configuration = configuration;
    }
    if (typeof this.configuration.basePath !== 'string') {
      const firstBasePath = Array.isArray(basePath) ? basePath[0] : undefined;
      if (firstBasePath != undefined) {
        basePath = firstBasePath;
      }
      if (typeof basePath !== 'string') {
        basePath = this.basePath;
      }
      this.configuration.basePath = basePath;
    }
    this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
  }
  addToHttpParams(httpParams, value, key) {
    if (typeof value === "object" && value instanceof Date === false) {
      httpParams = this.addToHttpParamsRecursive(httpParams, value);
    } else {
      httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
    }
    return httpParams;
  }
  addToHttpParamsRecursive(httpParams, value, key) {
    if (value == null) {
      return httpParams;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        if (key != null) {
          value.forEach(item => {
            httpParams = httpParams.append(key, item);
          });
          return httpParams;
        } else {
          throw Error("key may not be null if value is an array");
        }
      } else if (value instanceof Date) {
        if (key != null) {
          httpParams = httpParams.append(key, value.toISOString().substring(0, 10));
        } else {
          throw Error("key may not be null if value is Date");
        }
      } else {
        Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
      }
    } else if (key != null) {
      httpParams = httpParams.append(key, value);
    } else {
      throw Error("key may not be null if value is not object or array");
    }
    return httpParams;
  }
  walletCreateWalletCreate(createWalletSerializersRequest, observe = 'body', reportProgress = false, options) {
    if (createWalletSerializersRequest === null || createWalletSerializersRequest === undefined) {
      throw new Error('Required parameter createWalletSerializersRequest was null or undefined when calling walletCreateWalletCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/wallet/create_wallet`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: createWalletSerializersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  walletFxConvertCreate(postFxConvertSerializersRequest, observe = 'body', reportProgress = false, options) {
    if (postFxConvertSerializersRequest === null || postFxConvertSerializersRequest === undefined) {
      throw new Error('Required parameter postFxConvertSerializersRequest was null or undefined when calling walletFxConvertCreate.');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/wallet/fx_convert`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: postFxConvertSerializersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  walletGetWalletsCurrenciesList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/wallet/get_wallets_currencies`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  walletSetDefaultWalletCreate(getWalletDetailsParamsSerializersRequest, observe = 'body', reportProgress = false, options) {
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    // to determine the Content-Type header
    const consumes = ['application/json', 'application/x-www-form-urlencoded', 'multipart/form-data'];
    const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/wallet/set_default_wallet`;
    return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      body: getWalletDetailsParamsSerializersRequest,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  walletWalletTransactionsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/wallet/wallet_transactions`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  walletWalletsList(page, pageSize, searchJson, observe = 'body', reportProgress = false, options) {
    let localVarQueryParameters = new HttpParams({
      encoder: this.encoder
    });
    if (page !== undefined && page !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
    }
    if (pageSize !== undefined && pageSize !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, pageSize, 'page_size');
    }
    if (searchJson !== undefined && searchJson !== null) {
      localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchJson, 'search_json');
    }
    let localVarHeaders = this.defaultHeaders;
    let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
    if (localVarHttpHeaderAcceptSelected === undefined) {
      // to determine the Accept header
      const httpHeaderAccepts = ['application/json'];
      localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    }
    if (localVarHttpHeaderAcceptSelected !== undefined) {
      localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
    }
    let localVarHttpContext = options && options.context;
    if (localVarHttpContext === undefined) {
      localVarHttpContext = new HttpContext();
    }
    let localVarTransferCache = options && options.transferCache;
    if (localVarTransferCache === undefined) {
      localVarTransferCache = true;
    }
    let responseType_ = 'json';
    if (localVarHttpHeaderAcceptSelected) {
      if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
        responseType_ = 'text';
      } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
        responseType_ = 'json';
      } else {
        responseType_ = 'blob';
      }
    }
    let localVarPath = `/api/wallet/wallets`;
    return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
      context: localVarHttpContext,
      params: localVarQueryParameters,
      responseType: responseType_,
      withCredentials: this.configuration.withCredentials,
      headers: localVarHeaders,
      observe: observe,
      transferCache: localVarTransferCache,
      reportProgress: reportProgress
    });
  }
  static ɵfac = function WalletService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WalletService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(Configuration, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: WalletService,
    factory: WalletService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WalletService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.HttpClient
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [BASE_PATH]
    }]
  }, {
    type: Configuration,
    decorators: [{
      type: Optional
    }]
  }], null);
})();
const APIS = [AccountingService, ActivityStreamService, AdsService, ApiService, AuthService, BanksService, BidService, BrandedDomainHandlingService, BusinessRulesService, CalendarService, ChatService, CommunityApplicationService, ContactsService, CoworkersService, CrmService, DriveService, ElasticService, EmailClientService, ErpService, ErpSideService, FeaturesService, FileManagerService, HrmsService, InteractionsService, LocationService, LocationManagerService, MainProfileService, NotificationsService, OrgChartService, PartnerOfficeService, PaymentService, PublicBusinessService, PurchaseService, PushNotificationService, RbacRuleService, SalesTeamsService, SearchService, ServiceAreaService, SharedService, ShoppingCartService, SmartCubeService, SubscriptionService, SystemService, TargetMarketHandlingService, TasksService, TranslateService, UploadService, UserService, UsersService, WalletService];

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var AccessOptionPattern;
(function (AccessOptionPattern) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["Crm"] = "crm";
    RuleTypeEnum["Hrms"] = "hrms";
    RuleTypeEnum["CompanyStream"] = "company_stream";
    RuleTypeEnum["OrgChart"] = "org_chart";
    RuleTypeEnum["Chat"] = "chat";
    RuleTypeEnum["CoWorkers"] = "co_workers";
    RuleTypeEnum["SalesTeam"] = "sales_team";
    RuleTypeEnum["TargetMarket"] = "target_market";
    RuleTypeEnum["WebsiteAppManager"] = "website_app_manager";
    RuleTypeEnum["SocialConnections"] = "social_connections";
    RuleTypeEnum["PopularityCenter"] = "popularity_center";
    RuleTypeEnum["MarketingTools"] = "marketing_tools";
    RuleTypeEnum["TaskManager"] = "task_manager";
    RuleTypeEnum["EmailManager"] = "email_manager";
    RuleTypeEnum["WorktimeManager"] = "worktime_manager";
    RuleTypeEnum["LocationManager"] = "location_manager";
    RuleTypeEnum["SmartCube"] = "smart_cube";
    RuleTypeEnum["Bi"] = "bi";
    RuleTypeEnum["Tax"] = "tax";
    RuleTypeEnum["Transportation"] = "transportation";
    RuleTypeEnum["Insurance"] = "insurance";
    RuleTypeEnum["Payment"] = "payment";
    RuleTypeEnum["BankAccounts"] = "bank_accounts";
    RuleTypeEnum["Procurement"] = "procurement";
    RuleTypeEnum["Auction"] = "auction";
    RuleTypeEnum["Settings"] = "settings";
    RuleTypeEnum["RbacModule"] = "rbac_module";
  })(RuleTypeEnum = AccessOptionPattern.RuleTypeEnum || (AccessOptionPattern.RuleTypeEnum = {}));
})(AccessOptionPattern || (AccessOptionPattern = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var AccessOptionRule;
(function (AccessOptionRule) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["Crm"] = "crm";
    RuleTypeEnum["Hrms"] = "hrms";
    RuleTypeEnum["CompanyStream"] = "company_stream";
    RuleTypeEnum["OrgChart"] = "org_chart";
    RuleTypeEnum["Chat"] = "chat";
    RuleTypeEnum["CoWorkers"] = "co_workers";
    RuleTypeEnum["SalesTeam"] = "sales_team";
    RuleTypeEnum["TargetMarket"] = "target_market";
    RuleTypeEnum["WebsiteAppManager"] = "website_app_manager";
    RuleTypeEnum["SocialConnections"] = "social_connections";
    RuleTypeEnum["PopularityCenter"] = "popularity_center";
    RuleTypeEnum["MarketingTools"] = "marketing_tools";
    RuleTypeEnum["TaskManager"] = "task_manager";
    RuleTypeEnum["EmailManager"] = "email_manager";
    RuleTypeEnum["WorktimeManager"] = "worktime_manager";
    RuleTypeEnum["LocationManager"] = "location_manager";
    RuleTypeEnum["SmartCube"] = "smart_cube";
    RuleTypeEnum["Bi"] = "bi";
    RuleTypeEnum["Tax"] = "tax";
    RuleTypeEnum["Transportation"] = "transportation";
    RuleTypeEnum["Insurance"] = "insurance";
    RuleTypeEnum["Payment"] = "payment";
    RuleTypeEnum["BankAccounts"] = "bank_accounts";
    RuleTypeEnum["Procurement"] = "procurement";
    RuleTypeEnum["Auction"] = "auction";
    RuleTypeEnum["Settings"] = "settings";
    RuleTypeEnum["RbacModule"] = "rbac_module";
  })(RuleTypeEnum = AccessOptionRule.RuleTypeEnum || (AccessOptionRule.RuleTypeEnum = {}));
})(AccessOptionRule || (AccessOptionRule = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ActivateCommunityApplicationProfileRequest;
(function (ActivateCommunityApplicationProfileRequest) {
  let ProfileTypeEnum;
  (function (ProfileTypeEnum) {
    ProfileTypeEnum["LanguageExchange"] = "language_exchange";
    ProfileTypeEnum["BusinessNetworking"] = "business_networking";
    ProfileTypeEnum["Dating"] = "dating";
    ProfileTypeEnum["Garden"] = "garden";
    ProfileTypeEnum["TravelBuddies"] = "travel_buddies";
    ProfileTypeEnum["SecondChance"] = "second_chance";
    ProfileTypeEnum["BeGreen"] = "be_green";
    ProfileTypeEnum["BucketList"] = "bucket_list";
    ProfileTypeEnum["GoalManager"] = "goal_manager";
    ProfileTypeEnum["Pets"] = "pets";
    ProfileTypeEnum["Volunteer"] = "volunteer";
    ProfileTypeEnum["Cooking"] = "cooking";
    ProfileTypeEnum["Collectors"] = "collectors";
    ProfileTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ProfileTypeEnum["KidsSpace"] = "kids_space";
    ProfileTypeEnum["Sport"] = "sport";
    ProfileTypeEnum["Mindset"] = "mindset";
    ProfileTypeEnum["DiyHandmade"] = "diy_handmade";
    ProfileTypeEnum["Tech"] = "tech";
    ProfileTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ProfileTypeEnum["BToB"] = "b_to_b";
    ProfileTypeEnum["DigitalNomad"] = "digital_nomad";
    ProfileTypeEnum["Gaming"] = "gaming";
    ProfileTypeEnum["Erotic"] = "erotic";
    ProfileTypeEnum["Roommate"] = "roommate";
    ProfileTypeEnum["Aviation"] = "aviation";
    ProfileTypeEnum["Cars"] = "cars";
    ProfileTypeEnum["Boat"] = "boat";
    ProfileTypeEnum["Fitness"] = "fitness";
    ProfileTypeEnum["Events"] = "events";
    ProfileTypeEnum["TaskManager"] = "task_manager";
    ProfileTypeEnum["Services"] = "services";
    ProfileTypeEnum["Academy"] = "academy";
    ProfileTypeEnum["StoreFinder"] = "store_finder";
    ProfileTypeEnum["ActivityStream"] = "activity_stream";
    ProfileTypeEnum["Calendar"] = "calendar";
    ProfileTypeEnum["Contacts"] = "contacts";
    ProfileTypeEnum["Notes"] = "notes";
    ProfileTypeEnum["Drive"] = "drive";
    ProfileTypeEnum["Taskly"] = "taskly";
    ProfileTypeEnum["MarketSpace"] = "market_space";
    ProfileTypeEnum["Chat"] = "chat";
    ProfileTypeEnum["DoctorFinder"] = "doctor_finder";
    ProfileTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ProfileTypeEnum["Renting"] = "renting";
    ProfileTypeEnum["Booking"] = "booking";
    ProfileTypeEnum["Job"] = "job";
    ProfileTypeEnum["DigitalDownloads"] = "digital_downloads";
    ProfileTypeEnum["PhysicalProduct"] = "physical_product";
    ProfileTypeEnum["EmailManager"] = "email_manager";
    ProfileTypeEnum["RealEstate"] = "real_estate";
    ProfileTypeEnum["CoworkingSpace"] = "coworking_space";
    ProfileTypeEnum["Grocery"] = "grocery";
    ProfileTypeEnum["Fashion"] = "fashion";
    ProfileTypeEnum["HomeDesign"] = "home_design";
    ProfileTypeEnum["Taxi"] = "taxi";
    ProfileTypeEnum["SkillLink"] = "skill_link";
    ProfileTypeEnum["MediaChannels"] = "media_channels";
  })(ProfileTypeEnum = ActivateCommunityApplicationProfileRequest.ProfileTypeEnum || (ActivateCommunityApplicationProfileRequest.ProfileTypeEnum = {}));
})(ActivateCommunityApplicationProfileRequest || (ActivateCommunityApplicationProfileRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ActivityBusiness;
(function (ActivityBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = ActivityBusiness.SubscriptionTypeEnum || (ActivityBusiness.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = ActivityBusiness.IsFollowEnum || (ActivityBusiness.IsFollowEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = ActivityBusiness.IsBlockedEnum || (ActivityBusiness.IsBlockedEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = ActivityBusiness.IsReportedEnum || (ActivityBusiness.IsReportedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = ActivityBusiness.IsPendingEnum || (ActivityBusiness.IsPendingEnum = {}));
})(ActivityBusiness || (ActivityBusiness = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ActivityCoWorker;
(function (ActivityCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = ActivityCoWorker.StatusEnum || (ActivityCoWorker.StatusEnum = {}));
})(ActivityCoWorker || (ActivityCoWorker = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ActivityMediaAlbumItem;
(function (ActivityMediaAlbumItem) {
  let MediaFileTypeEnum;
  (function (MediaFileTypeEnum) {
    MediaFileTypeEnum["Image"] = "image";
    MediaFileTypeEnum["Video"] = "video";
    MediaFileTypeEnum["Audio"] = "audio";
    MediaFileTypeEnum["Document"] = "document";
    MediaFileTypeEnum["Zip"] = "zip";
    MediaFileTypeEnum["Other"] = "other";
  })(MediaFileTypeEnum = ActivityMediaAlbumItem.MediaFileTypeEnum || (ActivityMediaAlbumItem.MediaFileTypeEnum = {}));
})(ActivityMediaAlbumItem || (ActivityMediaAlbumItem = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ActivityUser;
(function (ActivityUser) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = ActivityUser.SubscriptionTypeEnum || (ActivityUser.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = ActivityUser.IsFollowEnum || (ActivityUser.IsFollowEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = ActivityUser.IsBlockedEnum || (ActivityUser.IsBlockedEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = ActivityUser.IsReportedEnum || (ActivityUser.IsReportedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = ActivityUser.IsPendingEnum || (ActivityUser.IsPendingEnum = {}));
})(ActivityUser || (ActivityUser = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var AddressBookModel;
(function (AddressBookModel) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = AddressBookModel.TypeEnum || (AddressBookModel.TypeEnum = {}));
})(AddressBookModel || (AddressBookModel = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var AddressBookRequest;
(function (AddressBookRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = AddressBookRequest.TypeEnum || (AddressBookRequest.TypeEnum = {}));
})(AddressBookRequest || (AddressBookRequest = {}));
var Album;
(function (Album) {
  let MediaShareEnum;
  (function (MediaShareEnum) {
    MediaShareEnum["Nobody"] = "nobody";
    MediaShareEnum["Anybody"] = "anybody";
    MediaShareEnum["ThoseFollowMe"] = "those_follow_me";
    MediaShareEnum["WhoIFollow"] = "who_i_follow";
    MediaShareEnum["Both"] = "both";
  })(MediaShareEnum = Album.MediaShareEnum || (Album.MediaShareEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = Album.IsReportedEnum || (Album.IsReportedEnum = {}));
})(Album || (Album = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var AlbumItems;
(function (AlbumItems) {
  let MediaFileTypeEnum;
  (function (MediaFileTypeEnum) {
    MediaFileTypeEnum["Image"] = "image";
    MediaFileTypeEnum["Video"] = "video";
    MediaFileTypeEnum["Audio"] = "audio";
    MediaFileTypeEnum["Document"] = "document";
    MediaFileTypeEnum["Zip"] = "zip";
    MediaFileTypeEnum["Other"] = "other";
  })(MediaFileTypeEnum = AlbumItems.MediaFileTypeEnum || (AlbumItems.MediaFileTypeEnum = {}));
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Adult"] = "adult";
    PostStateEnum["Normal"] = "normal";
    PostStateEnum["Sensitive"] = "sensitive";
  })(PostStateEnum = AlbumItems.PostStateEnum || (AlbumItems.PostStateEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = AlbumItems.IsReportedEnum || (AlbumItems.IsReportedEnum = {}));
})(AlbumItems || (AlbumItems = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var AllBusiness;
(function (AllBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = AllBusiness.SubscriptionTypeEnum || (AllBusiness.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = AllBusiness.IsFollowEnum || (AllBusiness.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = AllBusiness.IsReportedEnum || (AllBusiness.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = AllBusiness.IsBlockedEnum || (AllBusiness.IsBlockedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = AllBusiness.IsPendingEnum || (AllBusiness.IsPendingEnum = {}));
  let ErpTypeEnum;
  (function (ErpTypeEnum) {
    ErpTypeEnum["Personal"] = "personal";
    ErpTypeEnum["Business"] = "business";
  })(ErpTypeEnum = AllBusiness.ErpTypeEnum || (AllBusiness.ErpTypeEnum = {}));
  let LandingEnum;
  (function (LandingEnum) {
    LandingEnum["Ads"] = "ads";
    LandingEnum["Info"] = "info";
    LandingEnum["Activity"] = "activity";
    LandingEnum["MediaGallery"] = "media_gallery";
    LandingEnum["Followers"] = "followers";
    LandingEnum["Following"] = "following";
    LandingEnum["Coupon"] = "coupon";
    LandingEnum["Ratings"] = "ratings";
    LandingEnum["Location"] = "location";
  })(LandingEnum = AllBusiness.LandingEnum || (AllBusiness.LandingEnum = {}));
})(AllBusiness || (AllBusiness = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var ApplicationObject;
(function (ApplicationObject) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = ApplicationObject.IsSavedEnum || (ApplicationObject.IsSavedEnum = {}));
  let CommunityApplicationTypeEnum;
  (function (CommunityApplicationTypeEnum) {
    CommunityApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityApplicationTypeEnum["Dating"] = "dating";
    CommunityApplicationTypeEnum["Garden"] = "garden";
    CommunityApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityApplicationTypeEnum["SecondChance"] = "second_chance";
    CommunityApplicationTypeEnum["BeGreen"] = "be_green";
    CommunityApplicationTypeEnum["BucketList"] = "bucket_list";
    CommunityApplicationTypeEnum["GoalManager"] = "goal_manager";
    CommunityApplicationTypeEnum["Pets"] = "pets";
    CommunityApplicationTypeEnum["Volunteer"] = "volunteer";
    CommunityApplicationTypeEnum["Cooking"] = "cooking";
    CommunityApplicationTypeEnum["Collectors"] = "collectors";
    CommunityApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityApplicationTypeEnum["KidsSpace"] = "kids_space";
    CommunityApplicationTypeEnum["Sport"] = "sport";
    CommunityApplicationTypeEnum["Mindset"] = "mindset";
    CommunityApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityApplicationTypeEnum["Tech"] = "tech";
    CommunityApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityApplicationTypeEnum["BToB"] = "b_to_b";
    CommunityApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityApplicationTypeEnum["Gaming"] = "gaming";
    CommunityApplicationTypeEnum["Erotic"] = "erotic";
    CommunityApplicationTypeEnum["Roommate"] = "roommate";
    CommunityApplicationTypeEnum["Aviation"] = "aviation";
    CommunityApplicationTypeEnum["Cars"] = "cars";
    CommunityApplicationTypeEnum["Boat"] = "boat";
    CommunityApplicationTypeEnum["Fitness"] = "fitness";
    CommunityApplicationTypeEnum["Events"] = "events";
    CommunityApplicationTypeEnum["TaskManager"] = "task_manager";
    CommunityApplicationTypeEnum["Services"] = "services";
    CommunityApplicationTypeEnum["Academy"] = "academy";
    CommunityApplicationTypeEnum["StoreFinder"] = "store_finder";
    CommunityApplicationTypeEnum["ActivityStream"] = "activity_stream";
    CommunityApplicationTypeEnum["Calendar"] = "calendar";
    CommunityApplicationTypeEnum["Contacts"] = "contacts";
    CommunityApplicationTypeEnum["Notes"] = "notes";
    CommunityApplicationTypeEnum["Drive"] = "drive";
    CommunityApplicationTypeEnum["Taskly"] = "taskly";
    CommunityApplicationTypeEnum["MarketSpace"] = "market_space";
    CommunityApplicationTypeEnum["Chat"] = "chat";
    CommunityApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityApplicationTypeEnum["Renting"] = "renting";
    CommunityApplicationTypeEnum["Booking"] = "booking";
    CommunityApplicationTypeEnum["Job"] = "job";
    CommunityApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityApplicationTypeEnum["EmailManager"] = "email_manager";
    CommunityApplicationTypeEnum["RealEstate"] = "real_estate";
    CommunityApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityApplicationTypeEnum["Grocery"] = "grocery";
    CommunityApplicationTypeEnum["Fashion"] = "fashion";
    CommunityApplicationTypeEnum["HomeDesign"] = "home_design";
    CommunityApplicationTypeEnum["Taxi"] = "taxi";
    CommunityApplicationTypeEnum["SkillLink"] = "skill_link";
    CommunityApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityApplicationTypeEnum = ApplicationObject.CommunityApplicationTypeEnum || (ApplicationObject.CommunityApplicationTypeEnum = {}));
})(ApplicationObject || (ApplicationObject = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var BankDatabase;
(function (BankDatabase) {
  let BankTypeEnum;
  (function (BankTypeEnum) {
    BankTypeEnum["Traditional"] = "Traditional";
    BankTypeEnum["CompletelyOnline"] = "Completely_online";
  })(BankTypeEnum = BankDatabase.BankTypeEnum || (BankDatabase.BankTypeEnum = {}));
})(BankDatabase || (BankDatabase = {}));
var BankTransfer;
(function (BankTransfer) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Completed"] = "completed";
    StatusEnum["Cancelled"] = "cancelled";
    StatusEnum["Refund"] = "refund";
  })(StatusEnum = BankTransfer.StatusEnum || (BankTransfer.StatusEnum = {}));
})(BankTransfer || (BankTransfer = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var BasicCalculation;
(function (BasicCalculation) {
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["FreeShipping"] = "Free shipping";
    CalculationTypeEnum["ItemBased"] = "Item based";
    CalculationTypeEnum["NetTotal"] = "Net total";
    CalculationTypeEnum["NetWeight"] = "Net weight";
    CalculationTypeEnum["NetLength"] = "Net length";
    CalculationTypeEnum["NetVolume"] = "Net volume";
  })(CalculationTypeEnum = BasicCalculation.CalculationTypeEnum || (BasicCalculation.CalculationTypeEnum = {}));
})(BasicCalculation || (BasicCalculation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var BulkEditRequest;
(function (BulkEditRequest) {
  let ModelTypeEnum;
  (function (ModelTypeEnum) {
    ModelTypeEnum["SmartAd"] = "smart_ad";
    ModelTypeEnum["SmartItem"] = "smart_item";
    ModelTypeEnum["Contacts"] = "contacts";
    ModelTypeEnum["Prospect"] = "prospect";
    ModelTypeEnum["Deal"] = "deal";
    ModelTypeEnum["Lead"] = "lead";
    ModelTypeEnum["Offer"] = "offer";
    ModelTypeEnum["LocationManager"] = "location_manager";
    ModelTypeEnum["Task"] = "task";
    ModelTypeEnum["ServiceArea"] = "service_area";
    ModelTypeEnum["ProjectWorkgroup"] = "project_workgroup";
    ModelTypeEnum["Email"] = "email";
    ModelTypeEnum["Notes"] = "notes";
  })(ModelTypeEnum = BulkEditRequest.ModelTypeEnum || (BulkEditRequest.ModelTypeEnum = {}));
  let EditTypeEnum;
  (function (EditTypeEnum) {
    EditTypeEnum["Delete"] = "delete";
    EditTypeEnum["Activate"] = "activate";
    EditTypeEnum["Deactivate"] = "deactivate";
    EditTypeEnum["Draft"] = "draft";
    EditTypeEnum["UnDraft"] = "un_draft";
    EditTypeEnum["Scheduled"] = "scheduled";
    EditTypeEnum["Unscheduled"] = "unscheduled";
  })(EditTypeEnum = BulkEditRequest.EditTypeEnum || (BulkEditRequest.EditTypeEnum = {}));
})(BulkEditRequest || (BulkEditRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var Business;
(function (Business) {
  let ErpTypeEnum;
  (function (ErpTypeEnum) {
    ErpTypeEnum["Personal"] = "personal";
    ErpTypeEnum["Business"] = "business";
  })(ErpTypeEnum = Business.ErpTypeEnum || (Business.ErpTypeEnum = {}));
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = Business.SubscriptionTypeEnum || (Business.SubscriptionTypeEnum = {}));
})(Business || (Business = {}));
var BusinessActiveCoWorker;
(function (BusinessActiveCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = BusinessActiveCoWorker.StatusEnum || (BusinessActiveCoWorker.StatusEnum = {}));
})(BusinessActiveCoWorker || (BusinessActiveCoWorker = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var BusinessCoWorker;
(function (BusinessCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = BusinessCoWorker.StatusEnum || (BusinessCoWorker.StatusEnum = {}));
})(BusinessCoWorker || (BusinessCoWorker = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var BusinessProfileRequestRequest;
(function (BusinessProfileRequestRequest) {
  let FxModuleEnum;
  (function (FxModuleEnum) {
    FxModuleEnum["UseOwn"] = "use_own";
    FxModuleEnum["UseSystem"] = "use_system";
    FxModuleEnum["OnlyAdded"] = "only_added";
  })(FxModuleEnum = BusinessProfileRequestRequest.FxModuleEnum || (BusinessProfileRequestRequest.FxModuleEnum = {}));
  let ErpTypeEnum;
  (function (ErpTypeEnum) {
    ErpTypeEnum["Personal"] = "personal";
    ErpTypeEnum["Business"] = "business";
  })(ErpTypeEnum = BusinessProfileRequestRequest.ErpTypeEnum || (BusinessProfileRequestRequest.ErpTypeEnum = {}));
  let LandingEnum;
  (function (LandingEnum) {
    LandingEnum["Ads"] = "ads";
    LandingEnum["Info"] = "info";
    LandingEnum["Activity"] = "activity";
    LandingEnum["MediaGallery"] = "media_gallery";
    LandingEnum["Followers"] = "followers";
    LandingEnum["Following"] = "following";
    LandingEnum["Coupon"] = "coupon";
    LandingEnum["Ratings"] = "ratings";
    LandingEnum["Location"] = "location";
  })(LandingEnum = BusinessProfileRequestRequest.LandingEnum || (BusinessProfileRequestRequest.LandingEnum = {}));
})(BusinessProfileRequestRequest || (BusinessProfileRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Calendar;
(function (Calendar) {
  let WeekFirstDayEnum;
  (function (WeekFirstDayEnum) {
    WeekFirstDayEnum["Sunday"] = "Sunday";
    WeekFirstDayEnum["Monday"] = "Monday";
  })(WeekFirstDayEnum = Calendar.WeekFirstDayEnum || (Calendar.WeekFirstDayEnum = {}));
  let TimeFormatEnum;
  (function (TimeFormatEnum) {
    TimeFormatEnum["_12"] = "12";
    TimeFormatEnum["_24"] = "24";
  })(TimeFormatEnum = Calendar.TimeFormatEnum || (Calendar.TimeFormatEnum = {}));
  let CalendarTypeEnum;
  (function (CalendarTypeEnum) {
    CalendarTypeEnum["Service"] = "service";
    CalendarTypeEnum["Rent"] = "rent";
    CalendarTypeEnum["LocationEmployee"] = "location_employee";
    CalendarTypeEnum["LocationCustomer"] = "location_customer";
    CalendarTypeEnum["CoWorker"] = "co_worker";
    CalendarTypeEnum["Project"] = "project";
    CalendarTypeEnum["Workgroup"] = "workgroup";
    CalendarTypeEnum["Booking"] = "booking";
    CalendarTypeEnum["StaffWorkflow"] = "staff_workflow";
    CalendarTypeEnum["User"] = "user";
  })(CalendarTypeEnum = Calendar.CalendarTypeEnum || (Calendar.CalendarTypeEnum = {}));
})(Calendar || (Calendar = {}));
var CalendarAppointment;
(function (CalendarAppointment) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Confirmed"] = "confirmed";
    StatusEnum["Cancelled"] = "cancelled";
    StatusEnum["Completed"] = "completed";
  })(StatusEnum = CalendarAppointment.StatusEnum || (CalendarAppointment.StatusEnum = {}));
})(CalendarAppointment || (CalendarAppointment = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarAppointmentResponse;
(function (CalendarAppointmentResponse) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Confirmed"] = "confirmed";
    StatusEnum["Cancelled"] = "cancelled";
    StatusEnum["Completed"] = "completed";
  })(StatusEnum = CalendarAppointmentResponse.StatusEnum || (CalendarAppointmentResponse.StatusEnum = {}));
})(CalendarAppointmentResponse || (CalendarAppointmentResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarAvilability;
(function (CalendarAvilability) {
  let DayEnum;
  (function (DayEnum) {
    DayEnum["Sunday"] = "Sunday";
    DayEnum["Monday"] = "Monday";
    DayEnum["Tuesday"] = "Tuesday";
    DayEnum["Wednesday"] = "Wednesday";
    DayEnum["Thursday"] = "Thursday";
    DayEnum["Friday"] = "Friday";
    DayEnum["Saturday"] = "Saturday";
  })(DayEnum = CalendarAvilability.DayEnum || (CalendarAvilability.DayEnum = {}));
  let OpenEnum;
  (function (OpenEnum) {
    OpenEnum["OpenNonstop"] = "open_nonstop";
    OpenEnum["OpenByTime"] = "open_by_time";
    OpenEnum["Close"] = "close";
  })(OpenEnum = CalendarAvilability.OpenEnum || (CalendarAvilability.OpenEnum = {}));
})(CalendarAvilability || (CalendarAvilability = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarBreak;
(function (CalendarBreak) {
  let DayEnum;
  (function (DayEnum) {
    DayEnum["Sunday"] = "Sunday";
    DayEnum["Monday"] = "Monday";
    DayEnum["Tuesday"] = "Tuesday";
    DayEnum["Wednesday"] = "Wednesday";
    DayEnum["Thursday"] = "Thursday";
    DayEnum["Friday"] = "Friday";
    DayEnum["Saturday"] = "Saturday";
  })(DayEnum = CalendarBreak.DayEnum || (CalendarBreak.DayEnum = {}));
})(CalendarBreak || (CalendarBreak = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarBreakTimeCreateOrUpdate;
(function (CalendarBreakTimeCreateOrUpdate) {
  let WeekdayEnum;
  (function (WeekdayEnum) {
    WeekdayEnum["Sunday"] = "Sunday";
    WeekdayEnum["Monday"] = "Monday";
    WeekdayEnum["Tuesday"] = "Tuesday";
    WeekdayEnum["Wednesday"] = "Wednesday";
    WeekdayEnum["Thursday"] = "Thursday";
    WeekdayEnum["Friday"] = "Friday";
    WeekdayEnum["Saturday"] = "Saturday";
  })(WeekdayEnum = CalendarBreakTimeCreateOrUpdate.WeekdayEnum || (CalendarBreakTimeCreateOrUpdate.WeekdayEnum = {}));
})(CalendarBreakTimeCreateOrUpdate || (CalendarBreakTimeCreateOrUpdate = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarBreakTimeCreateOrUpdateRequest;
(function (CalendarBreakTimeCreateOrUpdateRequest) {
  let WeekdayEnum;
  (function (WeekdayEnum) {
    WeekdayEnum["Sunday"] = "Sunday";
    WeekdayEnum["Monday"] = "Monday";
    WeekdayEnum["Tuesday"] = "Tuesday";
    WeekdayEnum["Wednesday"] = "Wednesday";
    WeekdayEnum["Thursday"] = "Thursday";
    WeekdayEnum["Friday"] = "Friday";
    WeekdayEnum["Saturday"] = "Saturday";
  })(WeekdayEnum = CalendarBreakTimeCreateOrUpdateRequest.WeekdayEnum || (CalendarBreakTimeCreateOrUpdateRequest.WeekdayEnum = {}));
})(CalendarBreakTimeCreateOrUpdateRequest || (CalendarBreakTimeCreateOrUpdateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarCalendarDayPriceCreateOrUpdate;
(function (CalendarCalendarDayPriceCreateOrUpdate) {
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "discount";
    PriceTypeEnum["ExtraChange"] = "extra_change";
  })(PriceTypeEnum = CalendarCalendarDayPriceCreateOrUpdate.PriceTypeEnum || (CalendarCalendarDayPriceCreateOrUpdate.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["Fixed"] = "fixed";
    PriceChangerTypeEnum["Percentage"] = "percentage";
  })(PriceChangerTypeEnum = CalendarCalendarDayPriceCreateOrUpdate.PriceChangerTypeEnum || (CalendarCalendarDayPriceCreateOrUpdate.PriceChangerTypeEnum = {}));
})(CalendarCalendarDayPriceCreateOrUpdate || (CalendarCalendarDayPriceCreateOrUpdate = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarCalendarDayPriceCreateOrUpdateRequest;
(function (CalendarCalendarDayPriceCreateOrUpdateRequest) {
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "discount";
    PriceTypeEnum["ExtraChange"] = "extra_change";
  })(PriceTypeEnum = CalendarCalendarDayPriceCreateOrUpdateRequest.PriceTypeEnum || (CalendarCalendarDayPriceCreateOrUpdateRequest.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["Fixed"] = "fixed";
    PriceChangerTypeEnum["Percentage"] = "percentage";
  })(PriceChangerTypeEnum = CalendarCalendarDayPriceCreateOrUpdateRequest.PriceChangerTypeEnum || (CalendarCalendarDayPriceCreateOrUpdateRequest.PriceChangerTypeEnum = {}));
})(CalendarCalendarDayPriceCreateOrUpdateRequest || (CalendarCalendarDayPriceCreateOrUpdateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarCalendarHourPriceCreateOrUpdate;
(function (CalendarCalendarHourPriceCreateOrUpdate) {
  let WeekdayEnum;
  (function (WeekdayEnum) {
    WeekdayEnum["Sunday"] = "Sunday";
    WeekdayEnum["Monday"] = "Monday";
    WeekdayEnum["Tuesday"] = "Tuesday";
    WeekdayEnum["Wednesday"] = "Wednesday";
    WeekdayEnum["Thursday"] = "Thursday";
    WeekdayEnum["Friday"] = "Friday";
    WeekdayEnum["Saturday"] = "Saturday";
  })(WeekdayEnum = CalendarCalendarHourPriceCreateOrUpdate.WeekdayEnum || (CalendarCalendarHourPriceCreateOrUpdate.WeekdayEnum = {}));
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "discount";
    PriceTypeEnum["ExtraChange"] = "extra_change";
  })(PriceTypeEnum = CalendarCalendarHourPriceCreateOrUpdate.PriceTypeEnum || (CalendarCalendarHourPriceCreateOrUpdate.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["Fixed"] = "fixed";
    PriceChangerTypeEnum["Percentage"] = "percentage";
  })(PriceChangerTypeEnum = CalendarCalendarHourPriceCreateOrUpdate.PriceChangerTypeEnum || (CalendarCalendarHourPriceCreateOrUpdate.PriceChangerTypeEnum = {}));
})(CalendarCalendarHourPriceCreateOrUpdate || (CalendarCalendarHourPriceCreateOrUpdate = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarCalendarHourPriceCreateOrUpdateRequest;
(function (CalendarCalendarHourPriceCreateOrUpdateRequest) {
  let WeekdayEnum;
  (function (WeekdayEnum) {
    WeekdayEnum["Sunday"] = "Sunday";
    WeekdayEnum["Monday"] = "Monday";
    WeekdayEnum["Tuesday"] = "Tuesday";
    WeekdayEnum["Wednesday"] = "Wednesday";
    WeekdayEnum["Thursday"] = "Thursday";
    WeekdayEnum["Friday"] = "Friday";
    WeekdayEnum["Saturday"] = "Saturday";
  })(WeekdayEnum = CalendarCalendarHourPriceCreateOrUpdateRequest.WeekdayEnum || (CalendarCalendarHourPriceCreateOrUpdateRequest.WeekdayEnum = {}));
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "discount";
    PriceTypeEnum["ExtraChange"] = "extra_change";
  })(PriceTypeEnum = CalendarCalendarHourPriceCreateOrUpdateRequest.PriceTypeEnum || (CalendarCalendarHourPriceCreateOrUpdateRequest.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["Fixed"] = "fixed";
    PriceChangerTypeEnum["Percentage"] = "percentage";
  })(PriceChangerTypeEnum = CalendarCalendarHourPriceCreateOrUpdateRequest.PriceChangerTypeEnum || (CalendarCalendarHourPriceCreateOrUpdateRequest.PriceChangerTypeEnum = {}));
})(CalendarCalendarHourPriceCreateOrUpdateRequest || (CalendarCalendarHourPriceCreateOrUpdateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarCreateOrUpdateRequest;
(function (CalendarCreateOrUpdateRequest) {
  let WeekFirstDayEnum;
  (function (WeekFirstDayEnum) {
    WeekFirstDayEnum["Sunday"] = "Sunday";
    WeekFirstDayEnum["Monday"] = "Monday";
  })(WeekFirstDayEnum = CalendarCreateOrUpdateRequest.WeekFirstDayEnum || (CalendarCreateOrUpdateRequest.WeekFirstDayEnum = {}));
  let TimeFormatEnum;
  (function (TimeFormatEnum) {
    TimeFormatEnum["_12"] = "12";
    TimeFormatEnum["_24"] = "24";
  })(TimeFormatEnum = CalendarCreateOrUpdateRequest.TimeFormatEnum || (CalendarCreateOrUpdateRequest.TimeFormatEnum = {}));
  let CalendarTypeEnum;
  (function (CalendarTypeEnum) {
    CalendarTypeEnum["Service"] = "service";
    CalendarTypeEnum["Rent"] = "rent";
    CalendarTypeEnum["LocationEmployee"] = "location_employee";
    CalendarTypeEnum["LocationCustomer"] = "location_customer";
    CalendarTypeEnum["CoWorker"] = "co_worker";
    CalendarTypeEnum["Project"] = "project";
    CalendarTypeEnum["Workgroup"] = "workgroup";
    CalendarTypeEnum["Booking"] = "booking";
    CalendarTypeEnum["StaffWorkflow"] = "staff_workflow";
    CalendarTypeEnum["User"] = "user";
  })(CalendarTypeEnum = CalendarCreateOrUpdateRequest.CalendarTypeEnum || (CalendarCreateOrUpdateRequest.CalendarTypeEnum = {}));
})(CalendarCreateOrUpdateRequest || (CalendarCreateOrUpdateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarPrice;
(function (CalendarPrice) {
  let BasePriceTypeEnum;
  (function (BasePriceTypeEnum) {
    BasePriceTypeEnum["Minute"] = "minute";
    BasePriceTypeEnum["Hour"] = "hour";
    BasePriceTypeEnum["Day"] = "day";
    BasePriceTypeEnum["Week"] = "week";
    BasePriceTypeEnum["Month"] = "month";
  })(BasePriceTypeEnum = CalendarPrice.BasePriceTypeEnum || (CalendarPrice.BasePriceTypeEnum = {}));
})(CalendarPrice || (CalendarPrice = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarPriceItemWorkFlow;
(function (CalendarPriceItemWorkFlow) {
  let BasePriceTypeEnum;
  (function (BasePriceTypeEnum) {
    BasePriceTypeEnum["Minute"] = "minute";
    BasePriceTypeEnum["Hour"] = "hour";
    BasePriceTypeEnum["Day"] = "day";
    BasePriceTypeEnum["Week"] = "week";
    BasePriceTypeEnum["Month"] = "month";
  })(BasePriceTypeEnum = CalendarPriceItemWorkFlow.BasePriceTypeEnum || (CalendarPriceItemWorkFlow.BasePriceTypeEnum = {}));
})(CalendarPriceItemWorkFlow || (CalendarPriceItemWorkFlow = {}));
var CalendarPricingResponse;
(function (CalendarPricingResponse) {
  let BasePriceTypeEnum;
  (function (BasePriceTypeEnum) {
    BasePriceTypeEnum["Minute"] = "minute";
    BasePriceTypeEnum["Hour"] = "hour";
    BasePriceTypeEnum["Day"] = "day";
    BasePriceTypeEnum["Week"] = "week";
    BasePriceTypeEnum["Month"] = "month";
  })(BasePriceTypeEnum = CalendarPricingResponse.BasePriceTypeEnum || (CalendarPricingResponse.BasePriceTypeEnum = {}));
})(CalendarPricingResponse || (CalendarPricingResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarTimePeriodPriceCreateOrUpdate;
(function (CalendarTimePeriodPriceCreateOrUpdate) {
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "discount";
    PriceTypeEnum["ExtraChange"] = "extra_change";
  })(PriceTypeEnum = CalendarTimePeriodPriceCreateOrUpdate.PriceTypeEnum || (CalendarTimePeriodPriceCreateOrUpdate.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["Fixed"] = "fixed";
    PriceChangerTypeEnum["Percentage"] = "percentage";
  })(PriceChangerTypeEnum = CalendarTimePeriodPriceCreateOrUpdate.PriceChangerTypeEnum || (CalendarTimePeriodPriceCreateOrUpdate.PriceChangerTypeEnum = {}));
})(CalendarTimePeriodPriceCreateOrUpdate || (CalendarTimePeriodPriceCreateOrUpdate = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CalendarTimePeriodPriceCreateOrUpdateRequest;
(function (CalendarTimePeriodPriceCreateOrUpdateRequest) {
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "discount";
    PriceTypeEnum["ExtraChange"] = "extra_change";
  })(PriceTypeEnum = CalendarTimePeriodPriceCreateOrUpdateRequest.PriceTypeEnum || (CalendarTimePeriodPriceCreateOrUpdateRequest.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["Fixed"] = "fixed";
    PriceChangerTypeEnum["Percentage"] = "percentage";
  })(PriceChangerTypeEnum = CalendarTimePeriodPriceCreateOrUpdateRequest.PriceChangerTypeEnum || (CalendarTimePeriodPriceCreateOrUpdateRequest.PriceChangerTypeEnum = {}));
})(CalendarTimePeriodPriceCreateOrUpdateRequest || (CalendarTimePeriodPriceCreateOrUpdateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Catalog;
(function (Catalog) {
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
    CatalogTypeEnum["FoodComponent"] = "food_component";
    CatalogTypeEnum["Drink"] = "drink";
    CatalogTypeEnum["ServiceTask"] = "service_task";
  })(CatalogTypeEnum = Catalog.CatalogTypeEnum || (Catalog.CatalogTypeEnum = {}));
  let IsWishesEnum;
  (function (IsWishesEnum) {
    IsWishesEnum["Block"] = "block";
    IsWishesEnum["Unblock"] = "unblock";
    IsWishesEnum["Follow"] = "follow";
    IsWishesEnum["Pending"] = "pending";
    IsWishesEnum["Reject"] = "reject";
    IsWishesEnum["Accept"] = "accept";
    IsWishesEnum["Delete"] = "delete";
    IsWishesEnum["NotFollow"] = "not_follow";
    IsWishesEnum["Report"] = "report";
    IsWishesEnum["UnReport"] = "un_report";
    IsWishesEnum["Done"] = "done";
    IsWishesEnum["SavedActive"] = "saved_active";
    IsWishesEnum["UnSavedActive"] = "un_saved_active";
    IsWishesEnum["CrmSave"] = "crm_save";
    IsWishesEnum["CrmUnSave"] = "crm_un_save";
    IsWishesEnum["NotRequest"] = "not_request";
    IsWishesEnum["None"] = "none";
    IsWishesEnum["SavedEvent"] = "saved_event";
    IsWishesEnum["UnSavedEvent"] = "un_saved_event";
  })(IsWishesEnum = Catalog.IsWishesEnum || (Catalog.IsWishesEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = Catalog.IsSavedEnum || (Catalog.IsSavedEnum = {}));
})(Catalog || (Catalog = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CatalogCollectionResponse;
(function (CatalogCollectionResponse) {
  let CollectionTypeEnum;
  (function (CollectionTypeEnum) {
    CollectionTypeEnum["IsHaveCollection"] = "is_have_collection";
    CollectionTypeEnum["IsFavoriteWish"] = "is_favorite_wish";
  })(CollectionTypeEnum = CatalogCollectionResponse.CollectionTypeEnum || (CatalogCollectionResponse.CollectionTypeEnum = {}));
})(CatalogCollectionResponse || (CatalogCollectionResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ChangeProfileRequestRequest;
(function (ChangeProfileRequestRequest) {
  let GenderEnum;
  (function (GenderEnum) {
    GenderEnum["Unknown"] = "unknown";
    GenderEnum["Male"] = "male";
    GenderEnum["Female"] = "female";
    GenderEnum["Other"] = "other";
  })(GenderEnum = ChangeProfileRequestRequest.GenderEnum || (ChangeProfileRequestRequest.GenderEnum = {}));
})(ChangeProfileRequestRequest || (ChangeProfileRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Client;
(function (Client) {
  let ClientTypeEnum;
  (function (ClientTypeEnum) {
    ClientTypeEnum["User"] = "user";
    ClientTypeEnum["Business"] = "business";
  })(ClientTypeEnum = Client.ClientTypeEnum || (Client.ClientTypeEnum = {}));
})(Client || (Client = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var CombinedSubscriptionResponse;
(function (CombinedSubscriptionResponse) {
  let SubscriptionRenewTypeEnum;
  (function (SubscriptionRenewTypeEnum) {
    SubscriptionRenewTypeEnum["Manually"] = "Manually";
    SubscriptionRenewTypeEnum["Automatic"] = "Automatic";
  })(SubscriptionRenewTypeEnum = CombinedSubscriptionResponse.SubscriptionRenewTypeEnum || (CombinedSubscriptionResponse.SubscriptionRenewTypeEnum = {}));
  let DefaultPaymentMethodEnum;
  (function (DefaultPaymentMethodEnum) {
    DefaultPaymentMethodEnum["Wallet"] = "Wallet";
    DefaultPaymentMethodEnum["Stripe"] = "Stripe";
    DefaultPaymentMethodEnum["Paypal"] = "Paypal";
  })(DefaultPaymentMethodEnum = CombinedSubscriptionResponse.DefaultPaymentMethodEnum || (CombinedSubscriptionResponse.DefaultPaymentMethodEnum = {}));
})(CombinedSubscriptionResponse || (CombinedSubscriptionResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonAddon;
(function (CommonAddon) {
  let AddonTypeEnum;
  (function (AddonTypeEnum) {
    AddonTypeEnum["Server"] = "Server";
    AddonTypeEnum["Storage"] = "Storage";
    AddonTypeEnum["Domain"] = "Domain";
    AddonTypeEnum["Marketing"] = "Marketing";
    AddonTypeEnum["It"] = "IT";
    AddonTypeEnum["Sales"] = "Sales";
    AddonTypeEnum["Business"] = "Business";
  })(AddonTypeEnum = CommonAddon.AddonTypeEnum || (CommonAddon.AddonTypeEnum = {}));
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["Business"] = "Business";
    EntityTypeEnum["Personal"] = "Personal";
    EntityTypeEnum["Both"] = "Both";
  })(EntityTypeEnum = CommonAddon.EntityTypeEnum || (CommonAddon.EntityTypeEnum = {}));
})(CommonAddon || (CommonAddon = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonApplicationProfileSettings;
(function (CommonApplicationProfileSettings) {
  let CommunityTypeEnum;
  (function (CommunityTypeEnum) {
    CommunityTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityTypeEnum["Dating"] = "dating";
    CommunityTypeEnum["Garden"] = "garden";
    CommunityTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityTypeEnum["SecondChance"] = "second_chance";
    CommunityTypeEnum["BeGreen"] = "be_green";
    CommunityTypeEnum["BucketList"] = "bucket_list";
    CommunityTypeEnum["GoalManager"] = "goal_manager";
    CommunityTypeEnum["Pets"] = "pets";
    CommunityTypeEnum["Volunteer"] = "volunteer";
    CommunityTypeEnum["Cooking"] = "cooking";
    CommunityTypeEnum["Collectors"] = "collectors";
    CommunityTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityTypeEnum["KidsSpace"] = "kids_space";
    CommunityTypeEnum["Sport"] = "sport";
    CommunityTypeEnum["Mindset"] = "mindset";
    CommunityTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityTypeEnum["Tech"] = "tech";
    CommunityTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityTypeEnum["BToB"] = "b_to_b";
    CommunityTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityTypeEnum["Gaming"] = "gaming";
    CommunityTypeEnum["Erotic"] = "erotic";
    CommunityTypeEnum["Roommate"] = "roommate";
    CommunityTypeEnum["Aviation"] = "aviation";
    CommunityTypeEnum["Cars"] = "cars";
    CommunityTypeEnum["Boat"] = "boat";
    CommunityTypeEnum["Fitness"] = "fitness";
    CommunityTypeEnum["Events"] = "events";
    CommunityTypeEnum["TaskManager"] = "task_manager";
    CommunityTypeEnum["Services"] = "services";
    CommunityTypeEnum["Academy"] = "academy";
    CommunityTypeEnum["StoreFinder"] = "store_finder";
    CommunityTypeEnum["ActivityStream"] = "activity_stream";
    CommunityTypeEnum["Calendar"] = "calendar";
    CommunityTypeEnum["Contacts"] = "contacts";
    CommunityTypeEnum["Notes"] = "notes";
    CommunityTypeEnum["Drive"] = "drive";
    CommunityTypeEnum["Taskly"] = "taskly";
    CommunityTypeEnum["MarketSpace"] = "market_space";
    CommunityTypeEnum["Chat"] = "chat";
    CommunityTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityTypeEnum["Renting"] = "renting";
    CommunityTypeEnum["Booking"] = "booking";
    CommunityTypeEnum["Job"] = "job";
    CommunityTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityTypeEnum["EmailManager"] = "email_manager";
    CommunityTypeEnum["RealEstate"] = "real_estate";
    CommunityTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityTypeEnum["Grocery"] = "grocery";
    CommunityTypeEnum["Fashion"] = "fashion";
    CommunityTypeEnum["HomeDesign"] = "home_design";
    CommunityTypeEnum["Taxi"] = "taxi";
    CommunityTypeEnum["SkillLink"] = "skill_link";
    CommunityTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityTypeEnum = CommonApplicationProfileSettings.CommunityTypeEnum || (CommonApplicationProfileSettings.CommunityTypeEnum = {}));
})(CommonApplicationProfileSettings || (CommonApplicationProfileSettings = {}));
var CommonApplicationProfileSettingsLocation;
(function (CommonApplicationProfileSettingsLocation) {
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = CommonApplicationProfileSettingsLocation.LocationShowEnum || (CommonApplicationProfileSettingsLocation.LocationShowEnum = {}));
})(CommonApplicationProfileSettingsLocation || (CommonApplicationProfileSettingsLocation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonBusiness;
(function (CommonBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonBusiness.SubscriptionTypeEnum || (CommonBusiness.SubscriptionTypeEnum = {}));
})(CommonBusiness || (CommonBusiness = {}));
var CommonCategoryTemplate;
(function (CommonCategoryTemplate) {
  let CategoryTypeEnum;
  (function (CategoryTypeEnum) {
    CategoryTypeEnum["ServiceOfferedTemplate"] = "service_offered_template";
    CategoryTypeEnum["RentTemplate"] = "rent_template";
    CategoryTypeEnum["PhysicalProductTemplate"] = "physical_product_template";
    CategoryTypeEnum["DigitalProductTemplate"] = "digital_product_template";
    CategoryTypeEnum["DigitalMediaTemplate"] = "digital_media_template";
    CategoryTypeEnum["JobTemplate"] = "job_template";
    CategoryTypeEnum["BookingTemplate"] = "booking_template";
    CategoryTypeEnum["FoodBeverageTemplate"] = "food_beverage_template";
    CategoryTypeEnum["DomainPreviewPageTemplate"] = "domain_preview_page_template";
    CategoryTypeEnum["DomainPreviewSiteTemplate"] = "domain_preview_site_template";
    CategoryTypeEnum["DomainPreviewWidgetCollectionTemplate"] = "domain_preview_widget_collection_template";
    CategoryTypeEnum["CustomField"] = "custom_field";
    CategoryTypeEnum["Plant"] = "plant";
    CategoryTypeEnum["Animal"] = "animal";
    CategoryTypeEnum["App"] = "app";
    CategoryTypeEnum["Food"] = "food";
    CategoryTypeEnum["BucketList"] = "bucket_list";
    CategoryTypeEnum["Drink"] = "drink";
    CategoryTypeEnum["FoodComponent"] = "food_component";
  })(CategoryTypeEnum = CommonCategoryTemplate.CategoryTypeEnum || (CommonCategoryTemplate.CategoryTypeEnum = {}));
})(CommonCategoryTemplate || (CommonCategoryTemplate = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonCoWorker;
(function (CommonCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = CommonCoWorker.StatusEnum || (CommonCoWorker.StatusEnum = {}));
})(CommonCoWorker || (CommonCoWorker = {}));
var CommonContent;
(function (CommonContent) {
  let UploadSystemTypeEnum;
  (function (UploadSystemTypeEnum) {
    UploadSystemTypeEnum["Album"] = "Album";
    UploadSystemTypeEnum["PreviewPageMeta"] = "Preview Page Meta";
    UploadSystemTypeEnum["PageMeta"] = "Page Meta";
    UploadSystemTypeEnum["SmartItem"] = "Smart Item";
    UploadSystemTypeEnum["Academy"] = "Academy";
    UploadSystemTypeEnum["Application"] = "Application";
    UploadSystemTypeEnum["EventFileUpload"] = "Event File Upload";
    UploadSystemTypeEnum["SmartWorkflow"] = "Smart Workflow";
  })(UploadSystemTypeEnum = CommonContent.UploadSystemTypeEnum || (CommonContent.UploadSystemTypeEnum = {}));
  let ContentUploadTypeEnum;
  (function (ContentUploadTypeEnum) {
    ContentUploadTypeEnum["Image"] = "image";
    ContentUploadTypeEnum["Video"] = "video";
    ContentUploadTypeEnum["Audio"] = "audio";
    ContentUploadTypeEnum["Document"] = "document";
    ContentUploadTypeEnum["Zip"] = "zip";
    ContentUploadTypeEnum["Other"] = "other";
  })(ContentUploadTypeEnum = CommonContent.ContentUploadTypeEnum || (CommonContent.ContentUploadTypeEnum = {}));
})(CommonContent || (CommonContent = {}));
var CommonCustomFieldCategory;
(function (CommonCustomFieldCategory) {
  let SiFilterAccessEnum;
  (function (SiFilterAccessEnum) {
    SiFilterAccessEnum["Yes"] = "Yes";
    SiFilterAccessEnum["No"] = "No";
    SiFilterAccessEnum["Currency"] = "Currency";
  })(SiFilterAccessEnum = CommonCustomFieldCategory.SiFilterAccessEnum || (CommonCustomFieldCategory.SiFilterAccessEnum = {}));
  let SiUnitTypeFilterEnum;
  (function (SiUnitTypeFilterEnum) {
    SiUnitTypeFilterEnum["Length"] = "Length";
    SiUnitTypeFilterEnum["Weight"] = "Weight";
    SiUnitTypeFilterEnum["Volume"] = "Volume";
    SiUnitTypeFilterEnum["Area"] = "Area";
    SiUnitTypeFilterEnum["Time"] = "Time";
    SiUnitTypeFilterEnum["Frequency"] = "Frequency";
    SiUnitTypeFilterEnum["Speed"] = "Speed";
    SiUnitTypeFilterEnum["Mass"] = "Mass";
    SiUnitTypeFilterEnum["Temperature"] = "Temperature";
    SiUnitTypeFilterEnum["Current"] = "Current";
    SiUnitTypeFilterEnum["PrintingUnit"] = "Printing_unit";
    SiUnitTypeFilterEnum["Luminosity"] = "Luminosity";
    SiUnitTypeFilterEnum["Substance"] = "Substance";
    SiUnitTypeFilterEnum["Quantity"] = "Quantity";
  })(SiUnitTypeFilterEnum = CommonCustomFieldCategory.SiUnitTypeFilterEnum || (CommonCustomFieldCategory.SiUnitTypeFilterEnum = {}));
  let CustomTypeEnum;
  (function (CustomTypeEnum) {
    CustomTypeEnum["Text"] = "text";
    CustomTypeEnum["Radio"] = "radio";
    CustomTypeEnum["Checkbox"] = "checkbox";
    CustomTypeEnum["Dropdown"] = "dropdown";
    CustomTypeEnum["MultiSelect"] = "multi_select";
    CustomTypeEnum["InputNumber"] = "input_number";
    CustomTypeEnum["Range"] = "range";
  })(CustomTypeEnum = CommonCustomFieldCategory.CustomTypeEnum || (CommonCustomFieldCategory.CustomTypeEnum = {}));
  let PrivacyTypeEnum;
  (function (PrivacyTypeEnum) {
    PrivacyTypeEnum["Public"] = "public";
    PrivacyTypeEnum["Private"] = "private";
  })(PrivacyTypeEnum = CommonCustomFieldCategory.PrivacyTypeEnum || (CommonCustomFieldCategory.PrivacyTypeEnum = {}));
})(CommonCustomFieldCategory || (CommonCustomFieldCategory = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonEmailBusiness;
(function (CommonEmailBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonEmailBusiness.SubscriptionTypeEnum || (CommonEmailBusiness.SubscriptionTypeEnum = {}));
})(CommonEmailBusiness || (CommonEmailBusiness = {}));
var CommonEntityPolymorphic;
(function (CommonEntityPolymorphic) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["User"] = "user";
    TypeEnum["Business"] = "business";
  })(TypeEnum = CommonEntityPolymorphic.TypeEnum || (CommonEntityPolymorphic.TypeEnum = {}));
})(CommonEntityPolymorphic || (CommonEntityPolymorphic = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonJobCoWorker;
(function (CommonJobCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = CommonJobCoWorker.StatusEnum || (CommonJobCoWorker.StatusEnum = {}));
})(CommonJobCoWorker || (CommonJobCoWorker = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonLeaderCoWorker;
(function (CommonLeaderCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = CommonLeaderCoWorker.StatusEnum || (CommonLeaderCoWorker.StatusEnum = {}));
})(CommonLeaderCoWorker || (CommonLeaderCoWorker = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonLongBusiness;
(function (CommonLongBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonLongBusiness.SubscriptionTypeEnum || (CommonLongBusiness.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = CommonLongBusiness.IsFollowEnum || (CommonLongBusiness.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = CommonLongBusiness.IsReportedEnum || (CommonLongBusiness.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = CommonLongBusiness.IsBlockedEnum || (CommonLongBusiness.IsBlockedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = CommonLongBusiness.IsPendingEnum || (CommonLongBusiness.IsPendingEnum = {}));
})(CommonLongBusiness || (CommonLongBusiness = {}));
var CommonLongUser;
(function (CommonLongUser) {
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = CommonLongUser.IsFollowEnum || (CommonLongUser.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = CommonLongUser.IsReportedEnum || (CommonLongUser.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = CommonLongUser.IsBlockedEnum || (CommonLongUser.IsBlockedEnum = {}));
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonLongUser.SubscriptionTypeEnum || (CommonLongUser.SubscriptionTypeEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = CommonLongUser.IsPendingEnum || (CommonLongUser.IsPendingEnum = {}));
})(CommonLongUser || (CommonLongUser = {}));
var CommonMediaContent;
(function (CommonMediaContent) {
  let UploadSystemTypeEnum;
  (function (UploadSystemTypeEnum) {
    UploadSystemTypeEnum["Album"] = "Album";
    UploadSystemTypeEnum["PreviewPageMeta"] = "Preview Page Meta";
    UploadSystemTypeEnum["PageMeta"] = "Page Meta";
    UploadSystemTypeEnum["SmartItem"] = "Smart Item";
    UploadSystemTypeEnum["Academy"] = "Academy";
    UploadSystemTypeEnum["Application"] = "Application";
    UploadSystemTypeEnum["EventFileUpload"] = "Event File Upload";
    UploadSystemTypeEnum["SmartWorkflow"] = "Smart Workflow";
  })(UploadSystemTypeEnum = CommonMediaContent.UploadSystemTypeEnum || (CommonMediaContent.UploadSystemTypeEnum = {}));
  let ContentUploadTypeEnum;
  (function (ContentUploadTypeEnum) {
    ContentUploadTypeEnum["Image"] = "image";
    ContentUploadTypeEnum["Video"] = "video";
    ContentUploadTypeEnum["Audio"] = "audio";
    ContentUploadTypeEnum["Document"] = "document";
    ContentUploadTypeEnum["Zip"] = "zip";
    ContentUploadTypeEnum["Other"] = "other";
  })(ContentUploadTypeEnum = CommonMediaContent.ContentUploadTypeEnum || (CommonMediaContent.ContentUploadTypeEnum = {}));
})(CommonMediaContent || (CommonMediaContent = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonProspectUser;
(function (CommonProspectUser) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonProspectUser.SubscriptionTypeEnum || (CommonProspectUser.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = CommonProspectUser.IsFollowEnum || (CommonProspectUser.IsFollowEnum = {}));
})(CommonProspectUser || (CommonProspectUser = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonShortUser;
(function (CommonShortUser) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonShortUser.SubscriptionTypeEnum || (CommonShortUser.SubscriptionTypeEnum = {}));
})(CommonShortUser || (CommonShortUser = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommonSiUnits;
(function (CommonSiUnits) {
  let UnitTypeEnum;
  (function (UnitTypeEnum) {
    UnitTypeEnum["Length"] = "Length";
    UnitTypeEnum["Weight"] = "Weight";
    UnitTypeEnum["Volume"] = "Volume";
    UnitTypeEnum["Area"] = "Area";
    UnitTypeEnum["Time"] = "Time";
    UnitTypeEnum["Frequency"] = "Frequency";
    UnitTypeEnum["Speed"] = "Speed";
    UnitTypeEnum["Mass"] = "Mass";
    UnitTypeEnum["Temperature"] = "Temperature";
    UnitTypeEnum["Current"] = "Current";
    UnitTypeEnum["PrintingUnit"] = "Printing_unit";
    UnitTypeEnum["Luminosity"] = "Luminosity";
    UnitTypeEnum["Substance"] = "Substance";
    UnitTypeEnum["Quantity"] = "Quantity";
  })(UnitTypeEnum = CommonSiUnits.UnitTypeEnum || (CommonSiUnits.UnitTypeEnum = {}));
})(CommonSiUnits || (CommonSiUnits = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var CommonUser;
(function (CommonUser) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = CommonUser.SubscriptionTypeEnum || (CommonUser.SubscriptionTypeEnum = {}));
})(CommonUser || (CommonUser = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var CommunityApplicationLanguage;
(function (CommunityApplicationLanguage) {
  let LevelEnum;
  (function (LevelEnum) {
    LevelEnum["Learn"] = "learn";
    LevelEnum["Speak"] = "speak";
    LevelEnum["Native"] = "native";
  })(LevelEnum = CommunityApplicationLanguage.LevelEnum || (CommunityApplicationLanguage.LevelEnum = {}));
  let GradeEnum;
  (function (GradeEnum) {
    GradeEnum["Beginner"] = "beginner";
    GradeEnum["Intermediate"] = "intermediate";
    GradeEnum["Advanced"] = "advanced";
    GradeEnum["Native"] = "native";
    GradeEnum["Fluent"] = "fluent";
  })(GradeEnum = CommunityApplicationLanguage.GradeEnum || (CommunityApplicationLanguage.GradeEnum = {}));
})(CommunityApplicationLanguage || (CommunityApplicationLanguage = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CommunityApplicationProfile;
(function (CommunityApplicationProfile) {
  let CommunityApplicationTypeEnum;
  (function (CommunityApplicationTypeEnum) {
    CommunityApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityApplicationTypeEnum["Dating"] = "dating";
    CommunityApplicationTypeEnum["Garden"] = "garden";
    CommunityApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityApplicationTypeEnum["SecondChance"] = "second_chance";
    CommunityApplicationTypeEnum["BeGreen"] = "be_green";
    CommunityApplicationTypeEnum["BucketList"] = "bucket_list";
    CommunityApplicationTypeEnum["GoalManager"] = "goal_manager";
    CommunityApplicationTypeEnum["Pets"] = "pets";
    CommunityApplicationTypeEnum["Volunteer"] = "volunteer";
    CommunityApplicationTypeEnum["Cooking"] = "cooking";
    CommunityApplicationTypeEnum["Collectors"] = "collectors";
    CommunityApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityApplicationTypeEnum["KidsSpace"] = "kids_space";
    CommunityApplicationTypeEnum["Sport"] = "sport";
    CommunityApplicationTypeEnum["Mindset"] = "mindset";
    CommunityApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityApplicationTypeEnum["Tech"] = "tech";
    CommunityApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityApplicationTypeEnum["BToB"] = "b_to_b";
    CommunityApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityApplicationTypeEnum["Gaming"] = "gaming";
    CommunityApplicationTypeEnum["Erotic"] = "erotic";
    CommunityApplicationTypeEnum["Roommate"] = "roommate";
    CommunityApplicationTypeEnum["Aviation"] = "aviation";
    CommunityApplicationTypeEnum["Cars"] = "cars";
    CommunityApplicationTypeEnum["Boat"] = "boat";
    CommunityApplicationTypeEnum["Fitness"] = "fitness";
    CommunityApplicationTypeEnum["Events"] = "events";
    CommunityApplicationTypeEnum["TaskManager"] = "task_manager";
    CommunityApplicationTypeEnum["Services"] = "services";
    CommunityApplicationTypeEnum["Academy"] = "academy";
    CommunityApplicationTypeEnum["StoreFinder"] = "store_finder";
    CommunityApplicationTypeEnum["ActivityStream"] = "activity_stream";
    CommunityApplicationTypeEnum["Calendar"] = "calendar";
    CommunityApplicationTypeEnum["Contacts"] = "contacts";
    CommunityApplicationTypeEnum["Notes"] = "notes";
    CommunityApplicationTypeEnum["Drive"] = "drive";
    CommunityApplicationTypeEnum["Taskly"] = "taskly";
    CommunityApplicationTypeEnum["MarketSpace"] = "market_space";
    CommunityApplicationTypeEnum["Chat"] = "chat";
    CommunityApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityApplicationTypeEnum["Renting"] = "renting";
    CommunityApplicationTypeEnum["Booking"] = "booking";
    CommunityApplicationTypeEnum["Job"] = "job";
    CommunityApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityApplicationTypeEnum["EmailManager"] = "email_manager";
    CommunityApplicationTypeEnum["RealEstate"] = "real_estate";
    CommunityApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityApplicationTypeEnum["Grocery"] = "grocery";
    CommunityApplicationTypeEnum["Fashion"] = "fashion";
    CommunityApplicationTypeEnum["HomeDesign"] = "home_design";
    CommunityApplicationTypeEnum["Taxi"] = "taxi";
    CommunityApplicationTypeEnum["SkillLink"] = "skill_link";
    CommunityApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityApplicationTypeEnum = CommunityApplicationProfile.CommunityApplicationTypeEnum || (CommunityApplicationProfile.CommunityApplicationTypeEnum = {}));
})(CommunityApplicationProfile || (CommunityApplicationProfile = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Content;
(function (Content) {
  let UploadSystemTypeEnum;
  (function (UploadSystemTypeEnum) {
    UploadSystemTypeEnum["Album"] = "Album";
    UploadSystemTypeEnum["PreviewPageMeta"] = "Preview Page Meta";
    UploadSystemTypeEnum["PageMeta"] = "Page Meta";
    UploadSystemTypeEnum["SmartItem"] = "Smart Item";
    UploadSystemTypeEnum["Academy"] = "Academy";
    UploadSystemTypeEnum["Application"] = "Application";
    UploadSystemTypeEnum["EventFileUpload"] = "Event File Upload";
    UploadSystemTypeEnum["SmartWorkflow"] = "Smart Workflow";
  })(UploadSystemTypeEnum = Content.UploadSystemTypeEnum || (Content.UploadSystemTypeEnum = {}));
  let ContentUploadTypeEnum;
  (function (ContentUploadTypeEnum) {
    ContentUploadTypeEnum["Image"] = "image";
    ContentUploadTypeEnum["Video"] = "video";
    ContentUploadTypeEnum["Audio"] = "audio";
    ContentUploadTypeEnum["Document"] = "document";
    ContentUploadTypeEnum["Zip"] = "zip";
    ContentUploadTypeEnum["Other"] = "other";
  })(ContentUploadTypeEnum = Content.ContentUploadTypeEnum || (Content.ContentUploadTypeEnum = {}));
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Adult"] = "adult";
    PostStateEnum["Normal"] = "normal";
    PostStateEnum["Sensitive"] = "sensitive";
  })(PostStateEnum = Content.PostStateEnum || (Content.PostStateEnum = {}));
  let UploadAreaEnum;
  (function (UploadAreaEnum) {
    UploadAreaEnum["MediaUpload"] = "media_upload";
    UploadAreaEnum["AfterDownload"] = "after_download";
    UploadAreaEnum["AfterDownloadPaid"] = "after_download_paid";
  })(UploadAreaEnum = Content.UploadAreaEnum || (Content.UploadAreaEnum = {}));
})(Content || (Content = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CreateCodeRequest;
(function (CreateCodeRequest) {
  let CodeTypeEnum;
  (function (CodeTypeEnum) {
    CodeTypeEnum["HrmsInvitation"] = "hrms_invitation";
    CodeTypeEnum["Discount"] = "discount";
    CodeTypeEnum["Prospect"] = "prospect";
    CodeTypeEnum["User"] = "user";
    CodeTypeEnum["Business"] = "business";
  })(CodeTypeEnum = CreateCodeRequest.CodeTypeEnum || (CreateCodeRequest.CodeTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["User"] = "user";
    ServiceTypeEnum["Business"] = "business";
    ServiceTypeEnum["CoWorker"] = "co_worker";
  })(ServiceTypeEnum = CreateCodeRequest.ServiceTypeEnum || (CreateCodeRequest.ServiceTypeEnum = {}));
})(CreateCodeRequest || (CreateCodeRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CreatePostRequest;
(function (CreatePostRequest) {
  let PostTypeEnum;
  (function (PostTypeEnum) {
    PostTypeEnum["ShareAd"] = "share_ad";
    PostTypeEnum["SharedUser"] = "shared_user";
    PostTypeEnum["Album"] = "album";
    PostTypeEnum["Colored"] = "colored";
    PostTypeEnum["ImageBackground"] = "image_background";
    PostTypeEnum["Poll"] = "poll";
    PostTypeEnum["OutsideLink"] = "outside_link";
    PostTypeEnum["Swap"] = "swap";
    PostTypeEnum["Donate"] = "donate";
    PostTypeEnum["DonationWish"] = "donation_wish";
    PostTypeEnum["Adoption"] = "adoption";
    PostTypeEnum["Lost"] = "lost";
    PostTypeEnum["Find"] = "find";
    PostTypeEnum["Ad"] = "ad";
    PostTypeEnum["Catalog"] = "catalog";
  })(PostTypeEnum = CreatePostRequest.PostTypeEnum || (CreatePostRequest.PostTypeEnum = {}));
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Draft"] = "draft";
    PostStateEnum["Published"] = "published";
    PostStateEnum["Scheduled"] = "scheduled";
    PostStateEnum["Deleted"] = "deleted";
  })(PostStateEnum = CreatePostRequest.PostStateEnum || (CreatePostRequest.PostStateEnum = {}));
  let PostViewStateEnum;
  (function (PostViewStateEnum) {
    PostViewStateEnum["Adult"] = "adult";
    PostViewStateEnum["Normal"] = "normal";
    PostViewStateEnum["Sensitive"] = "sensitive";
  })(PostViewStateEnum = CreatePostRequest.PostViewStateEnum || (CreatePostRequest.PostViewStateEnum = {}));
  let PollTypeEnum;
  (function (PollTypeEnum) {
    PollTypeEnum["Single"] = "single";
    PollTypeEnum["Multiple"] = "multiple";
  })(PollTypeEnum = CreatePostRequest.PollTypeEnum || (CreatePostRequest.PollTypeEnum = {}));
  let PrivacyTypeEnum;
  (function (PrivacyTypeEnum) {
    PrivacyTypeEnum["Public"] = "public";
    PrivacyTypeEnum["Followers"] = "followers";
    PrivacyTypeEnum["Private"] = "private";
  })(PrivacyTypeEnum = CreatePostRequest.PrivacyTypeEnum || (CreatePostRequest.PrivacyTypeEnum = {}));
  let ApplicationTypeEnum;
  (function (ApplicationTypeEnum) {
    ApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    ApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    ApplicationTypeEnum["Dating"] = "dating";
    ApplicationTypeEnum["Garden"] = "garden";
    ApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    ApplicationTypeEnum["SecondChance"] = "second_chance";
    ApplicationTypeEnum["BeGreen"] = "be_green";
    ApplicationTypeEnum["BucketList"] = "bucket_list";
    ApplicationTypeEnum["GoalManager"] = "goal_manager";
    ApplicationTypeEnum["Pets"] = "pets";
    ApplicationTypeEnum["Volunteer"] = "volunteer";
    ApplicationTypeEnum["Cooking"] = "cooking";
    ApplicationTypeEnum["Collectors"] = "collectors";
    ApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ApplicationTypeEnum["KidsSpace"] = "kids_space";
    ApplicationTypeEnum["Sport"] = "sport";
    ApplicationTypeEnum["Mindset"] = "mindset";
    ApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    ApplicationTypeEnum["Tech"] = "tech";
    ApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ApplicationTypeEnum["BToB"] = "b_to_b";
    ApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    ApplicationTypeEnum["Gaming"] = "gaming";
    ApplicationTypeEnum["Erotic"] = "erotic";
    ApplicationTypeEnum["Roommate"] = "roommate";
    ApplicationTypeEnum["Aviation"] = "aviation";
    ApplicationTypeEnum["Cars"] = "cars";
    ApplicationTypeEnum["Boat"] = "boat";
    ApplicationTypeEnum["Fitness"] = "fitness";
    ApplicationTypeEnum["Events"] = "events";
    ApplicationTypeEnum["TaskManager"] = "task_manager";
    ApplicationTypeEnum["Services"] = "services";
    ApplicationTypeEnum["Academy"] = "academy";
    ApplicationTypeEnum["StoreFinder"] = "store_finder";
    ApplicationTypeEnum["ActivityStream"] = "activity_stream";
    ApplicationTypeEnum["Calendar"] = "calendar";
    ApplicationTypeEnum["Contacts"] = "contacts";
    ApplicationTypeEnum["Notes"] = "notes";
    ApplicationTypeEnum["Drive"] = "drive";
    ApplicationTypeEnum["Taskly"] = "taskly";
    ApplicationTypeEnum["MarketSpace"] = "market_space";
    ApplicationTypeEnum["Chat"] = "chat";
    ApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    ApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ApplicationTypeEnum["Renting"] = "renting";
    ApplicationTypeEnum["Booking"] = "booking";
    ApplicationTypeEnum["Job"] = "job";
    ApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    ApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    ApplicationTypeEnum["EmailManager"] = "email_manager";
    ApplicationTypeEnum["RealEstate"] = "real_estate";
    ApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    ApplicationTypeEnum["Grocery"] = "grocery";
    ApplicationTypeEnum["Fashion"] = "fashion";
    ApplicationTypeEnum["HomeDesign"] = "home_design";
    ApplicationTypeEnum["Taxi"] = "taxi";
    ApplicationTypeEnum["SkillLink"] = "skill_link";
    ApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(ApplicationTypeEnum = CreatePostRequest.ApplicationTypeEnum || (CreatePostRequest.ApplicationTypeEnum = {}));
  let StreamTypeEnum;
  (function (StreamTypeEnum) {
    StreamTypeEnum["CompanyStream"] = "company_stream";
    StreamTypeEnum["CoreStream"] = "core_stream";
  })(StreamTypeEnum = CreatePostRequest.StreamTypeEnum || (CreatePostRequest.StreamTypeEnum = {}));
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
  })(CatalogTypeEnum = CreatePostRequest.CatalogTypeEnum || (CreatePostRequest.CatalogTypeEnum = {}));
  let CatalogTypeWishEnum;
  (function (CatalogTypeWishEnum) {
    CatalogTypeWishEnum["Plant"] = "plant";
    CatalogTypeWishEnum["Animal"] = "animal";
    CatalogTypeWishEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeWishEnum["Food"] = "food";
    CatalogTypeWishEnum["BucketList"] = "bucket_list";
    CatalogTypeWishEnum["Cooking"] = "cooking";
  })(CatalogTypeWishEnum = CreatePostRequest.CatalogTypeWishEnum || (CreatePostRequest.CatalogTypeWishEnum = {}));
})(CreatePostRequest || (CreatePostRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var CrmAddress;
(function (CrmAddress) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = CrmAddress.TypeEnum || (CrmAddress.TypeEnum = {}));
})(CrmAddress || (CrmAddress = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmEmail;
(function (CrmEmail) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = CrmEmail.SharedTypeEnum || (CrmEmail.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
  })(TypeEnum = CrmEmail.TypeEnum || (CrmEmail.TypeEnum = {}));
})(CrmEmail || (CrmEmail = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmHistoryTypeEnum;
(function (CrmHistoryTypeEnum) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Edit"] = "Edit";
    TypeEnum["Dashboard"] = "Dashboard";
  })(TypeEnum = CrmHistoryTypeEnum.TypeEnum || (CrmHistoryTypeEnum.TypeEnum = {}));
  let ActionTypeEnum;
  (function (ActionTypeEnum) {
    ActionTypeEnum["CreateProspect"] = "CreateProspect";
    ActionTypeEnum["AddCorePerson"] = "AddCorePerson";
    ActionTypeEnum["AddCoreBusiness"] = "AddCoreBusiness";
    ActionTypeEnum["ResponsiblePersonAllocated"] = "ResponsiblePersonAllocated";
    ActionTypeEnum["DeleteProspect"] = "DeleteProspect";
    ActionTypeEnum["ReactiveProspect"] = "ReactiveProspect";
    ActionTypeEnum["UserRegistration"] = "UserRegistration";
    ActionTypeEnum["UserEmailVerification"] = "UserEmailVerification";
    ActionTypeEnum["CreateErp"] = "CreateErp";
    ActionTypeEnum["ContactForm"] = "ContactForm";
    ActionTypeEnum["AddEmail"] = "AddEmail";
    ActionTypeEnum["EditEmail"] = "EditEmail";
    ActionTypeEnum["ReportSent"] = "ReportSent";
    ActionTypeEnum["ReportUndo"] = "ReportUndo";
    ActionTypeEnum["AddBusiness"] = "AddBusiness";
    ActionTypeEnum["SubscriptionChanged"] = "SubscriptionChanged";
    ActionTypeEnum["SubscriptionCanceled"] = "SubscriptionCanceled";
    ActionTypeEnum["AddPhone"] = "AddPhone";
    ActionTypeEnum["EditPhone"] = "EditPhone";
    ActionTypeEnum["AddAddress"] = "AddAddress";
    ActionTypeEnum["EditAddress"] = "EditAddress";
    ActionTypeEnum["NotifyForm"] = "NotifyForm";
    ActionTypeEnum["AddKeyword"] = "AddKeyword";
    ActionTypeEnum["DeleteKeyword"] = "DeleteKeyword";
    ActionTypeEnum["DeleteCorePerson"] = "DeleteCorePerson";
    ActionTypeEnum["DeleteCoreBusiness"] = "DeleteCoreBusiness";
    ActionTypeEnum["EditResponsiblePerson"] = "EditResponsiblePerson";
    ActionTypeEnum["AddLanguage"] = "AddLanguage";
    ActionTypeEnum["EditLanguage"] = "EditLanguage";
    ActionTypeEnum["DeleteAddress"] = "DeleteAddress";
    ActionTypeEnum["DeleteEmail"] = "DeleteEmail";
    ActionTypeEnum["DeletePhone"] = "DeletePhone";
    ActionTypeEnum["InviteToBusiness"] = "InviteToBusiness";
    ActionTypeEnum["RemoveFromBusiness"] = "RemoveFromBusiness";
    ActionTypeEnum["CoWorkerAcceptedInvitation"] = "CoWorkerAcceptedInvitation";
    ActionTypeEnum["AddCorporatePosition"] = "AddCorporatePosition";
    ActionTypeEnum["EditCorporatePosition"] = "EditCorporatePosition";
    ActionTypeEnum["RemoveCorporatePosition"] = "RemoveCorporatePosition";
    ActionTypeEnum["AddCorporateDepartmentHead"] = "AddCorporateDepartmentHead";
    ActionTypeEnum["RemoveCorporateDepartmentHead"] = "RemoveCorporateDepartmentHead";
    ActionTypeEnum["RbacAllocation"] = "RbacAllocation";
    ActionTypeEnum["AddSystemAdministrator"] = "AddSystemAdministrator";
    ActionTypeEnum["RemoveSystemAdministrator"] = "RemoveSystemAdministrator";
    ActionTypeEnum["EditInvitation"] = "EditInvitation";
    ActionTypeEnum["AddLink"] = "AddLink";
    ActionTypeEnum["EditLink"] = "EditLink";
    ActionTypeEnum["DeleteLink"] = "DeleteLink";
    ActionTypeEnum["AddCommunicationLanguage"] = "AddCommunicationLanguage";
    ActionTypeEnum["DeleteUser"] = "DeleteUser";
    ActionTypeEnum["AddAddons"] = "AddAddons";
    ActionTypeEnum["CancelAddonSubscription"] = "CancelAddonSubscription";
    ActionTypeEnum["AddTask"] = "AddTask";
    ActionTypeEnum["ErpDeleted"] = "ErpDeleted";
    ActionTypeEnum["AddUserProspectToBusinessProspect"] = "AddUserProspectToBusinessProspect";
    ActionTypeEnum["DeleteUserProspectFromBusinessProspect"] = "DeleteUserProspectFromBusinessProspect";
    ActionTypeEnum["AddSalesTeam"] = "AddSalesTeam";
    ActionTypeEnum["RemoveSalesTeam"] = "RemoveSalesTeam";
    ActionTypeEnum["AddSalesTeamHead"] = "AddSalesTeamHead";
    ActionTypeEnum["RemoveSalesTeamHead"] = "RemoveSalesTeamHead";
    ActionTypeEnum["CoreUserFollowBusiness"] = "CoreUserFollowBusiness";
    ActionTypeEnum["CoreUserUnfollowBusiness"] = "CoreUserUnfollowBusiness";
    ActionTypeEnum["CoreBusinessUnfollowBusiness"] = "CoreBusinessUnfollowBusiness";
    ActionTypeEnum["CoreBusinessFollowBusiness"] = "CoreBusinessFollowBusiness";
    ActionTypeEnum["AddSharedEmail"] = "AddSharedEmail";
    ActionTypeEnum["DeleteSharedEmail"] = "DeleteSharedEmail";
    ActionTypeEnum["AddCoWorkerToSharedEmail"] = "AddCoWorkerToSharedEmail";
    ActionTypeEnum["RemoveCoWorkerFromSharedEmail"] = "RemoveCoWorkerFromSharedEmail";
    ActionTypeEnum["EditUserProspectToBusinessProspect"] = "EditUserProspectToBusinessProspect";
    ActionTypeEnum["AddDedicatedUserProspectToBusinessProspect"] = "AddDedicatedUserProspectToBusinessProspect";
    ActionTypeEnum["EditDedicatedUserProspectToBusinessProspect"] = "EditDedicatedUserProspectToBusinessProspect";
    ActionTypeEnum["DeleteDedicatedUserProspectFromBusinessProspect"] = "DeleteDedicatedUserProspectFromBusinessProspect";
    ActionTypeEnum["AddDeal"] = "AddDeal";
    ActionTypeEnum["EditDeal"] = "EditDeal";
    ActionTypeEnum["DeleteDeal"] = "DeleteDeal";
    ActionTypeEnum["AddOffer"] = "AddOffer";
    ActionTypeEnum["EditOffer"] = "EditOffer";
    ActionTypeEnum["DeleteOffer"] = "DeleteOffer";
    ActionTypeEnum["AddLead"] = "AddLead";
    ActionTypeEnum["EditLead"] = "EditLead";
    ActionTypeEnum["DeleteLead"] = "DeleteLead";
    ActionTypeEnum["AddBusinessProfile"] = "AddBusinessProfile";
    ActionTypeEnum["EditBusinessProfile"] = "EditBusinessProfile";
    ActionTypeEnum["DeleteBusinessProfile"] = "DeleteBusinessProfile";
    ActionTypeEnum["AddPersonProfile"] = "AddPersonProfile";
    ActionTypeEnum["EditPersonProfile"] = "EditPersonProfile";
    ActionTypeEnum["DeletePersonProfile"] = "DeletePersonProfile";
    ActionTypeEnum["AddTaskStep"] = "AddTaskStep";
    ActionTypeEnum["EditTaskStep"] = "EditTaskStep";
    ActionTypeEnum["DeleteTaskStep"] = "DeleteTaskStep";
    ActionTypeEnum["AddTaskStatus"] = "AddTaskStatus";
    ActionTypeEnum["EditTaskStatus"] = "EditTaskStatus";
    ActionTypeEnum["DeleteTaskStatus"] = "DeleteTaskStatus";
    ActionTypeEnum["AddTaskPipeline"] = "AddTaskPipeline";
    ActionTypeEnum["EditTaskPipeline"] = "EditTaskPipeline";
    ActionTypeEnum["DeleteTaskPipeline"] = "DeleteTaskPipeline";
    ActionTypeEnum["AddTaskProjectWorkgroup"] = "AddTaskProjectWorkgroup";
    ActionTypeEnum["EditTaskProjectWorkgroup"] = "EditTaskProjectWorkgroup";
    ActionTypeEnum["DeleteTaskProjectWorkgroup"] = "DeleteTaskProjectWorkgroup";
    ActionTypeEnum["AddTaskProjectWorkgroupMember"] = "AddTaskProjectWorkgroupMember";
    ActionTypeEnum["EditTaskProjectWorkgroupMember"] = "EditTaskProjectWorkgroupMember";
    ActionTypeEnum["DeleteTaskProjectWorkgroupMember"] = "DeleteTaskProjectWorkgroupMember";
    ActionTypeEnum["AddTaskToProjectWorkgroup"] = "AddTaskToProjectWorkgroup";
    ActionTypeEnum["RemoveTaskFromProjectWorkgroup"] = "RemoveTaskFromProjectWorkgroup";
    ActionTypeEnum["EditTask"] = "EditTask";
    ActionTypeEnum["DeleteTask"] = "DeleteTask";
    ActionTypeEnum["AddBankTransfer"] = "AddBankTransfer";
    ActionTypeEnum["EditBankTransfer"] = "EditBankTransfer";
    ActionTypeEnum["DeleteBankTransfer"] = "DeleteBankTransfer";
    ActionTypeEnum["OfferResponsiblePersonAllocated"] = "OfferResponsiblePersonAllocated";
    ActionTypeEnum["LeadResponsiblePersonAllocated"] = "LeadResponsiblePersonAllocated";
    ActionTypeEnum["DealResponsiblePersonAllocated"] = "DealResponsiblePersonAllocated";
    ActionTypeEnum["AddTextNoteProspect"] = "AddTextNoteProspect";
    ActionTypeEnum["EditTextNoteProspect"] = "EditTextNoteProspect";
    ActionTypeEnum["DeleteTextNoteProspect"] = "DeleteTextNoteProspect";
    ActionTypeEnum["AddAudioNoteProspect"] = "AddAudioNoteProspect";
    ActionTypeEnum["EditAudioNoteProspect"] = "EditAudioNoteProspect";
    ActionTypeEnum["DeleteAudioNoteProspect"] = "DeleteAudioNoteProspect";
    ActionTypeEnum["AddVideoNoteProspect"] = "AddVideoNoteProspect";
    ActionTypeEnum["EditVideoNoteProspect"] = "EditVideoNoteProspect";
    ActionTypeEnum["DeleteVideoNoteProspect"] = "DeleteVideoNoteProspect";
    ActionTypeEnum["AddMeetingProspect"] = "AddMeetingProspect";
    ActionTypeEnum["EditMeetingProspect"] = "EditMeetingProspect";
    ActionTypeEnum["DeleteMeetingProspect"] = "DeleteMeetingProspect";
    ActionTypeEnum["AddTaskProspect"] = "AddTaskProspect";
    ActionTypeEnum["EditTaskProspect"] = "EditTaskProspect";
    ActionTypeEnum["DeleteTaskProspect"] = "DeleteTaskProspect";
    ActionTypeEnum["AddIssueProspect"] = "AddIssueProspect";
    ActionTypeEnum["EditIssueProspect"] = "EditIssueProspect";
    ActionTypeEnum["DeleteIssueProspect"] = "DeleteIssueProspect";
    ActionTypeEnum["AddCaseProspect"] = "AddCaseProspect";
    ActionTypeEnum["EditCaseProspect"] = "EditCaseProspect";
    ActionTypeEnum["DeleteCaseProspect"] = "DeleteCaseProspect";
    ActionTypeEnum["AddFileUploadProspect"] = "AddFileUploadProspect";
    ActionTypeEnum["EditFileUploadProspect"] = "EditFileUploadProspect";
    ActionTypeEnum["DeleteFileUploadProspect"] = "DeleteFileUploadProspect";
    ActionTypeEnum["AddEmailProspect"] = "AddEmailProspect";
    ActionTypeEnum["EditEmailProspect"] = "EditEmailProspect";
    ActionTypeEnum["DeleteEmailProspect"] = "DeleteEmailProspect";
    ActionTypeEnum["AddOutsideCallProspect"] = "AddOutsideCallProspect";
    ActionTypeEnum["EditOutsideCallProspect"] = "EditOutsideCallProspect";
    ActionTypeEnum["DeleteOutsideCallProspect"] = "DeleteOutsideCallProspect";
    ActionTypeEnum["AddSmsProspect"] = "AddSmsProspect";
    ActionTypeEnum["EditSmsProspect"] = "EditSmsProspect";
    ActionTypeEnum["DeleteSmsProspect"] = "DeleteSmsProspect";
    ActionTypeEnum["AddTextNoteLead"] = "AddTextNoteLead";
    ActionTypeEnum["EditTextNoteLead"] = "EditTextNoteLead";
    ActionTypeEnum["DeleteTextNoteLead"] = "DeleteTextNoteLead";
    ActionTypeEnum["AddAudioNoteLead"] = "AddAudioNoteLead";
    ActionTypeEnum["EditAudioNoteLead"] = "EditAudioNoteLead";
    ActionTypeEnum["DeleteAudioNoteLead"] = "DeleteAudioNoteLead";
    ActionTypeEnum["AddVideoNoteLead"] = "AddVideoNoteLead";
    ActionTypeEnum["EditVideoNoteLead"] = "EditVideoNoteLead";
    ActionTypeEnum["DeleteVideoNoteLead"] = "DeleteVideoNoteLead";
    ActionTypeEnum["AddMeetingLead"] = "AddMeetingLead";
    ActionTypeEnum["EditMeetingLead"] = "EditMeetingLead";
    ActionTypeEnum["DeleteMeetingLead"] = "DeleteMeetingLead";
    ActionTypeEnum["AddTaskLead"] = "AddTaskLead";
    ActionTypeEnum["EditTaskLead"] = "EditTaskLead";
    ActionTypeEnum["DeleteTaskLead"] = "DeleteTaskLead";
    ActionTypeEnum["AddIssueLead"] = "AddIssueLead";
    ActionTypeEnum["EditIssueLead"] = "EditIssueLead";
    ActionTypeEnum["DeleteIssueLead"] = "DeleteIssueLead";
    ActionTypeEnum["AddCaseLead"] = "AddCaseLead";
    ActionTypeEnum["EditCaseLead"] = "EditCaseLead";
    ActionTypeEnum["DeleteCaseLead"] = "DeleteCaseLead";
    ActionTypeEnum["AddFileUploadLead"] = "AddFileUploadLead";
    ActionTypeEnum["EditFileUploadLead"] = "EditFileUploadLead";
    ActionTypeEnum["DeleteFileUploadLead"] = "DeleteFileUploadLead";
    ActionTypeEnum["AddEmailLead"] = "AddEmailLead";
    ActionTypeEnum["EditEmailLead"] = "EditEmailLead";
    ActionTypeEnum["DeleteEmailLead"] = "DeleteEmailLead";
    ActionTypeEnum["AddOutsideCallLead"] = "AddOutsideCallLead";
    ActionTypeEnum["EditOutsideCallLead"] = "EditOutsideCallLead";
    ActionTypeEnum["DeleteOutsideCallLead"] = "DeleteOutsideCallLead";
    ActionTypeEnum["AddSmsLead"] = "AddSmsLead";
    ActionTypeEnum["EditSmsLead"] = "EditSmsLead";
    ActionTypeEnum["DeleteSmsLead"] = "DeleteSmsLead";
    ActionTypeEnum["AddTextNoteOffer"] = "AddTextNoteOffer";
    ActionTypeEnum["EditTextNoteOffer"] = "EditTextNoteOffer";
    ActionTypeEnum["DeleteTextNoteOffer"] = "DeleteTextNoteOffer";
    ActionTypeEnum["AddAudioNoteOffer"] = "AddAudioNoteOffer";
    ActionTypeEnum["EditAudioNoteOffer"] = "EditAudioNoteOffer";
    ActionTypeEnum["DeleteAudioNoteOffer"] = "DeleteAudioNoteOffer";
    ActionTypeEnum["AddVideoNoteOffer"] = "AddVideoNoteOffer";
    ActionTypeEnum["EditVideoNoteOffer"] = "EditVideoNoteOffer";
    ActionTypeEnum["DeleteVideoNoteOffer"] = "DeleteVideoNoteOffer";
    ActionTypeEnum["AddMeetingOffer"] = "AddMeetingOffer";
    ActionTypeEnum["EditMeetingOffer"] = "EditMeetingOffer";
    ActionTypeEnum["DeleteMeetingOffer"] = "DeleteMeetingOffer";
    ActionTypeEnum["AddTaskOffer"] = "AddTaskOffer";
    ActionTypeEnum["EditTaskOffer"] = "EditTaskOffer";
    ActionTypeEnum["DeleteTaskOffer"] = "DeleteTaskOffer";
    ActionTypeEnum["AddIssueOffer"] = "AddIssueOffer";
    ActionTypeEnum["EditIssueOffer"] = "EditIssueOffer";
    ActionTypeEnum["DeleteIssueOffer"] = "DeleteIssueOffer";
    ActionTypeEnum["AddCaseOffer"] = "AddCaseOffer";
    ActionTypeEnum["EditCaseOffer"] = "EditCaseOffer";
    ActionTypeEnum["DeleteCaseOffer"] = "DeleteCaseOffer";
    ActionTypeEnum["AddFileUploadOffer"] = "AddFileUploadOffer";
    ActionTypeEnum["EditFileUploadOffer"] = "EditFileUploadOffer";
    ActionTypeEnum["DeleteFileUploadOffer"] = "DeleteFileUploadOffer";
    ActionTypeEnum["AddEmailOffer"] = "AddEmailOffer";
    ActionTypeEnum["EditEmailOffer"] = "EditEmailOffer";
    ActionTypeEnum["DeleteEmailOffer"] = "DeleteEmailOffer";
    ActionTypeEnum["AddOutsideCallOffer"] = "AddOutsideCallOffer";
    ActionTypeEnum["EditOutsideCallOffer"] = "EditOutsideCallOffer";
    ActionTypeEnum["DeleteOutsideCallOffer"] = "DeleteOutsideCallOffer";
    ActionTypeEnum["AddSmsOffer"] = "AddSmsOffer";
    ActionTypeEnum["EditSmsOffer"] = "EditSmsOffer";
    ActionTypeEnum["DeleteSmsOffer"] = "DeleteSmsOffer";
    ActionTypeEnum["AddTextNoteDeal"] = "AddTextNoteDeal";
    ActionTypeEnum["EditTextNoteDeal"] = "EditTextNoteDeal";
    ActionTypeEnum["DeleteTextNoteDeal"] = "DeleteTextNoteDeal";
    ActionTypeEnum["AddAudioNoteDeal"] = "AddAudioNoteDeal";
    ActionTypeEnum["EditAudioNoteDeal"] = "EditAudioNoteDeal";
    ActionTypeEnum["DeleteAudioNoteDeal"] = "DeleteAudioNoteDeal";
    ActionTypeEnum["AddVideoNoteDeal"] = "AddVideoNoteDeal";
    ActionTypeEnum["EditVideoNoteDeal"] = "EditVideoNoteDeal";
    ActionTypeEnum["DeleteVideoNoteDeal"] = "DeleteVideoNoteDeal";
    ActionTypeEnum["AddMeetingDeal"] = "AddMeetingDeal";
    ActionTypeEnum["EditMeetingDeal"] = "EditMeetingDeal";
    ActionTypeEnum["DeleteMeetingDeal"] = "DeleteMeetingDeal";
    ActionTypeEnum["AddTaskDeal"] = "AddTaskDeal";
    ActionTypeEnum["EditTaskDeal"] = "EditTaskDeal";
    ActionTypeEnum["DeleteTaskDeal"] = "DeleteTaskDeal";
    ActionTypeEnum["AddIssueDeal"] = "AddIssueDeal";
    ActionTypeEnum["EditIssueDeal"] = "EditIssueDeal";
    ActionTypeEnum["DeleteIssueDeal"] = "DeleteIssueDeal";
    ActionTypeEnum["AddCaseDeal"] = "AddCaseDeal";
    ActionTypeEnum["EditCaseDeal"] = "EditCaseDeal";
    ActionTypeEnum["DeleteCaseDeal"] = "DeleteCaseDeal";
    ActionTypeEnum["AddFileUploadDeal"] = "AddFileUploadDeal";
    ActionTypeEnum["EditFileUploadDeal"] = "EditFileUploadDeal";
    ActionTypeEnum["DeleteFileUploadDeal"] = "DeleteFileUploadDeal";
    ActionTypeEnum["AddEmailDeal"] = "AddEmailDeal";
    ActionTypeEnum["EditEmailDeal"] = "EditEmailDeal";
    ActionTypeEnum["DeleteEmailDeal"] = "DeleteEmailDeal";
    ActionTypeEnum["AddOutsideCallDeal"] = "AddOutsideCallDeal";
    ActionTypeEnum["EditOutsideCallDeal"] = "EditOutsideCallDeal";
    ActionTypeEnum["DeleteOutsideCallDeal"] = "DeleteOutsideCallDeal";
    ActionTypeEnum["AddSmsDeal"] = "AddSmsDeal";
    ActionTypeEnum["EditSmsDeal"] = "EditSmsDeal";
    ActionTypeEnum["DeleteSmsDeal"] = "DeleteSmsDeal";
    ActionTypeEnum["AddTextNoteCoreDeal"] = "AddTextNoteCoreDeal";
    ActionTypeEnum["EditTextNoteCoreDeal"] = "EditTextNoteCoreDeal";
    ActionTypeEnum["DeleteTextNoteCoreDeal"] = "DeleteTextNoteCoreDeal";
    ActionTypeEnum["AddAudioNoteCoreDeal"] = "AddAudioNoteCoreDeal";
    ActionTypeEnum["EditAudioNoteCoreDeal"] = "EditAudioNoteCoreDeal";
    ActionTypeEnum["DeleteAudioNoteCoreDeal"] = "DeleteAudioNoteCoreDeal";
    ActionTypeEnum["AddVideoNoteCoreDeal"] = "AddVideoNoteCoreDeal";
    ActionTypeEnum["EditVideoNoteCoreDeal"] = "EditVideoNoteCoreDeal";
    ActionTypeEnum["DeleteVideoNoteCoreDeal"] = "DeleteVideoNoteCoreDeal";
    ActionTypeEnum["AddMeetingCoreDeal"] = "AddMeetingCoreDeal";
    ActionTypeEnum["EditMeetingCoreDeal"] = "EditMeetingCoreDeal";
    ActionTypeEnum["DeleteMeetingCoreDeal"] = "DeleteMeetingCoreDeal";
    ActionTypeEnum["AddTaskCoreDeal"] = "AddTaskCoreDeal";
    ActionTypeEnum["EditTaskCoreDeal"] = "EditTaskCoreDeal";
    ActionTypeEnum["DeleteTaskCoreDeal"] = "DeleteTaskCoreDeal";
    ActionTypeEnum["AddIssueCoreDeal"] = "AddIssueCoreDeal";
    ActionTypeEnum["EditIssueCoreDeal"] = "EditIssueCoreDeal";
    ActionTypeEnum["DeleteIssueCoreDeal"] = "DeleteIssueCoreDeal";
    ActionTypeEnum["AddCaseCoreDeal"] = "AddCaseCoreDeal";
    ActionTypeEnum["EditCaseCoreDeal"] = "EditCaseCoreDeal";
    ActionTypeEnum["DeleteCaseCoreDeal"] = "DeleteCaseCoreDeal";
    ActionTypeEnum["AddFileUploadCoreDeal"] = "AddFileUploadCoreDeal";
    ActionTypeEnum["EditFileUploadCoreDeal"] = "EditFileUploadCoreDeal";
    ActionTypeEnum["DeleteFileUploadCoreDeal"] = "DeleteFileUploadCoreDeal";
    ActionTypeEnum["AddEmailCoreDeal"] = "AddEmailCoreDeal";
    ActionTypeEnum["EditEmailCoreDeal"] = "EditEmailCoreDeal";
    ActionTypeEnum["DeleteEmailCoreDeal"] = "DeleteEmailCoreDeal";
    ActionTypeEnum["AddOutsideCallCoreDeal"] = "AddOutsideCallCoreDeal";
    ActionTypeEnum["EditOutsideCallCoreDeal"] = "EditOutsideCallCoreDeal";
    ActionTypeEnum["DeleteOutsideCallCoreDeal"] = "DeleteOutsideCallCoreDeal";
    ActionTypeEnum["AddSmsCoreDeal"] = "AddSmsCoreDeal";
    ActionTypeEnum["EditSmsCoreDeal"] = "EditSmsCoreDeal";
    ActionTypeEnum["DeleteSmsCoreDeal"] = "DeleteSmsCoreDeal";
    ActionTypeEnum["AddTextNoteInventory"] = "AddTextNoteInventory";
    ActionTypeEnum["EditTextNoteInventory"] = "EditTextNoteInventory";
    ActionTypeEnum["DeleteTextNoteInventory"] = "DeleteTextNoteInventory";
    ActionTypeEnum["AddAudioNoteInventory"] = "AddAudioNoteInventory";
    ActionTypeEnum["EditAudioNoteInventory"] = "EditAudioNoteInventory";
    ActionTypeEnum["DeleteAudioNoteInventory"] = "DeleteAudioNoteInventory";
    ActionTypeEnum["AddVideoNoteInventory"] = "AddVideoNoteInventory";
    ActionTypeEnum["EditVideoNoteInventory"] = "EditVideoNoteInventory";
    ActionTypeEnum["DeleteVideoNoteInventory"] = "DeleteVideoNoteInventory";
    ActionTypeEnum["AddMeetingInventory"] = "AddMeetingInventory";
    ActionTypeEnum["EditMeetingInventory"] = "EditMeetingInventory";
    ActionTypeEnum["DeleteMeetingInventory"] = "DeleteMeetingInventory";
    ActionTypeEnum["AddTaskInventory"] = "AddTaskInventory";
    ActionTypeEnum["EditTaskInventory"] = "EditTaskInventory";
    ActionTypeEnum["DeleteTaskInventory"] = "DeleteTaskInventory";
    ActionTypeEnum["AddIssueInventory"] = "AddIssueInventory";
    ActionTypeEnum["EditIssueInventory"] = "EditIssueInventory";
    ActionTypeEnum["DeleteIssueInventory"] = "DeleteIssueInventory";
    ActionTypeEnum["AddCaseInventory"] = "AddCaseInventory";
    ActionTypeEnum["EditCaseInventory"] = "EditCaseInventory";
    ActionTypeEnum["DeleteCaseInventory"] = "DeleteCaseInventory";
    ActionTypeEnum["AddFileUploadInventory"] = "AddFileUploadInventory";
    ActionTypeEnum["EditFileUploadInventory"] = "EditFileUploadInventory";
    ActionTypeEnum["DeleteFileUploadInventory"] = "DeleteFileUploadInventory";
    ActionTypeEnum["AddEmailInventory"] = "AddEmailInventory";
    ActionTypeEnum["EditEmailInventory"] = "EditEmailInventory";
    ActionTypeEnum["DeleteEmailInventory"] = "DeleteEmailInventory";
    ActionTypeEnum["AddOutsideCallInventory"] = "AddOutsideCallInventory";
    ActionTypeEnum["EditOutsideCallInventory"] = "EditOutsideCallInventory";
    ActionTypeEnum["DeleteOutsideCallInventory"] = "DeleteOutsideCallInventory";
    ActionTypeEnum["AddSmsInventory"] = "AddSmsInventory";
    ActionTypeEnum["EditSmsInventory"] = "EditSmsInventory";
    ActionTypeEnum["DeleteSmsInventory"] = "DeleteSmsInventory";
    ActionTypeEnum["AddTextNoteAssetManager"] = "AddTextNoteAssetManager";
    ActionTypeEnum["EditTextNoteAssetManager"] = "EditTextNoteAssetManager";
    ActionTypeEnum["DeleteTextNoteAssetManager"] = "DeleteTextNoteAssetManager";
    ActionTypeEnum["AddAudioNoteAssetManager"] = "AddAudioNoteAssetManager";
    ActionTypeEnum["EditAudioNoteAssetManager"] = "EditAudioNoteAssetManager";
    ActionTypeEnum["DeleteAudioNoteAssetManager"] = "DeleteAudioNoteAssetManager";
    ActionTypeEnum["AddVideoNoteAssetManager"] = "AddVideoNoteAssetManager";
    ActionTypeEnum["EditVideoNoteAssetManager"] = "EditVideoNoteAssetManager";
    ActionTypeEnum["DeleteVideoNoteAssetManager"] = "DeleteVideoNoteAssetManager";
    ActionTypeEnum["AddMeetingAssetManager"] = "AddMeetingAssetManager";
    ActionTypeEnum["EditMeetingAssetManager"] = "EditMeetingAssetManager";
    ActionTypeEnum["DeleteMeetingAssetManager"] = "DeleteMeetingAssetManager";
    ActionTypeEnum["AddTaskAssetManager"] = "AddTaskAssetManager";
    ActionTypeEnum["EditTaskAssetManager"] = "EditTaskAssetManager";
    ActionTypeEnum["DeleteTaskAssetManager"] = "DeleteTaskAssetManager";
    ActionTypeEnum["AddIssueAssetManager"] = "AddIssueAssetManager";
    ActionTypeEnum["EditIssueAssetManager"] = "EditIssueAssetManager";
    ActionTypeEnum["DeleteIssueAssetManager"] = "DeleteIssueAssetManager";
    ActionTypeEnum["AddCaseAssetManager"] = "AddCaseAssetManager";
    ActionTypeEnum["EditCaseAssetManager"] = "EditCaseAssetManager";
    ActionTypeEnum["DeleteCaseAssetManager"] = "DeleteCaseAssetManager";
    ActionTypeEnum["AddFileUploadAssetManager"] = "AddFileUploadAssetManager";
    ActionTypeEnum["EditFileUploadAssetManager"] = "EditFileUploadAssetManager";
    ActionTypeEnum["DeleteFileUploadAssetManager"] = "DeleteFileUploadAssetManager";
    ActionTypeEnum["AddEmailAssetManager"] = "AddEmailAssetManager";
    ActionTypeEnum["EditEmailAssetManager"] = "EditEmailAssetManager";
    ActionTypeEnum["DeleteEmailAssetManager"] = "DeleteEmailAssetManager";
    ActionTypeEnum["AddOutsideCallAssetManager"] = "AddOutsideCallAssetManager";
    ActionTypeEnum["EditOutsideCallAssetManager"] = "EditOutsideCallAssetManager";
    ActionTypeEnum["DeleteOutsideCallAssetManager"] = "DeleteOutsideCallAssetManager";
    ActionTypeEnum["AddSmsAssetManager"] = "AddSmsAssetManager";
    ActionTypeEnum["EditSmsAssetManager"] = "EditSmsAssetManager";
    ActionTypeEnum["DeleteSmsAssetManager"] = "DeleteSmsAssetManager";
    ActionTypeEnum["AddTextNoteCatalog"] = "AddTextNoteCatalog";
    ActionTypeEnum["EditTextNoteCatalog"] = "EditTextNoteCatalog";
    ActionTypeEnum["DeleteTextNoteCatalog"] = "DeleteTextNoteCatalog";
    ActionTypeEnum["AddAudioNoteCatalog"] = "AddAudioNoteCatalog";
    ActionTypeEnum["EditAudioNoteCatalog"] = "EditAudioNoteCatalog";
    ActionTypeEnum["DeleteAudioNoteCatalog"] = "DeleteAudioNoteCatalog";
    ActionTypeEnum["AddVideoNoteCatalog"] = "AddVideoNoteCatalog";
    ActionTypeEnum["EditVideoNoteCatalog"] = "EditVideoNoteCatalog";
    ActionTypeEnum["DeleteVideoNoteCatalog"] = "DeleteVideoNoteCatalog";
    ActionTypeEnum["AddMeetingCatalog"] = "AddMeetingCatalog";
    ActionTypeEnum["EditMeetingCatalog"] = "EditMeetingCatalog";
    ActionTypeEnum["DeleteMeetingCatalog"] = "DeleteMeetingCatalog";
    ActionTypeEnum["AddTaskCatalog"] = "AddTaskCatalog";
    ActionTypeEnum["EditTaskCatalog"] = "EditTaskCatalog";
    ActionTypeEnum["DeleteTaskCatalog"] = "DeleteTaskCatalog";
    ActionTypeEnum["AddIssueCatalog"] = "AddIssueCatalog";
    ActionTypeEnum["EditIssueCatalog"] = "EditIssueCatalog";
    ActionTypeEnum["DeleteIssueCatalog"] = "DeleteIssueCatalog";
    ActionTypeEnum["AddCaseCatalog"] = "AddCaseCatalog";
    ActionTypeEnum["EditCaseCatalog"] = "EditCaseCatalog";
    ActionTypeEnum["DeleteCaseCatalog"] = "DeleteCaseCatalog";
    ActionTypeEnum["AddFileUploadCatalog"] = "AddFileUploadCatalog";
    ActionTypeEnum["EditFileUploadCatalog"] = "EditFileUploadCatalog";
    ActionTypeEnum["DeleteFileUploadCatalog"] = "DeleteFileUploadCatalog";
    ActionTypeEnum["AddEmailCatalog"] = "AddEmailCatalog";
    ActionTypeEnum["EditEmailCatalog"] = "EditEmailCatalog";
    ActionTypeEnum["DeleteEmailCatalog"] = "DeleteEmailCatalog";
    ActionTypeEnum["AddOutsideCallCatalog"] = "AddOutsideCallCatalog";
    ActionTypeEnum["EditOutsideCallCatalog"] = "EditOutsideCallCatalog";
    ActionTypeEnum["DeleteOutsideCallCatalog"] = "DeleteOutsideCallCatalog";
    ActionTypeEnum["AddSmsCatalog"] = "AddSmsCatalog";
    ActionTypeEnum["EditSmsCatalog"] = "EditSmsCatalog";
    ActionTypeEnum["DeleteSmsCatalog"] = "DeleteSmsCatalog";
    ActionTypeEnum["AddTextNoteCoreAssetManager"] = "AddTextNoteCoreAssetManager";
    ActionTypeEnum["EditTextNoteCoreAssetManager"] = "EditTextNoteCoreAssetManager";
    ActionTypeEnum["DeleteTextNoteCoreAssetManager"] = "DeleteTextNoteCoreAssetManager";
    ActionTypeEnum["AddAudioNoteCoreAssetManager"] = "AddAudioNoteCoreAssetManager";
    ActionTypeEnum["EditAudioNoteCoreAssetManager"] = "EditAudioNoteCoreAssetManager";
    ActionTypeEnum["DeleteAudioNoteCoreAssetManager"] = "DeleteAudioNoteCoreAssetManager";
    ActionTypeEnum["AddVideoNoteCoreAssetManager"] = "AddVideoNoteCoreAssetManager";
    ActionTypeEnum["EditVideoNoteCoreAssetManager"] = "EditVideoNoteCoreAssetManager";
    ActionTypeEnum["DeleteVideoNoteCoreAssetManager"] = "DeleteVideoNoteCoreAssetManager";
    ActionTypeEnum["AddMeetingCoreAssetManager"] = "AddMeetingCoreAssetManager";
    ActionTypeEnum["EditMeetingCoreAssetManager"] = "EditMeetingCoreAssetManager";
    ActionTypeEnum["DeleteMeetingCoreAssetManager"] = "DeleteMeetingCoreAssetManager";
    ActionTypeEnum["AddTaskCoreAssetManager"] = "AddTaskCoreAssetManager";
    ActionTypeEnum["EditTaskCoreAssetManager"] = "EditTaskCoreAssetManager";
    ActionTypeEnum["DeleteTaskCoreAssetManager"] = "DeleteTaskCoreAssetManager";
    ActionTypeEnum["AddIssueCoreAssetManager"] = "AddIssueCoreAssetManager";
    ActionTypeEnum["EditIssueCoreAssetManager"] = "EditIssueCoreAssetManager";
    ActionTypeEnum["DeleteIssueCoreAssetManager"] = "DeleteIssueCoreAssetManager";
    ActionTypeEnum["AddCaseCoreAssetManager"] = "AddCaseCoreAssetManager";
    ActionTypeEnum["EditCaseCoreAssetManager"] = "EditCaseCoreAssetManager";
    ActionTypeEnum["DeleteCaseCoreAssetManager"] = "DeleteCaseCoreAssetManager";
    ActionTypeEnum["AddFileUploadCoreAssetManager"] = "AddFileUploadCoreAssetManager";
    ActionTypeEnum["EditFileUploadCoreAssetManager"] = "EditFileUploadCoreAssetManager";
    ActionTypeEnum["DeleteFileUploadCoreAssetManager"] = "DeleteFileUploadCoreAssetManager";
    ActionTypeEnum["AddEmailCoreAssetManager"] = "AddEmailCoreAssetManager";
    ActionTypeEnum["EditEmailCoreAssetManager"] = "EditEmailCoreAssetManager";
    ActionTypeEnum["DeleteEmailCoreAssetManager"] = "DeleteEmailCoreAssetManager";
    ActionTypeEnum["AddOutsideCallCoreAssetManager"] = "AddOutsideCallCoreAssetManager";
    ActionTypeEnum["EditOutsideCallCoreAssetManager"] = "EditOutsideCallCoreAssetManager";
    ActionTypeEnum["DeleteOutsideCallCoreAssetManager"] = "DeleteOutsideCallCoreAssetManager";
    ActionTypeEnum["AddSmsCoreAssetManager"] = "AddSmsCoreAssetManager";
    ActionTypeEnum["EditSmsCoreAssetManager"] = "EditSmsCoreAssetManager";
    ActionTypeEnum["DeleteSmsCoreAssetManager"] = "DeleteSmsCoreAssetManager";
    ActionTypeEnum["AddTextNoteCoworker"] = "AddTextNoteCoworker";
    ActionTypeEnum["EditTextNoteCoworker"] = "EditTextNoteCoworker";
    ActionTypeEnum["DeleteTextNoteCoworker"] = "DeleteTextNoteCoworker";
    ActionTypeEnum["AddAudioNoteCoworker"] = "AddAudioNoteCoworker";
    ActionTypeEnum["EditAudioNoteCoworker"] = "EditAudioNoteCoworker";
    ActionTypeEnum["DeleteAudioNoteCoworker"] = "DeleteAudioNoteCoworker";
    ActionTypeEnum["AddVideoNoteCoworker"] = "AddVideoNoteCoworker";
    ActionTypeEnum["EditVideoNoteCoworker"] = "EditVideoNoteCoworker";
    ActionTypeEnum["DeleteVideoNoteCoworker"] = "DeleteVideoNoteCoworker";
    ActionTypeEnum["AddMeetingCoworker"] = "AddMeetingCoworker";
    ActionTypeEnum["EditMeetingCoworker"] = "EditMeetingCoworker";
    ActionTypeEnum["DeleteMeetingCoworker"] = "DeleteMeetingCoworker";
    ActionTypeEnum["AddTaskCoworker"] = "AddTaskCoworker";
    ActionTypeEnum["EditTaskCoworker"] = "EditTaskCoworker";
    ActionTypeEnum["DeleteTaskCoworker"] = "DeleteTaskCoworker";
    ActionTypeEnum["AddIssueCoworker"] = "AddIssueCoworker";
    ActionTypeEnum["EditIssueCoworker"] = "EditIssueCoworker";
    ActionTypeEnum["DeleteIssueCoworker"] = "DeleteIssueCoworker";
    ActionTypeEnum["AddCaseCoworker"] = "AddCaseCoworker";
    ActionTypeEnum["EditCaseCoworker"] = "EditCaseCoworker";
    ActionTypeEnum["DeleteCaseCoworker"] = "DeleteCaseCoworker";
    ActionTypeEnum["AddFileUploadCoworker"] = "AddFileUploadCoworker";
    ActionTypeEnum["EditFileUploadCoworker"] = "EditFileUploadCoworker";
    ActionTypeEnum["DeleteFileUploadCoworker"] = "DeleteFileUploadCoworker";
    ActionTypeEnum["AddEmailCoworker"] = "AddEmailCoworker";
    ActionTypeEnum["EditEmailCoworker"] = "EditEmailCoworker";
    ActionTypeEnum["DeleteEmailCoworker"] = "DeleteEmailCoworker";
    ActionTypeEnum["AddOutsideCallCoworker"] = "AddOutsideCallCoworker";
    ActionTypeEnum["EditOutsideCallCoworker"] = "EditOutsideCallCoworker";
    ActionTypeEnum["DeleteOutsideCallCoworker"] = "DeleteOutsideCallCoworker";
    ActionTypeEnum["AddSmsCoworker"] = "AddSmsCoworker";
    ActionTypeEnum["EditSmsCoworker"] = "EditSmsCoworker";
    ActionTypeEnum["DeleteSmsCoworker"] = "DeleteSmsCoworker";
    ActionTypeEnum["AddTextNoteTask"] = "AddTextNoteTask";
    ActionTypeEnum["EditTextNoteTask"] = "EditTextNoteTask";
    ActionTypeEnum["DeleteTextNoteTask"] = "DeleteTextNoteTask";
    ActionTypeEnum["AddAudioNoteTask"] = "AddAudioNoteTask";
    ActionTypeEnum["EditAudioNoteTask"] = "EditAudioNoteTask";
    ActionTypeEnum["DeleteAudioNoteTask"] = "DeleteAudioNoteTask";
    ActionTypeEnum["AddVideoNoteTask"] = "AddVideoNoteTask";
    ActionTypeEnum["EditVideoNoteTask"] = "EditVideoNoteTask";
    ActionTypeEnum["DeleteVideoNoteTask"] = "DeleteVideoNoteTask";
    ActionTypeEnum["AddMeetingTask"] = "AddMeetingTask";
    ActionTypeEnum["EditMeetingTask"] = "EditMeetingTask";
    ActionTypeEnum["DeleteMeetingTask"] = "DeleteMeetingTask";
    ActionTypeEnum["AddTaskTask"] = "AddTaskTask";
    ActionTypeEnum["EditTaskTask"] = "EditTaskTask";
    ActionTypeEnum["DeleteTaskTask"] = "DeleteTaskTask";
    ActionTypeEnum["AddIssueTask"] = "AddIssueTask";
    ActionTypeEnum["EditIssueTask"] = "EditIssueTask";
    ActionTypeEnum["DeleteIssueTask"] = "DeleteIssueTask";
    ActionTypeEnum["AddCaseTask"] = "AddCaseTask";
    ActionTypeEnum["EditCaseTask"] = "EditCaseTask";
    ActionTypeEnum["DeleteCaseTask"] = "DeleteCaseTask";
    ActionTypeEnum["AddFileUploadTask"] = "AddFileUploadTask";
    ActionTypeEnum["EditFileUploadTask"] = "EditFileUploadTask";
    ActionTypeEnum["DeleteFileUploadTask"] = "DeleteFileUploadTask";
    ActionTypeEnum["AddEmailTask"] = "AddEmailTask";
    ActionTypeEnum["EditEmailTask"] = "EditEmailTask";
    ActionTypeEnum["DeleteEmailTask"] = "DeleteEmailTask";
    ActionTypeEnum["AddOutsideCallTask"] = "AddOutsideCallTask";
    ActionTypeEnum["EditOutsideCallTask"] = "EditOutsideCallTask";
    ActionTypeEnum["DeleteOutsideCallTask"] = "DeleteOutsideCallTask";
    ActionTypeEnum["AddSmsTask"] = "AddSmsTask";
    ActionTypeEnum["EditSmsTask"] = "EditSmsTask";
    ActionTypeEnum["DeleteSmsTask"] = "DeleteSmsTask";
    ActionTypeEnum["AddTextNoteProjectWorkgroup"] = "AddTextNoteProjectWorkgroup";
    ActionTypeEnum["EditTextNoteProjectWorkgroup"] = "EditTextNoteProjectWorkgroup";
    ActionTypeEnum["DeleteTextNoteProjectWorkgroup"] = "DeleteTextNoteProjectWorkgroup";
    ActionTypeEnum["AddAudioNoteProjectWorkgroup"] = "AddAudioNoteProjectWorkgroup";
    ActionTypeEnum["EditAudioNoteProjectWorkgroup"] = "EditAudioNoteProjectWorkgroup";
    ActionTypeEnum["DeleteAudioNoteProjectWorkgroup"] = "DeleteAudioNoteProjectWorkgroup";
    ActionTypeEnum["AddVideoNoteProjectWorkgroup"] = "AddVideoNoteProjectWorkgroup";
    ActionTypeEnum["EditVideoNoteProjectWorkgroup"] = "EditVideoNoteProjectWorkgroup";
    ActionTypeEnum["DeleteVideoNoteProjectWorkgroup"] = "DeleteVideoNoteProjectWorkgroup";
    ActionTypeEnum["AddMeetingProjectWorkgroup"] = "AddMeetingProjectWorkgroup";
    ActionTypeEnum["EditMeetingProjectWorkgroup"] = "EditMeetingProjectWorkgroup";
    ActionTypeEnum["DeleteMeetingProjectWorkgroup"] = "DeleteMeetingProjectWorkgroup";
    ActionTypeEnum["AddIssueProjectWorkgroup"] = "AddIssueProjectWorkgroup";
    ActionTypeEnum["EditIssueProjectWorkgroup"] = "EditIssueProjectWorkgroup";
    ActionTypeEnum["DeleteIssueProjectWorkgroup"] = "DeleteIssueProjectWorkgroup";
    ActionTypeEnum["AddCaseProjectWorkgroup"] = "AddCaseProjectWorkgroup";
    ActionTypeEnum["EditCaseProjectWorkgroup"] = "EditCaseProjectWorkgroup";
    ActionTypeEnum["DeleteCaseProjectWorkgroup"] = "DeleteCaseProjectWorkgroup";
    ActionTypeEnum["AddFileUploadProjectWorkgroup"] = "AddFileUploadProjectWorkgroup";
    ActionTypeEnum["EditFileUploadProjectWorkgroup"] = "EditFileUploadProjectWorkgroup";
    ActionTypeEnum["DeleteFileUploadProjectWorkgroup"] = "DeleteFileUploadProjectWorkgroup";
    ActionTypeEnum["AddEmailProjectWorkgroup"] = "AddEmailProjectWorkgroup";
    ActionTypeEnum["EditEmailProjectWorkgroup"] = "EditEmailProjectWorkgroup";
    ActionTypeEnum["DeleteEmailProjectWorkgroup"] = "DeleteEmailProjectWorkgroup";
    ActionTypeEnum["AddOutsideCallProjectWorkgroup"] = "AddOutsideCallProjectWorkgroup";
    ActionTypeEnum["EditOutsideCallProjectWorkgroup"] = "EditOutsideCallProjectWorkgroup";
    ActionTypeEnum["DeleteOutsideCallProjectWorkgroup"] = "DeleteOutsideCallProjectWorkgroup";
    ActionTypeEnum["AddSmsProjectWorkgroup"] = "AddSmsProjectWorkgroup";
    ActionTypeEnum["EditSmsProjectWorkgroup"] = "EditSmsProjectWorkgroup";
    ActionTypeEnum["DeleteSmsProjectWorkgroup"] = "DeleteSmsProjectWorkgroup";
    ActionTypeEnum["AddTextNoteEmployee"] = "AddTextNoteEmployee";
    ActionTypeEnum["EditTextNoteEmployee"] = "EditTextNoteEmployee";
    ActionTypeEnum["DeleteTextNoteEmployee"] = "DeleteTextNoteEmployee";
    ActionTypeEnum["AddAudioNoteEmployee"] = "AddAudioNoteEmployee";
    ActionTypeEnum["EditAudioNoteEmployee"] = "EditAudioNoteEmployee";
    ActionTypeEnum["DeleteAudioNoteEmployee"] = "DeleteAudioNoteEmployee";
    ActionTypeEnum["AddVideoNoteEmployee"] = "AddVideoNoteEmployee";
    ActionTypeEnum["EditVideoNoteEmployee"] = "EditVideoNoteEmployee";
    ActionTypeEnum["DeleteVideoNoteEmployee"] = "DeleteVideoNoteEmployee";
    ActionTypeEnum["AddMeetingEmployee"] = "AddMeetingEmployee";
    ActionTypeEnum["EditMeetingEmployee"] = "EditMeetingEmployee";
    ActionTypeEnum["DeleteMeetingEmployee"] = "DeleteMeetingEmployee";
    ActionTypeEnum["AddTaskEmployee"] = "AddTaskEmployee";
    ActionTypeEnum["EditTaskEmployee"] = "EditTaskEmployee";
    ActionTypeEnum["DeleteTaskEmployee"] = "DeleteTaskEmployee";
    ActionTypeEnum["AddIssueEmployee"] = "AddIssueEmployee";
    ActionTypeEnum["EditIssueEmployee"] = "EditIssueEmployee";
    ActionTypeEnum["DeleteIssueEmployee"] = "DeleteIssueEmployee";
    ActionTypeEnum["AddCaseEmployee"] = "AddCaseEmployee";
    ActionTypeEnum["EditCaseEmployee"] = "EditCaseEmployee";
    ActionTypeEnum["DeleteCaseEmployee"] = "DeleteCaseEmployee";
    ActionTypeEnum["AddFileUploadEmployee"] = "AddFileUploadEmployee";
    ActionTypeEnum["EditFileUploadEmployee"] = "EditFileUploadEmployee";
    ActionTypeEnum["DeleteFileUploadEmployee"] = "DeleteFileUploadEmployee";
    ActionTypeEnum["AddEmailEmployee"] = "AddEmailEmployee";
    ActionTypeEnum["EditEmailEmployee"] = "EditEmailEmployee";
    ActionTypeEnum["DeleteEmailEmployee"] = "DeleteEmailEmployee";
    ActionTypeEnum["AddOutsideCallEmployee"] = "AddOutsideCallEmployee";
    ActionTypeEnum["EditOutsideCallEmployee"] = "EditOutsideCallEmployee";
    ActionTypeEnum["DeleteOutsideCallEmployee"] = "DeleteOutsideCallEmployee";
    ActionTypeEnum["AddSmsEmployee"] = "AddSmsEmployee";
    ActionTypeEnum["EditSmsEmployee"] = "EditSmsEmployee";
    ActionTypeEnum["DeleteSmsEmployee"] = "DeleteSmsEmployee";
    ActionTypeEnum["AddTextNoteUser"] = "AddTextNoteUser";
    ActionTypeEnum["EditTextNoteUser"] = "EditTextNoteUser";
    ActionTypeEnum["DeleteTextNoteUser"] = "DeleteTextNoteUser";
    ActionTypeEnum["AddAudioNoteUser"] = "AddAudioNoteUser";
    ActionTypeEnum["EditAudioNoteUser"] = "EditAudioNoteUser";
    ActionTypeEnum["DeleteAudioNoteUser"] = "DeleteAudioNoteUser";
    ActionTypeEnum["AddVideoNoteUser"] = "AddVideoNoteUser";
    ActionTypeEnum["EditVideoNoteUser"] = "EditVideoNoteUser";
    ActionTypeEnum["DeleteVideoNoteUser"] = "DeleteVideoNoteUser";
    ActionTypeEnum["AddMeetingUser"] = "AddMeetingUser";
    ActionTypeEnum["EditMeetingUser"] = "EditMeetingUser";
    ActionTypeEnum["DeleteMeetingUser"] = "DeleteMeetingUser";
    ActionTypeEnum["AddTaskUser"] = "AddTaskUser";
    ActionTypeEnum["EditTaskUser"] = "EditTaskUser";
    ActionTypeEnum["DeleteTaskUser"] = "DeleteTaskUser";
    ActionTypeEnum["AddIssueUser"] = "AddIssueUser";
    ActionTypeEnum["EditIssueUser"] = "EditIssueUser";
    ActionTypeEnum["DeleteIssueUser"] = "DeleteIssueUser";
    ActionTypeEnum["AddCaseUser"] = "AddCaseUser";
    ActionTypeEnum["EditCaseUser"] = "EditCaseUser";
    ActionTypeEnum["DeleteCaseUser"] = "DeleteCaseUser";
    ActionTypeEnum["AddFileUploadUser"] = "AddFileUploadUser";
    ActionTypeEnum["EditFileUploadUser"] = "EditFileUploadUser";
    ActionTypeEnum["DeleteFileUploadUser"] = "DeleteFileUploadUser";
    ActionTypeEnum["AddEmailUser"] = "AddEmailUser";
    ActionTypeEnum["EditEmailUser"] = "EditEmailUser";
    ActionTypeEnum["DeleteEmailUser"] = "DeleteEmailUser";
    ActionTypeEnum["AddOutsideCallUser"] = "AddOutsideCallUser";
    ActionTypeEnum["EditOutsideCallUser"] = "EditOutsideCallUser";
    ActionTypeEnum["DeleteOutsideCallUser"] = "DeleteOutsideCallUser";
    ActionTypeEnum["AddSmsUser"] = "AddSmsUser";
    ActionTypeEnum["EditSmsUser"] = "EditSmsUser";
    ActionTypeEnum["DeleteSmsUser"] = "DeleteSmsUser";
    ActionTypeEnum["AddPipelineStep"] = "AddPipelineStep";
    ActionTypeEnum["EditPipelineStep"] = "EditPipelineStep";
    ActionTypeEnum["DeletePipelineStep"] = "DeletePipelineStep";
    ActionTypeEnum["AddPipelineStatus"] = "AddPipelineStatus";
    ActionTypeEnum["EditPipelineStatus"] = "EditPipelineStatus";
    ActionTypeEnum["DeletePipelineStatus"] = "DeletePipelineStatus";
    ActionTypeEnum["DeleteResponsiblePerson"] = "DeleteResponsiblePerson";
    ActionTypeEnum["DeleteOfferResponsiblePerson"] = "DeleteOfferResponsiblePerson";
    ActionTypeEnum["DeleteLeadResponsiblePerson"] = "DeleteLeadResponsiblePerson";
    ActionTypeEnum["DeleteDealResponsiblePerson"] = "DeleteDealResponsiblePerson";
    ActionTypeEnum["DeleteBusiness"] = "DeleteBusiness";
    ActionTypeEnum["AddToCart"] = "AddToCart";
    ActionTypeEnum["RemoveFromCart"] = "RemoveFromCart";
    ActionTypeEnum["ExpiredInCart"] = "ExpiredInCart";
    ActionTypeEnum["ExpiredBid"] = "ExpiredBid";
    ActionTypeEnum["AuctionWon"] = "AuctionWon";
    ActionTypeEnum["AuctionDeal"] = "AuctionDeal";
  })(ActionTypeEnum = CrmHistoryTypeEnum.ActionTypeEnum || (CrmHistoryTypeEnum.ActionTypeEnum = {}));
})(CrmHistoryTypeEnum || (CrmHistoryTypeEnum = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmItem;
(function (CrmItem) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = CrmItem.IsSavedEnum || (CrmItem.IsSavedEnum = {}));
})(CrmItem || (CrmItem = {}));
var CrmLead;
(function (CrmLead) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = CrmLead.IsSavedEnum || (CrmLead.IsSavedEnum = {}));
})(CrmLead || (CrmLead = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmNotify;
(function (CrmNotify) {
  let NotifyTypeEnum;
  (function (NotifyTypeEnum) {
    NotifyTypeEnum["Notify"] = "notify";
    NotifyTypeEnum["BetaTest"] = "beta_test";
  })(NotifyTypeEnum = CrmNotify.NotifyTypeEnum || (CrmNotify.NotifyTypeEnum = {}));
})(CrmNotify || (CrmNotify = {}));
var CrmOffer;
(function (CrmOffer) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = CrmOffer.IsSavedEnum || (CrmOffer.IsSavedEnum = {}));
})(CrmOffer || (CrmOffer = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmPhone;
(function (CrmPhone) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = CrmPhone.SharedTypeEnum || (CrmPhone.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
  })(TypeEnum = CrmPhone.TypeEnum || (CrmPhone.TypeEnum = {}));
  let PhoneTypeEnum;
  (function (PhoneTypeEnum) {
    PhoneTypeEnum["Mobile"] = "Mobile";
    PhoneTypeEnum["Landline"] = "Landline";
  })(PhoneTypeEnum = CrmPhone.PhoneTypeEnum || (CrmPhone.PhoneTypeEnum = {}));
})(CrmPhone || (CrmPhone = {}));
var CrmProspect;
(function (CrmProspect) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = CrmProspect.TypeEnum || (CrmProspect.TypeEnum = {}));
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Deleted"] = "deleted";
  })(StatusEnum = CrmProspect.StatusEnum || (CrmProspect.StatusEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = CrmProspect.IsSavedEnum || (CrmProspect.IsSavedEnum = {}));
  let IsSavedEventEnum;
  (function (IsSavedEventEnum) {
    IsSavedEventEnum["Block"] = "block";
    IsSavedEventEnum["Unblock"] = "unblock";
    IsSavedEventEnum["Follow"] = "follow";
    IsSavedEventEnum["Pending"] = "pending";
    IsSavedEventEnum["Reject"] = "reject";
    IsSavedEventEnum["Accept"] = "accept";
    IsSavedEventEnum["Delete"] = "delete";
    IsSavedEventEnum["NotFollow"] = "not_follow";
    IsSavedEventEnum["Report"] = "report";
    IsSavedEventEnum["UnReport"] = "un_report";
    IsSavedEventEnum["Done"] = "done";
    IsSavedEventEnum["SavedActive"] = "saved_active";
    IsSavedEventEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEventEnum["CrmSave"] = "crm_save";
    IsSavedEventEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEventEnum["NotRequest"] = "not_request";
    IsSavedEventEnum["None"] = "none";
    IsSavedEventEnum["SavedEvent"] = "saved_event";
    IsSavedEventEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEventEnum = CrmProspect.IsSavedEventEnum || (CrmProspect.IsSavedEventEnum = {}));
})(CrmProspect || (CrmProspect = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmProspectRequestRequest;
(function (CrmProspectRequestRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = CrmProspectRequestRequest.TypeEnum || (CrmProspectRequestRequest.TypeEnum = {}));
})(CrmProspectRequestRequest || (CrmProspectRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var CrmProspectUserProfile;
(function (CrmProspectUserProfile) {
  let GenderEnum;
  (function (GenderEnum) {
    GenderEnum["Unknown"] = "unknown";
    GenderEnum["Male"] = "male";
    GenderEnum["Female"] = "female";
    GenderEnum["Other"] = "other";
  })(GenderEnum = CrmProspectUserProfile.GenderEnum || (CrmProspectUserProfile.GenderEnum = {}));
})(CrmProspectUserProfile || (CrmProspectUserProfile = {}));
var CrmTargetedCustomer;
(function (CrmTargetedCustomer) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Business"] = "Business";
    TypeEnum["Personal"] = "Personal";
  })(TypeEnum = CrmTargetedCustomer.TypeEnum || (CrmTargetedCustomer.TypeEnum = {}));
})(CrmTargetedCustomer || (CrmTargetedCustomer = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var CustomFieldArea;
(function (CustomFieldArea) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["Product"] = "product";
    ServiceTypeEnum["CrmProspectPerson"] = "crm_prospect_person";
    ServiceTypeEnum["CrmProspectBusiness"] = "crm_prospect_business";
  })(ServiceTypeEnum = CustomFieldArea.ServiceTypeEnum || (CustomFieldArea.ServiceTypeEnum = {}));
})(CustomFieldArea || (CustomFieldArea = {}));
var CustomerLocation;
(function (CustomerLocation) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = CustomerLocation.IsSavedEnum || (CustomerLocation.IsSavedEnum = {}));
})(CustomerLocation || (CustomerLocation = {}));
var CustomerLocationDetail;
(function (CustomerLocationDetail) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = CustomerLocationDetail.IsSavedEnum || (CustomerLocationDetail.IsSavedEnum = {}));
})(CustomerLocationDetail || (CustomerLocationDetail = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Deal;
(function (Deal) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = Deal.IsSavedEnum || (Deal.IsSavedEnum = {}));
})(Deal || (Deal = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var DealDetailAd;
(function (DealDetailAd) {
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = DealDetailAd.IsReportedEnum || (DealDetailAd.IsReportedEnum = {}));
})(DealDetailAd || (DealDetailAd = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var DealRatings;
(function (DealRatings) {
  let SaleTypeEnum;
  (function (SaleTypeEnum) {
    SaleTypeEnum["Procurement"] = "Procurement";
    SaleTypeEnum["Selling"] = "Selling";
  })(SaleTypeEnum = DealRatings.SaleTypeEnum || (DealRatings.SaleTypeEnum = {}));
  let PartnerTypeEnum;
  (function (PartnerTypeEnum) {
    PartnerTypeEnum["Buyer"] = "Buyer";
    PartnerTypeEnum["Seller"] = "Seller";
  })(PartnerTypeEnum = DealRatings.PartnerTypeEnum || (DealRatings.PartnerTypeEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = DealRatings.IsReportedEnum || (DealRatings.IsReportedEnum = {}));
})(DealRatings || (DealRatings = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var DetailRatingBusiness;
(function (DetailRatingBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = DetailRatingBusiness.SubscriptionTypeEnum || (DetailRatingBusiness.SubscriptionTypeEnum = {}));
})(DetailRatingBusiness || (DetailRatingBusiness = {}));
var DetailedAlbumItem;
(function (DetailedAlbumItem) {
  let MediaFileTypeEnum;
  (function (MediaFileTypeEnum) {
    MediaFileTypeEnum["Image"] = "image";
    MediaFileTypeEnum["Video"] = "video";
    MediaFileTypeEnum["Audio"] = "audio";
    MediaFileTypeEnum["Document"] = "document";
    MediaFileTypeEnum["Zip"] = "zip";
    MediaFileTypeEnum["Other"] = "other";
  })(MediaFileTypeEnum = DetailedAlbumItem.MediaFileTypeEnum || (DetailedAlbumItem.MediaFileTypeEnum = {}));
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Adult"] = "adult";
    PostStateEnum["Normal"] = "normal";
    PostStateEnum["Sensitive"] = "sensitive";
  })(PostStateEnum = DetailedAlbumItem.PostStateEnum || (DetailedAlbumItem.PostStateEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = DetailedAlbumItem.IsReportedEnum || (DetailedAlbumItem.IsReportedEnum = {}));
  let UploadSystemTypeEnum;
  (function (UploadSystemTypeEnum) {
    UploadSystemTypeEnum["Album"] = "Album";
    UploadSystemTypeEnum["PreviewPageMeta"] = "Preview Page Meta";
    UploadSystemTypeEnum["PageMeta"] = "Page Meta";
    UploadSystemTypeEnum["SmartItem"] = "Smart Item";
    UploadSystemTypeEnum["Academy"] = "Academy";
    UploadSystemTypeEnum["Application"] = "Application";
    UploadSystemTypeEnum["EventFileUpload"] = "Event File Upload";
    UploadSystemTypeEnum["SmartWorkflow"] = "Smart Workflow";
  })(UploadSystemTypeEnum = DetailedAlbumItem.UploadSystemTypeEnum || (DetailedAlbumItem.UploadSystemTypeEnum = {}));
})(DetailedAlbumItem || (DetailedAlbumItem = {}));
var DetailedBusinesses;
(function (DetailedBusinesses) {
  let CoWorkerStatusEnum;
  (function (CoWorkerStatusEnum) {
    CoWorkerStatusEnum["Inactive"] = "inactive";
    CoWorkerStatusEnum["Active"] = "active";
    CoWorkerStatusEnum["Pending"] = "pending";
  })(CoWorkerStatusEnum = DetailedBusinesses.CoWorkerStatusEnum || (DetailedBusinesses.CoWorkerStatusEnum = {}));
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = DetailedBusinesses.SubscriptionTypeEnum || (DetailedBusinesses.SubscriptionTypeEnum = {}));
  let FxModuleEnum;
  (function (FxModuleEnum) {
    FxModuleEnum["UseOwn"] = "use_own";
    FxModuleEnum["UseSystem"] = "use_system";
    FxModuleEnum["OnlyAdded"] = "only_added";
  })(FxModuleEnum = DetailedBusinesses.FxModuleEnum || (DetailedBusinesses.FxModuleEnum = {}));
  let ErpTypeEnum;
  (function (ErpTypeEnum) {
    ErpTypeEnum["Personal"] = "personal";
    ErpTypeEnum["Business"] = "business";
  })(ErpTypeEnum = DetailedBusinesses.ErpTypeEnum || (DetailedBusinesses.ErpTypeEnum = {}));
  let LandingEnum;
  (function (LandingEnum) {
    LandingEnum["Ads"] = "ads";
    LandingEnum["Info"] = "info";
    LandingEnum["Activity"] = "activity";
    LandingEnum["MediaGallery"] = "media_gallery";
    LandingEnum["Followers"] = "followers";
    LandingEnum["Following"] = "following";
    LandingEnum["Coupon"] = "coupon";
    LandingEnum["Ratings"] = "ratings";
    LandingEnum["Location"] = "location";
  })(LandingEnum = DetailedBusinesses.LandingEnum || (DetailedBusinesses.LandingEnum = {}));
})(DetailedBusinesses || (DetailedBusinesses = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Domain;
(function (Domain) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
  })(StatusEnum = Domain.StatusEnum || (Domain.StatusEnum = {}));
})(Domain || (Domain = {}));
var DomainPage;
(function (DomainPage) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = DomainPage.StateEnum || (DomainPage.StateEnum = {}));
})(DomainPage || (DomainPage = {}));
var DomainPreviewPage;
(function (DomainPreviewPage) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = DomainPreviewPage.StateEnum || (DomainPreviewPage.StateEnum = {}));
})(DomainPreviewPage || (DomainPreviewPage = {}));
var DomainPreviewSite;
(function (DomainPreviewSite) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = DomainPreviewSite.StateEnum || (DomainPreviewSite.StateEnum = {}));
})(DomainPreviewSite || (DomainPreviewSite = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var DomainPreviewWidgetCollection;
(function (DomainPreviewWidgetCollection) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Active"] = "active";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = DomainPreviewWidgetCollection.StateEnum || (DomainPreviewWidgetCollection.StateEnum = {}));
})(DomainPreviewWidgetCollection || (DomainPreviewWidgetCollection = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var DomainPureTranslation;
(function (DomainPureTranslation) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["PreviewPage"] = "preview_page";
    ServiceTypeEnum["PreviewSite"] = "preview_site";
    ServiceTypeEnum["PreviewWidgetCollection"] = "preview_widget_collection";
    ServiceTypeEnum["PreviewPageMeta"] = "preview_page_meta";
    ServiceTypeEnum["PreviewSiteToPage"] = "preview_site_to_page";
    ServiceTypeEnum["Page"] = "page";
    ServiceTypeEnum["Site"] = "site";
    ServiceTypeEnum["Widget"] = "widget";
    ServiceTypeEnum["Meta"] = "meta";
  })(ServiceTypeEnum = DomainPureTranslation.ServiceTypeEnum || (DomainPureTranslation.ServiceTypeEnum = {}));
})(DomainPureTranslation || (DomainPureTranslation = {}));
var DomainSite;
(function (DomainSite) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = DomainSite.StateEnum || (DomainSite.StateEnum = {}));
})(DomainSite || (DomainSite = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var DriveAlbum;
(function (DriveAlbum) {
  let MediaShareEnum;
  (function (MediaShareEnum) {
    MediaShareEnum["Nobody"] = "nobody";
    MediaShareEnum["Anybody"] = "anybody";
    MediaShareEnum["ThoseFollowMe"] = "those_follow_me";
    MediaShareEnum["WhoIFollow"] = "who_i_follow";
    MediaShareEnum["Both"] = "both";
  })(MediaShareEnum = DriveAlbum.MediaShareEnum || (DriveAlbum.MediaShareEnum = {}));
})(DriveAlbum || (DriveAlbum = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var DriveAlbumItem;
(function (DriveAlbumItem) {
  let MediaFileTypeEnum;
  (function (MediaFileTypeEnum) {
    MediaFileTypeEnum["Image"] = "image";
    MediaFileTypeEnum["Video"] = "video";
    MediaFileTypeEnum["Audio"] = "audio";
    MediaFileTypeEnum["Document"] = "document";
    MediaFileTypeEnum["Zip"] = "zip";
    MediaFileTypeEnum["Other"] = "other";
  })(MediaFileTypeEnum = DriveAlbumItem.MediaFileTypeEnum || (DriveAlbumItem.MediaFileTypeEnum = {}));
})(DriveAlbumItem || (DriveAlbumItem = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ElasticSearchBusiness;
(function (ElasticSearchBusiness) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["User"] = "user";
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["CoWorker"] = "co_worker";
    EntityTypeEnum["Ad"] = "ad";
    EntityTypeEnum["Catalog"] = "catalog";
    EntityTypeEnum["App"] = "app";
    EntityTypeEnum["Prospect"] = "prospect";
  })(EntityTypeEnum = ElasticSearchBusiness.EntityTypeEnum || (ElasticSearchBusiness.EntityTypeEnum = {}));
})(ElasticSearchBusiness || (ElasticSearchBusiness = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ElasticSearchPublic;
(function (ElasticSearchPublic) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["User"] = "user";
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["Ad"] = "ad";
    EntityTypeEnum["Catalog"] = "catalog";
    EntityTypeEnum["App"] = "app";
  })(EntityTypeEnum = ElasticSearchPublic.EntityTypeEnum || (ElasticSearchPublic.EntityTypeEnum = {}));
})(ElasticSearchPublic || (ElasticSearchPublic = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var EmployeeRuleByCoWorkerRule;
(function (EmployeeRuleByCoWorkerRule) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["Crm"] = "crm";
    RuleTypeEnum["Hrms"] = "hrms";
    RuleTypeEnum["CompanyStream"] = "company_stream";
    RuleTypeEnum["OrgChart"] = "org_chart";
    RuleTypeEnum["Chat"] = "chat";
    RuleTypeEnum["CoWorkers"] = "co_workers";
    RuleTypeEnum["SalesTeam"] = "sales_team";
    RuleTypeEnum["TargetMarket"] = "target_market";
    RuleTypeEnum["WebsiteAppManager"] = "website_app_manager";
    RuleTypeEnum["SocialConnections"] = "social_connections";
    RuleTypeEnum["PopularityCenter"] = "popularity_center";
    RuleTypeEnum["MarketingTools"] = "marketing_tools";
    RuleTypeEnum["TaskManager"] = "task_manager";
    RuleTypeEnum["EmailManager"] = "email_manager";
    RuleTypeEnum["WorktimeManager"] = "worktime_manager";
    RuleTypeEnum["LocationManager"] = "location_manager";
    RuleTypeEnum["SmartCube"] = "smart_cube";
    RuleTypeEnum["Bi"] = "bi";
    RuleTypeEnum["Tax"] = "tax";
    RuleTypeEnum["Transportation"] = "transportation";
    RuleTypeEnum["Insurance"] = "insurance";
    RuleTypeEnum["Payment"] = "payment";
    RuleTypeEnum["BankAccounts"] = "bank_accounts";
    RuleTypeEnum["Procurement"] = "procurement";
    RuleTypeEnum["Auction"] = "auction";
    RuleTypeEnum["Settings"] = "settings";
    RuleTypeEnum["RbacModule"] = "rbac_module";
  })(RuleTypeEnum = EmployeeRuleByCoWorkerRule.RuleTypeEnum || (EmployeeRuleByCoWorkerRule.RuleTypeEnum = {}));
  let AccessChoiceEnum;
  (function (AccessChoiceEnum) {
    AccessChoiceEnum["AccessDenied"] = "access_denied";
    AccessChoiceEnum["FullAccess"] = "full_access";
    AccessChoiceEnum["ReadOnly"] = "read_only";
    AccessChoiceEnum["OnlyView"] = "only_view";
    AccessChoiceEnum["OnlyViewEdit"] = "only_view_edit";
    AccessChoiceEnum["Personal"] = "personal";
    AccessChoiceEnum["PersonalDepartment"] = "personal_department";
    AccessChoiceEnum["PersonalDepartmentSub"] = "personal_department_sub";
    AccessChoiceEnum["All"] = "all";
    AccessChoiceEnum["AdminDeleteAccess"] = "admin_delete_access";
    AccessChoiceEnum["PersonalSalesTeam"] = "personal_sales_team";
    AccessChoiceEnum["PersonalDepartmentSalesTeam"] = "personal_department_sales_team";
    AccessChoiceEnum["PersonalDepartmentSubSalesTeam"] = "personal_department_sub_sales_team";
  })(AccessChoiceEnum = EmployeeRuleByCoWorkerRule.AccessChoiceEnum || (EmployeeRuleByCoWorkerRule.AccessChoiceEnum = {}));
})(EmployeeRuleByCoWorkerRule || (EmployeeRuleByCoWorkerRule = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ErpBusinesses;
(function (ErpBusinesses) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = ErpBusinesses.SubscriptionTypeEnum || (ErpBusinesses.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = ErpBusinesses.IsFollowEnum || (ErpBusinesses.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = ErpBusinesses.IsReportedEnum || (ErpBusinesses.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = ErpBusinesses.IsBlockedEnum || (ErpBusinesses.IsBlockedEnum = {}));
})(ErpBusinesses || (ErpBusinesses = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ErpRegistration;
(function (ErpRegistration) {
  let ErpTypeEnum;
  (function (ErpTypeEnum) {
    ErpTypeEnum["Personal"] = "personal";
    ErpTypeEnum["Business"] = "business";
  })(ErpTypeEnum = ErpRegistration.ErpTypeEnum || (ErpRegistration.ErpTypeEnum = {}));
})(ErpRegistration || (ErpRegistration = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ErpRegistrationRequest;
(function (ErpRegistrationRequest) {
  let ErpTypeEnum;
  (function (ErpTypeEnum) {
    ErpTypeEnum["Personal"] = "personal";
    ErpTypeEnum["Business"] = "business";
  })(ErpTypeEnum = ErpRegistrationRequest.ErpTypeEnum || (ErpRegistrationRequest.ErpTypeEnum = {}));
})(ErpRegistrationRequest || (ErpRegistrationRequest = {}));
var ErpSubscriptionPageResponse;
(function (ErpSubscriptionPageResponse) {
  let SubscriptionRenewTypeEnum;
  (function (SubscriptionRenewTypeEnum) {
    SubscriptionRenewTypeEnum["Manually"] = "Manually";
    SubscriptionRenewTypeEnum["Automatic"] = "Automatic";
  })(SubscriptionRenewTypeEnum = ErpSubscriptionPageResponse.SubscriptionRenewTypeEnum || (ErpSubscriptionPageResponse.SubscriptionRenewTypeEnum = {}));
  let DefaultPaymentMethodEnum;
  (function (DefaultPaymentMethodEnum) {
    DefaultPaymentMethodEnum["Wallet"] = "Wallet";
    DefaultPaymentMethodEnum["Stripe"] = "Stripe";
    DefaultPaymentMethodEnum["Paypal"] = "Paypal";
  })(DefaultPaymentMethodEnum = ErpSubscriptionPageResponse.DefaultPaymentMethodEnum || (ErpSubscriptionPageResponse.DefaultPaymentMethodEnum = {}));
})(ErpSubscriptionPageResponse || (ErpSubscriptionPageResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var GetComment;
(function (GetComment) {
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = GetComment.IsReportedEnum || (GetComment.IsReportedEnum = {}));
})(GetComment || (GetComment = {}));
var GetContent;
(function (GetContent) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = GetContent.SharedTypeEnum || (GetContent.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
    TypeEnum["Website"] = "website";
    TypeEnum["SocialMedia"] = "social_media";
    TypeEnum["Other2"] = "other";
  })(TypeEnum = GetContent.TypeEnum || (GetContent.TypeEnum = {}));
  let PhoneTypeEnum;
  (function (PhoneTypeEnum) {
    PhoneTypeEnum["Mobile"] = "Mobile";
    PhoneTypeEnum["Landline"] = "Landline";
  })(PhoneTypeEnum = GetContent.PhoneTypeEnum || (GetContent.PhoneTypeEnum = {}));
})(GetContent || (GetContent = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var GetCrmProspect;
(function (GetCrmProspect) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = GetCrmProspect.TypeEnum || (GetCrmProspect.TypeEnum = {}));
})(GetCrmProspect || (GetCrmProspect = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var GetPostAnswer;
(function (GetPostAnswer) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = GetPostAnswer.IsSavedEnum || (GetPostAnswer.IsSavedEnum = {}));
})(GetPostAnswer || (GetPostAnswer = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var History;
(function (History) {
  let ActionEnum;
  (function (ActionEnum) {
    ActionEnum["CreateProspect"] = "CreateProspect";
    ActionEnum["AddCorePerson"] = "AddCorePerson";
    ActionEnum["AddCoreBusiness"] = "AddCoreBusiness";
    ActionEnum["ResponsiblePersonAllocated"] = "ResponsiblePersonAllocated";
    ActionEnum["DeleteProspect"] = "DeleteProspect";
    ActionEnum["ReactiveProspect"] = "ReactiveProspect";
    ActionEnum["UserRegistration"] = "UserRegistration";
    ActionEnum["UserEmailVerification"] = "UserEmailVerification";
    ActionEnum["CreateErp"] = "CreateErp";
    ActionEnum["ContactForm"] = "ContactForm";
    ActionEnum["AddEmail"] = "AddEmail";
    ActionEnum["EditEmail"] = "EditEmail";
    ActionEnum["ReportSent"] = "ReportSent";
    ActionEnum["ReportUndo"] = "ReportUndo";
    ActionEnum["AddBusiness"] = "AddBusiness";
    ActionEnum["SubscriptionChanged"] = "SubscriptionChanged";
    ActionEnum["SubscriptionCanceled"] = "SubscriptionCanceled";
    ActionEnum["AddPhone"] = "AddPhone";
    ActionEnum["EditPhone"] = "EditPhone";
    ActionEnum["AddAddress"] = "AddAddress";
    ActionEnum["EditAddress"] = "EditAddress";
    ActionEnum["NotifyForm"] = "NotifyForm";
    ActionEnum["AddKeyword"] = "AddKeyword";
    ActionEnum["DeleteKeyword"] = "DeleteKeyword";
    ActionEnum["DeleteCorePerson"] = "DeleteCorePerson";
    ActionEnum["DeleteCoreBusiness"] = "DeleteCoreBusiness";
    ActionEnum["EditResponsiblePerson"] = "EditResponsiblePerson";
    ActionEnum["AddLanguage"] = "AddLanguage";
    ActionEnum["EditLanguage"] = "EditLanguage";
    ActionEnum["DeleteAddress"] = "DeleteAddress";
    ActionEnum["DeleteEmail"] = "DeleteEmail";
    ActionEnum["DeletePhone"] = "DeletePhone";
    ActionEnum["InviteToBusiness"] = "InviteToBusiness";
    ActionEnum["RemoveFromBusiness"] = "RemoveFromBusiness";
    ActionEnum["CoWorkerAcceptedInvitation"] = "CoWorkerAcceptedInvitation";
    ActionEnum["AddCorporatePosition"] = "AddCorporatePosition";
    ActionEnum["EditCorporatePosition"] = "EditCorporatePosition";
    ActionEnum["RemoveCorporatePosition"] = "RemoveCorporatePosition";
    ActionEnum["AddCorporateDepartmentHead"] = "AddCorporateDepartmentHead";
    ActionEnum["RemoveCorporateDepartmentHead"] = "RemoveCorporateDepartmentHead";
    ActionEnum["RbacAllocation"] = "RbacAllocation";
    ActionEnum["AddSystemAdministrator"] = "AddSystemAdministrator";
    ActionEnum["RemoveSystemAdministrator"] = "RemoveSystemAdministrator";
    ActionEnum["EditInvitation"] = "EditInvitation";
    ActionEnum["AddLink"] = "AddLink";
    ActionEnum["EditLink"] = "EditLink";
    ActionEnum["DeleteLink"] = "DeleteLink";
    ActionEnum["AddCommunicationLanguage"] = "AddCommunicationLanguage";
    ActionEnum["DeleteUser"] = "DeleteUser";
    ActionEnum["AddAddons"] = "AddAddons";
    ActionEnum["CancelAddonSubscription"] = "CancelAddonSubscription";
    ActionEnum["AddTask"] = "AddTask";
    ActionEnum["ErpDeleted"] = "ErpDeleted";
    ActionEnum["AddUserProspectToBusinessProspect"] = "AddUserProspectToBusinessProspect";
    ActionEnum["DeleteUserProspectFromBusinessProspect"] = "DeleteUserProspectFromBusinessProspect";
    ActionEnum["AddSalesTeam"] = "AddSalesTeam";
    ActionEnum["RemoveSalesTeam"] = "RemoveSalesTeam";
    ActionEnum["AddSalesTeamHead"] = "AddSalesTeamHead";
    ActionEnum["RemoveSalesTeamHead"] = "RemoveSalesTeamHead";
    ActionEnum["CoreUserFollowBusiness"] = "CoreUserFollowBusiness";
    ActionEnum["CoreUserUnfollowBusiness"] = "CoreUserUnfollowBusiness";
    ActionEnum["CoreBusinessUnfollowBusiness"] = "CoreBusinessUnfollowBusiness";
    ActionEnum["CoreBusinessFollowBusiness"] = "CoreBusinessFollowBusiness";
    ActionEnum["AddSharedEmail"] = "AddSharedEmail";
    ActionEnum["DeleteSharedEmail"] = "DeleteSharedEmail";
    ActionEnum["AddCoWorkerToSharedEmail"] = "AddCoWorkerToSharedEmail";
    ActionEnum["RemoveCoWorkerFromSharedEmail"] = "RemoveCoWorkerFromSharedEmail";
    ActionEnum["EditUserProspectToBusinessProspect"] = "EditUserProspectToBusinessProspect";
    ActionEnum["AddDedicatedUserProspectToBusinessProspect"] = "AddDedicatedUserProspectToBusinessProspect";
    ActionEnum["EditDedicatedUserProspectToBusinessProspect"] = "EditDedicatedUserProspectToBusinessProspect";
    ActionEnum["DeleteDedicatedUserProspectFromBusinessProspect"] = "DeleteDedicatedUserProspectFromBusinessProspect";
    ActionEnum["AddDeal"] = "AddDeal";
    ActionEnum["EditDeal"] = "EditDeal";
    ActionEnum["DeleteDeal"] = "DeleteDeal";
    ActionEnum["AddOffer"] = "AddOffer";
    ActionEnum["EditOffer"] = "EditOffer";
    ActionEnum["DeleteOffer"] = "DeleteOffer";
    ActionEnum["AddLead"] = "AddLead";
    ActionEnum["EditLead"] = "EditLead";
    ActionEnum["DeleteLead"] = "DeleteLead";
    ActionEnum["AddBusinessProfile"] = "AddBusinessProfile";
    ActionEnum["EditBusinessProfile"] = "EditBusinessProfile";
    ActionEnum["DeleteBusinessProfile"] = "DeleteBusinessProfile";
    ActionEnum["AddPersonProfile"] = "AddPersonProfile";
    ActionEnum["EditPersonProfile"] = "EditPersonProfile";
    ActionEnum["DeletePersonProfile"] = "DeletePersonProfile";
    ActionEnum["AddTaskStep"] = "AddTaskStep";
    ActionEnum["EditTaskStep"] = "EditTaskStep";
    ActionEnum["DeleteTaskStep"] = "DeleteTaskStep";
    ActionEnum["AddTaskStatus"] = "AddTaskStatus";
    ActionEnum["EditTaskStatus"] = "EditTaskStatus";
    ActionEnum["DeleteTaskStatus"] = "DeleteTaskStatus";
    ActionEnum["AddTaskPipeline"] = "AddTaskPipeline";
    ActionEnum["EditTaskPipeline"] = "EditTaskPipeline";
    ActionEnum["DeleteTaskPipeline"] = "DeleteTaskPipeline";
    ActionEnum["AddTaskProjectWorkgroup"] = "AddTaskProjectWorkgroup";
    ActionEnum["EditTaskProjectWorkgroup"] = "EditTaskProjectWorkgroup";
    ActionEnum["DeleteTaskProjectWorkgroup"] = "DeleteTaskProjectWorkgroup";
    ActionEnum["AddTaskProjectWorkgroupMember"] = "AddTaskProjectWorkgroupMember";
    ActionEnum["EditTaskProjectWorkgroupMember"] = "EditTaskProjectWorkgroupMember";
    ActionEnum["DeleteTaskProjectWorkgroupMember"] = "DeleteTaskProjectWorkgroupMember";
    ActionEnum["AddTaskToProjectWorkgroup"] = "AddTaskToProjectWorkgroup";
    ActionEnum["RemoveTaskFromProjectWorkgroup"] = "RemoveTaskFromProjectWorkgroup";
    ActionEnum["EditTask"] = "EditTask";
    ActionEnum["DeleteTask"] = "DeleteTask";
    ActionEnum["AddBankTransfer"] = "AddBankTransfer";
    ActionEnum["EditBankTransfer"] = "EditBankTransfer";
    ActionEnum["DeleteBankTransfer"] = "DeleteBankTransfer";
    ActionEnum["OfferResponsiblePersonAllocated"] = "OfferResponsiblePersonAllocated";
    ActionEnum["LeadResponsiblePersonAllocated"] = "LeadResponsiblePersonAllocated";
    ActionEnum["DealResponsiblePersonAllocated"] = "DealResponsiblePersonAllocated";
    ActionEnum["AddTextNoteProspect"] = "AddTextNoteProspect";
    ActionEnum["EditTextNoteProspect"] = "EditTextNoteProspect";
    ActionEnum["DeleteTextNoteProspect"] = "DeleteTextNoteProspect";
    ActionEnum["AddAudioNoteProspect"] = "AddAudioNoteProspect";
    ActionEnum["EditAudioNoteProspect"] = "EditAudioNoteProspect";
    ActionEnum["DeleteAudioNoteProspect"] = "DeleteAudioNoteProspect";
    ActionEnum["AddVideoNoteProspect"] = "AddVideoNoteProspect";
    ActionEnum["EditVideoNoteProspect"] = "EditVideoNoteProspect";
    ActionEnum["DeleteVideoNoteProspect"] = "DeleteVideoNoteProspect";
    ActionEnum["AddMeetingProspect"] = "AddMeetingProspect";
    ActionEnum["EditMeetingProspect"] = "EditMeetingProspect";
    ActionEnum["DeleteMeetingProspect"] = "DeleteMeetingProspect";
    ActionEnum["AddTaskProspect"] = "AddTaskProspect";
    ActionEnum["EditTaskProspect"] = "EditTaskProspect";
    ActionEnum["DeleteTaskProspect"] = "DeleteTaskProspect";
    ActionEnum["AddIssueProspect"] = "AddIssueProspect";
    ActionEnum["EditIssueProspect"] = "EditIssueProspect";
    ActionEnum["DeleteIssueProspect"] = "DeleteIssueProspect";
    ActionEnum["AddCaseProspect"] = "AddCaseProspect";
    ActionEnum["EditCaseProspect"] = "EditCaseProspect";
    ActionEnum["DeleteCaseProspect"] = "DeleteCaseProspect";
    ActionEnum["AddFileUploadProspect"] = "AddFileUploadProspect";
    ActionEnum["EditFileUploadProspect"] = "EditFileUploadProspect";
    ActionEnum["DeleteFileUploadProspect"] = "DeleteFileUploadProspect";
    ActionEnum["AddEmailProspect"] = "AddEmailProspect";
    ActionEnum["EditEmailProspect"] = "EditEmailProspect";
    ActionEnum["DeleteEmailProspect"] = "DeleteEmailProspect";
    ActionEnum["AddOutsideCallProspect"] = "AddOutsideCallProspect";
    ActionEnum["EditOutsideCallProspect"] = "EditOutsideCallProspect";
    ActionEnum["DeleteOutsideCallProspect"] = "DeleteOutsideCallProspect";
    ActionEnum["AddSmsProspect"] = "AddSmsProspect";
    ActionEnum["EditSmsProspect"] = "EditSmsProspect";
    ActionEnum["DeleteSmsProspect"] = "DeleteSmsProspect";
    ActionEnum["AddTextNoteLead"] = "AddTextNoteLead";
    ActionEnum["EditTextNoteLead"] = "EditTextNoteLead";
    ActionEnum["DeleteTextNoteLead"] = "DeleteTextNoteLead";
    ActionEnum["AddAudioNoteLead"] = "AddAudioNoteLead";
    ActionEnum["EditAudioNoteLead"] = "EditAudioNoteLead";
    ActionEnum["DeleteAudioNoteLead"] = "DeleteAudioNoteLead";
    ActionEnum["AddVideoNoteLead"] = "AddVideoNoteLead";
    ActionEnum["EditVideoNoteLead"] = "EditVideoNoteLead";
    ActionEnum["DeleteVideoNoteLead"] = "DeleteVideoNoteLead";
    ActionEnum["AddMeetingLead"] = "AddMeetingLead";
    ActionEnum["EditMeetingLead"] = "EditMeetingLead";
    ActionEnum["DeleteMeetingLead"] = "DeleteMeetingLead";
    ActionEnum["AddTaskLead"] = "AddTaskLead";
    ActionEnum["EditTaskLead"] = "EditTaskLead";
    ActionEnum["DeleteTaskLead"] = "DeleteTaskLead";
    ActionEnum["AddIssueLead"] = "AddIssueLead";
    ActionEnum["EditIssueLead"] = "EditIssueLead";
    ActionEnum["DeleteIssueLead"] = "DeleteIssueLead";
    ActionEnum["AddCaseLead"] = "AddCaseLead";
    ActionEnum["EditCaseLead"] = "EditCaseLead";
    ActionEnum["DeleteCaseLead"] = "DeleteCaseLead";
    ActionEnum["AddFileUploadLead"] = "AddFileUploadLead";
    ActionEnum["EditFileUploadLead"] = "EditFileUploadLead";
    ActionEnum["DeleteFileUploadLead"] = "DeleteFileUploadLead";
    ActionEnum["AddEmailLead"] = "AddEmailLead";
    ActionEnum["EditEmailLead"] = "EditEmailLead";
    ActionEnum["DeleteEmailLead"] = "DeleteEmailLead";
    ActionEnum["AddOutsideCallLead"] = "AddOutsideCallLead";
    ActionEnum["EditOutsideCallLead"] = "EditOutsideCallLead";
    ActionEnum["DeleteOutsideCallLead"] = "DeleteOutsideCallLead";
    ActionEnum["AddSmsLead"] = "AddSmsLead";
    ActionEnum["EditSmsLead"] = "EditSmsLead";
    ActionEnum["DeleteSmsLead"] = "DeleteSmsLead";
    ActionEnum["AddTextNoteOffer"] = "AddTextNoteOffer";
    ActionEnum["EditTextNoteOffer"] = "EditTextNoteOffer";
    ActionEnum["DeleteTextNoteOffer"] = "DeleteTextNoteOffer";
    ActionEnum["AddAudioNoteOffer"] = "AddAudioNoteOffer";
    ActionEnum["EditAudioNoteOffer"] = "EditAudioNoteOffer";
    ActionEnum["DeleteAudioNoteOffer"] = "DeleteAudioNoteOffer";
    ActionEnum["AddVideoNoteOffer"] = "AddVideoNoteOffer";
    ActionEnum["EditVideoNoteOffer"] = "EditVideoNoteOffer";
    ActionEnum["DeleteVideoNoteOffer"] = "DeleteVideoNoteOffer";
    ActionEnum["AddMeetingOffer"] = "AddMeetingOffer";
    ActionEnum["EditMeetingOffer"] = "EditMeetingOffer";
    ActionEnum["DeleteMeetingOffer"] = "DeleteMeetingOffer";
    ActionEnum["AddTaskOffer"] = "AddTaskOffer";
    ActionEnum["EditTaskOffer"] = "EditTaskOffer";
    ActionEnum["DeleteTaskOffer"] = "DeleteTaskOffer";
    ActionEnum["AddIssueOffer"] = "AddIssueOffer";
    ActionEnum["EditIssueOffer"] = "EditIssueOffer";
    ActionEnum["DeleteIssueOffer"] = "DeleteIssueOffer";
    ActionEnum["AddCaseOffer"] = "AddCaseOffer";
    ActionEnum["EditCaseOffer"] = "EditCaseOffer";
    ActionEnum["DeleteCaseOffer"] = "DeleteCaseOffer";
    ActionEnum["AddFileUploadOffer"] = "AddFileUploadOffer";
    ActionEnum["EditFileUploadOffer"] = "EditFileUploadOffer";
    ActionEnum["DeleteFileUploadOffer"] = "DeleteFileUploadOffer";
    ActionEnum["AddEmailOffer"] = "AddEmailOffer";
    ActionEnum["EditEmailOffer"] = "EditEmailOffer";
    ActionEnum["DeleteEmailOffer"] = "DeleteEmailOffer";
    ActionEnum["AddOutsideCallOffer"] = "AddOutsideCallOffer";
    ActionEnum["EditOutsideCallOffer"] = "EditOutsideCallOffer";
    ActionEnum["DeleteOutsideCallOffer"] = "DeleteOutsideCallOffer";
    ActionEnum["AddSmsOffer"] = "AddSmsOffer";
    ActionEnum["EditSmsOffer"] = "EditSmsOffer";
    ActionEnum["DeleteSmsOffer"] = "DeleteSmsOffer";
    ActionEnum["AddTextNoteDeal"] = "AddTextNoteDeal";
    ActionEnum["EditTextNoteDeal"] = "EditTextNoteDeal";
    ActionEnum["DeleteTextNoteDeal"] = "DeleteTextNoteDeal";
    ActionEnum["AddAudioNoteDeal"] = "AddAudioNoteDeal";
    ActionEnum["EditAudioNoteDeal"] = "EditAudioNoteDeal";
    ActionEnum["DeleteAudioNoteDeal"] = "DeleteAudioNoteDeal";
    ActionEnum["AddVideoNoteDeal"] = "AddVideoNoteDeal";
    ActionEnum["EditVideoNoteDeal"] = "EditVideoNoteDeal";
    ActionEnum["DeleteVideoNoteDeal"] = "DeleteVideoNoteDeal";
    ActionEnum["AddMeetingDeal"] = "AddMeetingDeal";
    ActionEnum["EditMeetingDeal"] = "EditMeetingDeal";
    ActionEnum["DeleteMeetingDeal"] = "DeleteMeetingDeal";
    ActionEnum["AddTaskDeal"] = "AddTaskDeal";
    ActionEnum["EditTaskDeal"] = "EditTaskDeal";
    ActionEnum["DeleteTaskDeal"] = "DeleteTaskDeal";
    ActionEnum["AddIssueDeal"] = "AddIssueDeal";
    ActionEnum["EditIssueDeal"] = "EditIssueDeal";
    ActionEnum["DeleteIssueDeal"] = "DeleteIssueDeal";
    ActionEnum["AddCaseDeal"] = "AddCaseDeal";
    ActionEnum["EditCaseDeal"] = "EditCaseDeal";
    ActionEnum["DeleteCaseDeal"] = "DeleteCaseDeal";
    ActionEnum["AddFileUploadDeal"] = "AddFileUploadDeal";
    ActionEnum["EditFileUploadDeal"] = "EditFileUploadDeal";
    ActionEnum["DeleteFileUploadDeal"] = "DeleteFileUploadDeal";
    ActionEnum["AddEmailDeal"] = "AddEmailDeal";
    ActionEnum["EditEmailDeal"] = "EditEmailDeal";
    ActionEnum["DeleteEmailDeal"] = "DeleteEmailDeal";
    ActionEnum["AddOutsideCallDeal"] = "AddOutsideCallDeal";
    ActionEnum["EditOutsideCallDeal"] = "EditOutsideCallDeal";
    ActionEnum["DeleteOutsideCallDeal"] = "DeleteOutsideCallDeal";
    ActionEnum["AddSmsDeal"] = "AddSmsDeal";
    ActionEnum["EditSmsDeal"] = "EditSmsDeal";
    ActionEnum["DeleteSmsDeal"] = "DeleteSmsDeal";
    ActionEnum["AddTextNoteCoreDeal"] = "AddTextNoteCoreDeal";
    ActionEnum["EditTextNoteCoreDeal"] = "EditTextNoteCoreDeal";
    ActionEnum["DeleteTextNoteCoreDeal"] = "DeleteTextNoteCoreDeal";
    ActionEnum["AddAudioNoteCoreDeal"] = "AddAudioNoteCoreDeal";
    ActionEnum["EditAudioNoteCoreDeal"] = "EditAudioNoteCoreDeal";
    ActionEnum["DeleteAudioNoteCoreDeal"] = "DeleteAudioNoteCoreDeal";
    ActionEnum["AddVideoNoteCoreDeal"] = "AddVideoNoteCoreDeal";
    ActionEnum["EditVideoNoteCoreDeal"] = "EditVideoNoteCoreDeal";
    ActionEnum["DeleteVideoNoteCoreDeal"] = "DeleteVideoNoteCoreDeal";
    ActionEnum["AddMeetingCoreDeal"] = "AddMeetingCoreDeal";
    ActionEnum["EditMeetingCoreDeal"] = "EditMeetingCoreDeal";
    ActionEnum["DeleteMeetingCoreDeal"] = "DeleteMeetingCoreDeal";
    ActionEnum["AddTaskCoreDeal"] = "AddTaskCoreDeal";
    ActionEnum["EditTaskCoreDeal"] = "EditTaskCoreDeal";
    ActionEnum["DeleteTaskCoreDeal"] = "DeleteTaskCoreDeal";
    ActionEnum["AddIssueCoreDeal"] = "AddIssueCoreDeal";
    ActionEnum["EditIssueCoreDeal"] = "EditIssueCoreDeal";
    ActionEnum["DeleteIssueCoreDeal"] = "DeleteIssueCoreDeal";
    ActionEnum["AddCaseCoreDeal"] = "AddCaseCoreDeal";
    ActionEnum["EditCaseCoreDeal"] = "EditCaseCoreDeal";
    ActionEnum["DeleteCaseCoreDeal"] = "DeleteCaseCoreDeal";
    ActionEnum["AddFileUploadCoreDeal"] = "AddFileUploadCoreDeal";
    ActionEnum["EditFileUploadCoreDeal"] = "EditFileUploadCoreDeal";
    ActionEnum["DeleteFileUploadCoreDeal"] = "DeleteFileUploadCoreDeal";
    ActionEnum["AddEmailCoreDeal"] = "AddEmailCoreDeal";
    ActionEnum["EditEmailCoreDeal"] = "EditEmailCoreDeal";
    ActionEnum["DeleteEmailCoreDeal"] = "DeleteEmailCoreDeal";
    ActionEnum["AddOutsideCallCoreDeal"] = "AddOutsideCallCoreDeal";
    ActionEnum["EditOutsideCallCoreDeal"] = "EditOutsideCallCoreDeal";
    ActionEnum["DeleteOutsideCallCoreDeal"] = "DeleteOutsideCallCoreDeal";
    ActionEnum["AddSmsCoreDeal"] = "AddSmsCoreDeal";
    ActionEnum["EditSmsCoreDeal"] = "EditSmsCoreDeal";
    ActionEnum["DeleteSmsCoreDeal"] = "DeleteSmsCoreDeal";
    ActionEnum["AddTextNoteInventory"] = "AddTextNoteInventory";
    ActionEnum["EditTextNoteInventory"] = "EditTextNoteInventory";
    ActionEnum["DeleteTextNoteInventory"] = "DeleteTextNoteInventory";
    ActionEnum["AddAudioNoteInventory"] = "AddAudioNoteInventory";
    ActionEnum["EditAudioNoteInventory"] = "EditAudioNoteInventory";
    ActionEnum["DeleteAudioNoteInventory"] = "DeleteAudioNoteInventory";
    ActionEnum["AddVideoNoteInventory"] = "AddVideoNoteInventory";
    ActionEnum["EditVideoNoteInventory"] = "EditVideoNoteInventory";
    ActionEnum["DeleteVideoNoteInventory"] = "DeleteVideoNoteInventory";
    ActionEnum["AddMeetingInventory"] = "AddMeetingInventory";
    ActionEnum["EditMeetingInventory"] = "EditMeetingInventory";
    ActionEnum["DeleteMeetingInventory"] = "DeleteMeetingInventory";
    ActionEnum["AddTaskInventory"] = "AddTaskInventory";
    ActionEnum["EditTaskInventory"] = "EditTaskInventory";
    ActionEnum["DeleteTaskInventory"] = "DeleteTaskInventory";
    ActionEnum["AddIssueInventory"] = "AddIssueInventory";
    ActionEnum["EditIssueInventory"] = "EditIssueInventory";
    ActionEnum["DeleteIssueInventory"] = "DeleteIssueInventory";
    ActionEnum["AddCaseInventory"] = "AddCaseInventory";
    ActionEnum["EditCaseInventory"] = "EditCaseInventory";
    ActionEnum["DeleteCaseInventory"] = "DeleteCaseInventory";
    ActionEnum["AddFileUploadInventory"] = "AddFileUploadInventory";
    ActionEnum["EditFileUploadInventory"] = "EditFileUploadInventory";
    ActionEnum["DeleteFileUploadInventory"] = "DeleteFileUploadInventory";
    ActionEnum["AddEmailInventory"] = "AddEmailInventory";
    ActionEnum["EditEmailInventory"] = "EditEmailInventory";
    ActionEnum["DeleteEmailInventory"] = "DeleteEmailInventory";
    ActionEnum["AddOutsideCallInventory"] = "AddOutsideCallInventory";
    ActionEnum["EditOutsideCallInventory"] = "EditOutsideCallInventory";
    ActionEnum["DeleteOutsideCallInventory"] = "DeleteOutsideCallInventory";
    ActionEnum["AddSmsInventory"] = "AddSmsInventory";
    ActionEnum["EditSmsInventory"] = "EditSmsInventory";
    ActionEnum["DeleteSmsInventory"] = "DeleteSmsInventory";
    ActionEnum["AddTextNoteAssetManager"] = "AddTextNoteAssetManager";
    ActionEnum["EditTextNoteAssetManager"] = "EditTextNoteAssetManager";
    ActionEnum["DeleteTextNoteAssetManager"] = "DeleteTextNoteAssetManager";
    ActionEnum["AddAudioNoteAssetManager"] = "AddAudioNoteAssetManager";
    ActionEnum["EditAudioNoteAssetManager"] = "EditAudioNoteAssetManager";
    ActionEnum["DeleteAudioNoteAssetManager"] = "DeleteAudioNoteAssetManager";
    ActionEnum["AddVideoNoteAssetManager"] = "AddVideoNoteAssetManager";
    ActionEnum["EditVideoNoteAssetManager"] = "EditVideoNoteAssetManager";
    ActionEnum["DeleteVideoNoteAssetManager"] = "DeleteVideoNoteAssetManager";
    ActionEnum["AddMeetingAssetManager"] = "AddMeetingAssetManager";
    ActionEnum["EditMeetingAssetManager"] = "EditMeetingAssetManager";
    ActionEnum["DeleteMeetingAssetManager"] = "DeleteMeetingAssetManager";
    ActionEnum["AddTaskAssetManager"] = "AddTaskAssetManager";
    ActionEnum["EditTaskAssetManager"] = "EditTaskAssetManager";
    ActionEnum["DeleteTaskAssetManager"] = "DeleteTaskAssetManager";
    ActionEnum["AddIssueAssetManager"] = "AddIssueAssetManager";
    ActionEnum["EditIssueAssetManager"] = "EditIssueAssetManager";
    ActionEnum["DeleteIssueAssetManager"] = "DeleteIssueAssetManager";
    ActionEnum["AddCaseAssetManager"] = "AddCaseAssetManager";
    ActionEnum["EditCaseAssetManager"] = "EditCaseAssetManager";
    ActionEnum["DeleteCaseAssetManager"] = "DeleteCaseAssetManager";
    ActionEnum["AddFileUploadAssetManager"] = "AddFileUploadAssetManager";
    ActionEnum["EditFileUploadAssetManager"] = "EditFileUploadAssetManager";
    ActionEnum["DeleteFileUploadAssetManager"] = "DeleteFileUploadAssetManager";
    ActionEnum["AddEmailAssetManager"] = "AddEmailAssetManager";
    ActionEnum["EditEmailAssetManager"] = "EditEmailAssetManager";
    ActionEnum["DeleteEmailAssetManager"] = "DeleteEmailAssetManager";
    ActionEnum["AddOutsideCallAssetManager"] = "AddOutsideCallAssetManager";
    ActionEnum["EditOutsideCallAssetManager"] = "EditOutsideCallAssetManager";
    ActionEnum["DeleteOutsideCallAssetManager"] = "DeleteOutsideCallAssetManager";
    ActionEnum["AddSmsAssetManager"] = "AddSmsAssetManager";
    ActionEnum["EditSmsAssetManager"] = "EditSmsAssetManager";
    ActionEnum["DeleteSmsAssetManager"] = "DeleteSmsAssetManager";
    ActionEnum["AddTextNoteCatalog"] = "AddTextNoteCatalog";
    ActionEnum["EditTextNoteCatalog"] = "EditTextNoteCatalog";
    ActionEnum["DeleteTextNoteCatalog"] = "DeleteTextNoteCatalog";
    ActionEnum["AddAudioNoteCatalog"] = "AddAudioNoteCatalog";
    ActionEnum["EditAudioNoteCatalog"] = "EditAudioNoteCatalog";
    ActionEnum["DeleteAudioNoteCatalog"] = "DeleteAudioNoteCatalog";
    ActionEnum["AddVideoNoteCatalog"] = "AddVideoNoteCatalog";
    ActionEnum["EditVideoNoteCatalog"] = "EditVideoNoteCatalog";
    ActionEnum["DeleteVideoNoteCatalog"] = "DeleteVideoNoteCatalog";
    ActionEnum["AddMeetingCatalog"] = "AddMeetingCatalog";
    ActionEnum["EditMeetingCatalog"] = "EditMeetingCatalog";
    ActionEnum["DeleteMeetingCatalog"] = "DeleteMeetingCatalog";
    ActionEnum["AddTaskCatalog"] = "AddTaskCatalog";
    ActionEnum["EditTaskCatalog"] = "EditTaskCatalog";
    ActionEnum["DeleteTaskCatalog"] = "DeleteTaskCatalog";
    ActionEnum["AddIssueCatalog"] = "AddIssueCatalog";
    ActionEnum["EditIssueCatalog"] = "EditIssueCatalog";
    ActionEnum["DeleteIssueCatalog"] = "DeleteIssueCatalog";
    ActionEnum["AddCaseCatalog"] = "AddCaseCatalog";
    ActionEnum["EditCaseCatalog"] = "EditCaseCatalog";
    ActionEnum["DeleteCaseCatalog"] = "DeleteCaseCatalog";
    ActionEnum["AddFileUploadCatalog"] = "AddFileUploadCatalog";
    ActionEnum["EditFileUploadCatalog"] = "EditFileUploadCatalog";
    ActionEnum["DeleteFileUploadCatalog"] = "DeleteFileUploadCatalog";
    ActionEnum["AddEmailCatalog"] = "AddEmailCatalog";
    ActionEnum["EditEmailCatalog"] = "EditEmailCatalog";
    ActionEnum["DeleteEmailCatalog"] = "DeleteEmailCatalog";
    ActionEnum["AddOutsideCallCatalog"] = "AddOutsideCallCatalog";
    ActionEnum["EditOutsideCallCatalog"] = "EditOutsideCallCatalog";
    ActionEnum["DeleteOutsideCallCatalog"] = "DeleteOutsideCallCatalog";
    ActionEnum["AddSmsCatalog"] = "AddSmsCatalog";
    ActionEnum["EditSmsCatalog"] = "EditSmsCatalog";
    ActionEnum["DeleteSmsCatalog"] = "DeleteSmsCatalog";
    ActionEnum["AddTextNoteCoreAssetManager"] = "AddTextNoteCoreAssetManager";
    ActionEnum["EditTextNoteCoreAssetManager"] = "EditTextNoteCoreAssetManager";
    ActionEnum["DeleteTextNoteCoreAssetManager"] = "DeleteTextNoteCoreAssetManager";
    ActionEnum["AddAudioNoteCoreAssetManager"] = "AddAudioNoteCoreAssetManager";
    ActionEnum["EditAudioNoteCoreAssetManager"] = "EditAudioNoteCoreAssetManager";
    ActionEnum["DeleteAudioNoteCoreAssetManager"] = "DeleteAudioNoteCoreAssetManager";
    ActionEnum["AddVideoNoteCoreAssetManager"] = "AddVideoNoteCoreAssetManager";
    ActionEnum["EditVideoNoteCoreAssetManager"] = "EditVideoNoteCoreAssetManager";
    ActionEnum["DeleteVideoNoteCoreAssetManager"] = "DeleteVideoNoteCoreAssetManager";
    ActionEnum["AddMeetingCoreAssetManager"] = "AddMeetingCoreAssetManager";
    ActionEnum["EditMeetingCoreAssetManager"] = "EditMeetingCoreAssetManager";
    ActionEnum["DeleteMeetingCoreAssetManager"] = "DeleteMeetingCoreAssetManager";
    ActionEnum["AddTaskCoreAssetManager"] = "AddTaskCoreAssetManager";
    ActionEnum["EditTaskCoreAssetManager"] = "EditTaskCoreAssetManager";
    ActionEnum["DeleteTaskCoreAssetManager"] = "DeleteTaskCoreAssetManager";
    ActionEnum["AddIssueCoreAssetManager"] = "AddIssueCoreAssetManager";
    ActionEnum["EditIssueCoreAssetManager"] = "EditIssueCoreAssetManager";
    ActionEnum["DeleteIssueCoreAssetManager"] = "DeleteIssueCoreAssetManager";
    ActionEnum["AddCaseCoreAssetManager"] = "AddCaseCoreAssetManager";
    ActionEnum["EditCaseCoreAssetManager"] = "EditCaseCoreAssetManager";
    ActionEnum["DeleteCaseCoreAssetManager"] = "DeleteCaseCoreAssetManager";
    ActionEnum["AddFileUploadCoreAssetManager"] = "AddFileUploadCoreAssetManager";
    ActionEnum["EditFileUploadCoreAssetManager"] = "EditFileUploadCoreAssetManager";
    ActionEnum["DeleteFileUploadCoreAssetManager"] = "DeleteFileUploadCoreAssetManager";
    ActionEnum["AddEmailCoreAssetManager"] = "AddEmailCoreAssetManager";
    ActionEnum["EditEmailCoreAssetManager"] = "EditEmailCoreAssetManager";
    ActionEnum["DeleteEmailCoreAssetManager"] = "DeleteEmailCoreAssetManager";
    ActionEnum["AddOutsideCallCoreAssetManager"] = "AddOutsideCallCoreAssetManager";
    ActionEnum["EditOutsideCallCoreAssetManager"] = "EditOutsideCallCoreAssetManager";
    ActionEnum["DeleteOutsideCallCoreAssetManager"] = "DeleteOutsideCallCoreAssetManager";
    ActionEnum["AddSmsCoreAssetManager"] = "AddSmsCoreAssetManager";
    ActionEnum["EditSmsCoreAssetManager"] = "EditSmsCoreAssetManager";
    ActionEnum["DeleteSmsCoreAssetManager"] = "DeleteSmsCoreAssetManager";
    ActionEnum["AddTextNoteCoworker"] = "AddTextNoteCoworker";
    ActionEnum["EditTextNoteCoworker"] = "EditTextNoteCoworker";
    ActionEnum["DeleteTextNoteCoworker"] = "DeleteTextNoteCoworker";
    ActionEnum["AddAudioNoteCoworker"] = "AddAudioNoteCoworker";
    ActionEnum["EditAudioNoteCoworker"] = "EditAudioNoteCoworker";
    ActionEnum["DeleteAudioNoteCoworker"] = "DeleteAudioNoteCoworker";
    ActionEnum["AddVideoNoteCoworker"] = "AddVideoNoteCoworker";
    ActionEnum["EditVideoNoteCoworker"] = "EditVideoNoteCoworker";
    ActionEnum["DeleteVideoNoteCoworker"] = "DeleteVideoNoteCoworker";
    ActionEnum["AddMeetingCoworker"] = "AddMeetingCoworker";
    ActionEnum["EditMeetingCoworker"] = "EditMeetingCoworker";
    ActionEnum["DeleteMeetingCoworker"] = "DeleteMeetingCoworker";
    ActionEnum["AddTaskCoworker"] = "AddTaskCoworker";
    ActionEnum["EditTaskCoworker"] = "EditTaskCoworker";
    ActionEnum["DeleteTaskCoworker"] = "DeleteTaskCoworker";
    ActionEnum["AddIssueCoworker"] = "AddIssueCoworker";
    ActionEnum["EditIssueCoworker"] = "EditIssueCoworker";
    ActionEnum["DeleteIssueCoworker"] = "DeleteIssueCoworker";
    ActionEnum["AddCaseCoworker"] = "AddCaseCoworker";
    ActionEnum["EditCaseCoworker"] = "EditCaseCoworker";
    ActionEnum["DeleteCaseCoworker"] = "DeleteCaseCoworker";
    ActionEnum["AddFileUploadCoworker"] = "AddFileUploadCoworker";
    ActionEnum["EditFileUploadCoworker"] = "EditFileUploadCoworker";
    ActionEnum["DeleteFileUploadCoworker"] = "DeleteFileUploadCoworker";
    ActionEnum["AddEmailCoworker"] = "AddEmailCoworker";
    ActionEnum["EditEmailCoworker"] = "EditEmailCoworker";
    ActionEnum["DeleteEmailCoworker"] = "DeleteEmailCoworker";
    ActionEnum["AddOutsideCallCoworker"] = "AddOutsideCallCoworker";
    ActionEnum["EditOutsideCallCoworker"] = "EditOutsideCallCoworker";
    ActionEnum["DeleteOutsideCallCoworker"] = "DeleteOutsideCallCoworker";
    ActionEnum["AddSmsCoworker"] = "AddSmsCoworker";
    ActionEnum["EditSmsCoworker"] = "EditSmsCoworker";
    ActionEnum["DeleteSmsCoworker"] = "DeleteSmsCoworker";
    ActionEnum["AddTextNoteTask"] = "AddTextNoteTask";
    ActionEnum["EditTextNoteTask"] = "EditTextNoteTask";
    ActionEnum["DeleteTextNoteTask"] = "DeleteTextNoteTask";
    ActionEnum["AddAudioNoteTask"] = "AddAudioNoteTask";
    ActionEnum["EditAudioNoteTask"] = "EditAudioNoteTask";
    ActionEnum["DeleteAudioNoteTask"] = "DeleteAudioNoteTask";
    ActionEnum["AddVideoNoteTask"] = "AddVideoNoteTask";
    ActionEnum["EditVideoNoteTask"] = "EditVideoNoteTask";
    ActionEnum["DeleteVideoNoteTask"] = "DeleteVideoNoteTask";
    ActionEnum["AddMeetingTask"] = "AddMeetingTask";
    ActionEnum["EditMeetingTask"] = "EditMeetingTask";
    ActionEnum["DeleteMeetingTask"] = "DeleteMeetingTask";
    ActionEnum["AddTaskTask"] = "AddTaskTask";
    ActionEnum["EditTaskTask"] = "EditTaskTask";
    ActionEnum["DeleteTaskTask"] = "DeleteTaskTask";
    ActionEnum["AddIssueTask"] = "AddIssueTask";
    ActionEnum["EditIssueTask"] = "EditIssueTask";
    ActionEnum["DeleteIssueTask"] = "DeleteIssueTask";
    ActionEnum["AddCaseTask"] = "AddCaseTask";
    ActionEnum["EditCaseTask"] = "EditCaseTask";
    ActionEnum["DeleteCaseTask"] = "DeleteCaseTask";
    ActionEnum["AddFileUploadTask"] = "AddFileUploadTask";
    ActionEnum["EditFileUploadTask"] = "EditFileUploadTask";
    ActionEnum["DeleteFileUploadTask"] = "DeleteFileUploadTask";
    ActionEnum["AddEmailTask"] = "AddEmailTask";
    ActionEnum["EditEmailTask"] = "EditEmailTask";
    ActionEnum["DeleteEmailTask"] = "DeleteEmailTask";
    ActionEnum["AddOutsideCallTask"] = "AddOutsideCallTask";
    ActionEnum["EditOutsideCallTask"] = "EditOutsideCallTask";
    ActionEnum["DeleteOutsideCallTask"] = "DeleteOutsideCallTask";
    ActionEnum["AddSmsTask"] = "AddSmsTask";
    ActionEnum["EditSmsTask"] = "EditSmsTask";
    ActionEnum["DeleteSmsTask"] = "DeleteSmsTask";
    ActionEnum["AddTextNoteProjectWorkgroup"] = "AddTextNoteProjectWorkgroup";
    ActionEnum["EditTextNoteProjectWorkgroup"] = "EditTextNoteProjectWorkgroup";
    ActionEnum["DeleteTextNoteProjectWorkgroup"] = "DeleteTextNoteProjectWorkgroup";
    ActionEnum["AddAudioNoteProjectWorkgroup"] = "AddAudioNoteProjectWorkgroup";
    ActionEnum["EditAudioNoteProjectWorkgroup"] = "EditAudioNoteProjectWorkgroup";
    ActionEnum["DeleteAudioNoteProjectWorkgroup"] = "DeleteAudioNoteProjectWorkgroup";
    ActionEnum["AddVideoNoteProjectWorkgroup"] = "AddVideoNoteProjectWorkgroup";
    ActionEnum["EditVideoNoteProjectWorkgroup"] = "EditVideoNoteProjectWorkgroup";
    ActionEnum["DeleteVideoNoteProjectWorkgroup"] = "DeleteVideoNoteProjectWorkgroup";
    ActionEnum["AddMeetingProjectWorkgroup"] = "AddMeetingProjectWorkgroup";
    ActionEnum["EditMeetingProjectWorkgroup"] = "EditMeetingProjectWorkgroup";
    ActionEnum["DeleteMeetingProjectWorkgroup"] = "DeleteMeetingProjectWorkgroup";
    ActionEnum["AddIssueProjectWorkgroup"] = "AddIssueProjectWorkgroup";
    ActionEnum["EditIssueProjectWorkgroup"] = "EditIssueProjectWorkgroup";
    ActionEnum["DeleteIssueProjectWorkgroup"] = "DeleteIssueProjectWorkgroup";
    ActionEnum["AddCaseProjectWorkgroup"] = "AddCaseProjectWorkgroup";
    ActionEnum["EditCaseProjectWorkgroup"] = "EditCaseProjectWorkgroup";
    ActionEnum["DeleteCaseProjectWorkgroup"] = "DeleteCaseProjectWorkgroup";
    ActionEnum["AddFileUploadProjectWorkgroup"] = "AddFileUploadProjectWorkgroup";
    ActionEnum["EditFileUploadProjectWorkgroup"] = "EditFileUploadProjectWorkgroup";
    ActionEnum["DeleteFileUploadProjectWorkgroup"] = "DeleteFileUploadProjectWorkgroup";
    ActionEnum["AddEmailProjectWorkgroup"] = "AddEmailProjectWorkgroup";
    ActionEnum["EditEmailProjectWorkgroup"] = "EditEmailProjectWorkgroup";
    ActionEnum["DeleteEmailProjectWorkgroup"] = "DeleteEmailProjectWorkgroup";
    ActionEnum["AddOutsideCallProjectWorkgroup"] = "AddOutsideCallProjectWorkgroup";
    ActionEnum["EditOutsideCallProjectWorkgroup"] = "EditOutsideCallProjectWorkgroup";
    ActionEnum["DeleteOutsideCallProjectWorkgroup"] = "DeleteOutsideCallProjectWorkgroup";
    ActionEnum["AddSmsProjectWorkgroup"] = "AddSmsProjectWorkgroup";
    ActionEnum["EditSmsProjectWorkgroup"] = "EditSmsProjectWorkgroup";
    ActionEnum["DeleteSmsProjectWorkgroup"] = "DeleteSmsProjectWorkgroup";
    ActionEnum["AddTextNoteEmployee"] = "AddTextNoteEmployee";
    ActionEnum["EditTextNoteEmployee"] = "EditTextNoteEmployee";
    ActionEnum["DeleteTextNoteEmployee"] = "DeleteTextNoteEmployee";
    ActionEnum["AddAudioNoteEmployee"] = "AddAudioNoteEmployee";
    ActionEnum["EditAudioNoteEmployee"] = "EditAudioNoteEmployee";
    ActionEnum["DeleteAudioNoteEmployee"] = "DeleteAudioNoteEmployee";
    ActionEnum["AddVideoNoteEmployee"] = "AddVideoNoteEmployee";
    ActionEnum["EditVideoNoteEmployee"] = "EditVideoNoteEmployee";
    ActionEnum["DeleteVideoNoteEmployee"] = "DeleteVideoNoteEmployee";
    ActionEnum["AddMeetingEmployee"] = "AddMeetingEmployee";
    ActionEnum["EditMeetingEmployee"] = "EditMeetingEmployee";
    ActionEnum["DeleteMeetingEmployee"] = "DeleteMeetingEmployee";
    ActionEnum["AddTaskEmployee"] = "AddTaskEmployee";
    ActionEnum["EditTaskEmployee"] = "EditTaskEmployee";
    ActionEnum["DeleteTaskEmployee"] = "DeleteTaskEmployee";
    ActionEnum["AddIssueEmployee"] = "AddIssueEmployee";
    ActionEnum["EditIssueEmployee"] = "EditIssueEmployee";
    ActionEnum["DeleteIssueEmployee"] = "DeleteIssueEmployee";
    ActionEnum["AddCaseEmployee"] = "AddCaseEmployee";
    ActionEnum["EditCaseEmployee"] = "EditCaseEmployee";
    ActionEnum["DeleteCaseEmployee"] = "DeleteCaseEmployee";
    ActionEnum["AddFileUploadEmployee"] = "AddFileUploadEmployee";
    ActionEnum["EditFileUploadEmployee"] = "EditFileUploadEmployee";
    ActionEnum["DeleteFileUploadEmployee"] = "DeleteFileUploadEmployee";
    ActionEnum["AddEmailEmployee"] = "AddEmailEmployee";
    ActionEnum["EditEmailEmployee"] = "EditEmailEmployee";
    ActionEnum["DeleteEmailEmployee"] = "DeleteEmailEmployee";
    ActionEnum["AddOutsideCallEmployee"] = "AddOutsideCallEmployee";
    ActionEnum["EditOutsideCallEmployee"] = "EditOutsideCallEmployee";
    ActionEnum["DeleteOutsideCallEmployee"] = "DeleteOutsideCallEmployee";
    ActionEnum["AddSmsEmployee"] = "AddSmsEmployee";
    ActionEnum["EditSmsEmployee"] = "EditSmsEmployee";
    ActionEnum["DeleteSmsEmployee"] = "DeleteSmsEmployee";
    ActionEnum["AddTextNoteUser"] = "AddTextNoteUser";
    ActionEnum["EditTextNoteUser"] = "EditTextNoteUser";
    ActionEnum["DeleteTextNoteUser"] = "DeleteTextNoteUser";
    ActionEnum["AddAudioNoteUser"] = "AddAudioNoteUser";
    ActionEnum["EditAudioNoteUser"] = "EditAudioNoteUser";
    ActionEnum["DeleteAudioNoteUser"] = "DeleteAudioNoteUser";
    ActionEnum["AddVideoNoteUser"] = "AddVideoNoteUser";
    ActionEnum["EditVideoNoteUser"] = "EditVideoNoteUser";
    ActionEnum["DeleteVideoNoteUser"] = "DeleteVideoNoteUser";
    ActionEnum["AddMeetingUser"] = "AddMeetingUser";
    ActionEnum["EditMeetingUser"] = "EditMeetingUser";
    ActionEnum["DeleteMeetingUser"] = "DeleteMeetingUser";
    ActionEnum["AddTaskUser"] = "AddTaskUser";
    ActionEnum["EditTaskUser"] = "EditTaskUser";
    ActionEnum["DeleteTaskUser"] = "DeleteTaskUser";
    ActionEnum["AddIssueUser"] = "AddIssueUser";
    ActionEnum["EditIssueUser"] = "EditIssueUser";
    ActionEnum["DeleteIssueUser"] = "DeleteIssueUser";
    ActionEnum["AddCaseUser"] = "AddCaseUser";
    ActionEnum["EditCaseUser"] = "EditCaseUser";
    ActionEnum["DeleteCaseUser"] = "DeleteCaseUser";
    ActionEnum["AddFileUploadUser"] = "AddFileUploadUser";
    ActionEnum["EditFileUploadUser"] = "EditFileUploadUser";
    ActionEnum["DeleteFileUploadUser"] = "DeleteFileUploadUser";
    ActionEnum["AddEmailUser"] = "AddEmailUser";
    ActionEnum["EditEmailUser"] = "EditEmailUser";
    ActionEnum["DeleteEmailUser"] = "DeleteEmailUser";
    ActionEnum["AddOutsideCallUser"] = "AddOutsideCallUser";
    ActionEnum["EditOutsideCallUser"] = "EditOutsideCallUser";
    ActionEnum["DeleteOutsideCallUser"] = "DeleteOutsideCallUser";
    ActionEnum["AddSmsUser"] = "AddSmsUser";
    ActionEnum["EditSmsUser"] = "EditSmsUser";
    ActionEnum["DeleteSmsUser"] = "DeleteSmsUser";
    ActionEnum["AddPipelineStep"] = "AddPipelineStep";
    ActionEnum["EditPipelineStep"] = "EditPipelineStep";
    ActionEnum["DeletePipelineStep"] = "DeletePipelineStep";
    ActionEnum["AddPipelineStatus"] = "AddPipelineStatus";
    ActionEnum["EditPipelineStatus"] = "EditPipelineStatus";
    ActionEnum["DeletePipelineStatus"] = "DeletePipelineStatus";
    ActionEnum["DeleteResponsiblePerson"] = "DeleteResponsiblePerson";
    ActionEnum["DeleteOfferResponsiblePerson"] = "DeleteOfferResponsiblePerson";
    ActionEnum["DeleteLeadResponsiblePerson"] = "DeleteLeadResponsiblePerson";
    ActionEnum["DeleteDealResponsiblePerson"] = "DeleteDealResponsiblePerson";
    ActionEnum["DeleteBusiness"] = "DeleteBusiness";
    ActionEnum["AddToCart"] = "AddToCart";
    ActionEnum["RemoveFromCart"] = "RemoveFromCart";
    ActionEnum["ExpiredInCart"] = "ExpiredInCart";
    ActionEnum["ExpiredBid"] = "ExpiredBid";
    ActionEnum["AuctionWon"] = "AuctionWon";
    ActionEnum["AuctionDeal"] = "AuctionDeal";
  })(ActionEnum = History.ActionEnum || (History.ActionEnum = {}));
  let UsageTypeEnum;
  (function (UsageTypeEnum) {
    UsageTypeEnum["Report"] = "Report";
    UsageTypeEnum["CrmContact"] = "CrmContact";
    UsageTypeEnum["CrmEmail"] = "CrmEmail";
    UsageTypeEnum["Empty"] = "Empty";
    UsageTypeEnum["SystemInvoice"] = "System Invoice";
    UsageTypeEnum["SubscriptionCancellation"] = "Subscription cancellation";
    UsageTypeEnum["CrmPhone"] = "CrmPhone";
    UsageTypeEnum["CrmAddress"] = "CrmAddress";
    UsageTypeEnum["CrmNotify"] = "CrmNotify";
    UsageTypeEnum["Keyword"] = "Keyword";
    UsageTypeEnum["Business"] = "Business";
    UsageTypeEnum["User"] = "User";
    UsageTypeEnum["CoWorker"] = "CoWorker";
    UsageTypeEnum["CrmLink"] = "CrmLink";
    UsageTypeEnum["Task"] = "Task";
    UsageTypeEnum["BusinessProspectUser"] = "BusinessProspectUser";
    UsageTypeEnum["Interaction"] = "Interaction";
    UsageTypeEnum["CrmOffer"] = "CrmOffer";
    UsageTypeEnum["CrmLead"] = "CrmLead";
    UsageTypeEnum["CrmDeal"] = "CrmDeal";
    UsageTypeEnum["HrmsInvitation"] = "HrmsInvitation";
    UsageTypeEnum["HrmsCoWorkerJob"] = "HrmsCoWorkerJob";
    UsageTypeEnum["HrmsTask"] = "HrmsTask";
    UsageTypeEnum["HrmsSalesTeam"] = "HrmsSalesTeam";
    UsageTypeEnum["HrmsSharedEmail"] = "HrmsSharedEmail";
    UsageTypeEnum["CrmProfile"] = "CrmProfile";
    UsageTypeEnum["Employee"] = "Employee";
  })(UsageTypeEnum = History.UsageTypeEnum || (History.UsageTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Edit"] = "Edit";
    TypeEnum["Dashboard"] = "Dashboard";
  })(TypeEnum = History.TypeEnum || (History.TypeEnum = {}));
})(History || (History = {}));
var HrmsBusinessCoWorker;
(function (HrmsBusinessCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = HrmsBusinessCoWorker.StatusEnum || (HrmsBusinessCoWorker.StatusEnum = {}));
})(HrmsBusinessCoWorker || (HrmsBusinessCoWorker = {}));
var HrmsCoWorker;
(function (HrmsCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = HrmsCoWorker.StatusEnum || (HrmsCoWorker.StatusEnum = {}));
})(HrmsCoWorker || (HrmsCoWorker = {}));
var HrmsCode;
(function (HrmsCode) {
  let CodeTypeEnum;
  (function (CodeTypeEnum) {
    CodeTypeEnum["HrmsInvitation"] = "hrms_invitation";
    CodeTypeEnum["Discount"] = "discount";
    CodeTypeEnum["Prospect"] = "prospect";
    CodeTypeEnum["User"] = "user";
    CodeTypeEnum["Business"] = "business";
  })(CodeTypeEnum = HrmsCode.CodeTypeEnum || (HrmsCode.CodeTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["User"] = "user";
    ServiceTypeEnum["Business"] = "business";
    ServiceTypeEnum["CoWorker"] = "co_worker";
  })(ServiceTypeEnum = HrmsCode.ServiceTypeEnum || (HrmsCode.ServiceTypeEnum = {}));
})(HrmsCode || (HrmsCode = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var InsuranceRule;
(function (InsuranceRule) {
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["FreeInsurance"] = "Free insurance";
    CalculationTypeEnum["ItemBased"] = "Item based";
    CalculationTypeEnum["NetTotal"] = "Net total";
    CalculationTypeEnum["NetWeight"] = "Net weight";
    CalculationTypeEnum["NetLength"] = "Net length";
    CalculationTypeEnum["NetVolume"] = "Net volume";
  })(CalculationTypeEnum = InsuranceRule.CalculationTypeEnum || (InsuranceRule.CalculationTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["Location"] = "Location";
  })(LocationLimitTypeEnum = InsuranceRule.LocationLimitTypeEnum || (InsuranceRule.LocationLimitTypeEnum = {}));
  let OfferFreeInsuranceTypeEnum;
  (function (OfferFreeInsuranceTypeEnum) {
    OfferFreeInsuranceTypeEnum["NoFreeInsurance"] = "No free insurance";
    OfferFreeInsuranceTypeEnum["FreeInsuranceIf"] = "Free insurance if";
  })(OfferFreeInsuranceTypeEnum = InsuranceRule.OfferFreeInsuranceTypeEnum || (InsuranceRule.OfferFreeInsuranceTypeEnum = {}));
})(InsuranceRule || (InsuranceRule = {}));
var Interaction;
(function (Interaction) {
  let InteractionStateEnum;
  (function (InteractionStateEnum) {
    InteractionStateEnum["Block"] = "block";
    InteractionStateEnum["Unblock"] = "unblock";
    InteractionStateEnum["Follow"] = "follow";
    InteractionStateEnum["Pending"] = "pending";
    InteractionStateEnum["Reject"] = "reject";
    InteractionStateEnum["Accept"] = "accept";
    InteractionStateEnum["Delete"] = "delete";
    InteractionStateEnum["NotFollow"] = "not_follow";
    InteractionStateEnum["Report"] = "report";
    InteractionStateEnum["UnReport"] = "un_report";
    InteractionStateEnum["Done"] = "done";
    InteractionStateEnum["SavedActive"] = "saved_active";
    InteractionStateEnum["UnSavedActive"] = "un_saved_active";
    InteractionStateEnum["CrmSave"] = "crm_save";
    InteractionStateEnum["CrmUnSave"] = "crm_un_save";
    InteractionStateEnum["NotRequest"] = "not_request";
    InteractionStateEnum["None"] = "none";
    InteractionStateEnum["SavedEvent"] = "saved_event";
    InteractionStateEnum["UnSavedEvent"] = "un_saved_event";
  })(InteractionStateEnum = Interaction.InteractionStateEnum || (Interaction.InteractionStateEnum = {}));
})(Interaction || (Interaction = {}));
var InteractionSmartAd;
(function (InteractionSmartAd) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = InteractionSmartAd.IsSavedEnum || (InteractionSmartAd.IsSavedEnum = {}));
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = InteractionSmartAd.CubeTypeEnum || (InteractionSmartAd.CubeTypeEnum = {}));
})(InteractionSmartAd || (InteractionSmartAd = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Invitation;
(function (Invitation) {
  let InvitationTypeEnum;
  (function (InvitationTypeEnum) {
    InvitationTypeEnum["Core"] = "core";
    InvitationTypeEnum["Email"] = "email";
  })(InvitationTypeEnum = Invitation.InvitationTypeEnum || (Invitation.InvitationTypeEnum = {}));
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["InvitationIncomplete"] = "invitation_incomplete";
    StatusEnum["InvitationComplete"] = "invitation_complete";
    StatusEnum["InvitationSent"] = "invitation_sent";
    StatusEnum["InvitationAccepted"] = "invitation_accepted";
    StatusEnum["InvitationRejected"] = "invitation_rejected";
    StatusEnum["ConvertedToEmployee"] = "converted_to_employee";
    StatusEnum["InvitationCancelled"] = "invitation_cancelled";
    StatusEnum["PendingHiringProcess"] = "pending_hiring_process";
  })(StatusEnum = Invitation.StatusEnum || (Invitation.StatusEnum = {}));
  let InvitationExpireEnum;
  (function (InvitationExpireEnum) {
    InvitationExpireEnum["No"] = "no";
    InvitationExpireEnum["Date"] = "date";
  })(InvitationExpireEnum = Invitation.InvitationExpireEnum || (Invitation.InvitationExpireEnum = {}));
  let CompanyStreamPostEnum;
  (function (CompanyStreamPostEnum) {
    CompanyStreamPostEnum["Yes"] = "yes";
    CompanyStreamPostEnum["No"] = "no";
  })(CompanyStreamPostEnum = Invitation.CompanyStreamPostEnum || (Invitation.CompanyStreamPostEnum = {}));
  let CompanyStreamToEnum;
  (function (CompanyStreamToEnum) {
    CompanyStreamToEnum["All"] = "all";
    CompanyStreamToEnum["SelectedDepartments"] = "selected_departments";
  })(CompanyStreamToEnum = Invitation.CompanyStreamToEnum || (Invitation.CompanyStreamToEnum = {}));
})(Invitation || (Invitation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var KeyWord;
(function (KeyWord) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Universal"] = "universal";
    TypeEnum["Skill"] = "skill";
    TypeEnum["JobTitle"] = "job_title";
    TypeEnum["Interest"] = "interest";
    TypeEnum["Catalog"] = "catalog";
    TypeEnum["Academy"] = "academy";
    TypeEnum["Application"] = "application";
    TypeEnum["Industry"] = "industry";
    TypeEnum["Garden"] = "garden";
    TypeEnum["IndustryCategory"] = "industry_category";
    TypeEnum["BucketListCategory"] = "bucket_list_category";
    TypeEnum["PaymentOnSpot"] = "payment_on_spot";
    TypeEnum["CategoryTemplate"] = "category_template";
    TypeEnum["CustomField"] = "custom_field";
    TypeEnum["SystemTaxRule"] = "system_tax_rule";
    TypeEnum["SmartAd"] = "smart_ad";
    TypeEnum["CrmTargetedCustomer"] = "crm_targeted_customer";
  })(TypeEnum = KeyWord.TypeEnum || (KeyWord.TypeEnum = {}));
})(KeyWord || (KeyWord = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var KeyWordService;
(function (KeyWordService) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Country"] = "country";
    ServiceTypeEnum["Street"] = "street";
    ServiceTypeEnum["City"] = "city";
    ServiceTypeEnum["State"] = "state";
    ServiceTypeEnum["Business"] = "business";
    ServiceTypeEnum["User"] = "user";
    ServiceTypeEnum["CrmProspect"] = "crm_prospect";
    ServiceTypeEnum["LocationManager"] = "location_manager";
    ServiceTypeEnum["Task"] = "task";
    ServiceTypeEnum["Application"] = "application";
    ServiceTypeEnum["Academy"] = "academy";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["PaymentOnSpot"] = "payment_on_spot";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["CustomField"] = "custom_field";
    ServiceTypeEnum["CoWorker"] = "co_worker";
    ServiceTypeEnum["SystemTaxRule"] = "system_tax_rule";
    ServiceTypeEnum["ProjectWorkgroup"] = "project_workgroup";
    ServiceTypeEnum["CrmTargetedCustomer"] = "crm_targeted_customer";
    ServiceTypeEnum["Contact"] = "contact";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["LanguageExchange"] = "language_exchange";
    ServiceTypeEnum["BusinessNetworking"] = "business_networking";
    ServiceTypeEnum["Dating"] = "dating";
    ServiceTypeEnum["Garden"] = "garden";
    ServiceTypeEnum["TravelBuddies"] = "travel_buddies";
    ServiceTypeEnum["SecondChance"] = "second_chance";
    ServiceTypeEnum["BeGreen"] = "be_green";
    ServiceTypeEnum["BucketList"] = "bucket_list";
    ServiceTypeEnum["GoalManager"] = "goal_manager";
    ServiceTypeEnum["Pets"] = "pets";
    ServiceTypeEnum["Volunteer"] = "volunteer";
    ServiceTypeEnum["Cooking"] = "cooking";
    ServiceTypeEnum["Collectors"] = "collectors";
    ServiceTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ServiceTypeEnum["KidsSpace"] = "kids_space";
    ServiceTypeEnum["Sport"] = "sport";
    ServiceTypeEnum["Mindset"] = "mindset";
    ServiceTypeEnum["DiyHandmade"] = "diy_handmade";
    ServiceTypeEnum["Tech"] = "tech";
    ServiceTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ServiceTypeEnum["BToB"] = "b_to_b";
    ServiceTypeEnum["DigitalNomad"] = "digital_nomad";
    ServiceTypeEnum["Gaming"] = "gaming";
    ServiceTypeEnum["Erotic"] = "erotic";
    ServiceTypeEnum["Roommate"] = "roommate";
    ServiceTypeEnum["Aviation"] = "aviation";
    ServiceTypeEnum["Cars"] = "cars";
    ServiceTypeEnum["Boat"] = "boat";
    ServiceTypeEnum["Fitness"] = "fitness";
    ServiceTypeEnum["Events"] = "events";
    ServiceTypeEnum["SkillLink"] = "skill_link";
  })(ServiceTypeEnum = KeyWordService.ServiceTypeEnum || (KeyWordService.ServiceTypeEnum = {}));
})(KeyWordService || (KeyWordService = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Link;
(function (Link) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = Link.SharedTypeEnum || (Link.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Website"] = "website";
    TypeEnum["SocialMedia"] = "social_media";
    TypeEnum["Other"] = "other";
  })(TypeEnum = Link.TypeEnum || (Link.TypeEnum = {}));
})(Link || (Link = {}));
var LocationLimit;
(function (LocationLimit) {
  let LocationTypeEnum;
  (function (LocationTypeEnum) {
    LocationTypeEnum["Country"] = "country";
    LocationTypeEnum["State"] = "state";
    LocationTypeEnum["City"] = "city";
  })(LocationTypeEnum = LocationLimit.LocationTypeEnum || (LocationLimit.LocationTypeEnum = {}));
})(LocationLimit || (LocationLimit = {}));
var LocationManagerBusiness;
(function (LocationManagerBusiness) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = LocationManagerBusiness.TypeEnum || (LocationManagerBusiness.TypeEnum = {}));
})(LocationManagerBusiness || (LocationManagerBusiness = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var LocationManagerBusinessRequestRequest;
(function (LocationManagerBusinessRequestRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = LocationManagerBusinessRequestRequest.TypeEnum || (LocationManagerBusinessRequestRequest.TypeEnum = {}));
})(LocationManagerBusinessRequestRequest || (LocationManagerBusinessRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var LocationTranslation;
(function (LocationTranslation) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Country"] = "country";
    ServiceTypeEnum["Province"] = "province";
    ServiceTypeEnum["City"] = "city";
    ServiceTypeEnum["Street"] = "street";
    ServiceTypeEnum["Zipcode"] = "zipcode";
    ServiceTypeEnum["Region"] = "region";
    ServiceTypeEnum["SubRegion"] = "sub_region";
  })(ServiceTypeEnum = LocationTranslation.ServiceTypeEnum || (LocationTranslation.ServiceTypeEnum = {}));
})(LocationTranslation || (LocationTranslation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var LoginResponse;
(function (LoginResponse) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = LoginResponse.SubscriptionTypeEnum || (LoginResponse.SubscriptionTypeEnum = {}));
})(LoginResponse || (LoginResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var LoginWithSocialMediaRequest;
(function (LoginWithSocialMediaRequest) {
  let SocialTypeEnum;
  (function (SocialTypeEnum) {
    SocialTypeEnum["Facebook"] = "facebook";
    SocialTypeEnum["Google"] = "google";
    SocialTypeEnum["Apple"] = "apple";
    SocialTypeEnum["Linkedin"] = "linkedin";
    SocialTypeEnum["Twitter"] = "twitter";
    SocialTypeEnum["Vk"] = "vk";
  })(SocialTypeEnum = LoginWithSocialMediaRequest.SocialTypeEnum || (LoginWithSocialMediaRequest.SocialTypeEnum = {}));
})(LoginWithSocialMediaRequest || (LoginWithSocialMediaRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var MarketDiscount;
(function (MarketDiscount) {
  let DiscountTypeEnum;
  (function (DiscountTypeEnum) {
    DiscountTypeEnum["OfferToAll"] = "Offer to all";
    DiscountTypeEnum["VoucherCode"] = "Voucher code";
    DiscountTypeEnum["OfferToSpecificUsers"] = "Offer to specific users";
  })(DiscountTypeEnum = MarketDiscount.DiscountTypeEnum || (MarketDiscount.DiscountTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = MarketDiscount.PriceChangerTypeEnum || (MarketDiscount.PriceChangerTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["AreaLimit"] = "Area limit";
    LocationLimitTypeEnum["CountryLimit"] = "Country limit";
  })(LocationLimitTypeEnum = MarketDiscount.LocationLimitTypeEnum || (MarketDiscount.LocationLimitTypeEnum = {}));
  let ShowTypeEnum;
  (function (ShowTypeEnum) {
    ShowTypeEnum["Public"] = "public";
    ShowTypeEnum["Private"] = "private";
  })(ShowTypeEnum = MarketDiscount.ShowTypeEnum || (MarketDiscount.ShowTypeEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = MarketDiscount.IsSavedEnum || (MarketDiscount.IsSavedEnum = {}));
})(MarketDiscount || (MarketDiscount = {}));
var MarketTarget;
(function (MarketTarget) {
  let TargetTypeEnum;
  (function (TargetTypeEnum) {
    TargetTypeEnum["Personal"] = "personal";
    TargetTypeEnum["Business"] = "business";
  })(TargetTypeEnum = MarketTarget.TargetTypeEnum || (MarketTarget.TargetTypeEnum = {}));
})(MarketTarget || (MarketTarget = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var MarketTargetCharacteristic;
(function (MarketTargetCharacteristic) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["LocationRegion"] = "location_region";
    ServiceTypeEnum["LocationSubRegion"] = "location_sub_region";
    ServiceTypeEnum["LocationCountry"] = "location_country";
    ServiceTypeEnum["LocationProvince"] = "location_province";
    ServiceTypeEnum["LocationCity"] = "location_city";
    ServiceTypeEnum["LocationZipcode"] = "location_zipcode";
    ServiceTypeEnum["ServiceArea"] = "service_area";
    ServiceTypeEnum["Skill"] = "skill";
    ServiceTypeEnum["JobTitle"] = "job_title";
    ServiceTypeEnum["Interest"] = "interest";
    ServiceTypeEnum["SalesRevenue"] = "sales_revenue";
    ServiceTypeEnum["NumberOfEmployees"] = "number_of_employees";
    ServiceTypeEnum["RegisteredCountry"] = "registered_country";
    ServiceTypeEnum["LegalEntityType"] = "legal_entity_type";
    ServiceTypeEnum["Language"] = "language";
    ServiceTypeEnum["Gender"] = "gender";
    ServiceTypeEnum["Age"] = "age";
    ServiceTypeEnum["YearInBusiness"] = "year_in_business";
  })(ServiceTypeEnum = MarketTargetCharacteristic.ServiceTypeEnum || (MarketTargetCharacteristic.ServiceTypeEnum = {}));
})(MarketTargetCharacteristic || (MarketTargetCharacteristic = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var Message;
(function (Message) {
  let CommunicationTypeEnum;
  (function (CommunicationTypeEnum) {
    CommunicationTypeEnum["Chat"] = "chat";
    CommunicationTypeEnum["Notification"] = "notification";
    CommunicationTypeEnum["Ping"] = "ping";
  })(CommunicationTypeEnum = Message.CommunicationTypeEnum || (Message.CommunicationTypeEnum = {}));
})(Message || (Message = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ModifyUserHrmsInvitationRequest;
(function (ModifyUserHrmsInvitationRequest) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["InvitationAccepted"] = "invitation_accepted";
    StatusEnum["InvitationRejected"] = "invitation_rejected";
  })(StatusEnum = ModifyUserHrmsInvitationRequest.StatusEnum || (ModifyUserHrmsInvitationRequest.StatusEnum = {}));
})(ModifyUserHrmsInvitationRequest || (ModifyUserHrmsInvitationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var MrAiTracker;
(function (MrAiTracker) {
  let ProfileTypeEnum;
  (function (ProfileTypeEnum) {
    ProfileTypeEnum["LanguageExchange"] = "language_exchange";
    ProfileTypeEnum["BusinessNetworking"] = "business_networking";
    ProfileTypeEnum["Dating"] = "dating";
    ProfileTypeEnum["Garden"] = "garden";
    ProfileTypeEnum["TravelBuddies"] = "travel_buddies";
    ProfileTypeEnum["SecondChance"] = "second_chance";
    ProfileTypeEnum["BeGreen"] = "be_green";
    ProfileTypeEnum["BucketList"] = "bucket_list";
    ProfileTypeEnum["GoalManager"] = "goal_manager";
    ProfileTypeEnum["Pets"] = "pets";
    ProfileTypeEnum["Volunteer"] = "volunteer";
    ProfileTypeEnum["Cooking"] = "cooking";
    ProfileTypeEnum["Collectors"] = "collectors";
    ProfileTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ProfileTypeEnum["KidsSpace"] = "kids_space";
    ProfileTypeEnum["Sport"] = "sport";
    ProfileTypeEnum["Mindset"] = "mindset";
    ProfileTypeEnum["DiyHandmade"] = "diy_handmade";
    ProfileTypeEnum["Tech"] = "tech";
    ProfileTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ProfileTypeEnum["BToB"] = "b_to_b";
    ProfileTypeEnum["DigitalNomad"] = "digital_nomad";
    ProfileTypeEnum["Gaming"] = "gaming";
    ProfileTypeEnum["Erotic"] = "erotic";
    ProfileTypeEnum["Roommate"] = "roommate";
    ProfileTypeEnum["Aviation"] = "aviation";
    ProfileTypeEnum["Cars"] = "cars";
    ProfileTypeEnum["Boat"] = "boat";
    ProfileTypeEnum["Fitness"] = "fitness";
    ProfileTypeEnum["Events"] = "events";
    ProfileTypeEnum["TaskManager"] = "task_manager";
    ProfileTypeEnum["Services"] = "services";
    ProfileTypeEnum["Academy"] = "academy";
    ProfileTypeEnum["StoreFinder"] = "store_finder";
    ProfileTypeEnum["ActivityStream"] = "activity_stream";
    ProfileTypeEnum["Calendar"] = "calendar";
    ProfileTypeEnum["Contacts"] = "contacts";
    ProfileTypeEnum["Notes"] = "notes";
    ProfileTypeEnum["Drive"] = "drive";
    ProfileTypeEnum["Taskly"] = "taskly";
    ProfileTypeEnum["MarketSpace"] = "market_space";
    ProfileTypeEnum["Chat"] = "chat";
    ProfileTypeEnum["DoctorFinder"] = "doctor_finder";
    ProfileTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ProfileTypeEnum["Renting"] = "renting";
    ProfileTypeEnum["Booking"] = "booking";
    ProfileTypeEnum["Job"] = "job";
    ProfileTypeEnum["DigitalDownloads"] = "digital_downloads";
    ProfileTypeEnum["PhysicalProduct"] = "physical_product";
    ProfileTypeEnum["EmailManager"] = "email_manager";
    ProfileTypeEnum["RealEstate"] = "real_estate";
    ProfileTypeEnum["CoworkingSpace"] = "coworking_space";
    ProfileTypeEnum["Grocery"] = "grocery";
    ProfileTypeEnum["Fashion"] = "fashion";
    ProfileTypeEnum["HomeDesign"] = "home_design";
    ProfileTypeEnum["Taxi"] = "taxi";
    ProfileTypeEnum["SkillLink"] = "skill_link";
    ProfileTypeEnum["MediaChannels"] = "media_channels";
  })(ProfileTypeEnum = MrAiTracker.ProfileTypeEnum || (MrAiTracker.ProfileTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Album"] = "album";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["Academy"] = "academy";
  })(ServiceTypeEnum = MrAiTracker.ServiceTypeEnum || (MrAiTracker.ServiceTypeEnum = {}));
})(MrAiTracker || (MrAiTracker = {}));
var MySubSubscriptionPageResponse;
(function (MySubSubscriptionPageResponse) {
  let SubscriptionRenewTypeEnum;
  (function (SubscriptionRenewTypeEnum) {
    SubscriptionRenewTypeEnum["Manually"] = "Manually";
    SubscriptionRenewTypeEnum["Automatic"] = "Automatic";
  })(SubscriptionRenewTypeEnum = MySubSubscriptionPageResponse.SubscriptionRenewTypeEnum || (MySubSubscriptionPageResponse.SubscriptionRenewTypeEnum = {}));
  let DefaultPaymentMethodEnum;
  (function (DefaultPaymentMethodEnum) {
    DefaultPaymentMethodEnum["Wallet"] = "Wallet";
    DefaultPaymentMethodEnum["Stripe"] = "Stripe";
    DefaultPaymentMethodEnum["Paypal"] = "Paypal";
  })(DefaultPaymentMethodEnum = MySubSubscriptionPageResponse.DefaultPaymentMethodEnum || (MySubSubscriptionPageResponse.DefaultPaymentMethodEnum = {}));
})(MySubSubscriptionPageResponse || (MySubSubscriptionPageResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var NotificationSystem;
(function (NotificationSystem) {
  let SenderTypeEnum;
  (function (SenderTypeEnum) {
    SenderTypeEnum["User"] = "user";
    SenderTypeEnum["Business"] = "business";
    SenderTypeEnum["Erp"] = "erp";
    SenderTypeEnum["BCoWorker"] = "b_co_worker";
    SenderTypeEnum["Rpa"] = "rpa";
  })(SenderTypeEnum = NotificationSystem.SenderTypeEnum || (NotificationSystem.SenderTypeEnum = {}));
  let PriorityEnum;
  (function (PriorityEnum) {
    PriorityEnum["High"] = "high";
    PriorityEnum["Normal"] = "normal";
    PriorityEnum["Low"] = "low";
  })(PriorityEnum = NotificationSystem.PriorityEnum || (NotificationSystem.PriorityEnum = {}));
  let NotificationTypeEnum;
  (function (NotificationTypeEnum) {
    NotificationTypeEnum["Unknown"] = "unknown";
    NotificationTypeEnum["User"] = "user";
    NotificationTypeEnum["Employee"] = "employee";
    NotificationTypeEnum["LanguageExchange"] = "language_exchange";
    NotificationTypeEnum["BusinessNetworking"] = "business_networking";
    NotificationTypeEnum["Dating"] = "dating";
    NotificationTypeEnum["Garden"] = "garden";
    NotificationTypeEnum["Event"] = "event";
    NotificationTypeEnum["TravelBuddies"] = "travel_buddies";
    NotificationTypeEnum["SecondChance"] = "second_chance";
    NotificationTypeEnum["BeGreen"] = "be_green";
    NotificationTypeEnum["BucketList"] = "bucket_list";
    NotificationTypeEnum["GoalManager"] = "goal_manager";
    NotificationTypeEnum["Pets"] = "pets";
    NotificationTypeEnum["Volunteer"] = "volunteer";
    NotificationTypeEnum["Cooking"] = "cooking";
  })(NotificationTypeEnum = NotificationSystem.NotificationTypeEnum || (NotificationSystem.NotificationTypeEnum = {}));
})(NotificationSystem || (NotificationSystem = {}));
var OcSystemAccounting;
(function (OcSystemAccounting) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["SubscriptionElite"] = "subscription_elite";
    TypeEnum["SubscriptionBeginner"] = "subscription_beginner";
    TypeEnum["SubscriptionProfessional"] = "subscription_professional";
    TypeEnum["SubscriptionEmpire"] = "subscription_empire";
    TypeEnum["AdvertiseBalanceUpload"] = "advertise_balance_upload";
    TypeEnum["AdvertiseSpent"] = "advertise_spent";
    TypeEnum["Adds"] = "adds";
    TypeEnum["FxConvert"] = "fx_convert";
    TypeEnum["SubscriptionDiamond"] = "subscription_diamond";
    TypeEnum["SubscriptionVip"] = "subscription_vip";
    TypeEnum["BalanceUpload"] = "balance_upload";
    TypeEnum["PoInstantCommission"] = "po_instant_commission";
    TypeEnum["PoPortfolioCommission"] = "po_portfolio_commission";
  })(TypeEnum = OcSystemAccounting.TypeEnum || (OcSystemAccounting.TypeEnum = {}));
})(OcSystemAccounting || (OcSystemAccounting = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PageResponse;
(function (PageResponse) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = PageResponse.StateEnum || (PageResponse.StateEnum = {}));
})(PageResponse || (PageResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PartnerVerify;
(function (PartnerVerify) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Accepted"] = "accepted";
    StatusEnum["Declined"] = "declined";
    StatusEnum["Cancelled"] = "cancelled";
  })(StatusEnum = PartnerVerify.StatusEnum || (PartnerVerify.StatusEnum = {}));
})(PartnerVerify || (PartnerVerify = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedLocationManagerBusinessPatchRequest;
(function (PatchedLocationManagerBusinessPatchRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = PatchedLocationManagerBusinessPatchRequest.TypeEnum || (PatchedLocationManagerBusinessPatchRequest.TypeEnum = {}));
})(PatchedLocationManagerBusinessPatchRequest || (PatchedLocationManagerBusinessPatchRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedModifyCoWorkerRequest;
(function (PatchedModifyCoWorkerRequest) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = PatchedModifyCoWorkerRequest.StatusEnum || (PatchedModifyCoWorkerRequest.StatusEnum = {}));
})(PatchedModifyCoWorkerRequest || (PatchedModifyCoWorkerRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchCrmProspectRequest;
(function (PatchedPatchCrmProspectRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Personal"] = "personal";
    TypeEnum["Business"] = "business";
  })(TypeEnum = PatchedPatchCrmProspectRequest.TypeEnum || (PatchedPatchCrmProspectRequest.TypeEnum = {}));
})(PatchedPatchCrmProspectRequest || (PatchedPatchCrmProspectRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchDomainPageRequest;
(function (PatchedPatchDomainPageRequest) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = PatchedPatchDomainPageRequest.StateEnum || (PatchedPatchDomainPageRequest.StateEnum = {}));
})(PatchedPatchDomainPageRequest || (PatchedPatchDomainPageRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchDomainSiteRequest;
(function (PatchedPatchDomainSiteRequest) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = PatchedPatchDomainSiteRequest.StateEnum || (PatchedPatchDomainSiteRequest.StateEnum = {}));
})(PatchedPatchDomainSiteRequest || (PatchedPatchDomainSiteRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PatchedPatchMarketDiscountRequest;
(function (PatchedPatchMarketDiscountRequest) {
  let DiscountTypeEnum;
  (function (DiscountTypeEnum) {
    DiscountTypeEnum["OfferToAll"] = "Offer to all";
    DiscountTypeEnum["VoucherCode"] = "Voucher code";
    DiscountTypeEnum["OfferToSpecificUsers"] = "Offer to specific users";
  })(DiscountTypeEnum = PatchedPatchMarketDiscountRequest.DiscountTypeEnum || (PatchedPatchMarketDiscountRequest.DiscountTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = PatchedPatchMarketDiscountRequest.PriceChangerTypeEnum || (PatchedPatchMarketDiscountRequest.PriceChangerTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["AreaLimit"] = "Area limit";
    LocationLimitTypeEnum["CountryLimit"] = "Country limit";
  })(LocationLimitTypeEnum = PatchedPatchMarketDiscountRequest.LocationLimitTypeEnum || (PatchedPatchMarketDiscountRequest.LocationLimitTypeEnum = {}));
  let ShowTypeEnum;
  (function (ShowTypeEnum) {
    ShowTypeEnum["Public"] = "public";
    ShowTypeEnum["Private"] = "private";
  })(ShowTypeEnum = PatchedPatchMarketDiscountRequest.ShowTypeEnum || (PatchedPatchMarketDiscountRequest.ShowTypeEnum = {}));
})(PatchedPatchMarketDiscountRequest || (PatchedPatchMarketDiscountRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchPostRequest;
(function (PatchedPatchPostRequest) {
  let ApplicationTypeEnum;
  (function (ApplicationTypeEnum) {
    ApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    ApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    ApplicationTypeEnum["Dating"] = "dating";
    ApplicationTypeEnum["Garden"] = "garden";
    ApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    ApplicationTypeEnum["SecondChance"] = "second_chance";
    ApplicationTypeEnum["BeGreen"] = "be_green";
    ApplicationTypeEnum["BucketList"] = "bucket_list";
    ApplicationTypeEnum["GoalManager"] = "goal_manager";
    ApplicationTypeEnum["Pets"] = "pets";
    ApplicationTypeEnum["Volunteer"] = "volunteer";
    ApplicationTypeEnum["Cooking"] = "cooking";
    ApplicationTypeEnum["Collectors"] = "collectors";
    ApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ApplicationTypeEnum["KidsSpace"] = "kids_space";
    ApplicationTypeEnum["Sport"] = "sport";
    ApplicationTypeEnum["Mindset"] = "mindset";
    ApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    ApplicationTypeEnum["Tech"] = "tech";
    ApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ApplicationTypeEnum["BToB"] = "b_to_b";
    ApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    ApplicationTypeEnum["Gaming"] = "gaming";
    ApplicationTypeEnum["Erotic"] = "erotic";
    ApplicationTypeEnum["Roommate"] = "roommate";
    ApplicationTypeEnum["Aviation"] = "aviation";
    ApplicationTypeEnum["Cars"] = "cars";
    ApplicationTypeEnum["Boat"] = "boat";
    ApplicationTypeEnum["Fitness"] = "fitness";
    ApplicationTypeEnum["Events"] = "events";
    ApplicationTypeEnum["TaskManager"] = "task_manager";
    ApplicationTypeEnum["Services"] = "services";
    ApplicationTypeEnum["Academy"] = "academy";
    ApplicationTypeEnum["StoreFinder"] = "store_finder";
    ApplicationTypeEnum["ActivityStream"] = "activity_stream";
    ApplicationTypeEnum["Calendar"] = "calendar";
    ApplicationTypeEnum["Contacts"] = "contacts";
    ApplicationTypeEnum["Notes"] = "notes";
    ApplicationTypeEnum["Drive"] = "drive";
    ApplicationTypeEnum["Taskly"] = "taskly";
    ApplicationTypeEnum["MarketSpace"] = "market_space";
    ApplicationTypeEnum["Chat"] = "chat";
    ApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    ApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ApplicationTypeEnum["Renting"] = "renting";
    ApplicationTypeEnum["Booking"] = "booking";
    ApplicationTypeEnum["Job"] = "job";
    ApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    ApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    ApplicationTypeEnum["EmailManager"] = "email_manager";
    ApplicationTypeEnum["RealEstate"] = "real_estate";
    ApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    ApplicationTypeEnum["Grocery"] = "grocery";
    ApplicationTypeEnum["Fashion"] = "fashion";
    ApplicationTypeEnum["HomeDesign"] = "home_design";
    ApplicationTypeEnum["Taxi"] = "taxi";
    ApplicationTypeEnum["SkillLink"] = "skill_link";
    ApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(ApplicationTypeEnum = PatchedPatchPostRequest.ApplicationTypeEnum || (PatchedPatchPostRequest.ApplicationTypeEnum = {}));
  let PostTypeEnum;
  (function (PostTypeEnum) {
    PostTypeEnum["ShareAd"] = "share_ad";
    PostTypeEnum["SharedUser"] = "shared_user";
    PostTypeEnum["Album"] = "album";
    PostTypeEnum["Colored"] = "colored";
    PostTypeEnum["ImageBackground"] = "image_background";
    PostTypeEnum["Poll"] = "poll";
    PostTypeEnum["OutsideLink"] = "outside_link";
    PostTypeEnum["Swap"] = "swap";
    PostTypeEnum["Donate"] = "donate";
    PostTypeEnum["DonationWish"] = "donation_wish";
    PostTypeEnum["Adoption"] = "adoption";
    PostTypeEnum["Lost"] = "lost";
    PostTypeEnum["Find"] = "find";
    PostTypeEnum["Ad"] = "ad";
    PostTypeEnum["Catalog"] = "catalog";
  })(PostTypeEnum = PatchedPatchPostRequest.PostTypeEnum || (PatchedPatchPostRequest.PostTypeEnum = {}));
  let PrivacyEnum;
  (function (PrivacyEnum) {
    PrivacyEnum["Public"] = "public";
    PrivacyEnum["Followers"] = "followers";
    PrivacyEnum["Private"] = "private";
  })(PrivacyEnum = PatchedPatchPostRequest.PrivacyEnum || (PatchedPatchPostRequest.PrivacyEnum = {}));
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Draft"] = "draft";
    PostStateEnum["Published"] = "published";
    PostStateEnum["Scheduled"] = "scheduled";
    PostStateEnum["Deleted"] = "deleted";
  })(PostStateEnum = PatchedPatchPostRequest.PostStateEnum || (PatchedPatchPostRequest.PostStateEnum = {}));
  let PostViewStateEnum;
  (function (PostViewStateEnum) {
    PostViewStateEnum["Adult"] = "adult";
    PostViewStateEnum["Normal"] = "normal";
    PostViewStateEnum["Sensitive"] = "sensitive";
  })(PostViewStateEnum = PatchedPatchPostRequest.PostViewStateEnum || (PatchedPatchPostRequest.PostViewStateEnum = {}));
  let PollTypeEnum;
  (function (PollTypeEnum) {
    PollTypeEnum["Single"] = "single";
    PollTypeEnum["Multiple"] = "multiple";
  })(PollTypeEnum = PatchedPatchPostRequest.PollTypeEnum || (PatchedPatchPostRequest.PollTypeEnum = {}));
  let StreamTypeEnum;
  (function (StreamTypeEnum) {
    StreamTypeEnum["CompanyStream"] = "company_stream";
    StreamTypeEnum["CoreStream"] = "core_stream";
  })(StreamTypeEnum = PatchedPatchPostRequest.StreamTypeEnum || (PatchedPatchPostRequest.StreamTypeEnum = {}));
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
  })(CatalogTypeEnum = PatchedPatchPostRequest.CatalogTypeEnum || (PatchedPatchPostRequest.CatalogTypeEnum = {}));
})(PatchedPatchPostRequest || (PatchedPatchPostRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchRoomRequest;
(function (PatchedPatchRoomRequest) {
  let PrivacyEnum;
  (function (PrivacyEnum) {
    PrivacyEnum["Private"] = "private";
    PrivacyEnum["Public"] = "public";
  })(PrivacyEnum = PatchedPatchRoomRequest.PrivacyEnum || (PatchedPatchRoomRequest.PrivacyEnum = {}));
})(PatchedPatchRoomRequest || (PatchedPatchRoomRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchSmartItemRequest;
(function (PatchedPatchSmartItemRequest) {
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = PatchedPatchSmartItemRequest.CubeTypeEnum || (PatchedPatchSmartItemRequest.CubeTypeEnum = {}));
  let InventoryTypeEnum;
  (function (InventoryTypeEnum) {
    InventoryTypeEnum["None"] = "none";
    InventoryTypeEnum["FinishedGoods"] = "finished_goods";
    InventoryTypeEnum["RawMaterials"] = "raw_materials";
    InventoryTypeEnum["Component"] = "component";
    InventoryTypeEnum["Wip"] = "wip";
    InventoryTypeEnum["PackingMaterials"] = "packing_materials";
    InventoryTypeEnum["Mro"] = "mro";
    InventoryTypeEnum["Others"] = "others";
    InventoryTypeEnum["LivePlant"] = "live_plant";
    InventoryTypeEnum["LiveAnimal"] = "live_animal";
    InventoryTypeEnum["Food"] = "food";
    InventoryTypeEnum["FoodComponent"] = "food_component";
    InventoryTypeEnum["Drink"] = "drink";
  })(InventoryTypeEnum = PatchedPatchSmartItemRequest.InventoryTypeEnum || (PatchedPatchSmartItemRequest.InventoryTypeEnum = {}));
  let AmountTypeEnum;
  (function (AmountTypeEnum) {
    AmountTypeEnum["Unlimited"] = "unlimited";
    AmountTypeEnum["Calculated"] = "calculated";
  })(AmountTypeEnum = PatchedPatchSmartItemRequest.AmountTypeEnum || (PatchedPatchSmartItemRequest.AmountTypeEnum = {}));
  let ManagingInventoryEnum;
  (function (ManagingInventoryEnum) {
    ManagingInventoryEnum["None"] = "none";
    ManagingInventoryEnum["Fifo"] = "fifo";
    ManagingInventoryEnum["Lifo"] = "lifo";
  })(ManagingInventoryEnum = PatchedPatchSmartItemRequest.ManagingInventoryEnum || (PatchedPatchSmartItemRequest.ManagingInventoryEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = PatchedPatchSmartItemRequest.IsOnlineTypeEnum || (PatchedPatchSmartItemRequest.IsOnlineTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = PatchedPatchSmartItemRequest.OnlineEmployeeEnum || (PatchedPatchSmartItemRequest.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = PatchedPatchSmartItemRequest.ServiceAreaEmployeeEnum || (PatchedPatchSmartItemRequest.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = PatchedPatchSmartItemRequest.LocationEmployeeEnum || (PatchedPatchSmartItemRequest.LocationEmployeeEnum = {}));
  let ServiceSettingsEnum;
  (function (ServiceSettingsEnum) {
    ServiceSettingsEnum["SmartSettings"] = "smart_settings";
    ServiceSettingsEnum["Custom"] = "custom";
  })(ServiceSettingsEnum = PatchedPatchSmartItemRequest.ServiceSettingsEnum || (PatchedPatchSmartItemRequest.ServiceSettingsEnum = {}));
  let ServiceBookingPreferenceSourceEnum;
  (function (ServiceBookingPreferenceSourceEnum) {
    ServiceBookingPreferenceSourceEnum["SmartSettings"] = "smart_settings";
    ServiceBookingPreferenceSourceEnum["Custom"] = "custom";
  })(ServiceBookingPreferenceSourceEnum = PatchedPatchSmartItemRequest.ServiceBookingPreferenceSourceEnum || (PatchedPatchSmartItemRequest.ServiceBookingPreferenceSourceEnum = {}));
  let ServiceBookingPreferenceEnum;
  (function (ServiceBookingPreferenceEnum) {
    ServiceBookingPreferenceEnum["SystemSelect"] = "system_select";
    ServiceBookingPreferenceEnum["CustomerOptionChoose"] = "customer_option_choose";
    ServiceBookingPreferenceEnum["CustomerForceChoose"] = "customer_force_choose";
  })(ServiceBookingPreferenceEnum = PatchedPatchSmartItemRequest.ServiceBookingPreferenceEnum || (PatchedPatchSmartItemRequest.ServiceBookingPreferenceEnum = {}));
  let WorkplaceTypeEnum;
  (function (WorkplaceTypeEnum) {
    WorkplaceTypeEnum["OnSite"] = "on_site";
    WorkplaceTypeEnum["Hybrid"] = "hybrid";
    WorkplaceTypeEnum["Remote"] = "remote";
  })(WorkplaceTypeEnum = PatchedPatchSmartItemRequest.WorkplaceTypeEnum || (PatchedPatchSmartItemRequest.WorkplaceTypeEnum = {}));
})(PatchedPatchSmartItemRequest || (PatchedPatchSmartItemRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PatchedPatchTaskRequest;
(function (PatchedPatchTaskRequest) {
  let TimePlaningEnum;
  (function (TimePlaningEnum) {
    TimePlaningEnum["Disable"] = "disable";
    TimePlaningEnum["Enable"] = "enable";
  })(TimePlaningEnum = PatchedPatchTaskRequest.TimePlaningEnum || (PatchedPatchTaskRequest.TimePlaningEnum = {}));
  let RepeatTermEnum;
  (function (RepeatTermEnum) {
    RepeatTermEnum["Day"] = "day";
    RepeatTermEnum["Week"] = "week";
    RepeatTermEnum["Month"] = "month";
    RepeatTermEnum["Year"] = "year";
  })(RepeatTermEnum = PatchedPatchTaskRequest.RepeatTermEnum || (PatchedPatchTaskRequest.RepeatTermEnum = {}));
  let RepeatEveryMonthTypeEnum;
  (function (RepeatEveryMonthTypeEnum) {
    RepeatEveryMonthTypeEnum["Day"] = "day";
    RepeatEveryMonthTypeEnum["Week"] = "week";
  })(RepeatEveryMonthTypeEnum = PatchedPatchTaskRequest.RepeatEveryMonthTypeEnum || (PatchedPatchTaskRequest.RepeatEveryMonthTypeEnum = {}));
  let RepeatEveryMonthWeekOptionEnum;
  (function (RepeatEveryMonthWeekOptionEnum) {
    RepeatEveryMonthWeekOptionEnum["FirstWeek"] = "first week";
    RepeatEveryMonthWeekOptionEnum["SecondWeek"] = "second week";
    RepeatEveryMonthWeekOptionEnum["ThirdWeek"] = "third week";
    RepeatEveryMonthWeekOptionEnum["FourthWeek"] = "fourth week";
    RepeatEveryMonthWeekOptionEnum["LastWeek"] = "last week";
  })(RepeatEveryMonthWeekOptionEnum = PatchedPatchTaskRequest.RepeatEveryMonthWeekOptionEnum || (PatchedPatchTaskRequest.RepeatEveryMonthWeekOptionEnum = {}));
  let RepeatWeekTermDayEnum;
  (function (RepeatWeekTermDayEnum) {
    RepeatWeekTermDayEnum["Monday"] = "Monday";
    RepeatWeekTermDayEnum["Tuesday"] = "Tuesday";
    RepeatWeekTermDayEnum["Wednesday"] = "Wednesday";
    RepeatWeekTermDayEnum["Thursday"] = "Thursday";
    RepeatWeekTermDayEnum["Friday"] = "Friday";
    RepeatWeekTermDayEnum["Saturday"] = "Saturday";
    RepeatWeekTermDayEnum["Sunday"] = "Sunday";
  })(RepeatWeekTermDayEnum = PatchedPatchTaskRequest.RepeatWeekTermDayEnum || (PatchedPatchTaskRequest.RepeatWeekTermDayEnum = {}));
  let RepeatEveryYearTypeEnum;
  (function (RepeatEveryYearTypeEnum) {
    RepeatEveryYearTypeEnum["DayMonth"] = "day_month";
    RepeatEveryYearTypeEnum["DayWeekMonth"] = "day_week_month";
  })(RepeatEveryYearTypeEnum = PatchedPatchTaskRequest.RepeatEveryYearTypeEnum || (PatchedPatchTaskRequest.RepeatEveryYearTypeEnum = {}));
  let RepeatEveryYearDayMonthSelectEnum;
  (function (RepeatEveryYearDayMonthSelectEnum) {
    RepeatEveryYearDayMonthSelectEnum["January"] = "January";
    RepeatEveryYearDayMonthSelectEnum["February"] = "February";
    RepeatEveryYearDayMonthSelectEnum["March"] = "March";
    RepeatEveryYearDayMonthSelectEnum["April"] = "April";
    RepeatEveryYearDayMonthSelectEnum["May"] = "May";
    RepeatEveryYearDayMonthSelectEnum["June"] = "June";
    RepeatEveryYearDayMonthSelectEnum["July"] = "July";
    RepeatEveryYearDayMonthSelectEnum["August"] = "August";
    RepeatEveryYearDayMonthSelectEnum["September"] = "September";
    RepeatEveryYearDayMonthSelectEnum["October"] = "October";
    RepeatEveryYearDayMonthSelectEnum["November"] = "November";
    RepeatEveryYearDayMonthSelectEnum["December"] = "December";
  })(RepeatEveryYearDayMonthSelectEnum = PatchedPatchTaskRequest.RepeatEveryYearDayMonthSelectEnum || (PatchedPatchTaskRequest.RepeatEveryYearDayMonthSelectEnum = {}));
  let RepeatEveryYearDayWeekMonthSelectEnum;
  (function (RepeatEveryYearDayWeekMonthSelectEnum) {
    RepeatEveryYearDayWeekMonthSelectEnum["FirstWeek"] = "first week";
    RepeatEveryYearDayWeekMonthSelectEnum["SecondWeek"] = "second week";
    RepeatEveryYearDayWeekMonthSelectEnum["ThirdWeek"] = "third week";
    RepeatEveryYearDayWeekMonthSelectEnum["FourthWeek"] = "fourth week";
    RepeatEveryYearDayWeekMonthSelectEnum["LastWeek"] = "last week";
  })(RepeatEveryYearDayWeekMonthSelectEnum = PatchedPatchTaskRequest.RepeatEveryYearDayWeekMonthSelectEnum || (PatchedPatchTaskRequest.RepeatEveryYearDayWeekMonthSelectEnum = {}));
  let RepeatEveryYearDayWeekMonthWeekTermDayEnum;
  (function (RepeatEveryYearDayWeekMonthWeekTermDayEnum) {
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Monday"] = "Monday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Tuesday"] = "Tuesday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Wednesday"] = "Wednesday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Thursday"] = "Thursday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Friday"] = "Friday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Saturday"] = "Saturday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Sunday"] = "Sunday";
  })(RepeatEveryYearDayWeekMonthWeekTermDayEnum = PatchedPatchTaskRequest.RepeatEveryYearDayWeekMonthWeekTermDayEnum || (PatchedPatchTaskRequest.RepeatEveryYearDayWeekMonthWeekTermDayEnum = {}));
  let RepeatEveryYearDayWeekMonthMonthSelectEnum;
  (function (RepeatEveryYearDayWeekMonthMonthSelectEnum) {
    RepeatEveryYearDayWeekMonthMonthSelectEnum["January"] = "January";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["February"] = "February";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["March"] = "March";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["April"] = "April";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["May"] = "May";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["June"] = "June";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["July"] = "July";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["August"] = "August";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["September"] = "September";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["October"] = "October";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["November"] = "November";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["December"] = "December";
  })(RepeatEveryYearDayWeekMonthMonthSelectEnum = PatchedPatchTaskRequest.RepeatEveryYearDayWeekMonthMonthSelectEnum || (PatchedPatchTaskRequest.RepeatEveryYearDayWeekMonthMonthSelectEnum = {}));
  let RepeatUntilTypeEnum;
  (function (RepeatUntilTypeEnum) {
    RepeatUntilTypeEnum["NoEnd"] = "no_end";
    RepeatUntilTypeEnum["EndDate"] = "end_date";
    RepeatUntilTypeEnum["ComplatedNumber"] = "complated_number";
  })(RepeatUntilTypeEnum = PatchedPatchTaskRequest.RepeatUntilTypeEnum || (PatchedPatchTaskRequest.RepeatUntilTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["Coworker"] = "Coworker";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
  })(ServiceTypeEnum = PatchedPatchTaskRequest.ServiceTypeEnum || (PatchedPatchTaskRequest.ServiceTypeEnum = {}));
})(PatchedPatchTaskRequest || (PatchedPatchTaskRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PaymentModify;
(function (PaymentModify) {
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "Discount";
    PriceTypeEnum["ExtraCharge"] = "Extra charge";
  })(PriceTypeEnum = PaymentModify.PriceTypeEnum || (PaymentModify.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = PaymentModify.PriceChangerTypeEnum || (PaymentModify.PriceChangerTypeEnum = {}));
})(PaymentModify || (PaymentModify = {}));
var PaymentOnSpot;
(function (PaymentOnSpot) {
  let PaymentMethodEnum;
  (function (PaymentMethodEnum) {
    PaymentMethodEnum["Online"] = "Online";
    PaymentMethodEnum["Offline"] = "Offline";
  })(PaymentMethodEnum = PaymentOnSpot.PaymentMethodEnum || (PaymentOnSpot.PaymentMethodEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["ShippingProvider"] = "shipping_provider";
    ServiceTypeEnum["InsuranceProvider"] = "insurance_provider";
    ServiceTypeEnum["PaymentProvider"] = "payment_provider";
    ServiceTypeEnum["LogisticsProvider"] = "logistics_provider";
    ServiceTypeEnum["Other"] = "other";
  })(ServiceTypeEnum = PaymentOnSpot.ServiceTypeEnum || (PaymentOnSpot.ServiceTypeEnum = {}));
})(PaymentOnSpot || (PaymentOnSpot = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Pipeline;
(function (Pipeline) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["SalesTeam"] = "Sales Team";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["Recruiting"] = "Recruiting";
    ServiceTypeEnum["Invitation"] = "Invitation";
  })(ServiceTypeEnum = Pipeline.ServiceTypeEnum || (Pipeline.ServiceTypeEnum = {}));
})(Pipeline || (Pipeline = {}));
var PipelineStatus;
(function (PipelineStatus) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["SalesTeam"] = "Sales Team";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["Recruiting"] = "Recruiting";
    ServiceTypeEnum["Invitation"] = "Invitation";
  })(ServiceTypeEnum = PipelineStatus.ServiceTypeEnum || (PipelineStatus.ServiceTypeEnum = {}));
})(PipelineStatus || (PipelineStatus = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostAcceptRequest;
(function (PostAcceptRequest) {
  let InteractionStateEnum;
  (function (InteractionStateEnum) {
    InteractionStateEnum["Accept"] = "accept";
    InteractionStateEnum["Reject"] = "reject";
    InteractionStateEnum["Delete"] = "delete";
  })(InteractionStateEnum = PostAcceptRequest.InteractionStateEnum || (PostAcceptRequest.InteractionStateEnum = {}));
})(PostAcceptRequest || (PostAcceptRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PostActivityStream;
(function (PostActivityStream) {
  let ApplicationTypeEnum;
  (function (ApplicationTypeEnum) {
    ApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    ApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    ApplicationTypeEnum["Dating"] = "dating";
    ApplicationTypeEnum["Garden"] = "garden";
    ApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    ApplicationTypeEnum["SecondChance"] = "second_chance";
    ApplicationTypeEnum["BeGreen"] = "be_green";
    ApplicationTypeEnum["BucketList"] = "bucket_list";
    ApplicationTypeEnum["GoalManager"] = "goal_manager";
    ApplicationTypeEnum["Pets"] = "pets";
    ApplicationTypeEnum["Volunteer"] = "volunteer";
    ApplicationTypeEnum["Cooking"] = "cooking";
    ApplicationTypeEnum["Collectors"] = "collectors";
    ApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ApplicationTypeEnum["KidsSpace"] = "kids_space";
    ApplicationTypeEnum["Sport"] = "sport";
    ApplicationTypeEnum["Mindset"] = "mindset";
    ApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    ApplicationTypeEnum["Tech"] = "tech";
    ApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ApplicationTypeEnum["BToB"] = "b_to_b";
    ApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    ApplicationTypeEnum["Gaming"] = "gaming";
    ApplicationTypeEnum["Erotic"] = "erotic";
    ApplicationTypeEnum["Roommate"] = "roommate";
    ApplicationTypeEnum["Aviation"] = "aviation";
    ApplicationTypeEnum["Cars"] = "cars";
    ApplicationTypeEnum["Boat"] = "boat";
    ApplicationTypeEnum["Fitness"] = "fitness";
    ApplicationTypeEnum["Events"] = "events";
    ApplicationTypeEnum["TaskManager"] = "task_manager";
    ApplicationTypeEnum["Services"] = "services";
    ApplicationTypeEnum["Academy"] = "academy";
    ApplicationTypeEnum["StoreFinder"] = "store_finder";
    ApplicationTypeEnum["ActivityStream"] = "activity_stream";
    ApplicationTypeEnum["Calendar"] = "calendar";
    ApplicationTypeEnum["Contacts"] = "contacts";
    ApplicationTypeEnum["Notes"] = "notes";
    ApplicationTypeEnum["Drive"] = "drive";
    ApplicationTypeEnum["Taskly"] = "taskly";
    ApplicationTypeEnum["MarketSpace"] = "market_space";
    ApplicationTypeEnum["Chat"] = "chat";
    ApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    ApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ApplicationTypeEnum["Renting"] = "renting";
    ApplicationTypeEnum["Booking"] = "booking";
    ApplicationTypeEnum["Job"] = "job";
    ApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    ApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    ApplicationTypeEnum["EmailManager"] = "email_manager";
    ApplicationTypeEnum["RealEstate"] = "real_estate";
    ApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    ApplicationTypeEnum["Grocery"] = "grocery";
    ApplicationTypeEnum["Fashion"] = "fashion";
    ApplicationTypeEnum["HomeDesign"] = "home_design";
    ApplicationTypeEnum["Taxi"] = "taxi";
    ApplicationTypeEnum["SkillLink"] = "skill_link";
    ApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(ApplicationTypeEnum = PostActivityStream.ApplicationTypeEnum || (PostActivityStream.ApplicationTypeEnum = {}));
  let PrivacyTypeEnum;
  (function (PrivacyTypeEnum) {
    PrivacyTypeEnum["Public"] = "public";
    PrivacyTypeEnum["Followers"] = "followers";
    PrivacyTypeEnum["Private"] = "private";
  })(PrivacyTypeEnum = PostActivityStream.PrivacyTypeEnum || (PostActivityStream.PrivacyTypeEnum = {}));
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Draft"] = "draft";
    PostStateEnum["Published"] = "published";
    PostStateEnum["Scheduled"] = "scheduled";
    PostStateEnum["Deleted"] = "deleted";
  })(PostStateEnum = PostActivityStream.PostStateEnum || (PostActivityStream.PostStateEnum = {}));
  let PostViewStateEnum;
  (function (PostViewStateEnum) {
    PostViewStateEnum["Adult"] = "adult";
    PostViewStateEnum["Normal"] = "normal";
    PostViewStateEnum["Sensitive"] = "sensitive";
  })(PostViewStateEnum = PostActivityStream.PostViewStateEnum || (PostActivityStream.PostViewStateEnum = {}));
  let PostTypeEnum;
  (function (PostTypeEnum) {
    PostTypeEnum["ShareAd"] = "share_ad";
    PostTypeEnum["SharedUser"] = "shared_user";
    PostTypeEnum["Album"] = "album";
    PostTypeEnum["Colored"] = "colored";
    PostTypeEnum["ImageBackground"] = "image_background";
    PostTypeEnum["Poll"] = "poll";
    PostTypeEnum["OutsideLink"] = "outside_link";
    PostTypeEnum["Swap"] = "swap";
    PostTypeEnum["Donate"] = "donate";
    PostTypeEnum["DonationWish"] = "donation_wish";
    PostTypeEnum["Adoption"] = "adoption";
    PostTypeEnum["Lost"] = "lost";
    PostTypeEnum["Find"] = "find";
    PostTypeEnum["Ad"] = "ad";
    PostTypeEnum["Catalog"] = "catalog";
  })(PostTypeEnum = PostActivityStream.PostTypeEnum || (PostActivityStream.PostTypeEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = PostActivityStream.IsReportedEnum || (PostActivityStream.IsReportedEnum = {}));
  let PollTypeEnum;
  (function (PollTypeEnum) {
    PollTypeEnum["Single"] = "single";
    PollTypeEnum["Multiple"] = "multiple";
  })(PollTypeEnum = PostActivityStream.PollTypeEnum || (PostActivityStream.PollTypeEnum = {}));
  let StreamTypeEnum;
  (function (StreamTypeEnum) {
    StreamTypeEnum["CompanyStream"] = "company_stream";
    StreamTypeEnum["CoreStream"] = "core_stream";
  })(StreamTypeEnum = PostActivityStream.StreamTypeEnum || (PostActivityStream.StreamTypeEnum = {}));
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
  })(CatalogTypeEnum = PostActivityStream.CatalogTypeEnum || (PostActivityStream.CatalogTypeEnum = {}));
  let CatalogTypeWishEnum;
  (function (CatalogTypeWishEnum) {
    CatalogTypeWishEnum["Plant"] = "plant";
    CatalogTypeWishEnum["Animal"] = "animal";
    CatalogTypeWishEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeWishEnum["Food"] = "food";
    CatalogTypeWishEnum["BucketList"] = "bucket_list";
    CatalogTypeWishEnum["Cooking"] = "cooking";
  })(CatalogTypeWishEnum = PostActivityStream.CatalogTypeWishEnum || (PostActivityStream.CatalogTypeWishEnum = {}));
})(PostActivityStream || (PostActivityStream = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostAlbumRequest;
(function (PostAlbumRequest) {
  let MediaShareEnum;
  (function (MediaShareEnum) {
    MediaShareEnum["Nobody"] = "nobody";
    MediaShareEnum["Anybody"] = "anybody";
    MediaShareEnum["ThoseFollowMe"] = "those_follow_me";
    MediaShareEnum["WhoIFollow"] = "who_i_follow";
    MediaShareEnum["Both"] = "both";
  })(MediaShareEnum = PostAlbumRequest.MediaShareEnum || (PostAlbumRequest.MediaShareEnum = {}));
})(PostAlbumRequest || (PostAlbumRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostAvaibilityRequest;
(function (PostAvaibilityRequest) {
  let OpenEnum;
  (function (OpenEnum) {
    OpenEnum["OpenNonstop"] = "open_nonstop";
    OpenEnum["OpenByTime"] = "open_by_time";
    OpenEnum["Close"] = "close";
  })(OpenEnum = PostAvaibilityRequest.OpenEnum || (PostAvaibilityRequest.OpenEnum = {}));
})(PostAvaibilityRequest || (PostAvaibilityRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostBankDatabaseRequest;
(function (PostBankDatabaseRequest) {
  let BankTypeEnum;
  (function (BankTypeEnum) {
    BankTypeEnum["Traditional"] = "Traditional";
    BankTypeEnum["CompletelyOnline"] = "Completely_online";
  })(BankTypeEnum = PostBankDatabaseRequest.BankTypeEnum || (PostBankDatabaseRequest.BankTypeEnum = {}));
})(PostBankDatabaseRequest || (PostBankDatabaseRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostBasicCalculationRequest;
(function (PostBasicCalculationRequest) {
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["FreeShipping"] = "Free shipping";
    CalculationTypeEnum["ItemBased"] = "Item based";
    CalculationTypeEnum["NetTotal"] = "Net total";
    CalculationTypeEnum["NetWeight"] = "Net weight";
    CalculationTypeEnum["NetLength"] = "Net length";
    CalculationTypeEnum["NetVolume"] = "Net volume";
  })(CalculationTypeEnum = PostBasicCalculationRequest.CalculationTypeEnum || (PostBasicCalculationRequest.CalculationTypeEnum = {}));
})(PostBasicCalculationRequest || (PostBasicCalculationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostBulkKeyWordServiceRequest;
(function (PostBulkKeyWordServiceRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Country"] = "country";
    ServiceTypeEnum["Street"] = "street";
    ServiceTypeEnum["City"] = "city";
    ServiceTypeEnum["State"] = "state";
    ServiceTypeEnum["Business"] = "business";
    ServiceTypeEnum["User"] = "user";
    ServiceTypeEnum["CrmProspect"] = "crm_prospect";
    ServiceTypeEnum["LocationManager"] = "location_manager";
    ServiceTypeEnum["Task"] = "task";
    ServiceTypeEnum["Application"] = "application";
    ServiceTypeEnum["Academy"] = "academy";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["PaymentOnSpot"] = "payment_on_spot";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["CustomField"] = "custom_field";
    ServiceTypeEnum["CoWorker"] = "co_worker";
    ServiceTypeEnum["SystemTaxRule"] = "system_tax_rule";
    ServiceTypeEnum["ProjectWorkgroup"] = "project_workgroup";
    ServiceTypeEnum["CrmTargetedCustomer"] = "crm_targeted_customer";
    ServiceTypeEnum["Contact"] = "contact";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["LanguageExchange"] = "language_exchange";
    ServiceTypeEnum["BusinessNetworking"] = "business_networking";
    ServiceTypeEnum["Dating"] = "dating";
    ServiceTypeEnum["Garden"] = "garden";
    ServiceTypeEnum["TravelBuddies"] = "travel_buddies";
    ServiceTypeEnum["SecondChance"] = "second_chance";
    ServiceTypeEnum["BeGreen"] = "be_green";
    ServiceTypeEnum["BucketList"] = "bucket_list";
    ServiceTypeEnum["GoalManager"] = "goal_manager";
    ServiceTypeEnum["Pets"] = "pets";
    ServiceTypeEnum["Volunteer"] = "volunteer";
    ServiceTypeEnum["Cooking"] = "cooking";
    ServiceTypeEnum["Collectors"] = "collectors";
    ServiceTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ServiceTypeEnum["KidsSpace"] = "kids_space";
    ServiceTypeEnum["Sport"] = "sport";
    ServiceTypeEnum["Mindset"] = "mindset";
    ServiceTypeEnum["DiyHandmade"] = "diy_handmade";
    ServiceTypeEnum["Tech"] = "tech";
    ServiceTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ServiceTypeEnum["BToB"] = "b_to_b";
    ServiceTypeEnum["DigitalNomad"] = "digital_nomad";
    ServiceTypeEnum["Gaming"] = "gaming";
    ServiceTypeEnum["Erotic"] = "erotic";
    ServiceTypeEnum["Roommate"] = "roommate";
    ServiceTypeEnum["Aviation"] = "aviation";
    ServiceTypeEnum["Cars"] = "cars";
    ServiceTypeEnum["Boat"] = "boat";
    ServiceTypeEnum["Fitness"] = "fitness";
    ServiceTypeEnum["Events"] = "events";
    ServiceTypeEnum["SkillLink"] = "skill_link";
  })(ServiceTypeEnum = PostBulkKeyWordServiceRequest.ServiceTypeEnum || (PostBulkKeyWordServiceRequest.ServiceTypeEnum = {}));
  let KeywordTypeEnum;
  (function (KeywordTypeEnum) {
    KeywordTypeEnum["Universal"] = "universal";
    KeywordTypeEnum["Skill"] = "skill";
    KeywordTypeEnum["JobTitle"] = "job_title";
    KeywordTypeEnum["Interest"] = "interest";
    KeywordTypeEnum["Catalog"] = "catalog";
    KeywordTypeEnum["Academy"] = "academy";
    KeywordTypeEnum["Application"] = "application";
    KeywordTypeEnum["Industry"] = "industry";
    KeywordTypeEnum["Garden"] = "garden";
    KeywordTypeEnum["IndustryCategory"] = "industry_category";
    KeywordTypeEnum["BucketListCategory"] = "bucket_list_category";
    KeywordTypeEnum["PaymentOnSpot"] = "payment_on_spot";
    KeywordTypeEnum["CategoryTemplate"] = "category_template";
    KeywordTypeEnum["CustomField"] = "custom_field";
    KeywordTypeEnum["SystemTaxRule"] = "system_tax_rule";
    KeywordTypeEnum["SmartAd"] = "smart_ad";
    KeywordTypeEnum["CrmTargetedCustomer"] = "crm_targeted_customer";
  })(KeywordTypeEnum = PostBulkKeyWordServiceRequest.KeywordTypeEnum || (PostBulkKeyWordServiceRequest.KeywordTypeEnum = {}));
})(PostBulkKeyWordServiceRequest || (PostBulkKeyWordServiceRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostBulkSmartPaymentRequest;
(function (PostBulkSmartPaymentRequest) {
  let PaymentTypeEnum;
  (function (PaymentTypeEnum) {
    PaymentTypeEnum["Offline"] = "Offline";
    PaymentTypeEnum["Direct"] = "Direct";
    PaymentTypeEnum["BankTransfer"] = "Bank transfer";
  })(PaymentTypeEnum = PostBulkSmartPaymentRequest.PaymentTypeEnum || (PostBulkSmartPaymentRequest.PaymentTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["SmartCube"] = "smart_cube";
    ServiceTypeEnum["LocationManager"] = "location_manager";
    ServiceTypeEnum["SmartLocationManager"] = "smart_location_manager";
  })(ServiceTypeEnum = PostBulkSmartPaymentRequest.ServiceTypeEnum || (PostBulkSmartPaymentRequest.ServiceTypeEnum = {}));
})(PostBulkSmartPaymentRequest || (PostBulkSmartPaymentRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostBulkUniversalLanguageRequest;
(function (PostBulkUniversalLanguageRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["AlbumDetail"] = "album_detail";
    ServiceTypeEnum["AdMediaDetail"] = "ad_media_detail";
    ServiceTypeEnum["CustomFieldDetail"] = "custom_field_detail";
    ServiceTypeEnum["CustomFieldPrefix"] = "custom_field_prefix";
    ServiceTypeEnum["CustomFieldSuffix"] = "custom_field_suffix";
    ServiceTypeEnum["SiUnitDetail"] = "si_unit_detail";
    ServiceTypeEnum["DigitalProduct"] = "digital_product";
    ServiceTypeEnum["PhysicalProduct"] = "physical_product";
    ServiceTypeEnum["Rent"] = "rent";
    ServiceTypeEnum["DigitalMedia"] = "digital_media";
    ServiceTypeEnum["Booking"] = "booking";
    ServiceTypeEnum["Job"] = "job";
    ServiceTypeEnum["FoodBeverage"] = "food_beverage";
    ServiceTypeEnum["ServiceOffered"] = "service_offered";
    ServiceTypeEnum["ActivityStream"] = "activity_stream";
    ServiceTypeEnum["ActivityStreamPoll"] = "activity_stream_poll";
    ServiceTypeEnum["Product"] = "product";
    ServiceTypeEnum["TargetMarket"] = "target_market";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["ProductLongDescription"] = "product_long_description";
    ServiceTypeEnum["DisclaimerPolicySmartWorkflow"] = "disclaimer_policy_smart_workflow";
    ServiceTypeEnum["ShippingPolicySmartWorkflow"] = "shipping_policy_smart_workflow";
    ServiceTypeEnum["InsurancePolicySmartWorkflow"] = "insurance_policy_smart_workflow";
    ServiceTypeEnum["RefundPolicySmartWorkflow"] = "refund_policy_smart_workflow";
    ServiceTypeEnum["DomainPreviewWidgetCollection"] = "domain_preview_widget_collection";
    ServiceTypeEnum["DomainPreviewSite"] = "domain_preview_site";
    ServiceTypeEnum["DomainPreviewPage"] = "domain_preview_page";
    ServiceTypeEnum["MarketDiscount"] = "market_discount";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartItemLongDescription"] = "smart_item_long_description";
    ServiceTypeEnum["SmartAdLongDescription"] = "smart_ad_long_description";
  })(ServiceTypeEnum = PostBulkUniversalLanguageRequest.ServiceTypeEnum || (PostBulkUniversalLanguageRequest.ServiceTypeEnum = {}));
})(PostBulkUniversalLanguageRequest || (PostBulkUniversalLanguageRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCalendarPriceItemRequest;
(function (PostCalendarPriceItemRequest) {
  let BasePriceTypeEnum;
  (function (BasePriceTypeEnum) {
    BasePriceTypeEnum["Minute"] = "minute";
    BasePriceTypeEnum["Hour"] = "hour";
    BasePriceTypeEnum["Day"] = "day";
    BasePriceTypeEnum["Week"] = "week";
    BasePriceTypeEnum["Month"] = "month";
  })(BasePriceTypeEnum = PostCalendarPriceItemRequest.BasePriceTypeEnum || (PostCalendarPriceItemRequest.BasePriceTypeEnum = {}));
})(PostCalendarPriceItemRequest || (PostCalendarPriceItemRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCalendarPriceItemWokrFlowRequest;
(function (PostCalendarPriceItemWokrFlowRequest) {
  let BasePriceTypeEnum;
  (function (BasePriceTypeEnum) {
    BasePriceTypeEnum["Minute"] = "minute";
    BasePriceTypeEnum["Hour"] = "hour";
    BasePriceTypeEnum["Day"] = "day";
    BasePriceTypeEnum["Week"] = "week";
    BasePriceTypeEnum["Month"] = "month";
  })(BasePriceTypeEnum = PostCalendarPriceItemWokrFlowRequest.BasePriceTypeEnum || (PostCalendarPriceItemWokrFlowRequest.BasePriceTypeEnum = {}));
})(PostCalendarPriceItemWokrFlowRequest || (PostCalendarPriceItemWokrFlowRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCatalogMultiRequest;
(function (PostCatalogMultiRequest) {
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
    CatalogTypeEnum["FoodComponent"] = "food_component";
    CatalogTypeEnum["Drink"] = "drink";
    CatalogTypeEnum["ServiceTask"] = "service_task";
  })(CatalogTypeEnum = PostCatalogMultiRequest.CatalogTypeEnum || (PostCatalogMultiRequest.CatalogTypeEnum = {}));
})(PostCatalogMultiRequest || (PostCatalogMultiRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCatalogRequest;
(function (PostCatalogRequest) {
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
    CatalogTypeEnum["FoodComponent"] = "food_component";
    CatalogTypeEnum["Drink"] = "drink";
    CatalogTypeEnum["ServiceTask"] = "service_task";
  })(CatalogTypeEnum = PostCatalogRequest.CatalogTypeEnum || (PostCatalogRequest.CatalogTypeEnum = {}));
})(PostCatalogRequest || (PostCatalogRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCategoryTemplateRequest;
(function (PostCategoryTemplateRequest) {
  let CategoryTypeEnum;
  (function (CategoryTypeEnum) {
    CategoryTypeEnum["ServiceOfferedTemplate"] = "service_offered_template";
    CategoryTypeEnum["RentTemplate"] = "rent_template";
    CategoryTypeEnum["PhysicalProductTemplate"] = "physical_product_template";
    CategoryTypeEnum["DigitalProductTemplate"] = "digital_product_template";
    CategoryTypeEnum["DigitalMediaTemplate"] = "digital_media_template";
    CategoryTypeEnum["JobTemplate"] = "job_template";
    CategoryTypeEnum["BookingTemplate"] = "booking_template";
    CategoryTypeEnum["FoodBeverageTemplate"] = "food_beverage_template";
    CategoryTypeEnum["DomainPreviewPageTemplate"] = "domain_preview_page_template";
    CategoryTypeEnum["DomainPreviewSiteTemplate"] = "domain_preview_site_template";
    CategoryTypeEnum["DomainPreviewWidgetCollectionTemplate"] = "domain_preview_widget_collection_template";
    CategoryTypeEnum["CustomField"] = "custom_field";
    CategoryTypeEnum["Plant"] = "plant";
    CategoryTypeEnum["Animal"] = "animal";
    CategoryTypeEnum["App"] = "app";
    CategoryTypeEnum["Food"] = "food";
    CategoryTypeEnum["BucketList"] = "bucket_list";
    CategoryTypeEnum["Drink"] = "drink";
    CategoryTypeEnum["FoodComponent"] = "food_component";
  })(CategoryTypeEnum = PostCategoryTemplateRequest.CategoryTypeEnum || (PostCategoryTemplateRequest.CategoryTypeEnum = {}));
})(PostCategoryTemplateRequest || (PostCategoryTemplateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCloudStorageRequest;
(function (PostCloudStorageRequest) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["User"] = "user";
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["CoWorker"] = "co_worker";
  })(EntityTypeEnum = PostCloudStorageRequest.EntityTypeEnum || (PostCloudStorageRequest.EntityTypeEnum = {}));
})(PostCloudStorageRequest || (PostCloudStorageRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCommentRequest;
(function (PostCommentRequest) {
  let StreamTypeEnum;
  (function (StreamTypeEnum) {
    StreamTypeEnum["CompanyStream"] = "company_stream";
    StreamTypeEnum["CoreStream"] = "core_stream";
  })(StreamTypeEnum = PostCommentRequest.StreamTypeEnum || (PostCommentRequest.StreamTypeEnum = {}));
})(PostCommentRequest || (PostCommentRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCommunityApplicationSettingsRequest;
(function (PostCommunityApplicationSettingsRequest) {
  let MeetTypeEnum;
  (function (MeetTypeEnum) {
    MeetTypeEnum["MessageCallPersonal"] = "message_call_personal";
    MeetTypeEnum["VideoAndAudioCall"] = "videoAndAudioCall";
    MeetTypeEnum["TextAndAudioCall"] = "textAndAudioCall";
    MeetTypeEnum["MeetInPerson"] = "meetInPerson";
  })(MeetTypeEnum = PostCommunityApplicationSettingsRequest.MeetTypeEnum || (PostCommunityApplicationSettingsRequest.MeetTypeEnum = {}));
  let MeetGenderEnum;
  (function (MeetGenderEnum) {
    MeetGenderEnum["Male"] = "male";
    MeetGenderEnum["Female"] = "female";
    MeetGenderEnum["Other"] = "other";
    MeetGenderEnum["FemaleMale"] = "female_male";
  })(MeetGenderEnum = PostCommunityApplicationSettingsRequest.MeetGenderEnum || (PostCommunityApplicationSettingsRequest.MeetGenderEnum = {}));
  let ProfileTypeEnum;
  (function (ProfileTypeEnum) {
    ProfileTypeEnum["LanguageExchange"] = "language_exchange";
    ProfileTypeEnum["BusinessNetworking"] = "business_networking";
    ProfileTypeEnum["Dating"] = "dating";
    ProfileTypeEnum["Garden"] = "garden";
    ProfileTypeEnum["TravelBuddies"] = "travel_buddies";
    ProfileTypeEnum["SecondChance"] = "second_chance";
    ProfileTypeEnum["BeGreen"] = "be_green";
    ProfileTypeEnum["BucketList"] = "bucket_list";
    ProfileTypeEnum["GoalManager"] = "goal_manager";
    ProfileTypeEnum["Pets"] = "pets";
    ProfileTypeEnum["Volunteer"] = "volunteer";
    ProfileTypeEnum["Cooking"] = "cooking";
    ProfileTypeEnum["Collectors"] = "collectors";
    ProfileTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ProfileTypeEnum["KidsSpace"] = "kids_space";
    ProfileTypeEnum["Sport"] = "sport";
    ProfileTypeEnum["Mindset"] = "mindset";
    ProfileTypeEnum["DiyHandmade"] = "diy_handmade";
    ProfileTypeEnum["Tech"] = "tech";
    ProfileTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ProfileTypeEnum["BToB"] = "b_to_b";
    ProfileTypeEnum["DigitalNomad"] = "digital_nomad";
    ProfileTypeEnum["Gaming"] = "gaming";
    ProfileTypeEnum["Erotic"] = "erotic";
    ProfileTypeEnum["Roommate"] = "roommate";
    ProfileTypeEnum["Aviation"] = "aviation";
    ProfileTypeEnum["Cars"] = "cars";
    ProfileTypeEnum["Boat"] = "boat";
    ProfileTypeEnum["Fitness"] = "fitness";
    ProfileTypeEnum["Events"] = "events";
    ProfileTypeEnum["TaskManager"] = "task_manager";
    ProfileTypeEnum["Services"] = "services";
    ProfileTypeEnum["Academy"] = "academy";
    ProfileTypeEnum["StoreFinder"] = "store_finder";
    ProfileTypeEnum["ActivityStream"] = "activity_stream";
    ProfileTypeEnum["Calendar"] = "calendar";
    ProfileTypeEnum["Contacts"] = "contacts";
    ProfileTypeEnum["Notes"] = "notes";
    ProfileTypeEnum["Drive"] = "drive";
    ProfileTypeEnum["Taskly"] = "taskly";
    ProfileTypeEnum["MarketSpace"] = "market_space";
    ProfileTypeEnum["Chat"] = "chat";
    ProfileTypeEnum["DoctorFinder"] = "doctor_finder";
    ProfileTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ProfileTypeEnum["Renting"] = "renting";
    ProfileTypeEnum["Booking"] = "booking";
    ProfileTypeEnum["Job"] = "job";
    ProfileTypeEnum["DigitalDownloads"] = "digital_downloads";
    ProfileTypeEnum["PhysicalProduct"] = "physical_product";
    ProfileTypeEnum["EmailManager"] = "email_manager";
    ProfileTypeEnum["RealEstate"] = "real_estate";
    ProfileTypeEnum["CoworkingSpace"] = "coworking_space";
    ProfileTypeEnum["Grocery"] = "grocery";
    ProfileTypeEnum["Fashion"] = "fashion";
    ProfileTypeEnum["HomeDesign"] = "home_design";
    ProfileTypeEnum["Taxi"] = "taxi";
    ProfileTypeEnum["SkillLink"] = "skill_link";
    ProfileTypeEnum["MediaChannels"] = "media_channels";
  })(ProfileTypeEnum = PostCommunityApplicationSettingsRequest.ProfileTypeEnum || (PostCommunityApplicationSettingsRequest.ProfileTypeEnum = {}));
  let WhoCanSeeWishesEnum;
  (function (WhoCanSeeWishesEnum) {
    WhoCanSeeWishesEnum["Nobody"] = "nobody";
    WhoCanSeeWishesEnum["Anybody"] = "anybody";
    WhoCanSeeWishesEnum["Followers"] = "followers";
  })(WhoCanSeeWishesEnum = PostCommunityApplicationSettingsRequest.WhoCanSeeWishesEnum || (PostCommunityApplicationSettingsRequest.WhoCanSeeWishesEnum = {}));
  let WhoCanSeeSavedEnum;
  (function (WhoCanSeeSavedEnum) {
    WhoCanSeeSavedEnum["Nobody"] = "nobody";
    WhoCanSeeSavedEnum["Anybody"] = "anybody";
    WhoCanSeeSavedEnum["Followers"] = "followers";
  })(WhoCanSeeSavedEnum = PostCommunityApplicationSettingsRequest.WhoCanSeeSavedEnum || (PostCommunityApplicationSettingsRequest.WhoCanSeeSavedEnum = {}));
  let WhoCanSeeMyLocationEnum;
  (function (WhoCanSeeMyLocationEnum) {
    WhoCanSeeMyLocationEnum["Nobody"] = "nobody";
    WhoCanSeeMyLocationEnum["Anybody"] = "anybody";
    WhoCanSeeMyLocationEnum["Followers"] = "followers";
  })(WhoCanSeeMyLocationEnum = PostCommunityApplicationSettingsRequest.WhoCanSeeMyLocationEnum || (PostCommunityApplicationSettingsRequest.WhoCanSeeMyLocationEnum = {}));
  let WhoCanSeeMyCityEnum;
  (function (WhoCanSeeMyCityEnum) {
    WhoCanSeeMyCityEnum["Nobody"] = "nobody";
    WhoCanSeeMyCityEnum["Anybody"] = "anybody";
    WhoCanSeeMyCityEnum["Followers"] = "followers";
  })(WhoCanSeeMyCityEnum = PostCommunityApplicationSettingsRequest.WhoCanSeeMyCityEnum || (PostCommunityApplicationSettingsRequest.WhoCanSeeMyCityEnum = {}));
  let WhoCanSeeMapEnum;
  (function (WhoCanSeeMapEnum) {
    WhoCanSeeMapEnum["Nobody"] = "nobody";
    WhoCanSeeMapEnum["Anybody"] = "anybody";
    WhoCanSeeMapEnum["Followers"] = "followers";
  })(WhoCanSeeMapEnum = PostCommunityApplicationSettingsRequest.WhoCanSeeMapEnum || (PostCommunityApplicationSettingsRequest.WhoCanSeeMapEnum = {}));
  let WhoIFollowEnum;
  (function (WhoIFollowEnum) {
    WhoIFollowEnum["Nobody"] = "nobody";
    WhoIFollowEnum["Anybody"] = "anybody";
    WhoIFollowEnum["Followers"] = "followers";
  })(WhoIFollowEnum = PostCommunityApplicationSettingsRequest.WhoIFollowEnum || (PostCommunityApplicationSettingsRequest.WhoIFollowEnum = {}));
})(PostCommunityApplicationSettingsRequest || (PostCommunityApplicationSettingsRequest = {}));
var PostContentRequest;
(function (PostContentRequest) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = PostContentRequest.SharedTypeEnum || (PostContentRequest.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
    TypeEnum["Website"] = "website";
    TypeEnum["SocialMedia"] = "social_media";
    TypeEnum["Other2"] = "other";
  })(TypeEnum = PostContentRequest.TypeEnum || (PostContentRequest.TypeEnum = {}));
  let PhoneTypeEnum;
  (function (PhoneTypeEnum) {
    PhoneTypeEnum["Mobile"] = "Mobile";
    PhoneTypeEnum["Landline"] = "Landline";
  })(PhoneTypeEnum = PostContentRequest.PhoneTypeEnum || (PostContentRequest.PhoneTypeEnum = {}));
  let SocialMediaTypeEnum;
  (function (SocialMediaTypeEnum) {
    SocialMediaTypeEnum["Facebook"] = "facebook";
    SocialMediaTypeEnum["Twitter"] = "twitter";
    SocialMediaTypeEnum["Linkedin"] = "linkedin";
    SocialMediaTypeEnum["Instagram"] = "instagram";
    SocialMediaTypeEnum["Youtube"] = "youtube";
    SocialMediaTypeEnum["Pinterest"] = "pinterest";
    SocialMediaTypeEnum["Tumblr"] = "tumblr";
    SocialMediaTypeEnum["Snapchat"] = "snapchat";
    SocialMediaTypeEnum["Reddit"] = "reddit";
    SocialMediaTypeEnum["Flickr"] = "flickr";
    SocialMediaTypeEnum["Vimeo"] = "vimeo";
    SocialMediaTypeEnum["Whatsapp"] = "whatsapp";
    SocialMediaTypeEnum["Telegram"] = "telegram";
    SocialMediaTypeEnum["Wechat"] = "wechat";
    SocialMediaTypeEnum["Line"] = "line";
    SocialMediaTypeEnum["Viber"] = "viber";
    SocialMediaTypeEnum["Skype"] = "skype";
    SocialMediaTypeEnum["Qq"] = "qq";
    SocialMediaTypeEnum["Kakaotalk"] = "kakaotalk";
    SocialMediaTypeEnum["Tiktok"] = "tiktok";
    SocialMediaTypeEnum["Medium"] = "medium";
    SocialMediaTypeEnum["Quora"] = "quora";
    SocialMediaTypeEnum["Twitch"] = "twitch";
    SocialMediaTypeEnum["Soundcloud"] = "soundcloud";
    SocialMediaTypeEnum["Spotify"] = "spotify";
    SocialMediaTypeEnum["Github"] = "github";
    SocialMediaTypeEnum["Dribbble"] = "dribbble";
    SocialMediaTypeEnum["Behance"] = "behance";
    SocialMediaTypeEnum["Deviantart"] = "deviantart";
    SocialMediaTypeEnum["Foursquare"] = "foursquare";
    SocialMediaTypeEnum["Meetup"] = "meetup";
    SocialMediaTypeEnum["Vk"] = "vk";
    SocialMediaTypeEnum["Ok"] = "ok";
    SocialMediaTypeEnum["Myspace"] = "myspace";
    SocialMediaTypeEnum["Renren"] = "renren";
    SocialMediaTypeEnum["Weibo"] = "weibo";
    SocialMediaTypeEnum["Xing"] = "xing";
  })(SocialMediaTypeEnum = PostContentRequest.SocialMediaTypeEnum || (PostContentRequest.SocialMediaTypeEnum = {}));
})(PostContentRequest || (PostContentRequest = {}));
var PostContentResponse;
(function (PostContentResponse) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = PostContentResponse.SharedTypeEnum || (PostContentResponse.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
    TypeEnum["Website"] = "website";
    TypeEnum["SocialMedia"] = "social_media";
    TypeEnum["Other2"] = "other";
  })(TypeEnum = PostContentResponse.TypeEnum || (PostContentResponse.TypeEnum = {}));
  let PhoneTypeEnum;
  (function (PhoneTypeEnum) {
    PhoneTypeEnum["Mobile"] = "Mobile";
    PhoneTypeEnum["Landline"] = "Landline";
  })(PhoneTypeEnum = PostContentResponse.PhoneTypeEnum || (PostContentResponse.PhoneTypeEnum = {}));
})(PostContentResponse || (PostContentResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCrmAddressRequest;
(function (PostCrmAddressRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = PostCrmAddressRequest.TypeEnum || (PostCrmAddressRequest.TypeEnum = {}));
})(PostCrmAddressRequest || (PostCrmAddressRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCrmEmailRequest;
(function (PostCrmEmailRequest) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = PostCrmEmailRequest.SharedTypeEnum || (PostCrmEmailRequest.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
  })(TypeEnum = PostCrmEmailRequest.TypeEnum || (PostCrmEmailRequest.TypeEnum = {}));
})(PostCrmEmailRequest || (PostCrmEmailRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCrmNotifyRequest;
(function (PostCrmNotifyRequest) {
  let NotifyTypeEnum;
  (function (NotifyTypeEnum) {
    NotifyTypeEnum["Notify"] = "notify";
    NotifyTypeEnum["BetaTest"] = "beta_test";
  })(NotifyTypeEnum = PostCrmNotifyRequest.NotifyTypeEnum || (PostCrmNotifyRequest.NotifyTypeEnum = {}));
})(PostCrmNotifyRequest || (PostCrmNotifyRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCrmPhoneRequest;
(function (PostCrmPhoneRequest) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = PostCrmPhoneRequest.SharedTypeEnum || (PostCrmPhoneRequest.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
  })(TypeEnum = PostCrmPhoneRequest.TypeEnum || (PostCrmPhoneRequest.TypeEnum = {}));
  let PhoneTypeEnum;
  (function (PhoneTypeEnum) {
    PhoneTypeEnum["Mobile"] = "Mobile";
    PhoneTypeEnum["Landline"] = "Landline";
  })(PhoneTypeEnum = PostCrmPhoneRequest.PhoneTypeEnum || (PostCrmPhoneRequest.PhoneTypeEnum = {}));
})(PostCrmPhoneRequest || (PostCrmPhoneRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostCustomFieldAreaRequest;
(function (PostCustomFieldAreaRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["Product"] = "product";
    ServiceTypeEnum["CrmProspectPerson"] = "crm_prospect_person";
    ServiceTypeEnum["CrmProspectBusiness"] = "crm_prospect_business";
  })(ServiceTypeEnum = PostCustomFieldAreaRequest.ServiceTypeEnum || (PostCustomFieldAreaRequest.ServiceTypeEnum = {}));
})(PostCustomFieldAreaRequest || (PostCustomFieldAreaRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PostCustomFieldCategoryRequest;
(function (PostCustomFieldCategoryRequest) {
  let SiFilterAccessEnum;
  (function (SiFilterAccessEnum) {
    SiFilterAccessEnum["Yes"] = "Yes";
    SiFilterAccessEnum["No"] = "No";
    SiFilterAccessEnum["Currency"] = "Currency";
  })(SiFilterAccessEnum = PostCustomFieldCategoryRequest.SiFilterAccessEnum || (PostCustomFieldCategoryRequest.SiFilterAccessEnum = {}));
  let SiUnitTypeFilterEnum;
  (function (SiUnitTypeFilterEnum) {
    SiUnitTypeFilterEnum["Length"] = "Length";
    SiUnitTypeFilterEnum["Weight"] = "Weight";
    SiUnitTypeFilterEnum["Volume"] = "Volume";
    SiUnitTypeFilterEnum["Area"] = "Area";
    SiUnitTypeFilterEnum["Time"] = "Time";
    SiUnitTypeFilterEnum["Frequency"] = "Frequency";
    SiUnitTypeFilterEnum["Speed"] = "Speed";
    SiUnitTypeFilterEnum["Mass"] = "Mass";
    SiUnitTypeFilterEnum["Temperature"] = "Temperature";
    SiUnitTypeFilterEnum["Current"] = "Current";
    SiUnitTypeFilterEnum["PrintingUnit"] = "Printing_unit";
    SiUnitTypeFilterEnum["Luminosity"] = "Luminosity";
    SiUnitTypeFilterEnum["Substance"] = "Substance";
    SiUnitTypeFilterEnum["Quantity"] = "Quantity";
  })(SiUnitTypeFilterEnum = PostCustomFieldCategoryRequest.SiUnitTypeFilterEnum || (PostCustomFieldCategoryRequest.SiUnitTypeFilterEnum = {}));
  let CustomTypeEnum;
  (function (CustomTypeEnum) {
    CustomTypeEnum["Text"] = "text";
    CustomTypeEnum["Radio"] = "radio";
    CustomTypeEnum["Checkbox"] = "checkbox";
    CustomTypeEnum["Dropdown"] = "dropdown";
    CustomTypeEnum["MultiSelect"] = "multi_select";
    CustomTypeEnum["InputNumber"] = "input_number";
    CustomTypeEnum["Range"] = "range";
  })(CustomTypeEnum = PostCustomFieldCategoryRequest.CustomTypeEnum || (PostCustomFieldCategoryRequest.CustomTypeEnum = {}));
  let PrivacyTypeEnum;
  (function (PrivacyTypeEnum) {
    PrivacyTypeEnum["Public"] = "public";
    PrivacyTypeEnum["Private"] = "private";
  })(PrivacyTypeEnum = PostCustomFieldCategoryRequest.PrivacyTypeEnum || (PostCustomFieldCategoryRequest.PrivacyTypeEnum = {}));
})(PostCustomFieldCategoryRequest || (PostCustomFieldCategoryRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostDealRatingsRequest;
(function (PostDealRatingsRequest) {
  let SaleTypeEnum;
  (function (SaleTypeEnum) {
    SaleTypeEnum["Procurement"] = "Procurement";
    SaleTypeEnum["Selling"] = "Selling";
  })(SaleTypeEnum = PostDealRatingsRequest.SaleTypeEnum || (PostDealRatingsRequest.SaleTypeEnum = {}));
  let PartnerTypeEnum;
  (function (PartnerTypeEnum) {
    PartnerTypeEnum["Buyer"] = "Buyer";
    PartnerTypeEnum["Seller"] = "Seller";
  })(PartnerTypeEnum = PostDealRatingsRequest.PartnerTypeEnum || (PostDealRatingsRequest.PartnerTypeEnum = {}));
})(PostDealRatingsRequest || (PostDealRatingsRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostDomainRequest;
(function (PostDomainRequest) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
  })(StatusEnum = PostDomainRequest.StatusEnum || (PostDomainRequest.StatusEnum = {}));
})(PostDomainRequest || (PostDomainRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostDomainSiteRequest;
(function (PostDomainSiteRequest) {
  let StateEnum;
  (function (StateEnum) {
    StateEnum["Published"] = "published";
    StateEnum["Inactive"] = "inactive";
  })(StateEnum = PostDomainSiteRequest.StateEnum || (PostDomainSiteRequest.StateEnum = {}));
})(PostDomainSiteRequest || (PostDomainSiteRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostDomainTranslationRequest;
(function (PostDomainTranslationRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["PreviewPage"] = "preview_page";
    ServiceTypeEnum["PreviewSite"] = "preview_site";
    ServiceTypeEnum["PreviewWidgetCollection"] = "preview_widget_collection";
    ServiceTypeEnum["PreviewPageMeta"] = "preview_page_meta";
    ServiceTypeEnum["PreviewSiteToPage"] = "preview_site_to_page";
    ServiceTypeEnum["Page"] = "page";
    ServiceTypeEnum["Site"] = "site";
    ServiceTypeEnum["Widget"] = "widget";
    ServiceTypeEnum["Meta"] = "meta";
  })(ServiceTypeEnum = PostDomainTranslationRequest.ServiceTypeEnum || (PostDomainTranslationRequest.ServiceTypeEnum = {}));
})(PostDomainTranslationRequest || (PostDomainTranslationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostDriveAlbumRequest;
(function (PostDriveAlbumRequest) {
  let MediaShareEnum;
  (function (MediaShareEnum) {
    MediaShareEnum["Nobody"] = "nobody";
    MediaShareEnum["Anybody"] = "anybody";
    MediaShareEnum["ThoseFollowMe"] = "those_follow_me";
    MediaShareEnum["WhoIFollow"] = "who_i_follow";
    MediaShareEnum["Both"] = "both";
  })(MediaShareEnum = PostDriveAlbumRequest.MediaShareEnum || (PostDriveAlbumRequest.MediaShareEnum = {}));
})(PostDriveAlbumRequest || (PostDriveAlbumRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostElasticSearchBusinessRequest;
(function (PostElasticSearchBusinessRequest) {
  let EntityTypeListEnum;
  (function (EntityTypeListEnum) {
    EntityTypeListEnum["User"] = "user";
    EntityTypeListEnum["Business"] = "business";
    EntityTypeListEnum["CoWorker"] = "co_worker";
    EntityTypeListEnum["Ad"] = "ad";
    EntityTypeListEnum["Catalog"] = "catalog";
    EntityTypeListEnum["App"] = "app";
    EntityTypeListEnum["Prospect"] = "prospect";
  })(EntityTypeListEnum = PostElasticSearchBusinessRequest.EntityTypeListEnum || (PostElasticSearchBusinessRequest.EntityTypeListEnum = {}));
})(PostElasticSearchBusinessRequest || (PostElasticSearchBusinessRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostElasticSearchPublicRequest;
(function (PostElasticSearchPublicRequest) {
  let EntityTypeListEnum;
  (function (EntityTypeListEnum) {
    EntityTypeListEnum["User"] = "user";
    EntityTypeListEnum["Business"] = "business";
    EntityTypeListEnum["Ad"] = "ad";
    EntityTypeListEnum["Catalog"] = "catalog";
    EntityTypeListEnum["App"] = "app";
  })(EntityTypeListEnum = PostElasticSearchPublicRequest.EntityTypeListEnum || (PostElasticSearchPublicRequest.EntityTypeListEnum = {}));
})(PostElasticSearchPublicRequest || (PostElasticSearchPublicRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostEmailRequest;
(function (PostEmailRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["CoreDeal"] = "CoreDeal";
    ServiceTypeEnum["Inventory"] = "Inventory";
    ServiceTypeEnum["AssetManager"] = "AssetManager";
    ServiceTypeEnum["Catalog"] = "Catalog";
    ServiceTypeEnum["CoreAssetManager"] = "CoreAssetManager";
    ServiceTypeEnum["Coworker"] = "Coworker";
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["ProjectWorkgroup"] = "ProjectWorkgroup";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["User"] = "User";
    ServiceTypeEnum["CrmTargetedCustomer"] = "Crm_targeted_customer";
  })(ServiceTypeEnum = PostEmailRequest.ServiceTypeEnum || (PostEmailRequest.ServiceTypeEnum = {}));
})(PostEmailRequest || (PostEmailRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PostInsuranceRuleRequest;
(function (PostInsuranceRuleRequest) {
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["FreeInsurance"] = "Free insurance";
    CalculationTypeEnum["ItemBased"] = "Item based";
    CalculationTypeEnum["NetTotal"] = "Net total";
    CalculationTypeEnum["NetWeight"] = "Net weight";
    CalculationTypeEnum["NetLength"] = "Net length";
    CalculationTypeEnum["NetVolume"] = "Net volume";
  })(CalculationTypeEnum = PostInsuranceRuleRequest.CalculationTypeEnum || (PostInsuranceRuleRequest.CalculationTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["Location"] = "Location";
  })(LocationLimitTypeEnum = PostInsuranceRuleRequest.LocationLimitTypeEnum || (PostInsuranceRuleRequest.LocationLimitTypeEnum = {}));
  let OfferFreeInsuranceTypeEnum;
  (function (OfferFreeInsuranceTypeEnum) {
    OfferFreeInsuranceTypeEnum["NoFreeInsurance"] = "No free insurance";
    OfferFreeInsuranceTypeEnum["FreeInsuranceIf"] = "Free insurance if";
  })(OfferFreeInsuranceTypeEnum = PostInsuranceRuleRequest.OfferFreeInsuranceTypeEnum || (PostInsuranceRuleRequest.OfferFreeInsuranceTypeEnum = {}));
})(PostInsuranceRuleRequest || (PostInsuranceRuleRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostInteractionRequest;
(function (PostInteractionRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["User"] = "user";
    ServiceTypeEnum["Business"] = "business";
    ServiceTypeEnum["ActivityStreamPost"] = "activity_stream_post";
    ServiceTypeEnum["ActivityStreamComment"] = "activity_stream_comment";
    ServiceTypeEnum["Album"] = "album";
    ServiceTypeEnum["AlbumItem"] = "album_item";
    ServiceTypeEnum["Erp"] = "erp";
    ServiceTypeEnum["Application"] = "application";
    ServiceTypeEnum["SubscriptionAddon"] = "subscription_addon";
    ServiceTypeEnum["Prospect"] = "prospect";
    ServiceTypeEnum["Deal"] = "deal";
    ServiceTypeEnum["Lead"] = "lead";
    ServiceTypeEnum["Offer"] = "offer";
    ServiceTypeEnum["Garden"] = "garden";
    ServiceTypeEnum["Event"] = "event";
    ServiceTypeEnum["PublicDiscount"] = "public_discount";
    ServiceTypeEnum["LocationManager"] = "location_manager";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["DealRating"] = "deal_rating";
  })(ServiceTypeEnum = PostInteractionRequest.ServiceTypeEnum || (PostInteractionRequest.ServiceTypeEnum = {}));
  let InteractionTypeEnum;
  (function (InteractionTypeEnum) {
    InteractionTypeEnum["Block"] = "block";
    InteractionTypeEnum["Follow"] = "follow";
    InteractionTypeEnum["Report"] = "report";
    InteractionTypeEnum["Request"] = "request";
    InteractionTypeEnum["ErpDelete"] = "erp_delete";
    InteractionTypeEnum["SubscriptionCancel"] = "subscription_cancel";
    InteractionTypeEnum["SubscriptionAddonCancel"] = "subscription_addon_cancel";
    InteractionTypeEnum["SavedActive"] = "saved_active";
    InteractionTypeEnum["CrmSave"] = "crm_save";
    InteractionTypeEnum["SavedEvent"] = "saved_event";
  })(InteractionTypeEnum = PostInteractionRequest.InteractionTypeEnum || (PostInteractionRequest.InteractionTypeEnum = {}));
})(PostInteractionRequest || (PostInteractionRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostInvitationRequest;
(function (PostInvitationRequest) {
  let InvitationTypeEnum;
  (function (InvitationTypeEnum) {
    InvitationTypeEnum["Core"] = "core";
    InvitationTypeEnum["Email"] = "email";
  })(InvitationTypeEnum = PostInvitationRequest.InvitationTypeEnum || (PostInvitationRequest.InvitationTypeEnum = {}));
  let InvitationExpireEnum;
  (function (InvitationExpireEnum) {
    InvitationExpireEnum["No"] = "no";
    InvitationExpireEnum["Date"] = "date";
  })(InvitationExpireEnum = PostInvitationRequest.InvitationExpireEnum || (PostInvitationRequest.InvitationExpireEnum = {}));
  let CompanyStreamPostEnum;
  (function (CompanyStreamPostEnum) {
    CompanyStreamPostEnum["Yes"] = "yes";
    CompanyStreamPostEnum["No"] = "no";
  })(CompanyStreamPostEnum = PostInvitationRequest.CompanyStreamPostEnum || (PostInvitationRequest.CompanyStreamPostEnum = {}));
  let CompanyStreamToEnum;
  (function (CompanyStreamToEnum) {
    CompanyStreamToEnum["All"] = "all";
    CompanyStreamToEnum["SelectedDepartments"] = "selected_departments";
  })(CompanyStreamToEnum = PostInvitationRequest.CompanyStreamToEnum || (PostInvitationRequest.CompanyStreamToEnum = {}));
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["InvitationIncomplete"] = "invitation_incomplete";
    StatusEnum["InvitationComplete"] = "invitation_complete";
    StatusEnum["InvitationSent"] = "invitation_sent";
    StatusEnum["InvitationAccepted"] = "invitation_accepted";
    StatusEnum["InvitationRejected"] = "invitation_rejected";
    StatusEnum["ConvertedToEmployee"] = "converted_to_employee";
    StatusEnum["InvitationCancelled"] = "invitation_cancelled";
    StatusEnum["PendingHiringProcess"] = "pending_hiring_process";
  })(StatusEnum = PostInvitationRequest.StatusEnum || (PostInvitationRequest.StatusEnum = {}));
})(PostInvitationRequest || (PostInvitationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostKeyWordRequest;
(function (PostKeyWordRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Universal"] = "universal";
    TypeEnum["Skill"] = "skill";
    TypeEnum["JobTitle"] = "job_title";
    TypeEnum["Interest"] = "interest";
    TypeEnum["Catalog"] = "catalog";
    TypeEnum["Academy"] = "academy";
    TypeEnum["Application"] = "application";
    TypeEnum["Industry"] = "industry";
    TypeEnum["Garden"] = "garden";
    TypeEnum["IndustryCategory"] = "industry_category";
    TypeEnum["BucketListCategory"] = "bucket_list_category";
    TypeEnum["PaymentOnSpot"] = "payment_on_spot";
    TypeEnum["CategoryTemplate"] = "category_template";
    TypeEnum["CustomField"] = "custom_field";
    TypeEnum["SystemTaxRule"] = "system_tax_rule";
    TypeEnum["SmartAd"] = "smart_ad";
    TypeEnum["CrmTargetedCustomer"] = "crm_targeted_customer";
  })(TypeEnum = PostKeyWordRequest.TypeEnum || (PostKeyWordRequest.TypeEnum = {}));
})(PostKeyWordRequest || (PostKeyWordRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostKeyWordServiceRequest;
(function (PostKeyWordServiceRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Country"] = "country";
    ServiceTypeEnum["Street"] = "street";
    ServiceTypeEnum["City"] = "city";
    ServiceTypeEnum["State"] = "state";
    ServiceTypeEnum["Business"] = "business";
    ServiceTypeEnum["User"] = "user";
    ServiceTypeEnum["CrmProspect"] = "crm_prospect";
    ServiceTypeEnum["LocationManager"] = "location_manager";
    ServiceTypeEnum["Task"] = "task";
    ServiceTypeEnum["Application"] = "application";
    ServiceTypeEnum["Academy"] = "academy";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["PaymentOnSpot"] = "payment_on_spot";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["CustomField"] = "custom_field";
    ServiceTypeEnum["CoWorker"] = "co_worker";
    ServiceTypeEnum["SystemTaxRule"] = "system_tax_rule";
    ServiceTypeEnum["ProjectWorkgroup"] = "project_workgroup";
    ServiceTypeEnum["CrmTargetedCustomer"] = "crm_targeted_customer";
    ServiceTypeEnum["Contact"] = "contact";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["LanguageExchange"] = "language_exchange";
    ServiceTypeEnum["BusinessNetworking"] = "business_networking";
    ServiceTypeEnum["Dating"] = "dating";
    ServiceTypeEnum["Garden"] = "garden";
    ServiceTypeEnum["TravelBuddies"] = "travel_buddies";
    ServiceTypeEnum["SecondChance"] = "second_chance";
    ServiceTypeEnum["BeGreen"] = "be_green";
    ServiceTypeEnum["BucketList"] = "bucket_list";
    ServiceTypeEnum["GoalManager"] = "goal_manager";
    ServiceTypeEnum["Pets"] = "pets";
    ServiceTypeEnum["Volunteer"] = "volunteer";
    ServiceTypeEnum["Cooking"] = "cooking";
    ServiceTypeEnum["Collectors"] = "collectors";
    ServiceTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ServiceTypeEnum["KidsSpace"] = "kids_space";
    ServiceTypeEnum["Sport"] = "sport";
    ServiceTypeEnum["Mindset"] = "mindset";
    ServiceTypeEnum["DiyHandmade"] = "diy_handmade";
    ServiceTypeEnum["Tech"] = "tech";
    ServiceTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ServiceTypeEnum["BToB"] = "b_to_b";
    ServiceTypeEnum["DigitalNomad"] = "digital_nomad";
    ServiceTypeEnum["Gaming"] = "gaming";
    ServiceTypeEnum["Erotic"] = "erotic";
    ServiceTypeEnum["Roommate"] = "roommate";
    ServiceTypeEnum["Aviation"] = "aviation";
    ServiceTypeEnum["Cars"] = "cars";
    ServiceTypeEnum["Boat"] = "boat";
    ServiceTypeEnum["Fitness"] = "fitness";
    ServiceTypeEnum["Events"] = "events";
    ServiceTypeEnum["SkillLink"] = "skill_link";
  })(ServiceTypeEnum = PostKeyWordServiceRequest.ServiceTypeEnum || (PostKeyWordServiceRequest.ServiceTypeEnum = {}));
})(PostKeyWordServiceRequest || (PostKeyWordServiceRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostLanguageToCommunityApplicationRequest;
(function (PostLanguageToCommunityApplicationRequest) {
  let LevelEnum;
  (function (LevelEnum) {
    LevelEnum["Learn"] = "learn";
    LevelEnum["Speak"] = "speak";
    LevelEnum["Native"] = "native";
  })(LevelEnum = PostLanguageToCommunityApplicationRequest.LevelEnum || (PostLanguageToCommunityApplicationRequest.LevelEnum = {}));
  let GradeEnum;
  (function (GradeEnum) {
    GradeEnum["Beginner"] = "beginner";
    GradeEnum["Intermediate"] = "intermediate";
    GradeEnum["Advanced"] = "advanced";
    GradeEnum["Native"] = "native";
    GradeEnum["Fluent"] = "fluent";
  })(GradeEnum = PostLanguageToCommunityApplicationRequest.GradeEnum || (PostLanguageToCommunityApplicationRequest.GradeEnum = {}));
  let ProfileTypeEnum;
  (function (ProfileTypeEnum) {
    ProfileTypeEnum["LanguageExchange"] = "language_exchange";
    ProfileTypeEnum["BusinessNetworking"] = "business_networking";
    ProfileTypeEnum["Dating"] = "dating";
    ProfileTypeEnum["Garden"] = "garden";
    ProfileTypeEnum["TravelBuddies"] = "travel_buddies";
    ProfileTypeEnum["SecondChance"] = "second_chance";
    ProfileTypeEnum["BeGreen"] = "be_green";
    ProfileTypeEnum["BucketList"] = "bucket_list";
    ProfileTypeEnum["GoalManager"] = "goal_manager";
    ProfileTypeEnum["Pets"] = "pets";
    ProfileTypeEnum["Volunteer"] = "volunteer";
    ProfileTypeEnum["Cooking"] = "cooking";
    ProfileTypeEnum["Collectors"] = "collectors";
    ProfileTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ProfileTypeEnum["KidsSpace"] = "kids_space";
    ProfileTypeEnum["Sport"] = "sport";
    ProfileTypeEnum["Mindset"] = "mindset";
    ProfileTypeEnum["DiyHandmade"] = "diy_handmade";
    ProfileTypeEnum["Tech"] = "tech";
    ProfileTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ProfileTypeEnum["BToB"] = "b_to_b";
    ProfileTypeEnum["DigitalNomad"] = "digital_nomad";
    ProfileTypeEnum["Gaming"] = "gaming";
    ProfileTypeEnum["Erotic"] = "erotic";
    ProfileTypeEnum["Roommate"] = "roommate";
    ProfileTypeEnum["Aviation"] = "aviation";
    ProfileTypeEnum["Cars"] = "cars";
    ProfileTypeEnum["Boat"] = "boat";
    ProfileTypeEnum["Fitness"] = "fitness";
    ProfileTypeEnum["Events"] = "events";
    ProfileTypeEnum["TaskManager"] = "task_manager";
    ProfileTypeEnum["Services"] = "services";
    ProfileTypeEnum["Academy"] = "academy";
    ProfileTypeEnum["StoreFinder"] = "store_finder";
    ProfileTypeEnum["ActivityStream"] = "activity_stream";
    ProfileTypeEnum["Calendar"] = "calendar";
    ProfileTypeEnum["Contacts"] = "contacts";
    ProfileTypeEnum["Notes"] = "notes";
    ProfileTypeEnum["Drive"] = "drive";
    ProfileTypeEnum["Taskly"] = "taskly";
    ProfileTypeEnum["MarketSpace"] = "market_space";
    ProfileTypeEnum["Chat"] = "chat";
    ProfileTypeEnum["DoctorFinder"] = "doctor_finder";
    ProfileTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ProfileTypeEnum["Renting"] = "renting";
    ProfileTypeEnum["Booking"] = "booking";
    ProfileTypeEnum["Job"] = "job";
    ProfileTypeEnum["DigitalDownloads"] = "digital_downloads";
    ProfileTypeEnum["PhysicalProduct"] = "physical_product";
    ProfileTypeEnum["EmailManager"] = "email_manager";
    ProfileTypeEnum["RealEstate"] = "real_estate";
    ProfileTypeEnum["CoworkingSpace"] = "coworking_space";
    ProfileTypeEnum["Grocery"] = "grocery";
    ProfileTypeEnum["Fashion"] = "fashion";
    ProfileTypeEnum["HomeDesign"] = "home_design";
    ProfileTypeEnum["Taxi"] = "taxi";
    ProfileTypeEnum["SkillLink"] = "skill_link";
    ProfileTypeEnum["MediaChannels"] = "media_channels";
  })(ProfileTypeEnum = PostLanguageToCommunityApplicationRequest.ProfileTypeEnum || (PostLanguageToCommunityApplicationRequest.ProfileTypeEnum = {}));
})(PostLanguageToCommunityApplicationRequest || (PostLanguageToCommunityApplicationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostLinkRequest;
(function (PostLinkRequest) {
  let SharedTypeEnum;
  (function (SharedTypeEnum) {
    SharedTypeEnum["Email"] = "email";
    SharedTypeEnum["Phone"] = "phone";
    SharedTypeEnum["Link"] = "link";
  })(SharedTypeEnum = PostLinkRequest.SharedTypeEnum || (PostLinkRequest.SharedTypeEnum = {}));
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Website"] = "website";
    TypeEnum["SocialMedia"] = "social_media";
    TypeEnum["Other"] = "other";
  })(TypeEnum = PostLinkRequest.TypeEnum || (PostLinkRequest.TypeEnum = {}));
  let SocialMediaTypeEnum;
  (function (SocialMediaTypeEnum) {
    SocialMediaTypeEnum["Facebook"] = "facebook";
    SocialMediaTypeEnum["Twitter"] = "twitter";
    SocialMediaTypeEnum["Linkedin"] = "linkedin";
    SocialMediaTypeEnum["Instagram"] = "instagram";
    SocialMediaTypeEnum["Youtube"] = "youtube";
    SocialMediaTypeEnum["Pinterest"] = "pinterest";
    SocialMediaTypeEnum["Tumblr"] = "tumblr";
    SocialMediaTypeEnum["Snapchat"] = "snapchat";
    SocialMediaTypeEnum["Reddit"] = "reddit";
    SocialMediaTypeEnum["Flickr"] = "flickr";
    SocialMediaTypeEnum["Vimeo"] = "vimeo";
    SocialMediaTypeEnum["Whatsapp"] = "whatsapp";
    SocialMediaTypeEnum["Telegram"] = "telegram";
    SocialMediaTypeEnum["Wechat"] = "wechat";
    SocialMediaTypeEnum["Line"] = "line";
    SocialMediaTypeEnum["Viber"] = "viber";
    SocialMediaTypeEnum["Skype"] = "skype";
    SocialMediaTypeEnum["Qq"] = "qq";
    SocialMediaTypeEnum["Kakaotalk"] = "kakaotalk";
    SocialMediaTypeEnum["Tiktok"] = "tiktok";
    SocialMediaTypeEnum["Medium"] = "medium";
    SocialMediaTypeEnum["Quora"] = "quora";
    SocialMediaTypeEnum["Twitch"] = "twitch";
    SocialMediaTypeEnum["Soundcloud"] = "soundcloud";
    SocialMediaTypeEnum["Spotify"] = "spotify";
    SocialMediaTypeEnum["Github"] = "github";
    SocialMediaTypeEnum["Dribbble"] = "dribbble";
    SocialMediaTypeEnum["Behance"] = "behance";
    SocialMediaTypeEnum["Deviantart"] = "deviantart";
    SocialMediaTypeEnum["Foursquare"] = "foursquare";
    SocialMediaTypeEnum["Meetup"] = "meetup";
    SocialMediaTypeEnum["Vk"] = "vk";
    SocialMediaTypeEnum["Ok"] = "ok";
    SocialMediaTypeEnum["Myspace"] = "myspace";
    SocialMediaTypeEnum["Renren"] = "renren";
    SocialMediaTypeEnum["Weibo"] = "weibo";
    SocialMediaTypeEnum["Xing"] = "xing";
  })(SocialMediaTypeEnum = PostLinkRequest.SocialMediaTypeEnum || (PostLinkRequest.SocialMediaTypeEnum = {}));
})(PostLinkRequest || (PostLinkRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostLocationLimitRequest;
(function (PostLocationLimitRequest) {
  let LocationTypeEnum;
  (function (LocationTypeEnum) {
    LocationTypeEnum["Country"] = "country";
    LocationTypeEnum["State"] = "state";
    LocationTypeEnum["City"] = "city";
  })(LocationTypeEnum = PostLocationLimitRequest.LocationTypeEnum || (PostLocationLimitRequest.LocationTypeEnum = {}));
})(PostLocationLimitRequest || (PostLocationLimitRequest = {}));
var PostMarketDiscountRequest;
(function (PostMarketDiscountRequest) {
  let DiscountTypeEnum;
  (function (DiscountTypeEnum) {
    DiscountTypeEnum["OfferToAll"] = "Offer to all";
    DiscountTypeEnum["VoucherCode"] = "Voucher code";
    DiscountTypeEnum["OfferToSpecificUsers"] = "Offer to specific users";
  })(DiscountTypeEnum = PostMarketDiscountRequest.DiscountTypeEnum || (PostMarketDiscountRequest.DiscountTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = PostMarketDiscountRequest.PriceChangerTypeEnum || (PostMarketDiscountRequest.PriceChangerTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["AreaLimit"] = "Area limit";
    LocationLimitTypeEnum["CountryLimit"] = "Country limit";
  })(LocationLimitTypeEnum = PostMarketDiscountRequest.LocationLimitTypeEnum || (PostMarketDiscountRequest.LocationLimitTypeEnum = {}));
  let ShowTypeEnum;
  (function (ShowTypeEnum) {
    ShowTypeEnum["Public"] = "public";
    ShowTypeEnum["Private"] = "private";
  })(ShowTypeEnum = PostMarketDiscountRequest.ShowTypeEnum || (PostMarketDiscountRequest.ShowTypeEnum = {}));
})(PostMarketDiscountRequest || (PostMarketDiscountRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostMessageTemplateRequest;
(function (PostMessageTemplateRequest) {
  let CommunicationTypeEnum;
  (function (CommunicationTypeEnum) {
    CommunicationTypeEnum["Chat"] = "chat";
    CommunicationTypeEnum["Notification"] = "notification";
    CommunicationTypeEnum["Ping"] = "ping";
  })(CommunicationTypeEnum = PostMessageTemplateRequest.CommunicationTypeEnum || (PostMessageTemplateRequest.CommunicationTypeEnum = {}));
})(PostMessageTemplateRequest || (PostMessageTemplateRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostMrAiTrackerRequest;
(function (PostMrAiTrackerRequest) {
  let ProfileTypeEnum;
  (function (ProfileTypeEnum) {
    ProfileTypeEnum["LanguageExchange"] = "language_exchange";
    ProfileTypeEnum["BusinessNetworking"] = "business_networking";
    ProfileTypeEnum["Dating"] = "dating";
    ProfileTypeEnum["Garden"] = "garden";
    ProfileTypeEnum["TravelBuddies"] = "travel_buddies";
    ProfileTypeEnum["SecondChance"] = "second_chance";
    ProfileTypeEnum["BeGreen"] = "be_green";
    ProfileTypeEnum["BucketList"] = "bucket_list";
    ProfileTypeEnum["GoalManager"] = "goal_manager";
    ProfileTypeEnum["Pets"] = "pets";
    ProfileTypeEnum["Volunteer"] = "volunteer";
    ProfileTypeEnum["Cooking"] = "cooking";
    ProfileTypeEnum["Collectors"] = "collectors";
    ProfileTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    ProfileTypeEnum["KidsSpace"] = "kids_space";
    ProfileTypeEnum["Sport"] = "sport";
    ProfileTypeEnum["Mindset"] = "mindset";
    ProfileTypeEnum["DiyHandmade"] = "diy_handmade";
    ProfileTypeEnum["Tech"] = "tech";
    ProfileTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    ProfileTypeEnum["BToB"] = "b_to_b";
    ProfileTypeEnum["DigitalNomad"] = "digital_nomad";
    ProfileTypeEnum["Gaming"] = "gaming";
    ProfileTypeEnum["Erotic"] = "erotic";
    ProfileTypeEnum["Roommate"] = "roommate";
    ProfileTypeEnum["Aviation"] = "aviation";
    ProfileTypeEnum["Cars"] = "cars";
    ProfileTypeEnum["Boat"] = "boat";
    ProfileTypeEnum["Fitness"] = "fitness";
    ProfileTypeEnum["Events"] = "events";
    ProfileTypeEnum["TaskManager"] = "task_manager";
    ProfileTypeEnum["Services"] = "services";
    ProfileTypeEnum["Academy"] = "academy";
    ProfileTypeEnum["StoreFinder"] = "store_finder";
    ProfileTypeEnum["ActivityStream"] = "activity_stream";
    ProfileTypeEnum["Calendar"] = "calendar";
    ProfileTypeEnum["Contacts"] = "contacts";
    ProfileTypeEnum["Notes"] = "notes";
    ProfileTypeEnum["Drive"] = "drive";
    ProfileTypeEnum["Taskly"] = "taskly";
    ProfileTypeEnum["MarketSpace"] = "market_space";
    ProfileTypeEnum["Chat"] = "chat";
    ProfileTypeEnum["DoctorFinder"] = "doctor_finder";
    ProfileTypeEnum["WellnessBeauty"] = "wellness_beauty";
    ProfileTypeEnum["Renting"] = "renting";
    ProfileTypeEnum["Booking"] = "booking";
    ProfileTypeEnum["Job"] = "job";
    ProfileTypeEnum["DigitalDownloads"] = "digital_downloads";
    ProfileTypeEnum["PhysicalProduct"] = "physical_product";
    ProfileTypeEnum["EmailManager"] = "email_manager";
    ProfileTypeEnum["RealEstate"] = "real_estate";
    ProfileTypeEnum["CoworkingSpace"] = "coworking_space";
    ProfileTypeEnum["Grocery"] = "grocery";
    ProfileTypeEnum["Fashion"] = "fashion";
    ProfileTypeEnum["HomeDesign"] = "home_design";
    ProfileTypeEnum["Taxi"] = "taxi";
    ProfileTypeEnum["SkillLink"] = "skill_link";
    ProfileTypeEnum["MediaChannels"] = "media_channels";
  })(ProfileTypeEnum = PostMrAiTrackerRequest.ProfileTypeEnum || (PostMrAiTrackerRequest.ProfileTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Album"] = "album";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["Academy"] = "academy";
  })(ServiceTypeEnum = PostMrAiTrackerRequest.ServiceTypeEnum || (PostMrAiTrackerRequest.ServiceTypeEnum = {}));
})(PostMrAiTrackerRequest || (PostMrAiTrackerRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostOrgChartRequest;
(function (PostOrgChartRequest) {
  let AreaTypeEnum;
  (function (AreaTypeEnum) {
    AreaTypeEnum["Department"] = "department";
    AreaTypeEnum["SalesTeam"] = "sales_team";
  })(AreaTypeEnum = PostOrgChartRequest.AreaTypeEnum || (PostOrgChartRequest.AreaTypeEnum = {}));
})(PostOrgChartRequest || (PostOrgChartRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostPartnerVerifyRequest;
(function (PostPartnerVerifyRequest) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Accepted"] = "accepted";
    StatusEnum["Declined"] = "declined";
    StatusEnum["Cancelled"] = "cancelled";
  })(StatusEnum = PostPartnerVerifyRequest.StatusEnum || (PostPartnerVerifyRequest.StatusEnum = {}));
})(PostPartnerVerifyRequest || (PostPartnerVerifyRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostPaymentModifyRequest;
(function (PostPaymentModifyRequest) {
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "Discount";
    PriceTypeEnum["ExtraCharge"] = "Extra charge";
  })(PriceTypeEnum = PostPaymentModifyRequest.PriceTypeEnum || (PostPaymentModifyRequest.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = PostPaymentModifyRequest.PriceChangerTypeEnum || (PostPaymentModifyRequest.PriceChangerTypeEnum = {}));
})(PostPaymentModifyRequest || (PostPaymentModifyRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostPaymentOnSpotRequest;
(function (PostPaymentOnSpotRequest) {
  let PaymentMethodEnum;
  (function (PaymentMethodEnum) {
    PaymentMethodEnum["Online"] = "Online";
    PaymentMethodEnum["Offline"] = "Offline";
  })(PaymentMethodEnum = PostPaymentOnSpotRequest.PaymentMethodEnum || (PostPaymentOnSpotRequest.PaymentMethodEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["ShippingProvider"] = "shipping_provider";
    ServiceTypeEnum["InsuranceProvider"] = "insurance_provider";
    ServiceTypeEnum["PaymentProvider"] = "payment_provider";
    ServiceTypeEnum["LogisticsProvider"] = "logistics_provider";
    ServiceTypeEnum["Other"] = "other";
  })(ServiceTypeEnum = PostPaymentOnSpotRequest.ServiceTypeEnum || (PostPaymentOnSpotRequest.ServiceTypeEnum = {}));
})(PostPaymentOnSpotRequest || (PostPaymentOnSpotRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostPersonRequest;
(function (PostPersonRequest) {
  let GenderEnum;
  (function (GenderEnum) {
    GenderEnum["Unknown"] = "unknown";
    GenderEnum["Male"] = "male";
    GenderEnum["Female"] = "female";
    GenderEnum["Other"] = "other";
  })(GenderEnum = PostPersonRequest.GenderEnum || (PostPersonRequest.GenderEnum = {}));
})(PostPersonRequest || (PostPersonRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostPipelineRequest;
(function (PostPipelineRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["SalesTeam"] = "Sales Team";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["Recruiting"] = "Recruiting";
    ServiceTypeEnum["Invitation"] = "Invitation";
  })(ServiceTypeEnum = PostPipelineRequest.ServiceTypeEnum || (PostPipelineRequest.ServiceTypeEnum = {}));
})(PostPipelineRequest || (PostPipelineRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostPipelineStatusRequest;
(function (PostPipelineStatusRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["SalesTeam"] = "Sales Team";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["Recruiting"] = "Recruiting";
    ServiceTypeEnum["Invitation"] = "Invitation";
  })(ServiceTypeEnum = PostPipelineStatusRequest.ServiceTypeEnum || (PostPipelineStatusRequest.ServiceTypeEnum = {}));
})(PostPipelineStatusRequest || (PostPipelineStatusRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PostRBACAccessRuleRequest;
(function (PostRBACAccessRuleRequest) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["Crm"] = "crm";
    RuleTypeEnum["Hrms"] = "hrms";
    RuleTypeEnum["CompanyStream"] = "company_stream";
    RuleTypeEnum["OrgChart"] = "org_chart";
    RuleTypeEnum["Chat"] = "chat";
    RuleTypeEnum["CoWorkers"] = "co_workers";
    RuleTypeEnum["SalesTeam"] = "sales_team";
    RuleTypeEnum["TargetMarket"] = "target_market";
    RuleTypeEnum["WebsiteAppManager"] = "website_app_manager";
    RuleTypeEnum["SocialConnections"] = "social_connections";
    RuleTypeEnum["PopularityCenter"] = "popularity_center";
    RuleTypeEnum["MarketingTools"] = "marketing_tools";
    RuleTypeEnum["TaskManager"] = "task_manager";
    RuleTypeEnum["EmailManager"] = "email_manager";
    RuleTypeEnum["WorktimeManager"] = "worktime_manager";
    RuleTypeEnum["LocationManager"] = "location_manager";
    RuleTypeEnum["SmartCube"] = "smart_cube";
    RuleTypeEnum["Bi"] = "bi";
    RuleTypeEnum["Tax"] = "tax";
    RuleTypeEnum["Transportation"] = "transportation";
    RuleTypeEnum["Insurance"] = "insurance";
    RuleTypeEnum["Payment"] = "payment";
    RuleTypeEnum["BankAccounts"] = "bank_accounts";
    RuleTypeEnum["Procurement"] = "procurement";
    RuleTypeEnum["Auction"] = "auction";
    RuleTypeEnum["Settings"] = "settings";
    RuleTypeEnum["RbacModule"] = "rbac_module";
  })(RuleTypeEnum = PostRBACAccessRuleRequest.RuleTypeEnum || (PostRBACAccessRuleRequest.RuleTypeEnum = {}));
})(PostRBACAccessRuleRequest || (PostRBACAccessRuleRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostRelateDriveToGalleryRequest;
(function (PostRelateDriveToGalleryRequest) {
  let PostStateEnum;
  (function (PostStateEnum) {
    PostStateEnum["Adult"] = "adult";
    PostStateEnum["Normal"] = "normal";
    PostStateEnum["Sensitive"] = "sensitive";
  })(PostStateEnum = PostRelateDriveToGalleryRequest.PostStateEnum || (PostRelateDriveToGalleryRequest.PostStateEnum = {}));
  let UploadSystemTypeEnum;
  (function (UploadSystemTypeEnum) {
    UploadSystemTypeEnum["Album"] = "Album";
    UploadSystemTypeEnum["PreviewPageMeta"] = "Preview Page Meta";
    UploadSystemTypeEnum["PageMeta"] = "Page Meta";
    UploadSystemTypeEnum["SmartItem"] = "Smart Item";
    UploadSystemTypeEnum["Academy"] = "Academy";
    UploadSystemTypeEnum["Application"] = "Application";
    UploadSystemTypeEnum["EventFileUpload"] = "Event File Upload";
    UploadSystemTypeEnum["SmartWorkflow"] = "Smart Workflow";
  })(UploadSystemTypeEnum = PostRelateDriveToGalleryRequest.UploadSystemTypeEnum || (PostRelateDriveToGalleryRequest.UploadSystemTypeEnum = {}));
  let UploadAreaEnum;
  (function (UploadAreaEnum) {
    UploadAreaEnum["MediaUpload"] = "media_upload";
    UploadAreaEnum["AfterDownload"] = "after_download";
    UploadAreaEnum["AfterDownloadPaid"] = "after_download_paid";
  })(UploadAreaEnum = PostRelateDriveToGalleryRequest.UploadAreaEnum || (PostRelateDriveToGalleryRequest.UploadAreaEnum = {}));
})(PostRelateDriveToGalleryRequest || (PostRelateDriveToGalleryRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostReplyEmailRequest;
(function (PostReplyEmailRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["CoreDeal"] = "CoreDeal";
    ServiceTypeEnum["Inventory"] = "Inventory";
    ServiceTypeEnum["AssetManager"] = "AssetManager";
    ServiceTypeEnum["Catalog"] = "Catalog";
    ServiceTypeEnum["CoreAssetManager"] = "CoreAssetManager";
    ServiceTypeEnum["Coworker"] = "Coworker";
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["ProjectWorkgroup"] = "ProjectWorkgroup";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["User"] = "User";
    ServiceTypeEnum["CrmTargetedCustomer"] = "Crm_targeted_customer";
  })(ServiceTypeEnum = PostReplyEmailRequest.ServiceTypeEnum || (PostReplyEmailRequest.ServiceTypeEnum = {}));
})(PostReplyEmailRequest || (PostReplyEmailRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostResourceExchangeRequest;
(function (PostResourceExchangeRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Catalog"] = "catalog";
  })(ServiceTypeEnum = PostResourceExchangeRequest.ServiceTypeEnum || (PostResourceExchangeRequest.ServiceTypeEnum = {}));
})(PostResourceExchangeRequest || (PostResourceExchangeRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostRoomMemberRequest;
(function (PostRoomMemberRequest) {
  let PermissionEnum;
  (function (PermissionEnum) {
    PermissionEnum["Admin"] = "admin";
    PermissionEnum["Moderator"] = "moderator";
    PermissionEnum["Member"] = "member";
  })(PermissionEnum = PostRoomMemberRequest.PermissionEnum || (PostRoomMemberRequest.PermissionEnum = {}));
})(PostRoomMemberRequest || (PostRoomMemberRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostRoomRequest;
(function (PostRoomRequest) {
  let RoomTypeEnum;
  (function (RoomTypeEnum) {
    RoomTypeEnum["Direct"] = "direct";
    RoomTypeEnum["Group"] = "group";
    RoomTypeEnum["Channel"] = "channel";
    RoomTypeEnum["Ad"] = "ad";
    RoomTypeEnum["App"] = "app";
    RoomTypeEnum["WgProject"] = "wg_project";
  })(RoomTypeEnum = PostRoomRequest.RoomTypeEnum || (PostRoomRequest.RoomTypeEnum = {}));
  let CommunityTypeEnum;
  (function (CommunityTypeEnum) {
    CommunityTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityTypeEnum["Dating"] = "dating";
    CommunityTypeEnum["Garden"] = "garden";
    CommunityTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityTypeEnum["SecondChance"] = "second_chance";
    CommunityTypeEnum["BeGreen"] = "be_green";
    CommunityTypeEnum["BucketList"] = "bucket_list";
    CommunityTypeEnum["GoalManager"] = "goal_manager";
    CommunityTypeEnum["Pets"] = "pets";
    CommunityTypeEnum["Volunteer"] = "volunteer";
    CommunityTypeEnum["Cooking"] = "cooking";
    CommunityTypeEnum["Collectors"] = "collectors";
    CommunityTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityTypeEnum["KidsSpace"] = "kids_space";
    CommunityTypeEnum["Sport"] = "sport";
    CommunityTypeEnum["Mindset"] = "mindset";
    CommunityTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityTypeEnum["Tech"] = "tech";
    CommunityTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityTypeEnum["BToB"] = "b_to_b";
    CommunityTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityTypeEnum["Gaming"] = "gaming";
    CommunityTypeEnum["Erotic"] = "erotic";
    CommunityTypeEnum["Roommate"] = "roommate";
    CommunityTypeEnum["Aviation"] = "aviation";
    CommunityTypeEnum["Cars"] = "cars";
    CommunityTypeEnum["Boat"] = "boat";
    CommunityTypeEnum["Fitness"] = "fitness";
    CommunityTypeEnum["Events"] = "events";
    CommunityTypeEnum["TaskManager"] = "task_manager";
    CommunityTypeEnum["Services"] = "services";
    CommunityTypeEnum["Academy"] = "academy";
    CommunityTypeEnum["StoreFinder"] = "store_finder";
    CommunityTypeEnum["ActivityStream"] = "activity_stream";
    CommunityTypeEnum["Calendar"] = "calendar";
    CommunityTypeEnum["Contacts"] = "contacts";
    CommunityTypeEnum["Notes"] = "notes";
    CommunityTypeEnum["Drive"] = "drive";
    CommunityTypeEnum["Taskly"] = "taskly";
    CommunityTypeEnum["MarketSpace"] = "market_space";
    CommunityTypeEnum["Chat"] = "chat";
    CommunityTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityTypeEnum["Renting"] = "renting";
    CommunityTypeEnum["Booking"] = "booking";
    CommunityTypeEnum["Job"] = "job";
    CommunityTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityTypeEnum["EmailManager"] = "email_manager";
    CommunityTypeEnum["RealEstate"] = "real_estate";
    CommunityTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityTypeEnum["Grocery"] = "grocery";
    CommunityTypeEnum["Fashion"] = "fashion";
    CommunityTypeEnum["HomeDesign"] = "home_design";
    CommunityTypeEnum["Taxi"] = "taxi";
    CommunityTypeEnum["SkillLink"] = "skill_link";
    CommunityTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityTypeEnum = PostRoomRequest.CommunityTypeEnum || (PostRoomRequest.CommunityTypeEnum = {}));
  let PrivacyEnum;
  (function (PrivacyEnum) {
    PrivacyEnum["Private"] = "private";
    PrivacyEnum["Public"] = "public";
  })(PrivacyEnum = PostRoomRequest.PrivacyEnum || (PostRoomRequest.PrivacyEnum = {}));
})(PostRoomRequest || (PostRoomRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSharedCloudStorageRequest;
(function (PostSharedCloudStorageRequest) {
  let SharedFromEntityEnum;
  (function (SharedFromEntityEnum) {
    SharedFromEntityEnum["User"] = "user";
    SharedFromEntityEnum["Business"] = "business";
    SharedFromEntityEnum["CoWorker"] = "co_worker";
  })(SharedFromEntityEnum = PostSharedCloudStorageRequest.SharedFromEntityEnum || (PostSharedCloudStorageRequest.SharedFromEntityEnum = {}));
  let SharedToEntityEnum;
  (function (SharedToEntityEnum) {
    SharedToEntityEnum["User"] = "user";
    SharedToEntityEnum["Business"] = "business";
    SharedToEntityEnum["CoWorker"] = "co_worker";
  })(SharedToEntityEnum = PostSharedCloudStorageRequest.SharedToEntityEnum || (PostSharedCloudStorageRequest.SharedToEntityEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["ServiceOffered"] = "Service Offered";
    ServiceTypeEnum["ActivityStream"] = "ActivityStream";
    ServiceTypeEnum["Album"] = "Album";
    ServiceTypeEnum["AlbumItem"] = "Album Item";
    ServiceTypeEnum["DigitalProduct"] = "Digital Product";
    ServiceTypeEnum["DigitalMedia"] = "Digital Media";
    ServiceTypeEnum["PhysicalProduct"] = "Physical Product";
    ServiceTypeEnum["Rent"] = "Rent";
    ServiceTypeEnum["Booking"] = "Booking";
    ServiceTypeEnum["Job"] = "Job";
    ServiceTypeEnum["FoodBeverage"] = "Food Beverage";
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["ProjectWorkgroup"] = "Project Workgroup";
    ServiceTypeEnum["AdRent"] = "Ad Rent";
    ServiceTypeEnum["AdDigitalProduct"] = "Ad Digital Product";
    ServiceTypeEnum["AdDigitalMedia"] = "Ad Digital Media";
    ServiceTypeEnum["AdPhysicalProduct"] = "Ad Physical Product";
    ServiceTypeEnum["AdBooking"] = "Ad Booking";
    ServiceTypeEnum["AdJob"] = "Ad Job";
    ServiceTypeEnum["AdFoodBeverage"] = "Ad Food Beverage";
    ServiceTypeEnum["AdServiceOffered"] = "Ad Service Offered";
    ServiceTypeEnum["Product"] = "Product";
    ServiceTypeEnum["Catalog"] = "Catalog";
    ServiceTypeEnum["PreviewPageMeta"] = "Preview Page Meta";
    ServiceTypeEnum["PageMeta"] = "Page Meta";
    ServiceTypeEnum["EventFileUpload"] = "Event File Upload";
  })(ServiceTypeEnum = PostSharedCloudStorageRequest.ServiceTypeEnum || (PostSharedCloudStorageRequest.ServiceTypeEnum = {}));
})(PostSharedCloudStorageRequest || (PostSharedCloudStorageRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSharedContactRequest;
(function (PostSharedContactRequest) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["Personal"] = "personal";
  })(EntityTypeEnum = PostSharedContactRequest.EntityTypeEnum || (PostSharedContactRequest.EntityTypeEnum = {}));
})(PostSharedContactRequest || (PostSharedContactRequest = {}));
var PostShippingRuleRequest;
(function (PostShippingRuleRequest) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["DayDelivery"] = "Day delivery";
    RuleTypeEnum["SameDayDelivery"] = "Same day delivery";
  })(RuleTypeEnum = PostShippingRuleRequest.RuleTypeEnum || (PostShippingRuleRequest.RuleTypeEnum = {}));
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["FreeShipping"] = "Free shipping";
    CalculationTypeEnum["ItemBased"] = "Item based";
    CalculationTypeEnum["NetTotal"] = "Net total";
    CalculationTypeEnum["NetWeight"] = "Net weight";
    CalculationTypeEnum["NetLength"] = "Net length";
    CalculationTypeEnum["NetVolume"] = "Net volume";
  })(CalculationTypeEnum = PostShippingRuleRequest.CalculationTypeEnum || (PostShippingRuleRequest.CalculationTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["Location"] = "Location";
  })(LocationLimitTypeEnum = PostShippingRuleRequest.LocationLimitTypeEnum || (PostShippingRuleRequest.LocationLimitTypeEnum = {}));
  let OfferFreeShippingTypeEnum;
  (function (OfferFreeShippingTypeEnum) {
    OfferFreeShippingTypeEnum["NoFreeShipping"] = "No free shipping";
    OfferFreeShippingTypeEnum["FreeShippingIf"] = "Free shipping if";
  })(OfferFreeShippingTypeEnum = PostShippingRuleRequest.OfferFreeShippingTypeEnum || (PostShippingRuleRequest.OfferFreeShippingTypeEnum = {}));
})(PostShippingRuleRequest || (PostShippingRuleRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartAdRequest;
(function (PostSmartAdRequest) {
  let ShowTextEnum;
  (function (ShowTextEnum) {
    ShowTextEnum["SmartItem"] = "smart_item";
    ShowTextEnum["SmartAd"] = "smart_ad";
  })(ShowTextEnum = PostSmartAdRequest.ShowTextEnum || (PostSmartAdRequest.ShowTextEnum = {}));
  let ResumeApplyEnum;
  (function (ResumeApplyEnum) {
    ResumeApplyEnum["Optional"] = "optional";
    ResumeApplyEnum["Required"] = "required";
  })(ResumeApplyEnum = PostSmartAdRequest.ResumeApplyEnum || (PostSmartAdRequest.ResumeApplyEnum = {}));
  let WorkplaceShowEnum;
  (function (WorkplaceShowEnum) {
    WorkplaceShowEnum["Show"] = "show";
    WorkplaceShowEnum["Hide"] = "hide";
  })(WorkplaceShowEnum = PostSmartAdRequest.WorkplaceShowEnum || (PostSmartAdRequest.WorkplaceShowEnum = {}));
  let CompanyShowEnum;
  (function (CompanyShowEnum) {
    CompanyShowEnum["Show"] = "show";
    CompanyShowEnum["Hide"] = "hide";
  })(CompanyShowEnum = PostSmartAdRequest.CompanyShowEnum || (PostSmartAdRequest.CompanyShowEnum = {}));
  let WorktimeTypeEnum;
  (function (WorktimeTypeEnum) {
    WorktimeTypeEnum["FullTime"] = "full_time";
    WorktimeTypeEnum["PartTime"] = "part_time";
    WorktimeTypeEnum["Temporary"] = "temporary";
    WorktimeTypeEnum["Freelance"] = "freelance";
  })(WorktimeTypeEnum = PostSmartAdRequest.WorktimeTypeEnum || (PostSmartAdRequest.WorktimeTypeEnum = {}));
  let JobOwnerEnum;
  (function (JobOwnerEnum) {
    JobOwnerEnum["Agency"] = "agency";
    JobOwnerEnum["Own"] = "own";
  })(JobOwnerEnum = PostSmartAdRequest.JobOwnerEnum || (PostSmartAdRequest.JobOwnerEnum = {}));
})(PostSmartAdRequest || (PostSmartAdRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartCheckoutRequestRequest;
(function (PostSmartCheckoutRequestRequest) {
  let PaymentTypeEnum;
  (function (PaymentTypeEnum) {
    PaymentTypeEnum["Offline"] = "Offline";
    PaymentTypeEnum["Direct"] = "Direct";
    PaymentTypeEnum["BankTransfer"] = "Bank Transfer";
  })(PaymentTypeEnum = PostSmartCheckoutRequestRequest.PaymentTypeEnum || (PostSmartCheckoutRequestRequest.PaymentTypeEnum = {}));
})(PostSmartCheckoutRequestRequest || (PostSmartCheckoutRequestRequest = {}));
var PostSmartItemRequest;
(function (PostSmartItemRequest) {
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = PostSmartItemRequest.CubeTypeEnum || (PostSmartItemRequest.CubeTypeEnum = {}));
  let InventoryTypeEnum;
  (function (InventoryTypeEnum) {
    InventoryTypeEnum["None"] = "none";
    InventoryTypeEnum["FinishedGoods"] = "finished_goods";
    InventoryTypeEnum["RawMaterials"] = "raw_materials";
    InventoryTypeEnum["Component"] = "component";
    InventoryTypeEnum["Wip"] = "wip";
    InventoryTypeEnum["PackingMaterials"] = "packing_materials";
    InventoryTypeEnum["Mro"] = "mro";
    InventoryTypeEnum["Others"] = "others";
    InventoryTypeEnum["LivePlant"] = "live_plant";
    InventoryTypeEnum["LiveAnimal"] = "live_animal";
    InventoryTypeEnum["Food"] = "food";
    InventoryTypeEnum["FoodComponent"] = "food_component";
    InventoryTypeEnum["Drink"] = "drink";
  })(InventoryTypeEnum = PostSmartItemRequest.InventoryTypeEnum || (PostSmartItemRequest.InventoryTypeEnum = {}));
  let AmountTypeEnum;
  (function (AmountTypeEnum) {
    AmountTypeEnum["Unlimited"] = "unlimited";
    AmountTypeEnum["Calculated"] = "calculated";
  })(AmountTypeEnum = PostSmartItemRequest.AmountTypeEnum || (PostSmartItemRequest.AmountTypeEnum = {}));
  let ManagingInventoryEnum;
  (function (ManagingInventoryEnum) {
    ManagingInventoryEnum["None"] = "none";
    ManagingInventoryEnum["Fifo"] = "fifo";
    ManagingInventoryEnum["Lifo"] = "lifo";
  })(ManagingInventoryEnum = PostSmartItemRequest.ManagingInventoryEnum || (PostSmartItemRequest.ManagingInventoryEnum = {}));
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
    CatalogTypeEnum["FoodComponent"] = "food_component";
    CatalogTypeEnum["Drink"] = "drink";
    CatalogTypeEnum["ServiceTask"] = "service_task";
  })(CatalogTypeEnum = PostSmartItemRequest.CatalogTypeEnum || (PostSmartItemRequest.CatalogTypeEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = PostSmartItemRequest.IsOnlineTypeEnum || (PostSmartItemRequest.IsOnlineTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = PostSmartItemRequest.OnlineEmployeeEnum || (PostSmartItemRequest.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = PostSmartItemRequest.ServiceAreaEmployeeEnum || (PostSmartItemRequest.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = PostSmartItemRequest.LocationEmployeeEnum || (PostSmartItemRequest.LocationEmployeeEnum = {}));
  let ServiceSettingsEnum;
  (function (ServiceSettingsEnum) {
    ServiceSettingsEnum["SmartSettings"] = "smart_settings";
    ServiceSettingsEnum["Custom"] = "custom";
  })(ServiceSettingsEnum = PostSmartItemRequest.ServiceSettingsEnum || (PostSmartItemRequest.ServiceSettingsEnum = {}));
  let ServiceBookingPreferenceSourceEnum;
  (function (ServiceBookingPreferenceSourceEnum) {
    ServiceBookingPreferenceSourceEnum["SmartSettings"] = "smart_settings";
    ServiceBookingPreferenceSourceEnum["Custom"] = "custom";
  })(ServiceBookingPreferenceSourceEnum = PostSmartItemRequest.ServiceBookingPreferenceSourceEnum || (PostSmartItemRequest.ServiceBookingPreferenceSourceEnum = {}));
  let ServiceBookingPreferenceEnum;
  (function (ServiceBookingPreferenceEnum) {
    ServiceBookingPreferenceEnum["SystemSelect"] = "system_select";
    ServiceBookingPreferenceEnum["CustomerOptionChoose"] = "customer_option_choose";
    ServiceBookingPreferenceEnum["CustomerForceChoose"] = "customer_force_choose";
  })(ServiceBookingPreferenceEnum = PostSmartItemRequest.ServiceBookingPreferenceEnum || (PostSmartItemRequest.ServiceBookingPreferenceEnum = {}));
})(PostSmartItemRequest || (PostSmartItemRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartItemSelectedCustomFieldRequest;
(function (PostSmartItemSelectedCustomFieldRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["CrmProspect"] = "crm_prospect";
  })(ServiceTypeEnum = PostSmartItemSelectedCustomFieldRequest.ServiceTypeEnum || (PostSmartItemSelectedCustomFieldRequest.ServiceTypeEnum = {}));
})(PostSmartItemSelectedCustomFieldRequest || (PostSmartItemSelectedCustomFieldRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartLocationManagerRequest;
(function (PostSmartLocationManagerRequest) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = PostSmartLocationManagerRequest.TypeEnum || (PostSmartLocationManagerRequest.TypeEnum = {}));
})(PostSmartLocationManagerRequest || (PostSmartLocationManagerRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartPolicyRequest;
(function (PostSmartPolicyRequest) {
  let PolicyTypeEnum;
  (function (PolicyTypeEnum) {
    PolicyTypeEnum["Refund"] = "refund";
    PolicyTypeEnum["Disclaimer"] = "disclaimer";
    PolicyTypeEnum["Shipping"] = "shipping";
    PolicyTypeEnum["Insurance"] = "insurance";
  })(PolicyTypeEnum = PostSmartPolicyRequest.PolicyTypeEnum || (PostSmartPolicyRequest.PolicyTypeEnum = {}));
})(PostSmartPolicyRequest || (PostSmartPolicyRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PostSmartSalesWorkflowRequest;
(function (PostSmartSalesWorkflowRequest) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Draft"] = "Draft";
    StatusEnum["Published"] = "Published";
    StatusEnum["Deleted"] = "Deleted";
    StatusEnum["Inactive"] = "Inactive";
  })(StatusEnum = PostSmartSalesWorkflowRequest.StatusEnum || (PostSmartSalesWorkflowRequest.StatusEnum = {}));
  let AcceptedBuyerTypeEnum;
  (function (AcceptedBuyerTypeEnum) {
    AcceptedBuyerTypeEnum["B2b"] = "b2b";
    AcceptedBuyerTypeEnum["B2c"] = "b2c";
    AcceptedBuyerTypeEnum["Both"] = "both";
  })(AcceptedBuyerTypeEnum = PostSmartSalesWorkflowRequest.AcceptedBuyerTypeEnum || (PostSmartSalesWorkflowRequest.AcceptedBuyerTypeEnum = {}));
  let ByTimeTypeEnum;
  (function (ByTimeTypeEnum) {
    ByTimeTypeEnum["Never"] = "never";
    ByTimeTypeEnum["ByTime"] = "by_time";
  })(ByTimeTypeEnum = PostSmartSalesWorkflowRequest.ByTimeTypeEnum || (PostSmartSalesWorkflowRequest.ByTimeTypeEnum = {}));
  let PricingTypeEnum;
  (function (PricingTypeEnum) {
    PricingTypeEnum["Custom"] = "Custom";
    PricingTypeEnum["Free"] = "Free";
    PricingTypeEnum["OnlyLead"] = "Only Lead";
    PricingTypeEnum["Auction"] = "Auction";
    PricingTypeEnum["OnlyForFollowers"] = "Only For Followers";
    PricingTypeEnum["OnlyForSubscribers"] = "Only For Subscribers";
    PricingTypeEnum["ConnectToInventory"] = "Connect to Inventory";
    PricingTypeEnum["OnlyContact"] = "Only Contact";
    PricingTypeEnum["ApplyForm"] = "Apply Form";
  })(PricingTypeEnum = PostSmartSalesWorkflowRequest.PricingTypeEnum || (PostSmartSalesWorkflowRequest.PricingTypeEnum = {}));
  let PriceBaseEnum;
  (function (PriceBaseEnum) {
    PriceBaseEnum["Brutto"] = "brutto";
    PriceBaseEnum["Netto"] = "netto";
  })(PriceBaseEnum = PostSmartSalesWorkflowRequest.PriceBaseEnum || (PostSmartSalesWorkflowRequest.PriceBaseEnum = {}));
  let RenewTypeEnum;
  (function (RenewTypeEnum) {
    RenewTypeEnum["RenewUntilSold"] = "renew_until_sold";
    RenewTypeEnum["RenewTimes"] = "renew_times";
  })(RenewTypeEnum = PostSmartSalesWorkflowRequest.RenewTypeEnum || (PostSmartSalesWorkflowRequest.RenewTypeEnum = {}));
  let LeadTypeEnum;
  (function (LeadTypeEnum) {
    LeadTypeEnum["OnlyChat"] = "only_chat";
    LeadTypeEnum["ChatAllCurrency"] = "chat_all_currency";
    LeadTypeEnum["OnlyAllCurrency"] = "only_all_currency";
    LeadTypeEnum["ChatSelectedCurrency"] = "chat_selected_currency";
    LeadTypeEnum["OnlySelectedCurrency"] = "only_selected_currency";
  })(LeadTypeEnum = PostSmartSalesWorkflowRequest.LeadTypeEnum || (PostSmartSalesWorkflowRequest.LeadTypeEnum = {}));
  let PaymentDirectEnum;
  (function (PaymentDirectEnum) {
    PaymentDirectEnum["All"] = "all";
    PaymentDirectEnum["Selected"] = "selected";
    PaymentDirectEnum["Disabled"] = "disabled";
  })(PaymentDirectEnum = PostSmartSalesWorkflowRequest.PaymentDirectEnum || (PostSmartSalesWorkflowRequest.PaymentDirectEnum = {}));
  let PaymentBankEnum;
  (function (PaymentBankEnum) {
    PaymentBankEnum["All"] = "all";
    PaymentBankEnum["Selected"] = "selected";
    PaymentBankEnum["Disabled"] = "disabled";
  })(PaymentBankEnum = PostSmartSalesWorkflowRequest.PaymentBankEnum || (PostSmartSalesWorkflowRequest.PaymentBankEnum = {}));
  let PaymentOfflineEnum;
  (function (PaymentOfflineEnum) {
    PaymentOfflineEnum["All"] = "all";
    PaymentOfflineEnum["Selected"] = "selected";
    PaymentOfflineEnum["Disabled"] = "disabled";
  })(PaymentOfflineEnum = PostSmartSalesWorkflowRequest.PaymentOfflineEnum || (PostSmartSalesWorkflowRequest.PaymentOfflineEnum = {}));
  let ShippingRuleEnum;
  (function (ShippingRuleEnum) {
    ShippingRuleEnum["All"] = "all";
    ShippingRuleEnum["Selected"] = "selected";
    ShippingRuleEnum["Disabled"] = "disabled";
  })(ShippingRuleEnum = PostSmartSalesWorkflowRequest.ShippingRuleEnum || (PostSmartSalesWorkflowRequest.ShippingRuleEnum = {}));
  let InsuranceRuleEnum;
  (function (InsuranceRuleEnum) {
    InsuranceRuleEnum["All"] = "all";
    InsuranceRuleEnum["Selected"] = "selected";
    InsuranceRuleEnum["Disabled"] = "disabled";
  })(InsuranceRuleEnum = PostSmartSalesWorkflowRequest.InsuranceRuleEnum || (PostSmartSalesWorkflowRequest.InsuranceRuleEnum = {}));
  let CustomFieldSourceEnum;
  (function (CustomFieldSourceEnum) {
    CustomFieldSourceEnum["All"] = "all";
    CustomFieldSourceEnum["Selected"] = "selected";
    CustomFieldSourceEnum["Disabled"] = "disabled";
  })(CustomFieldSourceEnum = PostSmartSalesWorkflowRequest.CustomFieldSourceEnum || (PostSmartSalesWorkflowRequest.CustomFieldSourceEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = PostSmartSalesWorkflowRequest.IsOnlineTypeEnum || (PostSmartSalesWorkflowRequest.IsOnlineTypeEnum = {}));
  let RentBookingPricingTypeEnum;
  (function (RentBookingPricingTypeEnum) {
    RentBookingPricingTypeEnum["SimpleBasePrice"] = "simple_base_price";
    RentBookingPricingTypeEnum["CombinedBasePrice"] = "combined_base_price";
  })(RentBookingPricingTypeEnum = PostSmartSalesWorkflowRequest.RentBookingPricingTypeEnum || (PostSmartSalesWorkflowRequest.RentBookingPricingTypeEnum = {}));
  let SelectedRentBasePriceTypeEnum;
  (function (SelectedRentBasePriceTypeEnum) {
    SelectedRentBasePriceTypeEnum["Minute"] = "minute";
    SelectedRentBasePriceTypeEnum["Hour"] = "hour";
    SelectedRentBasePriceTypeEnum["Day"] = "day";
    SelectedRentBasePriceTypeEnum["Week"] = "week";
    SelectedRentBasePriceTypeEnum["Month"] = "month";
  })(SelectedRentBasePriceTypeEnum = PostSmartSalesWorkflowRequest.SelectedRentBasePriceTypeEnum || (PostSmartSalesWorkflowRequest.SelectedRentBasePriceTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = PostSmartSalesWorkflowRequest.OnlineEmployeeEnum || (PostSmartSalesWorkflowRequest.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = PostSmartSalesWorkflowRequest.ServiceAreaEmployeeEnum || (PostSmartSalesWorkflowRequest.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = PostSmartSalesWorkflowRequest.LocationEmployeeEnum || (PostSmartSalesWorkflowRequest.LocationEmployeeEnum = {}));
  let ServiceSettingsEnum;
  (function (ServiceSettingsEnum) {
    ServiceSettingsEnum["SmartSettings"] = "smart_settings";
    ServiceSettingsEnum["Custom"] = "custom";
  })(ServiceSettingsEnum = PostSmartSalesWorkflowRequest.ServiceSettingsEnum || (PostSmartSalesWorkflowRequest.ServiceSettingsEnum = {}));
  let ServiceBookingPreferenceSourceEnum;
  (function (ServiceBookingPreferenceSourceEnum) {
    ServiceBookingPreferenceSourceEnum["SmartItem"] = "smart_item";
    ServiceBookingPreferenceSourceEnum["Custom"] = "custom";
  })(ServiceBookingPreferenceSourceEnum = PostSmartSalesWorkflowRequest.ServiceBookingPreferenceSourceEnum || (PostSmartSalesWorkflowRequest.ServiceBookingPreferenceSourceEnum = {}));
  let ServiceBookingPreferenceEnum;
  (function (ServiceBookingPreferenceEnum) {
    ServiceBookingPreferenceEnum["SystemSelect"] = "system_select";
    ServiceBookingPreferenceEnum["CustomerOptionChoose"] = "customer_option_choose";
    ServiceBookingPreferenceEnum["CustomerForceChoose"] = "customer_force_choose";
  })(ServiceBookingPreferenceEnum = PostSmartSalesWorkflowRequest.ServiceBookingPreferenceEnum || (PostSmartSalesWorkflowRequest.ServiceBookingPreferenceEnum = {}));
  let MediaGallerySourceTypeEnum;
  (function (MediaGallerySourceTypeEnum) {
    MediaGallerySourceTypeEnum["SmartItem"] = "smart_item";
    MediaGallerySourceTypeEnum["SmartWorkflow"] = "smart_workflow";
  })(MediaGallerySourceTypeEnum = PostSmartSalesWorkflowRequest.MediaGallerySourceTypeEnum || (PostSmartSalesWorkflowRequest.MediaGallerySourceTypeEnum = {}));
  let PricingTypeExpandEnum;
  (function (PricingTypeExpandEnum) {
    PricingTypeExpandEnum["OnlyContact"] = "only_contact";
    PricingTypeExpandEnum["ApplyForm"] = "apply_form";
  })(PricingTypeExpandEnum = PostSmartSalesWorkflowRequest.PricingTypeExpandEnum || (PostSmartSalesWorkflowRequest.PricingTypeExpandEnum = {}));
})(PostSmartSalesWorkflowRequest || (PostSmartSalesWorkflowRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartSettingsRequest;
(function (PostSmartSettingsRequest) {
  let SupportedLanguageTypeEnum;
  (function (SupportedLanguageTypeEnum) {
    SupportedLanguageTypeEnum["All"] = "all";
    SupportedLanguageTypeEnum["Selected"] = "selected";
  })(SupportedLanguageTypeEnum = PostSmartSettingsRequest.SupportedLanguageTypeEnum || (PostSmartSettingsRequest.SupportedLanguageTypeEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = PostSmartSettingsRequest.IsOnlineTypeEnum || (PostSmartSettingsRequest.IsOnlineTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = PostSmartSettingsRequest.OnlineEmployeeEnum || (PostSmartSettingsRequest.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = PostSmartSettingsRequest.ServiceAreaEmployeeEnum || (PostSmartSettingsRequest.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = PostSmartSettingsRequest.LocationEmployeeEnum || (PostSmartSettingsRequest.LocationEmployeeEnum = {}));
  let ServiceBookingPreferenceEnum;
  (function (ServiceBookingPreferenceEnum) {
    ServiceBookingPreferenceEnum["SystemSelect"] = "system_select";
    ServiceBookingPreferenceEnum["CustomerOptionChoose"] = "customer_option_choose";
    ServiceBookingPreferenceEnum["CustomerForceChoose"] = "customer_force_choose";
  })(ServiceBookingPreferenceEnum = PostSmartSettingsRequest.ServiceBookingPreferenceEnum || (PostSmartSettingsRequest.ServiceBookingPreferenceEnum = {}));
  let MediaContentTypeEnum;
  (function (MediaContentTypeEnum) {
    MediaContentTypeEnum["Normal"] = "normal";
    MediaContentTypeEnum["Sensitive"] = "sensitive";
    MediaContentTypeEnum["Adult"] = "adult";
  })(MediaContentTypeEnum = PostSmartSettingsRequest.MediaContentTypeEnum || (PostSmartSettingsRequest.MediaContentTypeEnum = {}));
  let MediaPriceTypeEnum;
  (function (MediaPriceTypeEnum) {
    MediaPriceTypeEnum["Free"] = "free";
    MediaPriceTypeEnum["PartlySubscription"] = "partly_subscription";
    MediaPriceTypeEnum["OnlySubscription"] = "only_subscription";
  })(MediaPriceTypeEnum = PostSmartSettingsRequest.MediaPriceTypeEnum || (PostSmartSettingsRequest.MediaPriceTypeEnum = {}));
})(PostSmartSettingsRequest || (PostSmartSettingsRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSmartStockRequest;
(function (PostSmartStockRequest) {
  let StockAdjustmentTypeEnum;
  (function (StockAdjustmentTypeEnum) {
    StockAdjustmentTypeEnum["Acquisition"] = "Acquisition";
    StockAdjustmentTypeEnum["WriteOff"] = "Write-off";
    StockAdjustmentTypeEnum["Transfer"] = "Transfer";
  })(StockAdjustmentTypeEnum = PostSmartStockRequest.StockAdjustmentTypeEnum || (PostSmartStockRequest.StockAdjustmentTypeEnum = {}));
  let ExpireTypeEnum;
  (function (ExpireTypeEnum) {
    ExpireTypeEnum["NoExpire"] = "no_expire";
    ExpireTypeEnum["YesExpire"] = "yes_expire";
  })(ExpireTypeEnum = PostSmartStockRequest.ExpireTypeEnum || (PostSmartStockRequest.ExpireTypeEnum = {}));
  let FromStockLocationTypeEnum;
  (function (FromStockLocationTypeEnum) {
    FromStockLocationTypeEnum["LocationManager"] = "location_manager";
    FromStockLocationTypeEnum["ProductLocationManager"] = "product_location_manager";
  })(FromStockLocationTypeEnum = PostSmartStockRequest.FromStockLocationTypeEnum || (PostSmartStockRequest.FromStockLocationTypeEnum = {}));
  let ToStockLocationTypeEnum;
  (function (ToStockLocationTypeEnum) {
    ToStockLocationTypeEnum["LocationManager"] = "location_manager";
    ToStockLocationTypeEnum["ProductLocationManager"] = "product_location_manager";
  })(ToStockLocationTypeEnum = PostSmartStockRequest.ToStockLocationTypeEnum || (PostSmartStockRequest.ToStockLocationTypeEnum = {}));
})(PostSmartStockRequest || (PostSmartStockRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostSystemTaxLocationLimitRequest;
(function (PostSystemTaxLocationLimitRequest) {
  let LocationTypeEnum;
  (function (LocationTypeEnum) {
    LocationTypeEnum["Country"] = "country";
    LocationTypeEnum["State"] = "state";
    LocationTypeEnum["City"] = "city";
  })(LocationTypeEnum = PostSystemTaxLocationLimitRequest.LocationTypeEnum || (PostSystemTaxLocationLimitRequest.LocationTypeEnum = {}));
})(PostSystemTaxLocationLimitRequest || (PostSystemTaxLocationLimitRequest = {}));
var PostSystemTaxRuleRequest;
(function (PostSystemTaxRuleRequest) {
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["Percentage"] = "Percentage";
    CalculationTypeEnum["FixAmountForEachDay"] = "Fix amount for each day";
    CalculationTypeEnum["OneTimeFee"] = "One time fee";
  })(CalculationTypeEnum = PostSystemTaxRuleRequest.CalculationTypeEnum || (PostSystemTaxRuleRequest.CalculationTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["Location"] = "Location";
  })(LocationLimitTypeEnum = PostSystemTaxRuleRequest.LocationLimitTypeEnum || (PostSystemTaxRuleRequest.LocationLimitTypeEnum = {}));
})(PostSystemTaxRuleRequest || (PostSystemTaxRuleRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostTargetMarketCharasteristicRequest;
(function (PostTargetMarketCharasteristicRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["LocationRegion"] = "location_region";
    ServiceTypeEnum["LocationSubRegion"] = "location_sub_region";
    ServiceTypeEnum["LocationCountry"] = "location_country";
    ServiceTypeEnum["LocationProvince"] = "location_province";
    ServiceTypeEnum["LocationCity"] = "location_city";
    ServiceTypeEnum["LocationZipcode"] = "location_zipcode";
    ServiceTypeEnum["ServiceArea"] = "service_area";
    ServiceTypeEnum["Skill"] = "skill";
    ServiceTypeEnum["JobTitle"] = "job_title";
    ServiceTypeEnum["Interest"] = "interest";
    ServiceTypeEnum["SalesRevenue"] = "sales_revenue";
    ServiceTypeEnum["NumberOfEmployees"] = "number_of_employees";
    ServiceTypeEnum["RegisteredCountry"] = "registered_country";
    ServiceTypeEnum["LegalEntityType"] = "legal_entity_type";
    ServiceTypeEnum["Language"] = "language";
    ServiceTypeEnum["Gender"] = "gender";
    ServiceTypeEnum["Age"] = "age";
    ServiceTypeEnum["YearInBusiness"] = "year_in_business";
  })(ServiceTypeEnum = PostTargetMarketCharasteristicRequest.ServiceTypeEnum || (PostTargetMarketCharasteristicRequest.ServiceTypeEnum = {}));
})(PostTargetMarketCharasteristicRequest || (PostTargetMarketCharasteristicRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostTargetMarketRequest;
(function (PostTargetMarketRequest) {
  let TargetTypeEnum;
  (function (TargetTypeEnum) {
    TargetTypeEnum["Personal"] = "personal";
    TargetTypeEnum["Business"] = "business";
  })(TargetTypeEnum = PostTargetMarketRequest.TargetTypeEnum || (PostTargetMarketRequest.TargetTypeEnum = {}));
})(PostTargetMarketRequest || (PostTargetMarketRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostTaskRequest;
(function (PostTaskRequest) {
  let TimePlaningEnum;
  (function (TimePlaningEnum) {
    TimePlaningEnum["Disable"] = "disable";
    TimePlaningEnum["Enable"] = "enable";
  })(TimePlaningEnum = PostTaskRequest.TimePlaningEnum || (PostTaskRequest.TimePlaningEnum = {}));
  let RepeatTermEnum;
  (function (RepeatTermEnum) {
    RepeatTermEnum["Day"] = "day";
    RepeatTermEnum["Week"] = "week";
    RepeatTermEnum["Month"] = "month";
    RepeatTermEnum["Year"] = "year";
  })(RepeatTermEnum = PostTaskRequest.RepeatTermEnum || (PostTaskRequest.RepeatTermEnum = {}));
  let RepeatEveryMonthTypeEnum;
  (function (RepeatEveryMonthTypeEnum) {
    RepeatEveryMonthTypeEnum["Day"] = "day";
    RepeatEveryMonthTypeEnum["Week"] = "week";
  })(RepeatEveryMonthTypeEnum = PostTaskRequest.RepeatEveryMonthTypeEnum || (PostTaskRequest.RepeatEveryMonthTypeEnum = {}));
  let RepeatEveryMonthWeekOptionEnum;
  (function (RepeatEveryMonthWeekOptionEnum) {
    RepeatEveryMonthWeekOptionEnum["FirstWeek"] = "first week";
    RepeatEveryMonthWeekOptionEnum["SecondWeek"] = "second week";
    RepeatEveryMonthWeekOptionEnum["ThirdWeek"] = "third week";
    RepeatEveryMonthWeekOptionEnum["FourthWeek"] = "fourth week";
    RepeatEveryMonthWeekOptionEnum["LastWeek"] = "last week";
  })(RepeatEveryMonthWeekOptionEnum = PostTaskRequest.RepeatEveryMonthWeekOptionEnum || (PostTaskRequest.RepeatEveryMonthWeekOptionEnum = {}));
  let RepeatWeekTermDayEnum;
  (function (RepeatWeekTermDayEnum) {
    RepeatWeekTermDayEnum["Monday"] = "Monday";
    RepeatWeekTermDayEnum["Tuesday"] = "Tuesday";
    RepeatWeekTermDayEnum["Wednesday"] = "Wednesday";
    RepeatWeekTermDayEnum["Thursday"] = "Thursday";
    RepeatWeekTermDayEnum["Friday"] = "Friday";
    RepeatWeekTermDayEnum["Saturday"] = "Saturday";
    RepeatWeekTermDayEnum["Sunday"] = "Sunday";
  })(RepeatWeekTermDayEnum = PostTaskRequest.RepeatWeekTermDayEnum || (PostTaskRequest.RepeatWeekTermDayEnum = {}));
  let RepeatEveryYearTypeEnum;
  (function (RepeatEveryYearTypeEnum) {
    RepeatEveryYearTypeEnum["DayMonth"] = "day_month";
    RepeatEveryYearTypeEnum["DayWeekMonth"] = "day_week_month";
  })(RepeatEveryYearTypeEnum = PostTaskRequest.RepeatEveryYearTypeEnum || (PostTaskRequest.RepeatEveryYearTypeEnum = {}));
  let RepeatEveryYearDayMonthSelectEnum;
  (function (RepeatEveryYearDayMonthSelectEnum) {
    RepeatEveryYearDayMonthSelectEnum["January"] = "January";
    RepeatEveryYearDayMonthSelectEnum["February"] = "February";
    RepeatEveryYearDayMonthSelectEnum["March"] = "March";
    RepeatEveryYearDayMonthSelectEnum["April"] = "April";
    RepeatEveryYearDayMonthSelectEnum["May"] = "May";
    RepeatEveryYearDayMonthSelectEnum["June"] = "June";
    RepeatEveryYearDayMonthSelectEnum["July"] = "July";
    RepeatEveryYearDayMonthSelectEnum["August"] = "August";
    RepeatEveryYearDayMonthSelectEnum["September"] = "September";
    RepeatEveryYearDayMonthSelectEnum["October"] = "October";
    RepeatEveryYearDayMonthSelectEnum["November"] = "November";
    RepeatEveryYearDayMonthSelectEnum["December"] = "December";
  })(RepeatEveryYearDayMonthSelectEnum = PostTaskRequest.RepeatEveryYearDayMonthSelectEnum || (PostTaskRequest.RepeatEveryYearDayMonthSelectEnum = {}));
  let RepeatEveryYearDayWeekMonthSelectEnum;
  (function (RepeatEveryYearDayWeekMonthSelectEnum) {
    RepeatEveryYearDayWeekMonthSelectEnum["FirstWeek"] = "first week";
    RepeatEveryYearDayWeekMonthSelectEnum["SecondWeek"] = "second week";
    RepeatEveryYearDayWeekMonthSelectEnum["ThirdWeek"] = "third week";
    RepeatEveryYearDayWeekMonthSelectEnum["FourthWeek"] = "fourth week";
    RepeatEveryYearDayWeekMonthSelectEnum["LastWeek"] = "last week";
  })(RepeatEveryYearDayWeekMonthSelectEnum = PostTaskRequest.RepeatEveryYearDayWeekMonthSelectEnum || (PostTaskRequest.RepeatEveryYearDayWeekMonthSelectEnum = {}));
  let RepeatEveryYearDayWeekMonthWeekTermDayEnum;
  (function (RepeatEveryYearDayWeekMonthWeekTermDayEnum) {
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Monday"] = "Monday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Tuesday"] = "Tuesday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Wednesday"] = "Wednesday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Thursday"] = "Thursday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Friday"] = "Friday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Saturday"] = "Saturday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Sunday"] = "Sunday";
  })(RepeatEveryYearDayWeekMonthWeekTermDayEnum = PostTaskRequest.RepeatEveryYearDayWeekMonthWeekTermDayEnum || (PostTaskRequest.RepeatEveryYearDayWeekMonthWeekTermDayEnum = {}));
  let RepeatEveryYearDayWeekMonthMonthSelectEnum;
  (function (RepeatEveryYearDayWeekMonthMonthSelectEnum) {
    RepeatEveryYearDayWeekMonthMonthSelectEnum["January"] = "January";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["February"] = "February";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["March"] = "March";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["April"] = "April";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["May"] = "May";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["June"] = "June";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["July"] = "July";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["August"] = "August";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["September"] = "September";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["October"] = "October";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["November"] = "November";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["December"] = "December";
  })(RepeatEveryYearDayWeekMonthMonthSelectEnum = PostTaskRequest.RepeatEveryYearDayWeekMonthMonthSelectEnum || (PostTaskRequest.RepeatEveryYearDayWeekMonthMonthSelectEnum = {}));
  let RepeatUntilTypeEnum;
  (function (RepeatUntilTypeEnum) {
    RepeatUntilTypeEnum["NoEnd"] = "no_end";
    RepeatUntilTypeEnum["EndDate"] = "end_date";
    RepeatUntilTypeEnum["ComplatedNumber"] = "complated_number";
  })(RepeatUntilTypeEnum = PostTaskRequest.RepeatUntilTypeEnum || (PostTaskRequest.RepeatUntilTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["Coworker"] = "Coworker";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
  })(ServiceTypeEnum = PostTaskRequest.ServiceTypeEnum || (PostTaskRequest.ServiceTypeEnum = {}));
  let TaskTypeEnum;
  (function (TaskTypeEnum) {
    TaskTypeEnum["Simple"] = "simple";
    TaskTypeEnum["Template"] = "template";
  })(TaskTypeEnum = PostTaskRequest.TaskTypeEnum || (PostTaskRequest.TaskTypeEnum = {}));
})(PostTaskRequest || (PostTaskRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostTaskRuleRequest;
(function (PostTaskRuleRequest) {
  let TaskManagementEnum;
  (function (TaskManagementEnum) {
    TaskManagementEnum["AccessDenied"] = "Access denied";
    TaskManagementEnum["FullAccess"] = "Full access";
    TaskManagementEnum["OnlyView"] = "Only view";
    TaskManagementEnum["OnlyViewEdit"] = "Only view edit";
  })(TaskManagementEnum = PostTaskRuleRequest.TaskManagementEnum || (PostTaskRuleRequest.TaskManagementEnum = {}));
  let TaskSettingsEnum;
  (function (TaskSettingsEnum) {
    TaskSettingsEnum["AccessDenied"] = "Access denied";
    TaskSettingsEnum["FullAccess"] = "Full access";
    TaskSettingsEnum["OnlyView"] = "Only view";
    TaskSettingsEnum["OnlyViewEdit"] = "Only view edit";
  })(TaskSettingsEnum = PostTaskRuleRequest.TaskSettingsEnum || (PostTaskRuleRequest.TaskSettingsEnum = {}));
})(PostTaskRuleRequest || (PostTaskRuleRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostTaskWorkGroupRequest;
(function (PostTaskWorkGroupRequest) {
  let CollaborationTypeEnum;
  (function (CollaborationTypeEnum) {
    CollaborationTypeEnum["Project"] = "project";
    CollaborationTypeEnum["Workgroup"] = "workgroup";
  })(CollaborationTypeEnum = PostTaskWorkGroupRequest.CollaborationTypeEnum || (PostTaskWorkGroupRequest.CollaborationTypeEnum = {}));
  let PrivacyTypeEnum;
  (function (PrivacyTypeEnum) {
    PrivacyTypeEnum["Public"] = "public";
    PrivacyTypeEnum["Private"] = "private";
    PrivacyTypeEnum["Hidden"] = "hidden";
  })(PrivacyTypeEnum = PostTaskWorkGroupRequest.PrivacyTypeEnum || (PostTaskWorkGroupRequest.PrivacyTypeEnum = {}));
})(PostTaskWorkGroupRequest || (PostTaskWorkGroupRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostUniversalLanguageTranslationRequest;
(function (PostUniversalLanguageTranslationRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["AlbumDetail"] = "album_detail";
    ServiceTypeEnum["AdMediaDetail"] = "ad_media_detail";
    ServiceTypeEnum["CustomFieldDetail"] = "custom_field_detail";
    ServiceTypeEnum["CustomFieldPrefix"] = "custom_field_prefix";
    ServiceTypeEnum["CustomFieldSuffix"] = "custom_field_suffix";
    ServiceTypeEnum["SiUnitDetail"] = "si_unit_detail";
    ServiceTypeEnum["DigitalProduct"] = "digital_product";
    ServiceTypeEnum["PhysicalProduct"] = "physical_product";
    ServiceTypeEnum["Rent"] = "rent";
    ServiceTypeEnum["DigitalMedia"] = "digital_media";
    ServiceTypeEnum["Booking"] = "booking";
    ServiceTypeEnum["Job"] = "job";
    ServiceTypeEnum["FoodBeverage"] = "food_beverage";
    ServiceTypeEnum["ServiceOffered"] = "service_offered";
    ServiceTypeEnum["ActivityStream"] = "activity_stream";
    ServiceTypeEnum["ActivityStreamPoll"] = "activity_stream_poll";
    ServiceTypeEnum["Product"] = "product";
    ServiceTypeEnum["TargetMarket"] = "target_market";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["ProductLongDescription"] = "product_long_description";
    ServiceTypeEnum["DisclaimerPolicySmartWorkflow"] = "disclaimer_policy_smart_workflow";
    ServiceTypeEnum["ShippingPolicySmartWorkflow"] = "shipping_policy_smart_workflow";
    ServiceTypeEnum["InsurancePolicySmartWorkflow"] = "insurance_policy_smart_workflow";
    ServiceTypeEnum["RefundPolicySmartWorkflow"] = "refund_policy_smart_workflow";
    ServiceTypeEnum["DomainPreviewWidgetCollection"] = "domain_preview_widget_collection";
    ServiceTypeEnum["DomainPreviewSite"] = "domain_preview_site";
    ServiceTypeEnum["DomainPreviewPage"] = "domain_preview_page";
    ServiceTypeEnum["MarketDiscount"] = "market_discount";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartItemLongDescription"] = "smart_item_long_description";
    ServiceTypeEnum["SmartAdLongDescription"] = "smart_ad_long_description";
  })(ServiceTypeEnum = PostUniversalLanguageTranslationRequest.ServiceTypeEnum || (PostUniversalLanguageTranslationRequest.ServiceTypeEnum = {}));
})(PostUniversalLanguageTranslationRequest || (PostUniversalLanguageTranslationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostUserCommonApplicationProfileSettingsRequest;
(function (PostUserCommonApplicationProfileSettingsRequest) {
  let CommunityApplicationTypeEnum;
  (function (CommunityApplicationTypeEnum) {
    CommunityApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityApplicationTypeEnum["Dating"] = "dating";
    CommunityApplicationTypeEnum["Garden"] = "garden";
    CommunityApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityApplicationTypeEnum["SecondChance"] = "second_chance";
    CommunityApplicationTypeEnum["BeGreen"] = "be_green";
    CommunityApplicationTypeEnum["BucketList"] = "bucket_list";
    CommunityApplicationTypeEnum["GoalManager"] = "goal_manager";
    CommunityApplicationTypeEnum["Pets"] = "pets";
    CommunityApplicationTypeEnum["Volunteer"] = "volunteer";
    CommunityApplicationTypeEnum["Cooking"] = "cooking";
    CommunityApplicationTypeEnum["Collectors"] = "collectors";
    CommunityApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityApplicationTypeEnum["KidsSpace"] = "kids_space";
    CommunityApplicationTypeEnum["Sport"] = "sport";
    CommunityApplicationTypeEnum["Mindset"] = "mindset";
    CommunityApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityApplicationTypeEnum["Tech"] = "tech";
    CommunityApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityApplicationTypeEnum["BToB"] = "b_to_b";
    CommunityApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityApplicationTypeEnum["Gaming"] = "gaming";
    CommunityApplicationTypeEnum["Erotic"] = "erotic";
    CommunityApplicationTypeEnum["Roommate"] = "roommate";
    CommunityApplicationTypeEnum["Aviation"] = "aviation";
    CommunityApplicationTypeEnum["Cars"] = "cars";
    CommunityApplicationTypeEnum["Boat"] = "boat";
    CommunityApplicationTypeEnum["Fitness"] = "fitness";
    CommunityApplicationTypeEnum["Events"] = "events";
    CommunityApplicationTypeEnum["TaskManager"] = "task_manager";
    CommunityApplicationTypeEnum["Services"] = "services";
    CommunityApplicationTypeEnum["Academy"] = "academy";
    CommunityApplicationTypeEnum["StoreFinder"] = "store_finder";
    CommunityApplicationTypeEnum["ActivityStream"] = "activity_stream";
    CommunityApplicationTypeEnum["Calendar"] = "calendar";
    CommunityApplicationTypeEnum["Contacts"] = "contacts";
    CommunityApplicationTypeEnum["Notes"] = "notes";
    CommunityApplicationTypeEnum["Drive"] = "drive";
    CommunityApplicationTypeEnum["Taskly"] = "taskly";
    CommunityApplicationTypeEnum["MarketSpace"] = "market_space";
    CommunityApplicationTypeEnum["Chat"] = "chat";
    CommunityApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityApplicationTypeEnum["Renting"] = "renting";
    CommunityApplicationTypeEnum["Booking"] = "booking";
    CommunityApplicationTypeEnum["Job"] = "job";
    CommunityApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityApplicationTypeEnum["EmailManager"] = "email_manager";
    CommunityApplicationTypeEnum["RealEstate"] = "real_estate";
    CommunityApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityApplicationTypeEnum["Grocery"] = "grocery";
    CommunityApplicationTypeEnum["Fashion"] = "fashion";
    CommunityApplicationTypeEnum["HomeDesign"] = "home_design";
    CommunityApplicationTypeEnum["Taxi"] = "taxi";
    CommunityApplicationTypeEnum["SkillLink"] = "skill_link";
    CommunityApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityApplicationTypeEnum = PostUserCommonApplicationProfileSettingsRequest.CommunityApplicationTypeEnum || (PostUserCommonApplicationProfileSettingsRequest.CommunityApplicationTypeEnum = {}));
})(PostUserCommonApplicationProfileSettingsRequest || (PostUserCommonApplicationProfileSettingsRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PostWfSelectedCustomFieldRequest;
(function (PostWfSelectedCustomFieldRequest) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["CategoryTemplate"] = "category_template";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
    ServiceTypeEnum["CrmProspect"] = "crm_prospect";
  })(ServiceTypeEnum = PostWfSelectedCustomFieldRequest.ServiceTypeEnum || (PostWfSelectedCustomFieldRequest.ServiceTypeEnum = {}));
})(PostWfSelectedCustomFieldRequest || (PostWfSelectedCustomFieldRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var Privacy;
(function (Privacy) {
  let WhoFollowEnum;
  (function (WhoFollowEnum) {
    WhoFollowEnum["Private"] = "private";
    WhoFollowEnum["Anybody"] = "anybody";
  })(WhoFollowEnum = Privacy.WhoFollowEnum || (Privacy.WhoFollowEnum = {}));
  let EmailAddressVisibilityEnum;
  (function (EmailAddressVisibilityEnum) {
    EmailAddressVisibilityEnum["Nobody"] = "nobody";
    EmailAddressVisibilityEnum["Anybody"] = "anybody";
    EmailAddressVisibilityEnum["ThoseFollowMe"] = "those_follow_me";
    EmailAddressVisibilityEnum["WhoIFollow"] = "who_i_follow";
  })(EmailAddressVisibilityEnum = Privacy.EmailAddressVisibilityEnum || (Privacy.EmailAddressVisibilityEnum = {}));
  let BirthdayEnum;
  (function (BirthdayEnum) {
    BirthdayEnum["Nobody"] = "nobody";
    BirthdayEnum["Anybody"] = "anybody";
    BirthdayEnum["ThoseFollowMe"] = "those_follow_me";
    BirthdayEnum["WhoIFollow"] = "who_i_follow";
  })(BirthdayEnum = Privacy.BirthdayEnum || (Privacy.BirthdayEnum = {}));
  let InviteToErpEnum;
  (function (InviteToErpEnum) {
    InviteToErpEnum["Nobody"] = "nobody";
    InviteToErpEnum["Anybody"] = "anybody";
    InviteToErpEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToErpEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToErpEnum = Privacy.InviteToErpEnum || (Privacy.InviteToErpEnum = {}));
  let InviteToBusinessEnum;
  (function (InviteToBusinessEnum) {
    InviteToBusinessEnum["Nobody"] = "nobody";
    InviteToBusinessEnum["Anybody"] = "anybody";
    InviteToBusinessEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToBusinessEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToBusinessEnum = Privacy.InviteToBusinessEnum || (Privacy.InviteToBusinessEnum = {}));
  let InviteToCommunityEnum;
  (function (InviteToCommunityEnum) {
    InviteToCommunityEnum["Nobody"] = "nobody";
    InviteToCommunityEnum["Anybody"] = "anybody";
    InviteToCommunityEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToCommunityEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToCommunityEnum = Privacy.InviteToCommunityEnum || (Privacy.InviteToCommunityEnum = {}));
  let MarkOnMediaEnum;
  (function (MarkOnMediaEnum) {
    MarkOnMediaEnum["Nobody"] = "nobody";
    MarkOnMediaEnum["Anybody"] = "anybody";
    MarkOnMediaEnum["ThoseFollowMe"] = "those_follow_me";
    MarkOnMediaEnum["WhoIFollow"] = "who_i_follow";
  })(MarkOnMediaEnum = Privacy.MarkOnMediaEnum || (Privacy.MarkOnMediaEnum = {}));
  let MessageMeEnum;
  (function (MessageMeEnum) {
    MessageMeEnum["Nobody"] = "nobody";
    MessageMeEnum["Anybody"] = "anybody";
    MessageMeEnum["ThoseFollowMe"] = "those_follow_me";
    MessageMeEnum["WhoIFollow"] = "who_i_follow";
  })(MessageMeEnum = Privacy.MessageMeEnum || (Privacy.MessageMeEnum = {}));
  let LocationFindMeEnum;
  (function (LocationFindMeEnum) {
    LocationFindMeEnum["Nobody"] = "nobody";
    LocationFindMeEnum["Anybody"] = "anybody";
    LocationFindMeEnum["ThoseFollowMe"] = "those_follow_me";
    LocationFindMeEnum["WhoIFollow"] = "who_i_follow";
  })(LocationFindMeEnum = Privacy.LocationFindMeEnum || (Privacy.LocationFindMeEnum = {}));
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = Privacy.LocationShowEnum || (Privacy.LocationShowEnum = {}));
})(Privacy || (Privacy = {}));
var ProfileSettings;
(function (ProfileSettings) {
  let GenderEnum;
  (function (GenderEnum) {
    GenderEnum["Unknown"] = "unknown";
    GenderEnum["Male"] = "male";
    GenderEnum["Female"] = "female";
    GenderEnum["Other"] = "other";
  })(GenderEnum = ProfileSettings.GenderEnum || (ProfileSettings.GenderEnum = {}));
})(ProfileSettings || (ProfileSettings = {}));
var ProfileSettingsLocation;
(function (ProfileSettingsLocation) {
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = ProfileSettingsLocation.LocationShowEnum || (ProfileSettingsLocation.LocationShowEnum = {}));
})(ProfileSettingsLocation || (ProfileSettingsLocation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var PublicMarketDiscount;
(function (PublicMarketDiscount) {
  let DiscountTypeEnum;
  (function (DiscountTypeEnum) {
    DiscountTypeEnum["OfferToAll"] = "Offer to all";
    DiscountTypeEnum["VoucherCode"] = "Voucher code";
    DiscountTypeEnum["OfferToSpecificUsers"] = "Offer to specific users";
  })(DiscountTypeEnum = PublicMarketDiscount.DiscountTypeEnum || (PublicMarketDiscount.DiscountTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = PublicMarketDiscount.PriceChangerTypeEnum || (PublicMarketDiscount.PriceChangerTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["AreaLimit"] = "Area limit";
    LocationLimitTypeEnum["CountryLimit"] = "Country limit";
  })(LocationLimitTypeEnum = PublicMarketDiscount.LocationLimitTypeEnum || (PublicMarketDiscount.LocationLimitTypeEnum = {}));
  let ShowTypeEnum;
  (function (ShowTypeEnum) {
    ShowTypeEnum["Public"] = "public";
    ShowTypeEnum["Private"] = "private";
  })(ShowTypeEnum = PublicMarketDiscount.ShowTypeEnum || (PublicMarketDiscount.ShowTypeEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = PublicMarketDiscount.IsSavedEnum || (PublicMarketDiscount.IsSavedEnum = {}));
})(PublicMarketDiscount || (PublicMarketDiscount = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var PublicProfile;
(function (PublicProfile) {
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = PublicProfile.IsFollowEnum || (PublicProfile.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = PublicProfile.IsReportedEnum || (PublicProfile.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = PublicProfile.IsBlockedEnum || (PublicProfile.IsBlockedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = PublicProfile.IsPendingEnum || (PublicProfile.IsPendingEnum = {}));
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = PublicProfile.SubscriptionTypeEnum || (PublicProfile.SubscriptionTypeEnum = {}));
  let MessageTypeEnum;
  (function (MessageTypeEnum) {
    MessageTypeEnum["Nobody"] = "nobody";
    MessageTypeEnum["Anybody"] = "anybody";
    MessageTypeEnum["ThoseFollowMe"] = "those_follow_me";
    MessageTypeEnum["WhoIFollow"] = "who_i_follow";
  })(MessageTypeEnum = PublicProfile.MessageTypeEnum || (PublicProfile.MessageTypeEnum = {}));
})(PublicProfile || (PublicProfile = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var RBACAccessRule;
(function (RBACAccessRule) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["Crm"] = "crm";
    RuleTypeEnum["Hrms"] = "hrms";
    RuleTypeEnum["CompanyStream"] = "company_stream";
    RuleTypeEnum["OrgChart"] = "org_chart";
    RuleTypeEnum["Chat"] = "chat";
    RuleTypeEnum["CoWorkers"] = "co_workers";
    RuleTypeEnum["SalesTeam"] = "sales_team";
    RuleTypeEnum["TargetMarket"] = "target_market";
    RuleTypeEnum["WebsiteAppManager"] = "website_app_manager";
    RuleTypeEnum["SocialConnections"] = "social_connections";
    RuleTypeEnum["PopularityCenter"] = "popularity_center";
    RuleTypeEnum["MarketingTools"] = "marketing_tools";
    RuleTypeEnum["TaskManager"] = "task_manager";
    RuleTypeEnum["EmailManager"] = "email_manager";
    RuleTypeEnum["WorktimeManager"] = "worktime_manager";
    RuleTypeEnum["LocationManager"] = "location_manager";
    RuleTypeEnum["SmartCube"] = "smart_cube";
    RuleTypeEnum["Bi"] = "bi";
    RuleTypeEnum["Tax"] = "tax";
    RuleTypeEnum["Transportation"] = "transportation";
    RuleTypeEnum["Insurance"] = "insurance";
    RuleTypeEnum["Payment"] = "payment";
    RuleTypeEnum["BankAccounts"] = "bank_accounts";
    RuleTypeEnum["Procurement"] = "procurement";
    RuleTypeEnum["Auction"] = "auction";
    RuleTypeEnum["Settings"] = "settings";
    RuleTypeEnum["RbacModule"] = "rbac_module";
  })(RuleTypeEnum = RBACAccessRule.RuleTypeEnum || (RBACAccessRule.RuleTypeEnum = {}));
})(RBACAccessRule || (RBACAccessRule = {}));
var RBACGroupAccess;
(function (RBACGroupAccess) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["Crm"] = "crm";
    RuleTypeEnum["Hrms"] = "hrms";
    RuleTypeEnum["CompanyStream"] = "company_stream";
    RuleTypeEnum["OrgChart"] = "org_chart";
    RuleTypeEnum["Chat"] = "chat";
    RuleTypeEnum["CoWorkers"] = "co_workers";
    RuleTypeEnum["SalesTeam"] = "sales_team";
    RuleTypeEnum["TargetMarket"] = "target_market";
    RuleTypeEnum["WebsiteAppManager"] = "website_app_manager";
    RuleTypeEnum["SocialConnections"] = "social_connections";
    RuleTypeEnum["PopularityCenter"] = "popularity_center";
    RuleTypeEnum["MarketingTools"] = "marketing_tools";
    RuleTypeEnum["TaskManager"] = "task_manager";
    RuleTypeEnum["EmailManager"] = "email_manager";
    RuleTypeEnum["WorktimeManager"] = "worktime_manager";
    RuleTypeEnum["LocationManager"] = "location_manager";
    RuleTypeEnum["SmartCube"] = "smart_cube";
    RuleTypeEnum["Bi"] = "bi";
    RuleTypeEnum["Tax"] = "tax";
    RuleTypeEnum["Transportation"] = "transportation";
    RuleTypeEnum["Insurance"] = "insurance";
    RuleTypeEnum["Payment"] = "payment";
    RuleTypeEnum["BankAccounts"] = "bank_accounts";
    RuleTypeEnum["Procurement"] = "procurement";
    RuleTypeEnum["Auction"] = "auction";
    RuleTypeEnum["Settings"] = "settings";
    RuleTypeEnum["RbacModule"] = "rbac_module";
  })(RuleTypeEnum = RBACGroupAccess.RuleTypeEnum || (RBACGroupAccess.RuleTypeEnum = {}));
})(RBACGroupAccess || (RBACGroupAccess = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var RegistrationRequest;
(function (RegistrationRequest) {
  let SocialTypeEnum;
  (function (SocialTypeEnum) {
    SocialTypeEnum["Facebook"] = "facebook";
    SocialTypeEnum["Google"] = "google";
    SocialTypeEnum["Apple"] = "apple";
    SocialTypeEnum["Linkedin"] = "linkedin";
    SocialTypeEnum["Twitter"] = "twitter";
    SocialTypeEnum["Vk"] = "vk";
  })(SocialTypeEnum = RegistrationRequest.SocialTypeEnum || (RegistrationRequest.SocialTypeEnum = {}));
})(RegistrationRequest || (RegistrationRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ResourceExchange;
(function (ResourceExchange) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Catalog"] = "catalog";
  })(ServiceTypeEnum = ResourceExchange.ServiceTypeEnum || (ResourceExchange.ServiceTypeEnum = {}));
})(ResourceExchange || (ResourceExchange = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ResponseCloudStorageDetail;
(function (ResponseCloudStorageDetail) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["User"] = "user";
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["CoWorker"] = "co_worker";
  })(EntityTypeEnum = ResponseCloudStorageDetail.EntityTypeEnum || (ResponseCloudStorageDetail.EntityTypeEnum = {}));
})(ResponseCloudStorageDetail || (ResponseCloudStorageDetail = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ResponseCloudStorageLimit;
(function (ResponseCloudStorageLimit) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["User"] = "user";
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["CoWorker"] = "co_worker";
  })(EntityTypeEnum = ResponseCloudStorageLimit.EntityTypeEnum || (ResponseCloudStorageLimit.EntityTypeEnum = {}));
})(ResponseCloudStorageLimit || (ResponseCloudStorageLimit = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ResponseDriveRestore;
(function (ResponseDriveRestore) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Album"] = "album";
    TypeEnum["AlbumItem"] = "album_item";
    TypeEnum["Image"] = "image";
    TypeEnum["Video"] = "video";
    TypeEnum["Audio"] = "audio";
    TypeEnum["Document"] = "document";
    TypeEnum["Zip"] = "zip";
    TypeEnum["Other"] = "other";
  })(TypeEnum = ResponseDriveRestore.TypeEnum || (ResponseDriveRestore.TypeEnum = {}));
})(ResponseDriveRestore || (ResponseDriveRestore = {}));
var ResponseRestore;
(function (ResponseRestore) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Album"] = "album";
    TypeEnum["AlbumItem"] = "album_item";
    TypeEnum["Image"] = "image";
    TypeEnum["Video"] = "video";
    TypeEnum["Audio"] = "audio";
    TypeEnum["Document"] = "document";
    TypeEnum["Zip"] = "zip";
    TypeEnum["Other"] = "other";
  })(TypeEnum = ResponseRestore.TypeEnum || (ResponseRestore.TypeEnum = {}));
})(ResponseRestore || (ResponseRestore = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Room;
(function (Room) {
  let RoomTypeEnum;
  (function (RoomTypeEnum) {
    RoomTypeEnum["Direct"] = "direct";
    RoomTypeEnum["Group"] = "group";
    RoomTypeEnum["Channel"] = "channel";
    RoomTypeEnum["Ad"] = "ad";
    RoomTypeEnum["App"] = "app";
    RoomTypeEnum["WgProject"] = "wg_project";
  })(RoomTypeEnum = Room.RoomTypeEnum || (Room.RoomTypeEnum = {}));
  let CommunityTypeEnum;
  (function (CommunityTypeEnum) {
    CommunityTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityTypeEnum["Dating"] = "dating";
    CommunityTypeEnum["Garden"] = "garden";
    CommunityTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityTypeEnum["SecondChance"] = "second_chance";
    CommunityTypeEnum["BeGreen"] = "be_green";
    CommunityTypeEnum["BucketList"] = "bucket_list";
    CommunityTypeEnum["GoalManager"] = "goal_manager";
    CommunityTypeEnum["Pets"] = "pets";
    CommunityTypeEnum["Volunteer"] = "volunteer";
    CommunityTypeEnum["Cooking"] = "cooking";
    CommunityTypeEnum["Collectors"] = "collectors";
    CommunityTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityTypeEnum["KidsSpace"] = "kids_space";
    CommunityTypeEnum["Sport"] = "sport";
    CommunityTypeEnum["Mindset"] = "mindset";
    CommunityTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityTypeEnum["Tech"] = "tech";
    CommunityTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityTypeEnum["BToB"] = "b_to_b";
    CommunityTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityTypeEnum["Gaming"] = "gaming";
    CommunityTypeEnum["Erotic"] = "erotic";
    CommunityTypeEnum["Roommate"] = "roommate";
    CommunityTypeEnum["Aviation"] = "aviation";
    CommunityTypeEnum["Cars"] = "cars";
    CommunityTypeEnum["Boat"] = "boat";
    CommunityTypeEnum["Fitness"] = "fitness";
    CommunityTypeEnum["Events"] = "events";
    CommunityTypeEnum["TaskManager"] = "task_manager";
    CommunityTypeEnum["Services"] = "services";
    CommunityTypeEnum["Academy"] = "academy";
    CommunityTypeEnum["StoreFinder"] = "store_finder";
    CommunityTypeEnum["ActivityStream"] = "activity_stream";
    CommunityTypeEnum["Calendar"] = "calendar";
    CommunityTypeEnum["Contacts"] = "contacts";
    CommunityTypeEnum["Notes"] = "notes";
    CommunityTypeEnum["Drive"] = "drive";
    CommunityTypeEnum["Taskly"] = "taskly";
    CommunityTypeEnum["MarketSpace"] = "market_space";
    CommunityTypeEnum["Chat"] = "chat";
    CommunityTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityTypeEnum["Renting"] = "renting";
    CommunityTypeEnum["Booking"] = "booking";
    CommunityTypeEnum["Job"] = "job";
    CommunityTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityTypeEnum["EmailManager"] = "email_manager";
    CommunityTypeEnum["RealEstate"] = "real_estate";
    CommunityTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityTypeEnum["Grocery"] = "grocery";
    CommunityTypeEnum["Fashion"] = "fashion";
    CommunityTypeEnum["HomeDesign"] = "home_design";
    CommunityTypeEnum["Taxi"] = "taxi";
    CommunityTypeEnum["SkillLink"] = "skill_link";
    CommunityTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityTypeEnum = Room.CommunityTypeEnum || (Room.CommunityTypeEnum = {}));
  let PrivacyEnum;
  (function (PrivacyEnum) {
    PrivacyEnum["Private"] = "private";
    PrivacyEnum["Public"] = "public";
  })(PrivacyEnum = Room.PrivacyEnum || (Room.PrivacyEnum = {}));
})(Room || (Room = {}));
var RoomMember;
(function (RoomMember) {
  let PermissionEnum;
  (function (PermissionEnum) {
    PermissionEnum["Admin"] = "admin";
    PermissionEnum["Moderator"] = "moderator";
    PermissionEnum["Member"] = "member";
  })(PermissionEnum = RoomMember.PermissionEnum || (RoomMember.PermissionEnum = {}));
})(RoomMember || (RoomMember = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var SalesCoWorker;
(function (SalesCoWorker) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Active"] = "active";
    StatusEnum["Inactive"] = "inactive";
    StatusEnum["Pending"] = "pending";
  })(StatusEnum = SalesCoWorker.StatusEnum || (SalesCoWorker.StatusEnum = {}));
})(SalesCoWorker || (SalesCoWorker = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var SearchSettings;
(function (SearchSettings) {
  let SafeSearchEnum;
  (function (SafeSearchEnum) {
    SafeSearchEnum["Strict"] = "strict";
    SafeSearchEnum["Moderate"] = "moderate";
    SafeSearchEnum["Off"] = "off";
    SafeSearchEnum["Empty"] = "";
  })(SafeSearchEnum = SearchSettings.SafeSearchEnum || (SearchSettings.SafeSearchEnum = {}));
  let SearchSuggestionEnum;
  (function (SearchSuggestionEnum) {
    SearchSuggestionEnum["On"] = "on";
    SearchSuggestionEnum["Off"] = "off";
    SearchSuggestionEnum["Empty"] = "";
  })(SearchSuggestionEnum = SearchSettings.SearchSuggestionEnum || (SearchSettings.SearchSuggestionEnum = {}));
  let ResultNewWindowEnum;
  (function (ResultNewWindowEnum) {
    ResultNewWindowEnum["Yes"] = "yes";
    ResultNewWindowEnum["No"] = "no";
    ResultNewWindowEnum["Empty"] = "";
  })(ResultNewWindowEnum = SearchSettings.ResultNewWindowEnum || (SearchSettings.ResultNewWindowEnum = {}));
  let ResultPerPageEnum;
  (function (ResultPerPageEnum) {
    ResultPerPageEnum["_10"] = "10";
    ResultPerPageEnum["_20"] = "20";
    ResultPerPageEnum["_30"] = "30";
    ResultPerPageEnum["_40"] = "40";
    ResultPerPageEnum["_80"] = "80";
    ResultPerPageEnum["Automatic"] = "automatic";
    ResultPerPageEnum["Empty"] = "";
  })(ResultPerPageEnum = SearchSettings.ResultPerPageEnum || (SearchSettings.ResultPerPageEnum = {}));
  let MaximumPostStateEnum;
  (function (MaximumPostStateEnum) {
    MaximumPostStateEnum["Adult"] = "adult";
    MaximumPostStateEnum["Normal"] = "normal";
    MaximumPostStateEnum["Sensitive"] = "sensitive";
    MaximumPostStateEnum["Empty"] = "";
  })(MaximumPostStateEnum = SearchSettings.MaximumPostStateEnum || (SearchSettings.MaximumPostStateEnum = {}));
})(SearchSettings || (SearchSettings = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var SearchSettingsRequest;
(function (SearchSettingsRequest) {
  let SafeSearchEnum;
  (function (SafeSearchEnum) {
    SafeSearchEnum["Strict"] = "strict";
    SafeSearchEnum["Moderate"] = "moderate";
    SafeSearchEnum["Off"] = "off";
    SafeSearchEnum["Empty"] = "";
  })(SafeSearchEnum = SearchSettingsRequest.SafeSearchEnum || (SearchSettingsRequest.SafeSearchEnum = {}));
  let SearchSuggestionEnum;
  (function (SearchSuggestionEnum) {
    SearchSuggestionEnum["On"] = "on";
    SearchSuggestionEnum["Off"] = "off";
    SearchSuggestionEnum["Empty"] = "";
  })(SearchSuggestionEnum = SearchSettingsRequest.SearchSuggestionEnum || (SearchSettingsRequest.SearchSuggestionEnum = {}));
  let ResultNewWindowEnum;
  (function (ResultNewWindowEnum) {
    ResultNewWindowEnum["Yes"] = "yes";
    ResultNewWindowEnum["No"] = "no";
    ResultNewWindowEnum["Empty"] = "";
  })(ResultNewWindowEnum = SearchSettingsRequest.ResultNewWindowEnum || (SearchSettingsRequest.ResultNewWindowEnum = {}));
  let ResultPerPageEnum;
  (function (ResultPerPageEnum) {
    ResultPerPageEnum["_10"] = "10";
    ResultPerPageEnum["_20"] = "20";
    ResultPerPageEnum["_30"] = "30";
    ResultPerPageEnum["_40"] = "40";
    ResultPerPageEnum["_80"] = "80";
    ResultPerPageEnum["Automatic"] = "automatic";
    ResultPerPageEnum["Empty"] = "";
  })(ResultPerPageEnum = SearchSettingsRequest.ResultPerPageEnum || (SearchSettingsRequest.ResultPerPageEnum = {}));
  let MaximumPostStateEnum;
  (function (MaximumPostStateEnum) {
    MaximumPostStateEnum["Adult"] = "adult";
    MaximumPostStateEnum["Normal"] = "normal";
    MaximumPostStateEnum["Sensitive"] = "sensitive";
    MaximumPostStateEnum["Empty"] = "";
  })(MaximumPostStateEnum = SearchSettingsRequest.MaximumPostStateEnum || (SearchSettingsRequest.MaximumPostStateEnum = {}));
})(SearchSettingsRequest || (SearchSettingsRequest = {}));
var SearchSettingsResponse;
(function (SearchSettingsResponse) {
  let SafeSearchEnum;
  (function (SafeSearchEnum) {
    SafeSearchEnum["Strict"] = "strict";
    SafeSearchEnum["Moderate"] = "moderate";
    SafeSearchEnum["Off"] = "off";
  })(SafeSearchEnum = SearchSettingsResponse.SafeSearchEnum || (SearchSettingsResponse.SafeSearchEnum = {}));
  let SearchSuggestionEnum;
  (function (SearchSuggestionEnum) {
    SearchSuggestionEnum["On"] = "on";
    SearchSuggestionEnum["Off"] = "off";
  })(SearchSuggestionEnum = SearchSettingsResponse.SearchSuggestionEnum || (SearchSettingsResponse.SearchSuggestionEnum = {}));
  let ResultNewWindowEnum;
  (function (ResultNewWindowEnum) {
    ResultNewWindowEnum["Yes"] = "yes";
    ResultNewWindowEnum["No"] = "no";
  })(ResultNewWindowEnum = SearchSettingsResponse.ResultNewWindowEnum || (SearchSettingsResponse.ResultNewWindowEnum = {}));
  let ResultPerPageEnum;
  (function (ResultPerPageEnum) {
    ResultPerPageEnum["_10"] = "10";
    ResultPerPageEnum["_20"] = "20";
    ResultPerPageEnum["_30"] = "30";
    ResultPerPageEnum["_40"] = "40";
    ResultPerPageEnum["_80"] = "80";
    ResultPerPageEnum["Automatic"] = "automatic";
  })(ResultPerPageEnum = SearchSettingsResponse.ResultPerPageEnum || (SearchSettingsResponse.ResultPerPageEnum = {}));
})(SearchSettingsResponse || (SearchSettingsResponse = {}));
var SelectUniversalLanguageValue;
(function (SelectUniversalLanguageValue) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["AlbumDetail"] = "album_detail";
    ServiceTypeEnum["AdMediaDetail"] = "ad_media_detail";
    ServiceTypeEnum["CustomFieldDetail"] = "custom_field_detail";
    ServiceTypeEnum["CustomFieldPrefix"] = "custom_field_prefix";
    ServiceTypeEnum["CustomFieldSuffix"] = "custom_field_suffix";
    ServiceTypeEnum["SiUnitDetail"] = "si_unit_detail";
    ServiceTypeEnum["DigitalProduct"] = "digital_product";
    ServiceTypeEnum["PhysicalProduct"] = "physical_product";
    ServiceTypeEnum["Rent"] = "rent";
    ServiceTypeEnum["DigitalMedia"] = "digital_media";
    ServiceTypeEnum["Booking"] = "booking";
    ServiceTypeEnum["Job"] = "job";
    ServiceTypeEnum["FoodBeverage"] = "food_beverage";
    ServiceTypeEnum["ServiceOffered"] = "service_offered";
    ServiceTypeEnum["ActivityStream"] = "activity_stream";
    ServiceTypeEnum["ActivityStreamPoll"] = "activity_stream_poll";
    ServiceTypeEnum["Product"] = "product";
    ServiceTypeEnum["TargetMarket"] = "target_market";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["ProductLongDescription"] = "product_long_description";
    ServiceTypeEnum["DisclaimerPolicySmartWorkflow"] = "disclaimer_policy_smart_workflow";
    ServiceTypeEnum["ShippingPolicySmartWorkflow"] = "shipping_policy_smart_workflow";
    ServiceTypeEnum["InsurancePolicySmartWorkflow"] = "insurance_policy_smart_workflow";
    ServiceTypeEnum["RefundPolicySmartWorkflow"] = "refund_policy_smart_workflow";
    ServiceTypeEnum["DomainPreviewWidgetCollection"] = "domain_preview_widget_collection";
    ServiceTypeEnum["DomainPreviewSite"] = "domain_preview_site";
    ServiceTypeEnum["DomainPreviewPage"] = "domain_preview_page";
    ServiceTypeEnum["MarketDiscount"] = "market_discount";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartItemLongDescription"] = "smart_item_long_description";
    ServiceTypeEnum["SmartAdLongDescription"] = "smart_ad_long_description";
  })(ServiceTypeEnum = SelectUniversalLanguageValue.ServiceTypeEnum || (SelectUniversalLanguageValue.ServiceTypeEnum = {}));
})(SelectUniversalLanguageValue || (SelectUniversalLanguageValue = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var SenderBusiness;
(function (SenderBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = SenderBusiness.SubscriptionTypeEnum || (SenderBusiness.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = SenderBusiness.IsFollowEnum || (SenderBusiness.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = SenderBusiness.IsReportedEnum || (SenderBusiness.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = SenderBusiness.IsBlockedEnum || (SenderBusiness.IsBlockedEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = SenderBusiness.IsPendingEnum || (SenderBusiness.IsPendingEnum = {}));
})(SenderBusiness || (SenderBusiness = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SenderUser;
(function (SenderUser) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = SenderUser.SubscriptionTypeEnum || (SenderUser.SubscriptionTypeEnum = {}));
  let IsPendingEnum;
  (function (IsPendingEnum) {
    IsPendingEnum["Block"] = "block";
    IsPendingEnum["Unblock"] = "unblock";
    IsPendingEnum["Follow"] = "follow";
    IsPendingEnum["Pending"] = "pending";
    IsPendingEnum["Reject"] = "reject";
    IsPendingEnum["Accept"] = "accept";
    IsPendingEnum["Delete"] = "delete";
    IsPendingEnum["NotFollow"] = "not_follow";
    IsPendingEnum["Report"] = "report";
    IsPendingEnum["UnReport"] = "un_report";
    IsPendingEnum["Done"] = "done";
    IsPendingEnum["SavedActive"] = "saved_active";
    IsPendingEnum["UnSavedActive"] = "un_saved_active";
    IsPendingEnum["CrmSave"] = "crm_save";
    IsPendingEnum["CrmUnSave"] = "crm_un_save";
    IsPendingEnum["NotRequest"] = "not_request";
    IsPendingEnum["None"] = "none";
    IsPendingEnum["SavedEvent"] = "saved_event";
    IsPendingEnum["UnSavedEvent"] = "un_saved_event";
  })(IsPendingEnum = SenderUser.IsPendingEnum || (SenderUser.IsPendingEnum = {}));
})(SenderUser || (SenderUser = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SharedContact;
(function (SharedContact) {
  let EntityTypeEnum;
  (function (EntityTypeEnum) {
    EntityTypeEnum["Business"] = "business";
    EntityTypeEnum["Personal"] = "personal";
  })(EntityTypeEnum = SharedContact.EntityTypeEnum || (SharedContact.EntityTypeEnum = {}));
})(SharedContact || (SharedContact = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SharedEvent;
(function (SharedEvent) {
  let EventTypeEnum;
  (function (EventTypeEnum) {
    EventTypeEnum["TextNote"] = "text_note";
    EventTypeEnum["AudioNote"] = "audio_note";
    EventTypeEnum["VideoNote"] = "video_note";
    EventTypeEnum["Meeting"] = "meeting";
    EventTypeEnum["Task"] = "task";
    EventTypeEnum["Issue"] = "issue";
    EventTypeEnum["Case"] = "case";
    EventTypeEnum["FileUpload"] = "file_upload";
    EventTypeEnum["Email"] = "email";
    EventTypeEnum["OutsideCall"] = "outside_call";
    EventTypeEnum["Sms"] = "sms";
  })(EventTypeEnum = SharedEvent.EventTypeEnum || (SharedEvent.EventTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["CoreDeal"] = "CoreDeal";
    ServiceTypeEnum["Inventory"] = "Inventory";
    ServiceTypeEnum["AssetManager"] = "AssetManager";
    ServiceTypeEnum["Catalog"] = "Catalog";
    ServiceTypeEnum["CoreAssetManager"] = "CoreAssetManager";
    ServiceTypeEnum["Coworker"] = "Coworker";
    ServiceTypeEnum["Task"] = "Task";
    ServiceTypeEnum["ProjectWorkgroup"] = "ProjectWorkgroup";
    ServiceTypeEnum["Employee"] = "Employee";
    ServiceTypeEnum["User"] = "User";
    ServiceTypeEnum["CrmTargetedCustomer"] = "Crm_targeted_customer";
  })(ServiceTypeEnum = SharedEvent.ServiceTypeEnum || (SharedEvent.ServiceTypeEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = SharedEvent.IsSavedEnum || (SharedEvent.IsSavedEnum = {}));
})(SharedEvent || (SharedEvent = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var ShippingRule;
(function (ShippingRule) {
  let RuleTypeEnum;
  (function (RuleTypeEnum) {
    RuleTypeEnum["DayDelivery"] = "Day delivery";
    RuleTypeEnum["SameDayDelivery"] = "Same day delivery";
  })(RuleTypeEnum = ShippingRule.RuleTypeEnum || (ShippingRule.RuleTypeEnum = {}));
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["FreeShipping"] = "Free shipping";
    CalculationTypeEnum["ItemBased"] = "Item based";
    CalculationTypeEnum["NetTotal"] = "Net total";
    CalculationTypeEnum["NetWeight"] = "Net weight";
    CalculationTypeEnum["NetLength"] = "Net length";
    CalculationTypeEnum["NetVolume"] = "Net volume";
  })(CalculationTypeEnum = ShippingRule.CalculationTypeEnum || (ShippingRule.CalculationTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["Location"] = "Location";
  })(LocationLimitTypeEnum = ShippingRule.LocationLimitTypeEnum || (ShippingRule.LocationLimitTypeEnum = {}));
  let OfferFreeShippingTypeEnum;
  (function (OfferFreeShippingTypeEnum) {
    OfferFreeShippingTypeEnum["NoFreeShipping"] = "No free shipping";
    OfferFreeShippingTypeEnum["FreeShippingIf"] = "Free shipping if";
  })(OfferFreeShippingTypeEnum = ShippingRule.OfferFreeShippingTypeEnum || (ShippingRule.OfferFreeShippingTypeEnum = {}));
})(ShippingRule || (ShippingRule = {}));
var ShoppingCartItem;
(function (ShoppingCartItem) {
  let AdCubeTypeEnum;
  (function (AdCubeTypeEnum) {
    AdCubeTypeEnum["PhysicalProduct"] = "physical_product";
    AdCubeTypeEnum["DigitalProduct"] = "digital_product";
    AdCubeTypeEnum["Rent"] = "rent";
    AdCubeTypeEnum["ServiceOffered"] = "service_offered";
    AdCubeTypeEnum["DigitalMedia"] = "digital_media";
    AdCubeTypeEnum["Booking"] = "booking";
    AdCubeTypeEnum["Job"] = "job";
    AdCubeTypeEnum["FoodDrink"] = "food_drink";
    AdCubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    AdCubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    AdCubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(AdCubeTypeEnum = ShoppingCartItem.AdCubeTypeEnum || (ShoppingCartItem.AdCubeTypeEnum = {}));
})(ShoppingCartItem || (ShoppingCartItem = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ShortCatalog;
(function (ShortCatalog) {
  let CatalogTypeEnum;
  (function (CatalogTypeEnum) {
    CatalogTypeEnum["Plant"] = "plant";
    CatalogTypeEnum["Animal"] = "animal";
    CatalogTypeEnum["PhysicalProduct"] = "physical_product";
    CatalogTypeEnum["Food"] = "food";
    CatalogTypeEnum["BucketList"] = "bucket_list";
    CatalogTypeEnum["Cooking"] = "cooking";
    CatalogTypeEnum["FoodComponent"] = "food_component";
    CatalogTypeEnum["Drink"] = "drink";
    CatalogTypeEnum["ServiceTask"] = "service_task";
  })(CatalogTypeEnum = ShortCatalog.CatalogTypeEnum || (ShortCatalog.CatalogTypeEnum = {}));
})(ShortCatalog || (ShortCatalog = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ShortDriveAlbumItem;
(function (ShortDriveAlbumItem) {
  let MediaFileTypeEnum;
  (function (MediaFileTypeEnum) {
    MediaFileTypeEnum["Image"] = "image";
    MediaFileTypeEnum["Video"] = "video";
    MediaFileTypeEnum["Audio"] = "audio";
    MediaFileTypeEnum["Document"] = "document";
    MediaFileTypeEnum["Zip"] = "zip";
    MediaFileTypeEnum["Other"] = "other";
  })(MediaFileTypeEnum = ShortDriveAlbumItem.MediaFileTypeEnum || (ShortDriveAlbumItem.MediaFileTypeEnum = {}));
})(ShortDriveAlbumItem || (ShortDriveAlbumItem = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ShortEmail;
(function (ShortEmail) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
  })(TypeEnum = ShortEmail.TypeEnum || (ShortEmail.TypeEnum = {}));
})(ShortEmail || (ShortEmail = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ShortLink;
(function (ShortLink) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Website"] = "website";
    TypeEnum["SocialMedia"] = "social_media";
    TypeEnum["Other"] = "other";
  })(TypeEnum = ShortLink.TypeEnum || (ShortLink.TypeEnum = {}));
  let SocialMediaTypeEnum;
  (function (SocialMediaTypeEnum) {
    SocialMediaTypeEnum["Facebook"] = "facebook";
    SocialMediaTypeEnum["Twitter"] = "twitter";
    SocialMediaTypeEnum["Linkedin"] = "linkedin";
    SocialMediaTypeEnum["Instagram"] = "instagram";
    SocialMediaTypeEnum["Youtube"] = "youtube";
    SocialMediaTypeEnum["Pinterest"] = "pinterest";
    SocialMediaTypeEnum["Tumblr"] = "tumblr";
    SocialMediaTypeEnum["Snapchat"] = "snapchat";
    SocialMediaTypeEnum["Reddit"] = "reddit";
    SocialMediaTypeEnum["Flickr"] = "flickr";
    SocialMediaTypeEnum["Vimeo"] = "vimeo";
    SocialMediaTypeEnum["Whatsapp"] = "whatsapp";
    SocialMediaTypeEnum["Telegram"] = "telegram";
    SocialMediaTypeEnum["Wechat"] = "wechat";
    SocialMediaTypeEnum["Line"] = "line";
    SocialMediaTypeEnum["Viber"] = "viber";
    SocialMediaTypeEnum["Skype"] = "skype";
    SocialMediaTypeEnum["Qq"] = "qq";
    SocialMediaTypeEnum["Kakaotalk"] = "kakaotalk";
    SocialMediaTypeEnum["Tiktok"] = "tiktok";
    SocialMediaTypeEnum["Medium"] = "medium";
    SocialMediaTypeEnum["Quora"] = "quora";
    SocialMediaTypeEnum["Twitch"] = "twitch";
    SocialMediaTypeEnum["Soundcloud"] = "soundcloud";
    SocialMediaTypeEnum["Spotify"] = "spotify";
    SocialMediaTypeEnum["Github"] = "github";
    SocialMediaTypeEnum["Dribbble"] = "dribbble";
    SocialMediaTypeEnum["Behance"] = "behance";
    SocialMediaTypeEnum["Deviantart"] = "deviantart";
    SocialMediaTypeEnum["Foursquare"] = "foursquare";
    SocialMediaTypeEnum["Meetup"] = "meetup";
    SocialMediaTypeEnum["Vk"] = "vk";
    SocialMediaTypeEnum["Ok"] = "ok";
    SocialMediaTypeEnum["Myspace"] = "myspace";
    SocialMediaTypeEnum["Renren"] = "renren";
    SocialMediaTypeEnum["Weibo"] = "weibo";
    SocialMediaTypeEnum["Xing"] = "xing";
  })(SocialMediaTypeEnum = ShortLink.SocialMediaTypeEnum || (ShortLink.SocialMediaTypeEnum = {}));
})(ShortLink || (ShortLink = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var ShortPhone;
(function (ShortPhone) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Work"] = "Work";
    TypeEnum["Private"] = "Private";
    TypeEnum["Other"] = "Other";
  })(TypeEnum = ShortPhone.TypeEnum || (ShortPhone.TypeEnum = {}));
  let PhoneTypeEnum;
  (function (PhoneTypeEnum) {
    PhoneTypeEnum["Mobile"] = "Mobile";
    PhoneTypeEnum["Landline"] = "Landline";
  })(PhoneTypeEnum = ShortPhone.PhoneTypeEnum || (ShortPhone.PhoneTypeEnum = {}));
})(ShortPhone || (ShortPhone = {}));
var SiUnits;
(function (SiUnits) {
  let UnitTypeEnum;
  (function (UnitTypeEnum) {
    UnitTypeEnum["Length"] = "Length";
    UnitTypeEnum["Weight"] = "Weight";
    UnitTypeEnum["Volume"] = "Volume";
    UnitTypeEnum["Area"] = "Area";
    UnitTypeEnum["Time"] = "Time";
    UnitTypeEnum["Frequency"] = "Frequency";
    UnitTypeEnum["Speed"] = "Speed";
    UnitTypeEnum["Mass"] = "Mass";
    UnitTypeEnum["Temperature"] = "Temperature";
    UnitTypeEnum["Current"] = "Current";
    UnitTypeEnum["PrintingUnit"] = "Printing_unit";
    UnitTypeEnum["Luminosity"] = "Luminosity";
    UnitTypeEnum["Substance"] = "Substance";
    UnitTypeEnum["Quantity"] = "Quantity";
  })(UnitTypeEnum = SiUnits.UnitTypeEnum || (SiUnits.UnitTypeEnum = {}));
})(SiUnits || (SiUnits = {}));
var SmartAd;
(function (SmartAd) {
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = SmartAd.IsSavedEnum || (SmartAd.IsSavedEnum = {}));
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = SmartAd.CubeTypeEnum || (SmartAd.CubeTypeEnum = {}));
  let RentBookingPricingTypeEnum;
  (function (RentBookingPricingTypeEnum) {
    RentBookingPricingTypeEnum["SimpleBasePrice"] = "simple_base_price";
    RentBookingPricingTypeEnum["CombinedBasePrice"] = "combined_base_price";
  })(RentBookingPricingTypeEnum = SmartAd.RentBookingPricingTypeEnum || (SmartAd.RentBookingPricingTypeEnum = {}));
  let SelectedRentBasePriceTypeEnum;
  (function (SelectedRentBasePriceTypeEnum) {
    SelectedRentBasePriceTypeEnum["Minute"] = "minute";
    SelectedRentBasePriceTypeEnum["Hour"] = "hour";
    SelectedRentBasePriceTypeEnum["Day"] = "day";
    SelectedRentBasePriceTypeEnum["Week"] = "week";
    SelectedRentBasePriceTypeEnum["Month"] = "month";
  })(SelectedRentBasePriceTypeEnum = SmartAd.SelectedRentBasePriceTypeEnum || (SmartAd.SelectedRentBasePriceTypeEnum = {}));
  let WorktimeTypeEnum;
  (function (WorktimeTypeEnum) {
    WorktimeTypeEnum["FullTime"] = "full_time";
    WorktimeTypeEnum["PartTime"] = "part_time";
    WorktimeTypeEnum["Temporary"] = "temporary";
    WorktimeTypeEnum["Freelance"] = "freelance";
  })(WorktimeTypeEnum = SmartAd.WorktimeTypeEnum || (SmartAd.WorktimeTypeEnum = {}));
  let JobOwnerEnum;
  (function (JobOwnerEnum) {
    JobOwnerEnum["Agency"] = "agency";
    JobOwnerEnum["Own"] = "own";
  })(JobOwnerEnum = SmartAd.JobOwnerEnum || (SmartAd.JobOwnerEnum = {}));
  let ShowTextEnum;
  (function (ShowTextEnum) {
    ShowTextEnum["SmartItem"] = "smart_item";
    ShowTextEnum["SmartAd"] = "smart_ad";
  })(ShowTextEnum = SmartAd.ShowTextEnum || (SmartAd.ShowTextEnum = {}));
  let CustomFieldSourceEnum;
  (function (CustomFieldSourceEnum) {
    CustomFieldSourceEnum["All"] = "all";
    CustomFieldSourceEnum["Selected"] = "selected";
    CustomFieldSourceEnum["Disabled"] = "disabled";
  })(CustomFieldSourceEnum = SmartAd.CustomFieldSourceEnum || (SmartAd.CustomFieldSourceEnum = {}));
  let MediaGallerySourceTypeEnum;
  (function (MediaGallerySourceTypeEnum) {
    MediaGallerySourceTypeEnum["SmartItem"] = "smart_item";
    MediaGallerySourceTypeEnum["SmartWorkflow"] = "smart_workflow";
  })(MediaGallerySourceTypeEnum = SmartAd.MediaGallerySourceTypeEnum || (SmartAd.MediaGallerySourceTypeEnum = {}));
  let ResumeApplyEnum;
  (function (ResumeApplyEnum) {
    ResumeApplyEnum["Optional"] = "optional";
    ResumeApplyEnum["Required"] = "required";
  })(ResumeApplyEnum = SmartAd.ResumeApplyEnum || (SmartAd.ResumeApplyEnum = {}));
  let WorkplaceShowEnum;
  (function (WorkplaceShowEnum) {
    WorkplaceShowEnum["Show"] = "show";
    WorkplaceShowEnum["Hide"] = "hide";
  })(WorkplaceShowEnum = SmartAd.WorkplaceShowEnum || (SmartAd.WorkplaceShowEnum = {}));
  let CompanyShowEnum;
  (function (CompanyShowEnum) {
    CompanyShowEnum["Show"] = "show";
    CompanyShowEnum["Hide"] = "hide";
  })(CompanyShowEnum = SmartAd.CompanyShowEnum || (SmartAd.CompanyShowEnum = {}));
})(SmartAd || (SmartAd = {}));
var SmartAdDetail;
(function (SmartAdDetail) {
  let PricingTypeEnum;
  (function (PricingTypeEnum) {
    PricingTypeEnum["Custom"] = "Custom";
    PricingTypeEnum["Free"] = "Free";
    PricingTypeEnum["OnlyLead"] = "Only Lead";
    PricingTypeEnum["Auction"] = "Auction";
    PricingTypeEnum["OnlyForFollowers"] = "Only For Followers";
    PricingTypeEnum["OnlyForSubscribers"] = "Only For Subscribers";
    PricingTypeEnum["ConnectToInventory"] = "Connect to Inventory";
    PricingTypeEnum["OnlyContact"] = "Only Contact";
    PricingTypeEnum["ApplyForm"] = "Apply Form";
  })(PricingTypeEnum = SmartAdDetail.PricingTypeEnum || (SmartAdDetail.PricingTypeEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = SmartAdDetail.IsSavedEnum || (SmartAdDetail.IsSavedEnum = {}));
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = SmartAdDetail.CubeTypeEnum || (SmartAdDetail.CubeTypeEnum = {}));
  let RentBookingPricingTypeEnum;
  (function (RentBookingPricingTypeEnum) {
    RentBookingPricingTypeEnum["SimpleBasePrice"] = "simple_base_price";
    RentBookingPricingTypeEnum["CombinedBasePrice"] = "combined_base_price";
  })(RentBookingPricingTypeEnum = SmartAdDetail.RentBookingPricingTypeEnum || (SmartAdDetail.RentBookingPricingTypeEnum = {}));
  let SelectedRentBasePriceTypeEnum;
  (function (SelectedRentBasePriceTypeEnum) {
    SelectedRentBasePriceTypeEnum["Minute"] = "minute";
    SelectedRentBasePriceTypeEnum["Hour"] = "hour";
    SelectedRentBasePriceTypeEnum["Day"] = "day";
    SelectedRentBasePriceTypeEnum["Week"] = "week";
    SelectedRentBasePriceTypeEnum["Month"] = "month";
  })(SelectedRentBasePriceTypeEnum = SmartAdDetail.SelectedRentBasePriceTypeEnum || (SmartAdDetail.SelectedRentBasePriceTypeEnum = {}));
  let WorktimeTypeEnum;
  (function (WorktimeTypeEnum) {
    WorktimeTypeEnum["FullTime"] = "full_time";
    WorktimeTypeEnum["PartTime"] = "part_time";
    WorktimeTypeEnum["Temporary"] = "temporary";
    WorktimeTypeEnum["Freelance"] = "freelance";
  })(WorktimeTypeEnum = SmartAdDetail.WorktimeTypeEnum || (SmartAdDetail.WorktimeTypeEnum = {}));
  let JobOwnerEnum;
  (function (JobOwnerEnum) {
    JobOwnerEnum["Agency"] = "agency";
    JobOwnerEnum["Own"] = "own";
  })(JobOwnerEnum = SmartAdDetail.JobOwnerEnum || (SmartAdDetail.JobOwnerEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = SmartAdDetail.IsReportedEnum || (SmartAdDetail.IsReportedEnum = {}));
  let LeadTypeEnum;
  (function (LeadTypeEnum) {
    LeadTypeEnum["OnlyChat"] = "only_chat";
    LeadTypeEnum["ChatAllCurrency"] = "chat_all_currency";
    LeadTypeEnum["OnlyAllCurrency"] = "only_all_currency";
    LeadTypeEnum["ChatSelectedCurrency"] = "chat_selected_currency";
    LeadTypeEnum["OnlySelectedCurrency"] = "only_selected_currency";
  })(LeadTypeEnum = SmartAdDetail.LeadTypeEnum || (SmartAdDetail.LeadTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = SmartAdDetail.IsFollowEnum || (SmartAdDetail.IsFollowEnum = {}));
  let CustomFieldSourceEnum;
  (function (CustomFieldSourceEnum) {
    CustomFieldSourceEnum["All"] = "all";
    CustomFieldSourceEnum["Selected"] = "selected";
    CustomFieldSourceEnum["Disabled"] = "disabled";
  })(CustomFieldSourceEnum = SmartAdDetail.CustomFieldSourceEnum || (SmartAdDetail.CustomFieldSourceEnum = {}));
})(SmartAdDetail || (SmartAdDetail = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var SmartAdPayment;
(function (SmartAdPayment) {
  let PaymentTypeEnum;
  (function (PaymentTypeEnum) {
    PaymentTypeEnum["Offline"] = "Offline";
    PaymentTypeEnum["Direct"] = "Direct";
    PaymentTypeEnum["BankTransfer"] = "Bank transfer";
  })(PaymentTypeEnum = SmartAdPayment.PaymentTypeEnum || (SmartAdPayment.PaymentTypeEnum = {}));
  let PriceTypeEnum;
  (function (PriceTypeEnum) {
    PriceTypeEnum["Discount"] = "Discount";
    PriceTypeEnum["ExtraCharge"] = "Extra charge";
  })(PriceTypeEnum = SmartAdPayment.PriceTypeEnum || (SmartAdPayment.PriceTypeEnum = {}));
  let PriceChangerTypeEnum;
  (function (PriceChangerTypeEnum) {
    PriceChangerTypeEnum["FixAmount"] = "Fix amount";
    PriceChangerTypeEnum["Percentage"] = "Percentage";
  })(PriceChangerTypeEnum = SmartAdPayment.PriceChangerTypeEnum || (SmartAdPayment.PriceChangerTypeEnum = {}));
})(SmartAdPayment || (SmartAdPayment = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SmartCheckoutResponse;
(function (SmartCheckoutResponse) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Processing"] = "processing";
    StatusEnum["Completed"] = "completed";
    StatusEnum["Failed"] = "failed";
    StatusEnum["Cancelled"] = "cancelled";
  })(StatusEnum = SmartCheckoutResponse.StatusEnum || (SmartCheckoutResponse.StatusEnum = {}));
  let PaymentStatusEnum;
  (function (PaymentStatusEnum) {
    PaymentStatusEnum["Unpaid"] = "unpaid";
    PaymentStatusEnum["Paid"] = "paid";
    PaymentStatusEnum["PendingBankTransfer"] = "pending_bank_transfer";
  })(PaymentStatusEnum = SmartCheckoutResponse.PaymentStatusEnum || (SmartCheckoutResponse.PaymentStatusEnum = {}));
  let ShippingStatusEnum;
  (function (ShippingStatusEnum) {
    ShippingStatusEnum["Unshipped"] = "unshipped";
    ShippingStatusEnum["Shipped"] = "shipped";
    ShippingStatusEnum["Delivered"] = "delivered";
  })(ShippingStatusEnum = SmartCheckoutResponse.ShippingStatusEnum || (SmartCheckoutResponse.ShippingStatusEnum = {}));
})(SmartCheckoutResponse || (SmartCheckoutResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SmartCoreAd;
(function (SmartCoreAd) {
  let PricingTypeEnum;
  (function (PricingTypeEnum) {
    PricingTypeEnum["Custom"] = "Custom";
    PricingTypeEnum["Free"] = "Free";
    PricingTypeEnum["OnlyLead"] = "Only Lead";
    PricingTypeEnum["Auction"] = "Auction";
    PricingTypeEnum["OnlyForFollowers"] = "Only For Followers";
    PricingTypeEnum["OnlyForSubscribers"] = "Only For Subscribers";
    PricingTypeEnum["ConnectToInventory"] = "Connect to Inventory";
    PricingTypeEnum["OnlyContact"] = "Only Contact";
    PricingTypeEnum["ApplyForm"] = "Apply Form";
  })(PricingTypeEnum = SmartCoreAd.PricingTypeEnum || (SmartCoreAd.PricingTypeEnum = {}));
  let IsSavedEnum;
  (function (IsSavedEnum) {
    IsSavedEnum["Block"] = "block";
    IsSavedEnum["Unblock"] = "unblock";
    IsSavedEnum["Follow"] = "follow";
    IsSavedEnum["Pending"] = "pending";
    IsSavedEnum["Reject"] = "reject";
    IsSavedEnum["Accept"] = "accept";
    IsSavedEnum["Delete"] = "delete";
    IsSavedEnum["NotFollow"] = "not_follow";
    IsSavedEnum["Report"] = "report";
    IsSavedEnum["UnReport"] = "un_report";
    IsSavedEnum["Done"] = "done";
    IsSavedEnum["SavedActive"] = "saved_active";
    IsSavedEnum["UnSavedActive"] = "un_saved_active";
    IsSavedEnum["CrmSave"] = "crm_save";
    IsSavedEnum["CrmUnSave"] = "crm_un_save";
    IsSavedEnum["NotRequest"] = "not_request";
    IsSavedEnum["None"] = "none";
    IsSavedEnum["SavedEvent"] = "saved_event";
    IsSavedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsSavedEnum = SmartCoreAd.IsSavedEnum || (SmartCoreAd.IsSavedEnum = {}));
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = SmartCoreAd.CubeTypeEnum || (SmartCoreAd.CubeTypeEnum = {}));
  let RentBookingPricingTypeEnum;
  (function (RentBookingPricingTypeEnum) {
    RentBookingPricingTypeEnum["SimpleBasePrice"] = "simple_base_price";
    RentBookingPricingTypeEnum["CombinedBasePrice"] = "combined_base_price";
  })(RentBookingPricingTypeEnum = SmartCoreAd.RentBookingPricingTypeEnum || (SmartCoreAd.RentBookingPricingTypeEnum = {}));
  let SelectedRentBasePriceTypeEnum;
  (function (SelectedRentBasePriceTypeEnum) {
    SelectedRentBasePriceTypeEnum["Minute"] = "minute";
    SelectedRentBasePriceTypeEnum["Hour"] = "hour";
    SelectedRentBasePriceTypeEnum["Day"] = "day";
    SelectedRentBasePriceTypeEnum["Week"] = "week";
    SelectedRentBasePriceTypeEnum["Month"] = "month";
  })(SelectedRentBasePriceTypeEnum = SmartCoreAd.SelectedRentBasePriceTypeEnum || (SmartCoreAd.SelectedRentBasePriceTypeEnum = {}));
  let WorktimeTypeEnum;
  (function (WorktimeTypeEnum) {
    WorktimeTypeEnum["FullTime"] = "full_time";
    WorktimeTypeEnum["PartTime"] = "part_time";
    WorktimeTypeEnum["Temporary"] = "temporary";
    WorktimeTypeEnum["Freelance"] = "freelance";
  })(WorktimeTypeEnum = SmartCoreAd.WorktimeTypeEnum || (SmartCoreAd.WorktimeTypeEnum = {}));
  let JobOwnerEnum;
  (function (JobOwnerEnum) {
    JobOwnerEnum["Agency"] = "agency";
    JobOwnerEnum["Own"] = "own";
  })(JobOwnerEnum = SmartCoreAd.JobOwnerEnum || (SmartCoreAd.JobOwnerEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = SmartCoreAd.IsReportedEnum || (SmartCoreAd.IsReportedEnum = {}));
  let LeadTypeEnum;
  (function (LeadTypeEnum) {
    LeadTypeEnum["OnlyChat"] = "only_chat";
    LeadTypeEnum["ChatAllCurrency"] = "chat_all_currency";
    LeadTypeEnum["OnlyAllCurrency"] = "only_all_currency";
    LeadTypeEnum["ChatSelectedCurrency"] = "chat_selected_currency";
    LeadTypeEnum["OnlySelectedCurrency"] = "only_selected_currency";
  })(LeadTypeEnum = SmartCoreAd.LeadTypeEnum || (SmartCoreAd.LeadTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = SmartCoreAd.IsFollowEnum || (SmartCoreAd.IsFollowEnum = {}));
})(SmartCoreAd || (SmartCoreAd = {}));
var SmartItem;
(function (SmartItem) {
  let CubeTypeEnum;
  (function (CubeTypeEnum) {
    CubeTypeEnum["PhysicalProduct"] = "physical_product";
    CubeTypeEnum["DigitalProduct"] = "digital_product";
    CubeTypeEnum["Rent"] = "rent";
    CubeTypeEnum["ServiceOffered"] = "service_offered";
    CubeTypeEnum["DigitalMedia"] = "digital_media";
    CubeTypeEnum["Booking"] = "booking";
    CubeTypeEnum["Job"] = "job";
    CubeTypeEnum["FoodDrink"] = "food_drink";
    CubeTypeEnum["DigitalMagazine"] = "digital_magazine";
    CubeTypeEnum["DigitalPodcast"] = "digital_podcast";
    CubeTypeEnum["KnowledgeAcademy"] = "knowledge_academy";
  })(CubeTypeEnum = SmartItem.CubeTypeEnum || (SmartItem.CubeTypeEnum = {}));
  let InventoryTypeEnum;
  (function (InventoryTypeEnum) {
    InventoryTypeEnum["None"] = "none";
    InventoryTypeEnum["FinishedGoods"] = "finished_goods";
    InventoryTypeEnum["RawMaterials"] = "raw_materials";
    InventoryTypeEnum["Component"] = "component";
    InventoryTypeEnum["Wip"] = "wip";
    InventoryTypeEnum["PackingMaterials"] = "packing_materials";
    InventoryTypeEnum["Mro"] = "mro";
    InventoryTypeEnum["Others"] = "others";
    InventoryTypeEnum["LivePlant"] = "live_plant";
    InventoryTypeEnum["LiveAnimal"] = "live_animal";
    InventoryTypeEnum["Food"] = "food";
    InventoryTypeEnum["FoodComponent"] = "food_component";
    InventoryTypeEnum["Drink"] = "drink";
  })(InventoryTypeEnum = SmartItem.InventoryTypeEnum || (SmartItem.InventoryTypeEnum = {}));
  let AmountTypeEnum;
  (function (AmountTypeEnum) {
    AmountTypeEnum["Unlimited"] = "unlimited";
    AmountTypeEnum["Calculated"] = "calculated";
  })(AmountTypeEnum = SmartItem.AmountTypeEnum || (SmartItem.AmountTypeEnum = {}));
  let ManagingInventoryEnum;
  (function (ManagingInventoryEnum) {
    ManagingInventoryEnum["None"] = "none";
    ManagingInventoryEnum["Fifo"] = "fifo";
    ManagingInventoryEnum["Lifo"] = "lifo";
  })(ManagingInventoryEnum = SmartItem.ManagingInventoryEnum || (SmartItem.ManagingInventoryEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = SmartItem.IsOnlineTypeEnum || (SmartItem.IsOnlineTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = SmartItem.OnlineEmployeeEnum || (SmartItem.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = SmartItem.ServiceAreaEmployeeEnum || (SmartItem.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = SmartItem.LocationEmployeeEnum || (SmartItem.LocationEmployeeEnum = {}));
  let ServiceSettingsEnum;
  (function (ServiceSettingsEnum) {
    ServiceSettingsEnum["SmartSettings"] = "smart_settings";
    ServiceSettingsEnum["Custom"] = "custom";
  })(ServiceSettingsEnum = SmartItem.ServiceSettingsEnum || (SmartItem.ServiceSettingsEnum = {}));
  let WorkplaceTypeEnum;
  (function (WorkplaceTypeEnum) {
    WorkplaceTypeEnum["OnSite"] = "on_site";
    WorkplaceTypeEnum["Hybrid"] = "hybrid";
    WorkplaceTypeEnum["Remote"] = "remote";
  })(WorkplaceTypeEnum = SmartItem.WorkplaceTypeEnum || (SmartItem.WorkplaceTypeEnum = {}));
})(SmartItem || (SmartItem = {}));
var SmartLocationManager;
(function (SmartLocationManager) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["Warehouse"] = "warehouse";
    TypeEnum["Store"] = "store";
    TypeEnum["Office"] = "office";
    TypeEnum["Factory"] = "factory";
    TypeEnum["Other"] = "other";
    TypeEnum["Restaurant"] = "restaurant";
    TypeEnum["Hotel"] = "hotel";
    TypeEnum["Hospital"] = "hospital";
    TypeEnum["School"] = "school";
    TypeEnum["University"] = "university";
    TypeEnum["Government"] = "government";
    TypeEnum["NonGovernmentalOrganization"] = "non_governmental_organization";
    TypeEnum["ReligiousOrganization"] = "religious_organization";
    TypeEnum["Charity"] = "charity";
    TypeEnum["RegisteredBusiness"] = "registered_business";
    TypeEnum["PersonalAddress"] = "personal_address";
  })(TypeEnum = SmartLocationManager.TypeEnum || (SmartLocationManager.TypeEnum = {}));
})(SmartLocationManager || (SmartLocationManager = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var SmartPayment;
(function (SmartPayment) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["SmartCube"] = "smart_cube";
    ServiceTypeEnum["LocationManager"] = "location_manager";
    ServiceTypeEnum["SmartLocationManager"] = "smart_location_manager";
  })(ServiceTypeEnum = SmartPayment.ServiceTypeEnum || (SmartPayment.ServiceTypeEnum = {}));
  let PaymentTypeEnum;
  (function (PaymentTypeEnum) {
    PaymentTypeEnum["Offline"] = "Offline";
    PaymentTypeEnum["Direct"] = "Direct";
    PaymentTypeEnum["BankTransfer"] = "Bank transfer";
  })(PaymentTypeEnum = SmartPayment.PaymentTypeEnum || (SmartPayment.PaymentTypeEnum = {}));
})(SmartPayment || (SmartPayment = {}));
var SmartPolicy;
(function (SmartPolicy) {
  let PolicyTypeEnum;
  (function (PolicyTypeEnum) {
    PolicyTypeEnum["Refund"] = "refund";
    PolicyTypeEnum["Disclaimer"] = "disclaimer";
    PolicyTypeEnum["Shipping"] = "shipping";
    PolicyTypeEnum["Insurance"] = "insurance";
  })(PolicyTypeEnum = SmartPolicy.PolicyTypeEnum || (SmartPolicy.PolicyTypeEnum = {}));
})(SmartPolicy || (SmartPolicy = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SmartSalesWorkflow;
(function (SmartSalesWorkflow) {
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Draft"] = "Draft";
    StatusEnum["Published"] = "Published";
    StatusEnum["Deleted"] = "Deleted";
    StatusEnum["Inactive"] = "Inactive";
  })(StatusEnum = SmartSalesWorkflow.StatusEnum || (SmartSalesWorkflow.StatusEnum = {}));
  let AcceptedBuyerTypeEnum;
  (function (AcceptedBuyerTypeEnum) {
    AcceptedBuyerTypeEnum["B2b"] = "b2b";
    AcceptedBuyerTypeEnum["B2c"] = "b2c";
    AcceptedBuyerTypeEnum["Both"] = "both";
  })(AcceptedBuyerTypeEnum = SmartSalesWorkflow.AcceptedBuyerTypeEnum || (SmartSalesWorkflow.AcceptedBuyerTypeEnum = {}));
  let ByTimeTypeEnum;
  (function (ByTimeTypeEnum) {
    ByTimeTypeEnum["Never"] = "never";
    ByTimeTypeEnum["ByTime"] = "by_time";
  })(ByTimeTypeEnum = SmartSalesWorkflow.ByTimeTypeEnum || (SmartSalesWorkflow.ByTimeTypeEnum = {}));
  let PricingTypeEnum;
  (function (PricingTypeEnum) {
    PricingTypeEnum["Custom"] = "Custom";
    PricingTypeEnum["Free"] = "Free";
    PricingTypeEnum["OnlyLead"] = "Only Lead";
    PricingTypeEnum["Auction"] = "Auction";
    PricingTypeEnum["OnlyForFollowers"] = "Only For Followers";
    PricingTypeEnum["OnlyForSubscribers"] = "Only For Subscribers";
    PricingTypeEnum["ConnectToInventory"] = "Connect to Inventory";
    PricingTypeEnum["OnlyContact"] = "Only Contact";
    PricingTypeEnum["ApplyForm"] = "Apply Form";
  })(PricingTypeEnum = SmartSalesWorkflow.PricingTypeEnum || (SmartSalesWorkflow.PricingTypeEnum = {}));
  let PriceBaseEnum;
  (function (PriceBaseEnum) {
    PriceBaseEnum["Brutto"] = "brutto";
    PriceBaseEnum["Netto"] = "netto";
  })(PriceBaseEnum = SmartSalesWorkflow.PriceBaseEnum || (SmartSalesWorkflow.PriceBaseEnum = {}));
  let RenewTypeEnum;
  (function (RenewTypeEnum) {
    RenewTypeEnum["RenewUntilSold"] = "renew_until_sold";
    RenewTypeEnum["RenewTimes"] = "renew_times";
  })(RenewTypeEnum = SmartSalesWorkflow.RenewTypeEnum || (SmartSalesWorkflow.RenewTypeEnum = {}));
  let LeadTypeEnum;
  (function (LeadTypeEnum) {
    LeadTypeEnum["OnlyChat"] = "only_chat";
    LeadTypeEnum["ChatAllCurrency"] = "chat_all_currency";
    LeadTypeEnum["OnlyAllCurrency"] = "only_all_currency";
    LeadTypeEnum["ChatSelectedCurrency"] = "chat_selected_currency";
    LeadTypeEnum["OnlySelectedCurrency"] = "only_selected_currency";
  })(LeadTypeEnum = SmartSalesWorkflow.LeadTypeEnum || (SmartSalesWorkflow.LeadTypeEnum = {}));
  let PaymentDirectEnum;
  (function (PaymentDirectEnum) {
    PaymentDirectEnum["All"] = "all";
    PaymentDirectEnum["Selected"] = "selected";
    PaymentDirectEnum["Disabled"] = "disabled";
  })(PaymentDirectEnum = SmartSalesWorkflow.PaymentDirectEnum || (SmartSalesWorkflow.PaymentDirectEnum = {}));
  let PaymentBankEnum;
  (function (PaymentBankEnum) {
    PaymentBankEnum["All"] = "all";
    PaymentBankEnum["Selected"] = "selected";
    PaymentBankEnum["Disabled"] = "disabled";
  })(PaymentBankEnum = SmartSalesWorkflow.PaymentBankEnum || (SmartSalesWorkflow.PaymentBankEnum = {}));
  let PaymentOfflineEnum;
  (function (PaymentOfflineEnum) {
    PaymentOfflineEnum["All"] = "all";
    PaymentOfflineEnum["Selected"] = "selected";
    PaymentOfflineEnum["Disabled"] = "disabled";
  })(PaymentOfflineEnum = SmartSalesWorkflow.PaymentOfflineEnum || (SmartSalesWorkflow.PaymentOfflineEnum = {}));
  let ShippingRuleEnum;
  (function (ShippingRuleEnum) {
    ShippingRuleEnum["All"] = "all";
    ShippingRuleEnum["Selected"] = "selected";
    ShippingRuleEnum["Disabled"] = "disabled";
  })(ShippingRuleEnum = SmartSalesWorkflow.ShippingRuleEnum || (SmartSalesWorkflow.ShippingRuleEnum = {}));
  let InsuranceRuleEnum;
  (function (InsuranceRuleEnum) {
    InsuranceRuleEnum["All"] = "all";
    InsuranceRuleEnum["Selected"] = "selected";
    InsuranceRuleEnum["Disabled"] = "disabled";
  })(InsuranceRuleEnum = SmartSalesWorkflow.InsuranceRuleEnum || (SmartSalesWorkflow.InsuranceRuleEnum = {}));
  let CustomFieldSourceEnum;
  (function (CustomFieldSourceEnum) {
    CustomFieldSourceEnum["All"] = "all";
    CustomFieldSourceEnum["Selected"] = "selected";
    CustomFieldSourceEnum["Disabled"] = "disabled";
  })(CustomFieldSourceEnum = SmartSalesWorkflow.CustomFieldSourceEnum || (SmartSalesWorkflow.CustomFieldSourceEnum = {}));
  let RentBookingPricingTypeEnum;
  (function (RentBookingPricingTypeEnum) {
    RentBookingPricingTypeEnum["SimpleBasePrice"] = "simple_base_price";
    RentBookingPricingTypeEnum["CombinedBasePrice"] = "combined_base_price";
  })(RentBookingPricingTypeEnum = SmartSalesWorkflow.RentBookingPricingTypeEnum || (SmartSalesWorkflow.RentBookingPricingTypeEnum = {}));
  let SelectedRentBasePriceTypeEnum;
  (function (SelectedRentBasePriceTypeEnum) {
    SelectedRentBasePriceTypeEnum["Minute"] = "minute";
    SelectedRentBasePriceTypeEnum["Hour"] = "hour";
    SelectedRentBasePriceTypeEnum["Day"] = "day";
    SelectedRentBasePriceTypeEnum["Week"] = "week";
    SelectedRentBasePriceTypeEnum["Month"] = "month";
  })(SelectedRentBasePriceTypeEnum = SmartSalesWorkflow.SelectedRentBasePriceTypeEnum || (SmartSalesWorkflow.SelectedRentBasePriceTypeEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = SmartSalesWorkflow.IsOnlineTypeEnum || (SmartSalesWorkflow.IsOnlineTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = SmartSalesWorkflow.OnlineEmployeeEnum || (SmartSalesWorkflow.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = SmartSalesWorkflow.ServiceAreaEmployeeEnum || (SmartSalesWorkflow.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = SmartSalesWorkflow.LocationEmployeeEnum || (SmartSalesWorkflow.LocationEmployeeEnum = {}));
  let ServiceSettingsEnum;
  (function (ServiceSettingsEnum) {
    ServiceSettingsEnum["SmartSettings"] = "smart_settings";
    ServiceSettingsEnum["Custom"] = "custom";
  })(ServiceSettingsEnum = SmartSalesWorkflow.ServiceSettingsEnum || (SmartSalesWorkflow.ServiceSettingsEnum = {}));
  let MediaGallerySourceTypeEnum;
  (function (MediaGallerySourceTypeEnum) {
    MediaGallerySourceTypeEnum["SmartItem"] = "smart_item";
    MediaGallerySourceTypeEnum["SmartWorkflow"] = "smart_workflow";
  })(MediaGallerySourceTypeEnum = SmartSalesWorkflow.MediaGallerySourceTypeEnum || (SmartSalesWorkflow.MediaGallerySourceTypeEnum = {}));
  let PricingTypeExpandEnum;
  (function (PricingTypeExpandEnum) {
    PricingTypeExpandEnum["OnlyContact"] = "only_contact";
    PricingTypeExpandEnum["ApplyForm"] = "apply_form";
  })(PricingTypeExpandEnum = SmartSalesWorkflow.PricingTypeExpandEnum || (SmartSalesWorkflow.PricingTypeExpandEnum = {}));
})(SmartSalesWorkflow || (SmartSalesWorkflow = {}));
var SmartSettings;
(function (SmartSettings) {
  let SupportedLanguageTypeEnum;
  (function (SupportedLanguageTypeEnum) {
    SupportedLanguageTypeEnum["All"] = "all";
    SupportedLanguageTypeEnum["Selected"] = "selected";
  })(SupportedLanguageTypeEnum = SmartSettings.SupportedLanguageTypeEnum || (SmartSettings.SupportedLanguageTypeEnum = {}));
  let IsOnlineTypeEnum;
  (function (IsOnlineTypeEnum) {
    IsOnlineTypeEnum["OnlineToo"] = "online_too";
    IsOnlineTypeEnum["OnlyOnline"] = "only_online";
    IsOnlineTypeEnum["DisableOnline"] = "disable_online";
  })(IsOnlineTypeEnum = SmartSettings.IsOnlineTypeEnum || (SmartSettings.IsOnlineTypeEnum = {}));
  let OnlineEmployeeEnum;
  (function (OnlineEmployeeEnum) {
    OnlineEmployeeEnum["All"] = "all";
    OnlineEmployeeEnum["Selected"] = "selected";
  })(OnlineEmployeeEnum = SmartSettings.OnlineEmployeeEnum || (SmartSettings.OnlineEmployeeEnum = {}));
  let ServiceAreaEmployeeEnum;
  (function (ServiceAreaEmployeeEnum) {
    ServiceAreaEmployeeEnum["All"] = "all";
    ServiceAreaEmployeeEnum["Selected"] = "selected";
  })(ServiceAreaEmployeeEnum = SmartSettings.ServiceAreaEmployeeEnum || (SmartSettings.ServiceAreaEmployeeEnum = {}));
  let LocationEmployeeEnum;
  (function (LocationEmployeeEnum) {
    LocationEmployeeEnum["All"] = "all";
    LocationEmployeeEnum["Selected"] = "selected";
  })(LocationEmployeeEnum = SmartSettings.LocationEmployeeEnum || (SmartSettings.LocationEmployeeEnum = {}));
  let MediaContentTypeEnum;
  (function (MediaContentTypeEnum) {
    MediaContentTypeEnum["Normal"] = "normal";
    MediaContentTypeEnum["Sensitive"] = "sensitive";
    MediaContentTypeEnum["Adult"] = "adult";
  })(MediaContentTypeEnum = SmartSettings.MediaContentTypeEnum || (SmartSettings.MediaContentTypeEnum = {}));
  let MediaPriceTypeEnum;
  (function (MediaPriceTypeEnum) {
    MediaPriceTypeEnum["Free"] = "free";
    MediaPriceTypeEnum["PartlySubscription"] = "partly_subscription";
    MediaPriceTypeEnum["OnlySubscription"] = "only_subscription";
  })(MediaPriceTypeEnum = SmartSettings.MediaPriceTypeEnum || (SmartSettings.MediaPriceTypeEnum = {}));
})(SmartSettings || (SmartSettings = {}));
var SmartStock;
(function (SmartStock) {
  let StockAdjustmentTypeEnum;
  (function (StockAdjustmentTypeEnum) {
    StockAdjustmentTypeEnum["Acquisition"] = "Acquisition";
    StockAdjustmentTypeEnum["WriteOff"] = "Write-off";
    StockAdjustmentTypeEnum["Transfer"] = "Transfer";
  })(StockAdjustmentTypeEnum = SmartStock.StockAdjustmentTypeEnum || (SmartStock.StockAdjustmentTypeEnum = {}));
  let ExpireTypeEnum;
  (function (ExpireTypeEnum) {
    ExpireTypeEnum["NoExpire"] = "no_expire";
    ExpireTypeEnum["YesExpire"] = "yes_expire";
  })(ExpireTypeEnum = SmartStock.ExpireTypeEnum || (SmartStock.ExpireTypeEnum = {}));
  let FromStockLocationTypeEnum;
  (function (FromStockLocationTypeEnum) {
    FromStockLocationTypeEnum["LocationManager"] = "location_manager";
    FromStockLocationTypeEnum["ProductLocationManager"] = "product_location_manager";
  })(FromStockLocationTypeEnum = SmartStock.FromStockLocationTypeEnum || (SmartStock.FromStockLocationTypeEnum = {}));
  let ToStockLocationTypeEnum;
  (function (ToStockLocationTypeEnum) {
    ToStockLocationTypeEnum["LocationManager"] = "location_manager";
    ToStockLocationTypeEnum["ProductLocationManager"] = "product_location_manager";
  })(ToStockLocationTypeEnum = SmartStock.ToStockLocationTypeEnum || (SmartStock.ToStockLocationTypeEnum = {}));
})(SmartStock || (SmartStock = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var SystemTaxRule;
(function (SystemTaxRule) {
  let CalculationTypeEnum;
  (function (CalculationTypeEnum) {
    CalculationTypeEnum["Percentage"] = "Percentage";
    CalculationTypeEnum["FixAmountForEachDay"] = "Fix amount for each day";
    CalculationTypeEnum["OneTimeFee"] = "One time fee";
  })(CalculationTypeEnum = SystemTaxRule.CalculationTypeEnum || (SystemTaxRule.CalculationTypeEnum = {}));
  let LocationLimitTypeEnum;
  (function (LocationLimitTypeEnum) {
    LocationLimitTypeEnum["NoLimit"] = "No limit";
    LocationLimitTypeEnum["Location"] = "Location";
  })(LocationLimitTypeEnum = SystemTaxRule.LocationLimitTypeEnum || (SystemTaxRule.LocationLimitTypeEnum = {}));
})(SystemTaxRule || (SystemTaxRule = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var Task;
(function (Task) {
  let TimePlaningEnum;
  (function (TimePlaningEnum) {
    TimePlaningEnum["Disable"] = "disable";
    TimePlaningEnum["Enable"] = "enable";
  })(TimePlaningEnum = Task.TimePlaningEnum || (Task.TimePlaningEnum = {}));
  let RepeatTermEnum;
  (function (RepeatTermEnum) {
    RepeatTermEnum["Day"] = "day";
    RepeatTermEnum["Week"] = "week";
    RepeatTermEnum["Month"] = "month";
    RepeatTermEnum["Year"] = "year";
  })(RepeatTermEnum = Task.RepeatTermEnum || (Task.RepeatTermEnum = {}));
  let RepeatEveryMonthTypeEnum;
  (function (RepeatEveryMonthTypeEnum) {
    RepeatEveryMonthTypeEnum["Day"] = "day";
    RepeatEveryMonthTypeEnum["Week"] = "week";
  })(RepeatEveryMonthTypeEnum = Task.RepeatEveryMonthTypeEnum || (Task.RepeatEveryMonthTypeEnum = {}));
  let RepeatEveryMonthWeekOptionEnum;
  (function (RepeatEveryMonthWeekOptionEnum) {
    RepeatEveryMonthWeekOptionEnum["FirstWeek"] = "first week";
    RepeatEveryMonthWeekOptionEnum["SecondWeek"] = "second week";
    RepeatEveryMonthWeekOptionEnum["ThirdWeek"] = "third week";
    RepeatEveryMonthWeekOptionEnum["FourthWeek"] = "fourth week";
    RepeatEveryMonthWeekOptionEnum["LastWeek"] = "last week";
  })(RepeatEveryMonthWeekOptionEnum = Task.RepeatEveryMonthWeekOptionEnum || (Task.RepeatEveryMonthWeekOptionEnum = {}));
  let RepeatWeekTermDayEnum;
  (function (RepeatWeekTermDayEnum) {
    RepeatWeekTermDayEnum["Monday"] = "Monday";
    RepeatWeekTermDayEnum["Tuesday"] = "Tuesday";
    RepeatWeekTermDayEnum["Wednesday"] = "Wednesday";
    RepeatWeekTermDayEnum["Thursday"] = "Thursday";
    RepeatWeekTermDayEnum["Friday"] = "Friday";
    RepeatWeekTermDayEnum["Saturday"] = "Saturday";
    RepeatWeekTermDayEnum["Sunday"] = "Sunday";
  })(RepeatWeekTermDayEnum = Task.RepeatWeekTermDayEnum || (Task.RepeatWeekTermDayEnum = {}));
  let RepeatEveryYearTypeEnum;
  (function (RepeatEveryYearTypeEnum) {
    RepeatEveryYearTypeEnum["DayMonth"] = "day_month";
    RepeatEveryYearTypeEnum["DayWeekMonth"] = "day_week_month";
  })(RepeatEveryYearTypeEnum = Task.RepeatEveryYearTypeEnum || (Task.RepeatEveryYearTypeEnum = {}));
  let RepeatEveryYearDayMonthSelectEnum;
  (function (RepeatEveryYearDayMonthSelectEnum) {
    RepeatEveryYearDayMonthSelectEnum["January"] = "January";
    RepeatEveryYearDayMonthSelectEnum["February"] = "February";
    RepeatEveryYearDayMonthSelectEnum["March"] = "March";
    RepeatEveryYearDayMonthSelectEnum["April"] = "April";
    RepeatEveryYearDayMonthSelectEnum["May"] = "May";
    RepeatEveryYearDayMonthSelectEnum["June"] = "June";
    RepeatEveryYearDayMonthSelectEnum["July"] = "July";
    RepeatEveryYearDayMonthSelectEnum["August"] = "August";
    RepeatEveryYearDayMonthSelectEnum["September"] = "September";
    RepeatEveryYearDayMonthSelectEnum["October"] = "October";
    RepeatEveryYearDayMonthSelectEnum["November"] = "November";
    RepeatEveryYearDayMonthSelectEnum["December"] = "December";
  })(RepeatEveryYearDayMonthSelectEnum = Task.RepeatEveryYearDayMonthSelectEnum || (Task.RepeatEveryYearDayMonthSelectEnum = {}));
  let RepeatEveryYearDayWeekMonthSelectEnum;
  (function (RepeatEveryYearDayWeekMonthSelectEnum) {
    RepeatEveryYearDayWeekMonthSelectEnum["FirstWeek"] = "first week";
    RepeatEveryYearDayWeekMonthSelectEnum["SecondWeek"] = "second week";
    RepeatEveryYearDayWeekMonthSelectEnum["ThirdWeek"] = "third week";
    RepeatEveryYearDayWeekMonthSelectEnum["FourthWeek"] = "fourth week";
    RepeatEveryYearDayWeekMonthSelectEnum["LastWeek"] = "last week";
  })(RepeatEveryYearDayWeekMonthSelectEnum = Task.RepeatEveryYearDayWeekMonthSelectEnum || (Task.RepeatEveryYearDayWeekMonthSelectEnum = {}));
  let RepeatEveryYearDayWeekMonthWeekTermDayEnum;
  (function (RepeatEveryYearDayWeekMonthWeekTermDayEnum) {
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Monday"] = "Monday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Tuesday"] = "Tuesday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Wednesday"] = "Wednesday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Thursday"] = "Thursday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Friday"] = "Friday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Saturday"] = "Saturday";
    RepeatEveryYearDayWeekMonthWeekTermDayEnum["Sunday"] = "Sunday";
  })(RepeatEveryYearDayWeekMonthWeekTermDayEnum = Task.RepeatEveryYearDayWeekMonthWeekTermDayEnum || (Task.RepeatEveryYearDayWeekMonthWeekTermDayEnum = {}));
  let RepeatEveryYearDayWeekMonthMonthSelectEnum;
  (function (RepeatEveryYearDayWeekMonthMonthSelectEnum) {
    RepeatEveryYearDayWeekMonthMonthSelectEnum["January"] = "January";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["February"] = "February";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["March"] = "March";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["April"] = "April";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["May"] = "May";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["June"] = "June";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["July"] = "July";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["August"] = "August";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["September"] = "September";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["October"] = "October";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["November"] = "November";
    RepeatEveryYearDayWeekMonthMonthSelectEnum["December"] = "December";
  })(RepeatEveryYearDayWeekMonthMonthSelectEnum = Task.RepeatEveryYearDayWeekMonthMonthSelectEnum || (Task.RepeatEveryYearDayWeekMonthMonthSelectEnum = {}));
  let RepeatUntilTypeEnum;
  (function (RepeatUntilTypeEnum) {
    RepeatUntilTypeEnum["NoEnd"] = "no_end";
    RepeatUntilTypeEnum["EndDate"] = "end_date";
    RepeatUntilTypeEnum["ComplatedNumber"] = "complated_number";
  })(RepeatUntilTypeEnum = Task.RepeatUntilTypeEnum || (Task.RepeatUntilTypeEnum = {}));
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["Prospect"] = "Prospect";
    ServiceTypeEnum["Lead"] = "Lead";
    ServiceTypeEnum["Offer"] = "Offer";
    ServiceTypeEnum["Deal"] = "Deal";
    ServiceTypeEnum["Coworker"] = "Coworker";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartAd"] = "smart_ad";
  })(ServiceTypeEnum = Task.ServiceTypeEnum || (Task.ServiceTypeEnum = {}));
  let TaskTypeEnum;
  (function (TaskTypeEnum) {
    TaskTypeEnum["Simple"] = "simple";
    TaskTypeEnum["Template"] = "template";
  })(TaskTypeEnum = Task.TaskTypeEnum || (Task.TaskTypeEnum = {}));
})(Task || (Task = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var TaskRule;
(function (TaskRule) {
  let TaskManagementEnum;
  (function (TaskManagementEnum) {
    TaskManagementEnum["AccessDenied"] = "Access denied";
    TaskManagementEnum["FullAccess"] = "Full access";
    TaskManagementEnum["OnlyView"] = "Only view";
    TaskManagementEnum["OnlyViewEdit"] = "Only view edit";
  })(TaskManagementEnum = TaskRule.TaskManagementEnum || (TaskRule.TaskManagementEnum = {}));
  let TaskSettingsEnum;
  (function (TaskSettingsEnum) {
    TaskSettingsEnum["AccessDenied"] = "Access denied";
    TaskSettingsEnum["FullAccess"] = "Full access";
    TaskSettingsEnum["OnlyView"] = "Only view";
    TaskSettingsEnum["OnlyViewEdit"] = "Only view edit";
  })(TaskSettingsEnum = TaskRule.TaskSettingsEnum || (TaskRule.TaskSettingsEnum = {}));
})(TaskRule || (TaskRule = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var TaskWorkGroup;
(function (TaskWorkGroup) {
  let CollaborationTypeEnum;
  (function (CollaborationTypeEnum) {
    CollaborationTypeEnum["Project"] = "project";
    CollaborationTypeEnum["Workgroup"] = "workgroup";
  })(CollaborationTypeEnum = TaskWorkGroup.CollaborationTypeEnum || (TaskWorkGroup.CollaborationTypeEnum = {}));
  let PrivacyTypeEnum;
  (function (PrivacyTypeEnum) {
    PrivacyTypeEnum["Public"] = "public";
    PrivacyTypeEnum["Private"] = "private";
    PrivacyTypeEnum["Hidden"] = "hidden";
  })(PrivacyTypeEnum = TaskWorkGroup.PrivacyTypeEnum || (TaskWorkGroup.PrivacyTypeEnum = {}));
})(TaskWorkGroup || (TaskWorkGroup = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var TaskWorkGroupRule;
(function (TaskWorkGroupRule) {
  let TaskManagementEnum;
  (function (TaskManagementEnum) {
    TaskManagementEnum["AccessDenied"] = "Access denied";
    TaskManagementEnum["FullAccess"] = "Full access";
    TaskManagementEnum["OnlyView"] = "Only view";
    TaskManagementEnum["OnlyViewEdit"] = "Only view edit";
  })(TaskManagementEnum = TaskWorkGroupRule.TaskManagementEnum || (TaskWorkGroupRule.TaskManagementEnum = {}));
  let TaskSettingsEnum;
  (function (TaskSettingsEnum) {
    TaskSettingsEnum["AccessDenied"] = "Access denied";
    TaskSettingsEnum["FullAccess"] = "Full access";
    TaskSettingsEnum["OnlyView"] = "Only view";
    TaskSettingsEnum["OnlyViewEdit"] = "Only view edit";
  })(TaskSettingsEnum = TaskWorkGroupRule.TaskSettingsEnum || (TaskWorkGroupRule.TaskSettingsEnum = {}));
})(TaskWorkGroupRule || (TaskWorkGroupRule = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var TransactionSerializers;
(function (TransactionSerializers) {
  let TypeEnum;
  (function (TypeEnum) {
    TypeEnum["SubscriptionElite"] = "subscription_elite";
    TypeEnum["SubscriptionBeginner"] = "subscription_beginner";
    TypeEnum["SubscriptionProfessional"] = "subscription_professional";
    TypeEnum["SubscriptionEmpire"] = "subscription_empire";
    TypeEnum["AdvertiseBalanceUpload"] = "advertise_balance_upload";
    TypeEnum["AdvertiseSpent"] = "advertise_spent";
    TypeEnum["Adds"] = "adds";
    TypeEnum["FxConvert"] = "fx_convert";
    TypeEnum["SubscriptionDiamond"] = "subscription_diamond";
    TypeEnum["SubscriptionVip"] = "subscription_vip";
    TypeEnum["BalanceUpload"] = "balance_upload";
    TypeEnum["PoInstantCommission"] = "po_instant_commission";
    TypeEnum["PoPortfolioCommission"] = "po_portfolio_commission";
  })(TypeEnum = TransactionSerializers.TypeEnum || (TransactionSerializers.TypeEnum = {}));
  let StatusEnum;
  (function (StatusEnum) {
    StatusEnum["Pending"] = "pending";
    StatusEnum["Paid"] = "paid";
    StatusEnum["Cancelled"] = "cancelled";
  })(StatusEnum = TransactionSerializers.StatusEnum || (TransactionSerializers.StatusEnum = {}));
})(TransactionSerializers || (TransactionSerializers = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var UniversalLanguage;
(function (UniversalLanguage) {
  let ServiceTypeEnum;
  (function (ServiceTypeEnum) {
    ServiceTypeEnum["AlbumDetail"] = "album_detail";
    ServiceTypeEnum["AdMediaDetail"] = "ad_media_detail";
    ServiceTypeEnum["CustomFieldDetail"] = "custom_field_detail";
    ServiceTypeEnum["CustomFieldPrefix"] = "custom_field_prefix";
    ServiceTypeEnum["CustomFieldSuffix"] = "custom_field_suffix";
    ServiceTypeEnum["SiUnitDetail"] = "si_unit_detail";
    ServiceTypeEnum["DigitalProduct"] = "digital_product";
    ServiceTypeEnum["PhysicalProduct"] = "physical_product";
    ServiceTypeEnum["Rent"] = "rent";
    ServiceTypeEnum["DigitalMedia"] = "digital_media";
    ServiceTypeEnum["Booking"] = "booking";
    ServiceTypeEnum["Job"] = "job";
    ServiceTypeEnum["FoodBeverage"] = "food_beverage";
    ServiceTypeEnum["ServiceOffered"] = "service_offered";
    ServiceTypeEnum["ActivityStream"] = "activity_stream";
    ServiceTypeEnum["ActivityStreamPoll"] = "activity_stream_poll";
    ServiceTypeEnum["Product"] = "product";
    ServiceTypeEnum["TargetMarket"] = "target_market";
    ServiceTypeEnum["Catalog"] = "catalog";
    ServiceTypeEnum["ProductLongDescription"] = "product_long_description";
    ServiceTypeEnum["DisclaimerPolicySmartWorkflow"] = "disclaimer_policy_smart_workflow";
    ServiceTypeEnum["ShippingPolicySmartWorkflow"] = "shipping_policy_smart_workflow";
    ServiceTypeEnum["InsurancePolicySmartWorkflow"] = "insurance_policy_smart_workflow";
    ServiceTypeEnum["RefundPolicySmartWorkflow"] = "refund_policy_smart_workflow";
    ServiceTypeEnum["DomainPreviewWidgetCollection"] = "domain_preview_widget_collection";
    ServiceTypeEnum["DomainPreviewSite"] = "domain_preview_site";
    ServiceTypeEnum["DomainPreviewPage"] = "domain_preview_page";
    ServiceTypeEnum["MarketDiscount"] = "market_discount";
    ServiceTypeEnum["SmartItem"] = "smart_item";
    ServiceTypeEnum["SmartItemLongDescription"] = "smart_item_long_description";
    ServiceTypeEnum["SmartAdLongDescription"] = "smart_ad_long_description";
  })(ServiceTypeEnum = UniversalLanguage.ServiceTypeEnum || (UniversalLanguage.ServiceTypeEnum = {}));
})(UniversalLanguage || (UniversalLanguage = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var UpdatePrivacyRequestRequest;
(function (UpdatePrivacyRequestRequest) {
  let WhoFollowEnum;
  (function (WhoFollowEnum) {
    WhoFollowEnum["Private"] = "private";
    WhoFollowEnum["Anybody"] = "anybody";
  })(WhoFollowEnum = UpdatePrivacyRequestRequest.WhoFollowEnum || (UpdatePrivacyRequestRequest.WhoFollowEnum = {}));
  let EmailAddressVisibilityEnum;
  (function (EmailAddressVisibilityEnum) {
    EmailAddressVisibilityEnum["Nobody"] = "nobody";
    EmailAddressVisibilityEnum["Anybody"] = "anybody";
    EmailAddressVisibilityEnum["ThoseFollowMe"] = "those_follow_me";
    EmailAddressVisibilityEnum["WhoIFollow"] = "who_i_follow";
  })(EmailAddressVisibilityEnum = UpdatePrivacyRequestRequest.EmailAddressVisibilityEnum || (UpdatePrivacyRequestRequest.EmailAddressVisibilityEnum = {}));
  let BirthdayEnum;
  (function (BirthdayEnum) {
    BirthdayEnum["Nobody"] = "nobody";
    BirthdayEnum["Anybody"] = "anybody";
    BirthdayEnum["ThoseFollowMe"] = "those_follow_me";
    BirthdayEnum["WhoIFollow"] = "who_i_follow";
  })(BirthdayEnum = UpdatePrivacyRequestRequest.BirthdayEnum || (UpdatePrivacyRequestRequest.BirthdayEnum = {}));
  let InviteToErpEnum;
  (function (InviteToErpEnum) {
    InviteToErpEnum["Nobody"] = "nobody";
    InviteToErpEnum["Anybody"] = "anybody";
    InviteToErpEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToErpEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToErpEnum = UpdatePrivacyRequestRequest.InviteToErpEnum || (UpdatePrivacyRequestRequest.InviteToErpEnum = {}));
  let InviteToCommunityEnum;
  (function (InviteToCommunityEnum) {
    InviteToCommunityEnum["Nobody"] = "nobody";
    InviteToCommunityEnum["Anybody"] = "anybody";
    InviteToCommunityEnum["ThoseFollowMe"] = "those_follow_me";
    InviteToCommunityEnum["WhoIFollow"] = "who_i_follow";
  })(InviteToCommunityEnum = UpdatePrivacyRequestRequest.InviteToCommunityEnum || (UpdatePrivacyRequestRequest.InviteToCommunityEnum = {}));
  let MarkOnMediaEnum;
  (function (MarkOnMediaEnum) {
    MarkOnMediaEnum["Nobody"] = "nobody";
    MarkOnMediaEnum["Anybody"] = "anybody";
    MarkOnMediaEnum["ThoseFollowMe"] = "those_follow_me";
    MarkOnMediaEnum["WhoIFollow"] = "who_i_follow";
  })(MarkOnMediaEnum = UpdatePrivacyRequestRequest.MarkOnMediaEnum || (UpdatePrivacyRequestRequest.MarkOnMediaEnum = {}));
  let MessageMeEnum;
  (function (MessageMeEnum) {
    MessageMeEnum["Nobody"] = "nobody";
    MessageMeEnum["Anybody"] = "anybody";
    MessageMeEnum["ThoseFollowMe"] = "those_follow_me";
    MessageMeEnum["WhoIFollow"] = "who_i_follow";
  })(MessageMeEnum = UpdatePrivacyRequestRequest.MessageMeEnum || (UpdatePrivacyRequestRequest.MessageMeEnum = {}));
  let LocationFindMeEnum;
  (function (LocationFindMeEnum) {
    LocationFindMeEnum["Nobody"] = "nobody";
    LocationFindMeEnum["Anybody"] = "anybody";
    LocationFindMeEnum["ThoseFollowMe"] = "those_follow_me";
    LocationFindMeEnum["WhoIFollow"] = "who_i_follow";
  })(LocationFindMeEnum = UpdatePrivacyRequestRequest.LocationFindMeEnum || (UpdatePrivacyRequestRequest.LocationFindMeEnum = {}));
  let LocationShowEnum;
  (function (LocationShowEnum) {
    LocationShowEnum["DontShow"] = "dont_show";
    LocationShowEnum["Country"] = "country";
    LocationShowEnum["CountryAndCity"] = "country_and_city";
  })(LocationShowEnum = UpdatePrivacyRequestRequest.LocationShowEnum || (UpdatePrivacyRequestRequest.LocationShowEnum = {}));
})(UpdatePrivacyRequestRequest || (UpdatePrivacyRequestRequest = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

var UserBusiness;
(function (UserBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = UserBusiness.SubscriptionTypeEnum || (UserBusiness.SubscriptionTypeEnum = {}));
})(UserBusiness || (UserBusiness = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var UserCollectionRequest;
(function (UserCollectionRequest) {
  let SaveStatusEnum;
  (function (SaveStatusEnum) {
    SaveStatusEnum["SavedActive"] = "saved_active";
    SaveStatusEnum["UnSavedActive"] = "un_saved_active";
  })(SaveStatusEnum = UserCollectionRequest.SaveStatusEnum || (UserCollectionRequest.SaveStatusEnum = {}));
  let CompletionStatusEnum;
  (function (CompletionStatusEnum) {
    CompletionStatusEnum["NotStarted"] = "not_started";
    CompletionStatusEnum["InProgress"] = "in_progress";
    CompletionStatusEnum["Completed"] = "completed";
    CompletionStatusEnum["Uncompleted"] = "uncompleted";
  })(CompletionStatusEnum = UserCollectionRequest.CompletionStatusEnum || (UserCollectionRequest.CompletionStatusEnum = {}));
  let CollectionTypeEnum;
  (function (CollectionTypeEnum) {
    CollectionTypeEnum["IsHaveCollection"] = "is_have_collection";
    CollectionTypeEnum["IsFavoriteWish"] = "is_favorite_wish";
  })(CollectionTypeEnum = UserCollectionRequest.CollectionTypeEnum || (UserCollectionRequest.CollectionTypeEnum = {}));
})(UserCollectionRequest || (UserCollectionRequest = {}));
var UserCollectionResponse;
(function (UserCollectionResponse) {
  let CollectionTypeEnum;
  (function (CollectionTypeEnum) {
    CollectionTypeEnum["IsHaveCollection"] = "is_have_collection";
    CollectionTypeEnum["IsFavoriteWish"] = "is_favorite_wish";
  })(CollectionTypeEnum = UserCollectionResponse.CollectionTypeEnum || (UserCollectionResponse.CollectionTypeEnum = {}));
})(UserCollectionResponse || (UserCollectionResponse = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var UserCommonApplicationProfileSettings;
(function (UserCommonApplicationProfileSettings) {
  let CommunityApplicationTypeEnum;
  (function (CommunityApplicationTypeEnum) {
    CommunityApplicationTypeEnum["LanguageExchange"] = "language_exchange";
    CommunityApplicationTypeEnum["BusinessNetworking"] = "business_networking";
    CommunityApplicationTypeEnum["Dating"] = "dating";
    CommunityApplicationTypeEnum["Garden"] = "garden";
    CommunityApplicationTypeEnum["TravelBuddies"] = "travel_buddies";
    CommunityApplicationTypeEnum["SecondChance"] = "second_chance";
    CommunityApplicationTypeEnum["BeGreen"] = "be_green";
    CommunityApplicationTypeEnum["BucketList"] = "bucket_list";
    CommunityApplicationTypeEnum["GoalManager"] = "goal_manager";
    CommunityApplicationTypeEnum["Pets"] = "pets";
    CommunityApplicationTypeEnum["Volunteer"] = "volunteer";
    CommunityApplicationTypeEnum["Cooking"] = "cooking";
    CommunityApplicationTypeEnum["Collectors"] = "collectors";
    CommunityApplicationTypeEnum["AgricultureLivestock"] = "agriculture_livestock";
    CommunityApplicationTypeEnum["KidsSpace"] = "kids_space";
    CommunityApplicationTypeEnum["Sport"] = "sport";
    CommunityApplicationTypeEnum["Mindset"] = "mindset";
    CommunityApplicationTypeEnum["DiyHandmade"] = "diy_handmade";
    CommunityApplicationTypeEnum["Tech"] = "tech";
    CommunityApplicationTypeEnum["CryptoBlockchain"] = "crypto_blockchain";
    CommunityApplicationTypeEnum["BToB"] = "b_to_b";
    CommunityApplicationTypeEnum["DigitalNomad"] = "digital_nomad";
    CommunityApplicationTypeEnum["Gaming"] = "gaming";
    CommunityApplicationTypeEnum["Erotic"] = "erotic";
    CommunityApplicationTypeEnum["Roommate"] = "roommate";
    CommunityApplicationTypeEnum["Aviation"] = "aviation";
    CommunityApplicationTypeEnum["Cars"] = "cars";
    CommunityApplicationTypeEnum["Boat"] = "boat";
    CommunityApplicationTypeEnum["Fitness"] = "fitness";
    CommunityApplicationTypeEnum["Events"] = "events";
    CommunityApplicationTypeEnum["TaskManager"] = "task_manager";
    CommunityApplicationTypeEnum["Services"] = "services";
    CommunityApplicationTypeEnum["Academy"] = "academy";
    CommunityApplicationTypeEnum["StoreFinder"] = "store_finder";
    CommunityApplicationTypeEnum["ActivityStream"] = "activity_stream";
    CommunityApplicationTypeEnum["Calendar"] = "calendar";
    CommunityApplicationTypeEnum["Contacts"] = "contacts";
    CommunityApplicationTypeEnum["Notes"] = "notes";
    CommunityApplicationTypeEnum["Drive"] = "drive";
    CommunityApplicationTypeEnum["Taskly"] = "taskly";
    CommunityApplicationTypeEnum["MarketSpace"] = "market_space";
    CommunityApplicationTypeEnum["Chat"] = "chat";
    CommunityApplicationTypeEnum["DoctorFinder"] = "doctor_finder";
    CommunityApplicationTypeEnum["WellnessBeauty"] = "wellness_beauty";
    CommunityApplicationTypeEnum["Renting"] = "renting";
    CommunityApplicationTypeEnum["Booking"] = "booking";
    CommunityApplicationTypeEnum["Job"] = "job";
    CommunityApplicationTypeEnum["DigitalDownloads"] = "digital_downloads";
    CommunityApplicationTypeEnum["PhysicalProduct"] = "physical_product";
    CommunityApplicationTypeEnum["EmailManager"] = "email_manager";
    CommunityApplicationTypeEnum["RealEstate"] = "real_estate";
    CommunityApplicationTypeEnum["CoworkingSpace"] = "coworking_space";
    CommunityApplicationTypeEnum["Grocery"] = "grocery";
    CommunityApplicationTypeEnum["Fashion"] = "fashion";
    CommunityApplicationTypeEnum["HomeDesign"] = "home_design";
    CommunityApplicationTypeEnum["Taxi"] = "taxi";
    CommunityApplicationTypeEnum["SkillLink"] = "skill_link";
    CommunityApplicationTypeEnum["MediaChannels"] = "media_channels";
  })(CommunityApplicationTypeEnum = UserCommonApplicationProfileSettings.CommunityApplicationTypeEnum || (UserCommonApplicationProfileSettings.CommunityApplicationTypeEnum = {}));
})(UserCommonApplicationProfileSettings || (UserCommonApplicationProfileSettings = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */
var UserHrmsBusiness;
(function (UserHrmsBusiness) {
  let SubscriptionTypeEnum;
  (function (SubscriptionTypeEnum) {
    SubscriptionTypeEnum["Free"] = "Free";
    SubscriptionTypeEnum["Elite"] = "Elite";
    SubscriptionTypeEnum["Beginner"] = "Beginner";
    SubscriptionTypeEnum["Professional"] = "Professional";
    SubscriptionTypeEnum["Empire"] = "Empire";
    SubscriptionTypeEnum["Diamond"] = "Diamond";
    SubscriptionTypeEnum["Vip"] = "VIP";
  })(SubscriptionTypeEnum = UserHrmsBusiness.SubscriptionTypeEnum || (UserHrmsBusiness.SubscriptionTypeEnum = {}));
  let IsFollowEnum;
  (function (IsFollowEnum) {
    IsFollowEnum["Block"] = "block";
    IsFollowEnum["Unblock"] = "unblock";
    IsFollowEnum["Follow"] = "follow";
    IsFollowEnum["Pending"] = "pending";
    IsFollowEnum["Reject"] = "reject";
    IsFollowEnum["Accept"] = "accept";
    IsFollowEnum["Delete"] = "delete";
    IsFollowEnum["NotFollow"] = "not_follow";
    IsFollowEnum["Report"] = "report";
    IsFollowEnum["UnReport"] = "un_report";
    IsFollowEnum["Done"] = "done";
    IsFollowEnum["SavedActive"] = "saved_active";
    IsFollowEnum["UnSavedActive"] = "un_saved_active";
    IsFollowEnum["CrmSave"] = "crm_save";
    IsFollowEnum["CrmUnSave"] = "crm_un_save";
    IsFollowEnum["NotRequest"] = "not_request";
    IsFollowEnum["None"] = "none";
    IsFollowEnum["SavedEvent"] = "saved_event";
    IsFollowEnum["UnSavedEvent"] = "un_saved_event";
  })(IsFollowEnum = UserHrmsBusiness.IsFollowEnum || (UserHrmsBusiness.IsFollowEnum = {}));
  let IsReportedEnum;
  (function (IsReportedEnum) {
    IsReportedEnum["Block"] = "block";
    IsReportedEnum["Unblock"] = "unblock";
    IsReportedEnum["Follow"] = "follow";
    IsReportedEnum["Pending"] = "pending";
    IsReportedEnum["Reject"] = "reject";
    IsReportedEnum["Accept"] = "accept";
    IsReportedEnum["Delete"] = "delete";
    IsReportedEnum["NotFollow"] = "not_follow";
    IsReportedEnum["Report"] = "report";
    IsReportedEnum["UnReport"] = "un_report";
    IsReportedEnum["Done"] = "done";
    IsReportedEnum["SavedActive"] = "saved_active";
    IsReportedEnum["UnSavedActive"] = "un_saved_active";
    IsReportedEnum["CrmSave"] = "crm_save";
    IsReportedEnum["CrmUnSave"] = "crm_un_save";
    IsReportedEnum["NotRequest"] = "not_request";
    IsReportedEnum["None"] = "none";
    IsReportedEnum["SavedEvent"] = "saved_event";
    IsReportedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsReportedEnum = UserHrmsBusiness.IsReportedEnum || (UserHrmsBusiness.IsReportedEnum = {}));
  let IsBlockedEnum;
  (function (IsBlockedEnum) {
    IsBlockedEnum["Block"] = "block";
    IsBlockedEnum["Unblock"] = "unblock";
    IsBlockedEnum["Follow"] = "follow";
    IsBlockedEnum["Pending"] = "pending";
    IsBlockedEnum["Reject"] = "reject";
    IsBlockedEnum["Accept"] = "accept";
    IsBlockedEnum["Delete"] = "delete";
    IsBlockedEnum["NotFollow"] = "not_follow";
    IsBlockedEnum["Report"] = "report";
    IsBlockedEnum["UnReport"] = "un_report";
    IsBlockedEnum["Done"] = "done";
    IsBlockedEnum["SavedActive"] = "saved_active";
    IsBlockedEnum["UnSavedActive"] = "un_saved_active";
    IsBlockedEnum["CrmSave"] = "crm_save";
    IsBlockedEnum["CrmUnSave"] = "crm_un_save";
    IsBlockedEnum["NotRequest"] = "not_request";
    IsBlockedEnum["None"] = "none";
    IsBlockedEnum["SavedEvent"] = "saved_event";
    IsBlockedEnum["UnSavedEvent"] = "un_saved_event";
  })(IsBlockedEnum = UserHrmsBusiness.IsBlockedEnum || (UserHrmsBusiness.IsBlockedEnum = {}));
})(UserHrmsBusiness || (UserHrmsBusiness = {}));
var UserInvitation;
(function (UserInvitation) {
  let InvitationExpireEnum;
  (function (InvitationExpireEnum) {
    InvitationExpireEnum["No"] = "no";
    InvitationExpireEnum["Date"] = "date";
  })(InvitationExpireEnum = UserInvitation.InvitationExpireEnum || (UserInvitation.InvitationExpireEnum = {}));
})(UserInvitation || (UserInvitation = {}));

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

/**
 * Offconon API
 * Offconon API
 *
 * The version of the OpenAPI document: 1.0.0
 * Do not edit the class manually.
 */

class ApiModule {
  static forRoot(configurationFactory) {
    return {
      ngModule: ApiModule,
      providers: [{
        provide: Configuration,
        useFactory: configurationFactory
      }]
    };
  }
  constructor(parentModule, http) {
    if (parentModule) {
      throw new Error('ApiModule is already loaded. Import in your base AppModule only.');
    }
    if (!http) {
      throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
    }
  }
  static ɵfac = function ApiModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApiModule)(i0.ɵɵinject(ApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: ApiModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ApiModule, [{
    type: NgModule,
    args: [{
      imports: [],
      declarations: [],
      exports: [],
      providers: []
    }]
  }], () => [{
    type: ApiModule,
    decorators: [{
      type: Optional
    }, {
      type: SkipSelf
    }]
  }, {
    type: i1.HttpClient,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/*
 * Public API Surface of core-api
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, AccessOptionPattern, AccessOptionRule, AccountingService, ActivateCommunityApplicationProfileRequest, ActivityBusiness, ActivityCoWorker, ActivityMediaAlbumItem, ActivityStreamService, ActivityUser, AddressBookModel, AddressBookRequest, AdsService, Album, AlbumItems, AllBusiness, ApiModule, ApiService, ApplicationObject, AuthService, BASE_PATH, BankDatabase, BankTransfer, BanksService, BasicCalculation, BidService, BrandedDomainHandlingService, BulkEditRequest, Business, BusinessActiveCoWorker, BusinessCoWorker, BusinessProfileRequestRequest, BusinessRulesService, COLLECTION_FORMATS, Calendar, CalendarAppointment, CalendarAppointmentResponse, CalendarAvilability, CalendarBreak, CalendarBreakTimeCreateOrUpdate, CalendarBreakTimeCreateOrUpdateRequest, CalendarCalendarDayPriceCreateOrUpdate, CalendarCalendarDayPriceCreateOrUpdateRequest, CalendarCalendarHourPriceCreateOrUpdate, CalendarCalendarHourPriceCreateOrUpdateRequest, CalendarCreateOrUpdateRequest, CalendarPrice, CalendarPriceItemWorkFlow, CalendarPricingResponse, CalendarService, CalendarTimePeriodPriceCreateOrUpdate, CalendarTimePeriodPriceCreateOrUpdateRequest, Catalog, CatalogCollectionResponse, ChangeProfileRequestRequest, ChatService, Client, CombinedSubscriptionResponse, CommonAddon, CommonApplicationProfileSettings, CommonApplicationProfileSettingsLocation, CommonBusiness, CommonCategoryTemplate, CommonCoWorker, CommonContent, CommonCustomFieldCategory, CommonEmailBusiness, CommonEntityPolymorphic, CommonJobCoWorker, CommonLeaderCoWorker, CommonLongBusiness, CommonLongUser, CommonMediaContent, CommonProspectUser, CommonShortUser, CommonSiUnits, CommonUser, CommunityApplicationLanguage, CommunityApplicationProfile, CommunityApplicationService, Configuration, ContactsService, Content, CoworkersService, CreateCodeRequest, CreatePostRequest, CrmAddress, CrmEmail, CrmHistoryTypeEnum, CrmItem, CrmLead, CrmNotify, CrmOffer, CrmPhone, CrmProspect, CrmProspectRequestRequest, CrmProspectUserProfile, CrmService, CrmTargetedCustomer, CustomFieldArea, CustomerLocation, CustomerLocationDetail, Deal, DealDetailAd, DealRatings, DetailRatingBusiness, DetailedAlbumItem, DetailedBusinesses, Domain, DomainPage, DomainPreviewPage, DomainPreviewSite, DomainPreviewWidgetCollection, DomainPureTranslation, DomainSite, DriveAlbum, DriveAlbumItem, DriveService, ElasticSearchBusiness, ElasticSearchPublic, ElasticService, EmailClientService, EmployeeRuleByCoWorkerRule, ErpBusinesses, ErpRegistration, ErpRegistrationRequest, ErpService, ErpSideService, ErpSubscriptionPageResponse, FeaturesService, FileManagerService, GetComment, GetContent, GetCrmProspect, GetPostAnswer, History, HrmsBusinessCoWorker, HrmsCoWorker, HrmsCode, HrmsService, InsuranceRule, Interaction, InteractionSmartAd, InteractionsService, Invitation, KeyWord, KeyWordService, Link, LocationLimit, LocationManagerBusiness, LocationManagerBusinessRequestRequest, LocationManagerService, LocationService, LocationTranslation, LoginResponse, LoginWithSocialMediaRequest, MainProfileService, MarketDiscount, MarketTarget, MarketTargetCharacteristic, Message, ModifyUserHrmsInvitationRequest, MrAiTracker, MySubSubscriptionPageResponse, NotificationSystem, NotificationsService, OcSystemAccounting, OrgChartService, PageResponse, PartnerOfficeService, PartnerVerify, PatchedLocationManagerBusinessPatchRequest, PatchedModifyCoWorkerRequest, PatchedPatchCrmProspectRequest, PatchedPatchDomainPageRequest, PatchedPatchDomainSiteRequest, PatchedPatchMarketDiscountRequest, PatchedPatchPostRequest, PatchedPatchRoomRequest, PatchedPatchSmartItemRequest, PatchedPatchTaskRequest, PaymentModify, PaymentOnSpot, PaymentService, Pipeline, PipelineStatus, PostAcceptRequest, PostActivityStream, PostAlbumRequest, PostAvaibilityRequest, PostBankDatabaseRequest, PostBasicCalculationRequest, PostBulkKeyWordServiceRequest, PostBulkSmartPaymentRequest, PostBulkUniversalLanguageRequest, PostCalendarPriceItemRequest, PostCalendarPriceItemWokrFlowRequest, PostCatalogMultiRequest, PostCatalogRequest, PostCategoryTemplateRequest, PostCloudStorageRequest, PostCommentRequest, PostCommunityApplicationSettingsRequest, PostContentRequest, PostContentResponse, PostCrmAddressRequest, PostCrmEmailRequest, PostCrmNotifyRequest, PostCrmPhoneRequest, PostCustomFieldAreaRequest, PostCustomFieldCategoryRequest, PostDealRatingsRequest, PostDomainRequest, PostDomainSiteRequest, PostDomainTranslationRequest, PostDriveAlbumRequest, PostElasticSearchBusinessRequest, PostElasticSearchPublicRequest, PostEmailRequest, PostInsuranceRuleRequest, PostInteractionRequest, PostInvitationRequest, PostKeyWordRequest, PostKeyWordServiceRequest, PostLanguageToCommunityApplicationRequest, PostLinkRequest, PostLocationLimitRequest, PostMarketDiscountRequest, PostMessageTemplateRequest, PostMrAiTrackerRequest, PostOrgChartRequest, PostPartnerVerifyRequest, PostPaymentModifyRequest, PostPaymentOnSpotRequest, PostPersonRequest, PostPipelineRequest, PostPipelineStatusRequest, PostRBACAccessRuleRequest, PostRelateDriveToGalleryRequest, PostReplyEmailRequest, PostResourceExchangeRequest, PostRoomMemberRequest, PostRoomRequest, PostSharedCloudStorageRequest, PostSharedContactRequest, PostShippingRuleRequest, PostSmartAdRequest, PostSmartCheckoutRequestRequest, PostSmartItemRequest, PostSmartItemSelectedCustomFieldRequest, PostSmartLocationManagerRequest, PostSmartPolicyRequest, PostSmartSalesWorkflowRequest, PostSmartSettingsRequest, PostSmartStockRequest, PostSystemTaxLocationLimitRequest, PostSystemTaxRuleRequest, PostTargetMarketCharasteristicRequest, PostTargetMarketRequest, PostTaskRequest, PostTaskRuleRequest, PostTaskWorkGroupRequest, PostUniversalLanguageTranslationRequest, PostUserCommonApplicationProfileSettingsRequest, PostWfSelectedCustomFieldRequest, Privacy, ProfileSettings, ProfileSettingsLocation, PublicBusinessService, PublicMarketDiscount, PublicProfile, PurchaseService, PushNotificationService, RBACAccessRule, RBACGroupAccess, RbacRuleService, RegistrationRequest, ResourceExchange, ResponseCloudStorageDetail, ResponseCloudStorageLimit, ResponseDriveRestore, ResponseRestore, Room, RoomMember, SalesCoWorker, SalesTeamsService, SearchService, SearchSettings, SearchSettingsRequest, SearchSettingsResponse, SelectUniversalLanguageValue, SenderBusiness, SenderUser, ServiceAreaService, SharedContact, SharedEvent, SharedService, ShippingRule, ShoppingCartItem, ShoppingCartService, ShortCatalog, ShortDriveAlbumItem, ShortEmail, ShortLink, ShortPhone, SiUnits, SmartAd, SmartAdDetail, SmartAdPayment, SmartCheckoutResponse, SmartCoreAd, SmartCubeService, SmartItem, SmartLocationManager, SmartPayment, SmartPolicy, SmartSalesWorkflow, SmartSettings, SmartStock, SubscriptionService, SystemService, SystemTaxRule, TargetMarketHandlingService, Task, TaskRule, TaskWorkGroup, TaskWorkGroupRule, TasksService, TransactionSerializers, TranslateService, UniversalLanguage, UpdatePrivacyRequestRequest, UploadService, UserBusiness, UserCollectionRequest, UserCollectionResponse, UserCommonApplicationProfileSettings, UserHrmsBusiness, UserInvitation, UserService, UsersService, WalletService };
